/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { createClient } from '@supabase/supabase-js';
import { buildPedigreeTree, flattenPedigreeTree, MAX_GENERATIONS as PED_MAX_GEN } from './pedigreeTree';
import { pdfjs, Document as PdfDocument, Page as PdfPage } from 'react-pdf';

// Verified via a real react-pdf spike test under this project's CRA build —
// a known friction point for pdfjs-dist worker bundling elsewhere, but this
// CDN-hosted worker config renders correctly here (see PR notes).
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ── Supabase ──────────────────────────────────────────────────────────────────
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lpzfdmrqstxalhsxnviq.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxwemZkbXJxc3R4YWxoc3hudmlxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzczOTI3MDksImV4cCI6MjA5Mjk2ODcwOX0._YWWe3O1cETq7ynZ5OIP0lId5ZOTTVpcpFEMijDasRA';
const supabase = createClient(supabaseUrl, supabaseKey);

// ── Supabase DB Helpers ──────────────────────────────────────────────────────
const SB = {
  // Convert app dog object to DB columns
  dogToDB: (d) => ({
    id: d.id, type: d.type||'purebred', breed: d.breed||'', breed_id: d.breedId||null, designer_breed_id: d.designerBreedId||null, custom_cross_id: d.customCrossId||null, name: d.name||'',
    call_name: d.callName||'', sex: d.sex||'M', color: d.color||'',
    dob: d.dob||null, country: d.country||'United States',
    akc_num: d.akcNum||'', asca_num: d.ascaNum||'', ukc_num: d.ukcNum||'',
    fci_num: d.fciNum||'', kc_num: d.kcNum||'', ckc_num: d.ckcNum||'',
    ankc_num: d.ankcNum||'', nzkc_num: d.nzkcNum||'', sv_num: d.svNum||'',
    arba_num: d.arbaNum||'', napr_num: d.naprNum||'', other_reg_num: d.otherRegNum||'',
    reg_num: d.regNum||'', reg_body: d.regBody||'AKC',
    ofa_hips: d.ofaHips||'', ofa_elbows: d.ofaElbows||'', ofa_eyes: d.ofaEyes||'',
    ofa_hips_cert: d.ofaHipsCert||'', ofa_elbows_cert: d.ofaElbowsCert||'',
    ofa_hips_registry: d.ofaHipsRegistry||'OFA', ofa_hips_di: d.ofaHipsDi||'',
    ofa_cardiac: d.ofaCardiac||'', ofa_thyroid: d.ofaThyroid||'',
    chic_num: d.chicNum||'', cerf_num: d.cerfNum||'', baer: d.baer||'',
    mdr1: d.mdr1||'', vwd: d.vwd||'', dm: d.dm||'', hsf4: d.hsf4||'',
    cea: d.cea||'', brucellosis: d.brucellosis||'', other_health: d.otherHealth||'',
    titles: d.titles||'', breeder_id: d.breederId||null,
    sire_id: d.sireId||null, dam_id: d.damId||null,
    sire_name_manual: d.sireNameManual||'', dam_name_manual: d.damNameManual||'',
    photo_url: d.photoUrl||'', notes: d.notes||'', hidden: d.hidden||false, dna_num: d.dnaNum||'',
    health_notes: d.healthNotes||'', original_kennel: d.originalKennel||'',
    record_owner_id: d.recordOwnerId||null, recorded_by_id: d.recordedById||null,
    record_type: d.recordType||'owned',
    health_verified_override: d.healthVerifiedOverride ?? null,
  }),
  // Convert DB row to app dog object
  dogFromDB: (r) => ({
    id: r.id, type: r.type, breed: r.breed, breedId: r.breed_id, designerBreedId: r.designer_breed_id, customCrossId: r.custom_cross_id, name: r.name,
    callName: r.call_name, sex: r.sex, color: r.color,
    dob: r.dob||'', country: r.country,
    akcNum: r.akc_num, ascaNum: r.asca_num, ukcNum: r.ukc_num,
    fciNum: r.fci_num, kcNum: r.kc_num, ckcNum: r.ckc_num,
    ankcNum: r.ankc_num, nzkcNum: r.nzkc_num, svNum: r.sv_num,
    arbaNum: r.arba_num, naprNum: r.napr_num, otherRegNum: r.other_reg_num,
    regNum: r.reg_num, regBody: r.reg_body,
    ofaHips: r.ofa_hips, ofaElbows: r.ofa_elbows, ofaEyes: r.ofa_eyes,
    ofaHipsCert: r.ofa_hips_cert, ofaElbowsCert: r.ofa_elbows_cert,
    ofaHipsRegistry: r.ofa_hips_registry||'OFA', ofaHipsDi: r.ofa_hips_di,
    ofaCardiac: r.ofa_cardiac, ofaThyroid: r.ofa_thyroid,
    chicNum: r.chic_num, cerfNum: r.cerf_num, baer: r.baer,
    mdr1: r.mdr1, vwd: r.vwd, dm: r.dm, hsf4: r.hsf4,
    cea: r.cea, brucellosis: r.brucellosis, otherHealth: r.other_health,
    titles: r.titles, breederId: r.breeder_id,
    sireId: r.sire_id, damId: r.dam_id,
    sireNameManual: r.sire_name_manual, damNameManual: r.dam_name_manual,
    photoUrl: r.photo_url, notes: r.notes, hidden: r.hidden, dnaNum: r.dna_num||'',
    healthNotes: r.health_notes||'', originalKennel: r.original_kennel||'',
    source: r.source||'manual',
    recordOwnerId: r.record_owner_id||null, recordedById: r.recorded_by_id||null,
    recordType: r.record_type||'owned',
    healthVerifiedOverride: r.health_verified_override,
    // Read-only from the client's perspective — currentOwnerMemberId is set
    // exclusively by the transfer-accept server endpoint, bredByBreederId is
    // set once at creation and locked immutable by a DB trigger. Neither
    // belongs in dogToDB; a regular dog-edit save must never touch them.
    currentOwnerMemberId: r.current_owner_member_id||null,
    bredByBreederId: r.bred_by_breeder_id||null,
  }),
  // Convert breeder
  breederToDB: (b) => ({
    id: b.id, kennel: b.kennel||'', name: b.name||'', email: b.email||'',
    country: b.country||'United States', region: b.region||'',
    website: b.website||'', bio: b.bio||'',
    joined: b.joined||new Date().toISOString().split('T')[0],
    tiers: b.tiers||['foundation'], photo_url: b.photoUrl||'',
    verified: b.verified||false, blocked: b.blocked||false,
    kennel_type: b.kennelType || 'reference',
    kennel_categories: (b.kennelCategories && b.kennelCategories.length) ? b.kennelCategories : ['purebred','designer','custom'],
    is_public: b.isPublic !== false,
    is_elite: b.isElite || false,
    logo_url: b.logoUrl || null,
    external_waitlist_url: b.externalWaitlistUrl || null,
    waitlist_policy: b.waitlistPolicy || null,
    waitlist_payment_link: b.waitlistPaymentLink || null,
  }),
  breederFromDB: (r) => ({
    id: r.id, kennel: r.kennel, name: r.name, email: r.email,
    country: r.country, region: r.region, website: r.website, bio: r.bio,
    joined: r.joined, tiers: r.tiers||['foundation'],
    photoUrl: r.photo_url, verified: r.verified, blocked: r.blocked,
    kennelType: r.kennel_type || 'reference',
    kennelCategories: (r.kennel_categories && r.kennel_categories.length) ? r.kennel_categories : ['purebred','designer','custom'],
    isPublic: r.is_public !== false,
    trustedBreeder: r.trusted_breeder || false,
    isElite: r.is_elite || false,
    logoUrl: r.logo_url || null,
    externalWaitlistUrl: r.external_waitlist_url || null,
    waitlistPolicy: r.waitlist_policy || null,
    waitlistPaymentLink: r.waitlist_payment_link || null,
  }),
  // Dog records (back office)
  recordToDB: (r) => ({
    id: r.id, dog_id: r.dogId, breeder_id: r.breederId,
    record_type: r.recordType, record_date: r.recordDate||null,
    data: r.data||{}, document_url: r.documentUrl||null,
    notes: r.notes||'',
  }),
  recordFromDB: (r) => ({
    id: r.id, dogId: r.dog_id, breederId: r.breeder_id,
    recordType: r.record_type, recordDate: r.record_date||null,
    data: r.data||{}, documentUrl: r.document_url||null,
    notes: r.notes||'', createdAt: r.created_at,
  }),
  // Litter
 litterToDB: (l) => ({
  id: l.id,
  breeder_id: l.breederId || null,
  custom_name: l.customName || null,
  breed: l.breed || '',
  breed_id: l.breedId || null,
  designer_breed_id: l.designerBreedId || null,
  sire_id: l.sireId || null,
  dam_id: l.damId || null,
  whelped: l.whelped || null,
  count: l.count || 0,
  available: l.available || 0,
  colors: l.colors || '',
  country: l.country || 'United States',
  price: l.price || '',
 description: l.description || '',
  status: l.status || 'active',
  litter_status: l.litterStatus || 'current',
  photo_url: l.photoUrl || '',
}),

litterFromDB: (r) => ({
  id: r.id,
  breederId: r.breeder_id,
  customName: r.custom_name || '',
  breed: r.breed,
  breedId: r.breed_id,
  designerBreedId: r.designer_breed_id,
  sireId: r.sire_id,
  damId: r.dam_id,
  whelped: r.whelped || '',
  count: r.count,
  available: r.available,
  colors: r.colors,
  country: r.country,
  price: r.price,
 description: r.description,
  status: r.status,
  litterStatus: r.litter_status || 'current',
  photoUrl: r.photo_url || '',
}),
};
 
// ── Stripe Links ──────────────────────────────────────────────────────────────
const STRIPE = {
  single:     "https://donate.stripe.com/4gMeVccK7a266ryb6PcQU0i",
  bundle:     "https://donate.stripe.com/bJe4gyaBZ3DI17ea2LcQU0j",
  full:       "https://donate.stripe.com/cNibJ0h0n2zE03a3EncQU0k",
  foundation: "https://donate.stripe.com/fZuaEWcK78Y2g28gr9cQU0l",
  litter:     "https://donate.stripe.com/bJe3cu5hF8Y22bi3EncQU0m",
  litter_yr:  "https://donate.stripe.com/28EfZg39xfmqdU0caTcQU0n",
  verified:   "https://donate.stripe.com/dRm9ASdObgqu03afn5cQU0o",
  kennel:     "https://donate.stripe.com/7sY5kCaBZ2zE2bi6QzcQU0p",
  kennel_yr:  "https://donate.stripe.com/3cI28qfWj8Y203agr9cQU0q",
  foundation_mo: "https://buy.stripe.com/14A9AS25t6PUeY41wfcQU0t",
  pro_mo:        "https://donate.stripe.com/aFa4gy4dB3DI2bi8YHcQU0u",
  elite_mo:      "https://donate.stripe.com/eVqeVceSfb6aeY4ej1cQU0s",
  single_reg:    "https://donate.stripe.com/3cI6oGeSfdei6ry8YHcQU0r",
};

// ── Design Tokens ─────────────────────────────────────────────────────────────
const C = {
  // ── existing tokens (internal pages) ──────────────────────────────────────
  bg:"#05080f",bg2:"#0a1020",bg3:"#0f1830",bg4:"#152040",
  border:"#1a2d4a",border2:"#223560",
  gold:"#C9922A",goldL:"#E8B95A",goldXL:"#ead978",
  goldDim:"#8A6020",
  cream:"#EDE8DF",creamD:"#b8b0a0",slate:"#6a7a98",slateL:"#8a9ab8",
  muted:"#7A8296",
  emerald:"#2a6a42",emeraldL:"#3aaa68",
  sapphire:"#1a4a8a",sapphireL:"#2a6ad4",
  amber:"#c87820",red:"#c03838",
  violet:"#5a3aa8",violetL:"#8a6ad8",
  card:"#0c1828",cardH:"#101e30",
  // ── concept palette tokens (homepage + incremental adoption) ──────────────
  ink:"#05070D",      // deepest page background
  navy:"#0A0D16",     // secondary background
  surface:"#0F1320",  // card / panel surface
  line:"#1C2235",     // fine borders and dividers
  lime:"#84CC16",     // health / active accent
  purple:"#9B6BF0",   // feature / litter accent
  fontSerif:"'Cormorant Garamond', serif",
  fontSans:"'Jost', sans-serif",
};

const mdr1C = v => ({Clear:C.emeraldL,Carrier:C.goldL,Affected:C.red}[v]||C.slate);

// Reusable ornament styles (concept: ornamental rules, diamonds, corner frames)
const ORN = {
  line:    {flex:1,height:1,background:'linear-gradient(90deg,transparent,rgba(201,146,42,0.5),transparent)',maxWidth:120},
  lineR:   {flex:1,height:1,background:'linear-gradient(270deg,transparent,rgba(201,146,42,0.5),transparent)',maxWidth:120},
  seg:     {width:40,height:1,background:'linear-gradient(90deg,transparent,rgba(201,146,42,0.6))'},
  segR:    {width:40,height:1,background:'linear-gradient(90deg,rgba(201,146,42,0.6),transparent)'},
  diamond: {width:7,height:7,transform:'rotate(45deg)',background:C.gold,borderRadius:1,flexShrink:0},
  dot:     {width:3,height:3,transform:'rotate(45deg)',background:C.goldDim,borderRadius:0.5,flexShrink:0},
  crest:   {fontFamily:C.fontSerif,color:C.gold,fontSize:18,lineHeight:1,fontWeight:500,letterSpacing:'0.05em'},
  frameTL: {position:'absolute',top:24,left:24,width:40,height:40,borderTop:'1px solid rgba(201,146,42,0.35)',borderLeft:'1px solid rgba(201,146,42,0.35)',pointerEvents:'none'},
  frameTR: {position:'absolute',top:24,right:24,width:40,height:40,borderTop:'1px solid rgba(201,146,42,0.35)',borderRight:'1px solid rgba(201,146,42,0.35)',pointerEvents:'none'},
  frameBL: {position:'absolute',bottom:24,left:24,width:40,height:40,borderBottom:'1px solid rgba(201,146,42,0.35)',borderLeft:'1px solid rgba(201,146,42,0.35)',pointerEvents:'none'},
  frameBR: {position:'absolute',bottom:24,right:24,width:40,height:40,borderBottom:'1px solid rgba(201,146,42,0.35)',borderRight:'1px solid rgba(201,146,42,0.35)',pointerEvents:'none'},
};

// ── Data ──────────────────────────────────────────────────────────────────────
const COUNTRIES = ["United States","United Kingdom","Australia","Canada","Germany","France","Netherlands","Belgium","Sweden","Norway","Denmark","Finland","New Zealand","Ireland","Switzerland","Austria","Spain","Italy","Portugal","Brazil","Argentina","South Africa","Japan","South Korea","Other"];
const REG_BODIES = ["AKC","ASCA","UKC","FCI","KC (UK)","CKC","ANKC","NZKC","SV","VDH","LOF","ENCI","SKK","NKK","DKK","ARBA","NAPR","Other"];
const BREEDS = ["Australian Shepherd","Border Collie","Shetland Sheepdog","Rough Collie","Belgian Malinois","German Shepherd","Golden Retriever","Labrador Retriever","Doberman Pinscher","Rottweiler","Boxer","Poodle (Standard)","Siberian Husky","Alaskan Malamute","Samoyed","Akita","Shiba Inu","Beagle","Basset Hound","Rhodesian Ridgeback","Yorkshire Terrier","Chihuahua","Pomeranian","Shih Tzu","Other Purebred"];

const SEED = {
  breeders: {},
  dogs: {},
  litters: {},
};

// ── Pedigree ──────────────────────────────────────────────────────────────────
// Build tree allowing same dog to appear in multiple positions (line breeding)
function buildTree(id,dogs,g=0,max=4,visited=new Set()){
  if(!id||!dogs[id]||g>max) return null;
  const d=dogs[id];
  // Allow dog to appear multiple times — line breeding is valid
  // But stop recursing if we'd go deeper than max
  if(g>=max) return {...d,g,sire:null,dam:null};
  return{
    ...d,g,
    sire: d.sireId ? buildTree(d.sireId,dogs,g+1,max,new Set(visited)) : null,
    dam:  d.damId  ? buildTree(d.damId, dogs,g+1,max,new Set(visited)) : null,
  };
}

function buildCols(root,max=4){
  const cols=[];
  const fill=(nodes,g)=>{
    if(g>max) return;
    cols[g]=nodes;
    fill(nodes.flatMap(n=>[n?.sire||null, n?.dam||null]),g+1);
  };
  fill([root],0);
  return cols;
}

// Count how many times each dog appears in the pedigree (for line breeding highlight)
function countAppearances(tree){
  const counts={};
  const traverse=(node)=>{
    if(!node) return;
    counts[node.id]=(counts[node.id]||0)+1;
    traverse(node.sire);
    traverse(node.dam);
  };
  traverse(tree);
  return counts;
}

// ── Tier Rank ─────────────────────────────────────────────────────────────────
// 0=free, 1=foundation, 2=professional, 3=elite, 4=admin.
// role='breeder' + tier=NULL → legacy subscriber, elite fallback until manually set.
// Non-breeder with credits (old one-time purchase flow) → 1, can still download certs.
function getTierRank(member, isAdminFlag) {
  if (!member) return 0;
  if (isAdminFlag) return 4;
  if (member.role === 'breeder') {
    if (member.tier === 'elite')        return 3;
    if (member.tier === 'professional') return 2;
    if (member.tier === 'foundation')   return 1;
    return 3; // NULL tier: legacy subscriber → treat as elite
  }
  if ((member.pedigree_credits || 0) > 0 || (member.registration_credits || 0) > 0) return 1;
  return 0;
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const GS = () => <style>{`
  *{box-sizing:border-box;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-track{background:${C.bg};}
  ::-webkit-scrollbar-thumb{background:${C.gold}55;border-radius:3px;}
  .bgl{background:linear-gradient(135deg,${C.gold},${C.goldL});color:${C.bg};font-weight:700;border:none;cursor:pointer;border-radius:8px;padding:11px 28px;font-family:sans-serif;font-size:14px;transition:opacity 0.2s;}
  .bgl:hover{opacity:0.85;}
  .bos{background:transparent;border:1px solid ${C.gold};color:${C.goldL};cursor:pointer;border-radius:6px;padding:7px 16px;font-family:sans-serif;font-size:12px;transition:all 0.2s;}
  .bos:hover{background:${C.gold}14;}
  .bgs{background:transparent;border:1px solid ${C.border2};color:${C.slate};cursor:pointer;border-radius:6px;padding:7px 14px;font-family:sans-serif;font-size:12px;transition:all 0.2s;}
  .bgs:hover{border-color:${C.slateL};color:${C.cream};}
  .breg{background:linear-gradient(180deg,${C.goldL} 0%,${C.gold} 100%);color:${C.bg};font-weight:600;border:none;cursor:pointer;border-radius:20px;padding:8px 18px;font-family:sans-serif;font-size:12.5px;box-shadow:0 4px 18px rgba(201,146,42,0.3);transition:opacity 0.2s;}
  .breg:hover{opacity:0.85;}
  .na{background:${C.gold};color:${C.bg};font-weight:600;border:none;cursor:pointer;border-radius:20px;padding:8px 16px;font-family:sans-serif;font-size:12.5px;}
  .ni{background:transparent;border:1px solid ${C.line};color:${C.muted};cursor:pointer;border-radius:20px;padding:8px 16px;font-family:sans-serif;font-size:12.5px;transition:all 0.2s;}
  .ni:hover{border-color:${C.gold}88;color:${C.goldL};}
  .fi{background:rgba(255,255,255,0.05);border:1px solid ${C.border};border-radius:7px;color:${C.cream};padding:10px 14px;width:100%;font-family:sans-serif;font-size:14px;}
  .fi:focus{outline:none;border-color:${C.gold};background:rgba(255,255,255,0.08);}
  /* <select>'s closed box already reads fine (cream on the dark .fi background,
     inherited above) — this only targets the OPEN option list, which most
     browsers render with their own native (usually light) popup background
     that the app's dark theme can't otherwise reach. Without this, the open
     list inherits .fi's cream text onto that light native background and
     becomes nearly unreadable. */
  .fi option{background:${C.bg3};color:${C.cream};}
  .dcard{border:1px solid;border-radius:10px;padding:16px;transition:transform 0.15s,box-shadow 0.15s;}
  .dcard:hover{transform:translateY(-2px);box-shadow:0 10px 32px rgba(0,0,0,0.45);}
  .pc:hover{transform:scale(1.03);z-index:2;position:relative;}
  .tl{background:none;border:none;color:${C.gold};cursor:pointer;font-family:sans-serif;font-size:13px;padding:0;}
  .tl:hover{color:${C.goldL};}
  .auth-input{background:rgba(255,255,255,0.06);border:1px solid ${C.border};border-radius:8px;color:${C.cream};padding:12px 16px;width:100%;font-family:sans-serif;font-size:15px;outline:none;transition:border 0.2s;}
  .auth-input:focus{border-color:${C.gold};}

 @media(max-width:768px){
    nav button{padding:4px 8px;font-size:11px;}
    .dcard{padding:12px;}
  }
`}</style>;
// ═════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [payModal, setPayModal] = useState(null);
  const [user, setUser]         = useState(null);
  const [member, setMember]     = useState(null);
  const [authView, setAuthView] = useState('home'); // home|login|signup|member
  const [data, setData]         = useState(SEED);
  const [dbLoaded, setDbLoaded] = useState(false);
  const [dbLoading, setDbLoading] = useState(true);
  const [breeds, setBreeds]     = useState([]);
  const [breederBreeds, setBreederBreeds] = useState({});
  const [designerBreeds, setDesignerBreeds] = useState([]);
  const [breederDesignerBreeds, setBreederDesignerBreeds] = useState({});
  const [customCrosses, setCustomCrosses] = useState({});
  const [healthRequirements, setHealthRequirements] = useState([]);
  const [view, setView]         = useState("home");
  const [focusDog, setFocusDog]   = useState(null);
  const [focusBreeder, setFocusBreeder] = useState(null);
  const [modal, setModal]   = useState(null);
  const [editDog, setEditDog] = useState(null);
  const [sec, setSec]       = useState("pure");
  const [filters, setFilters] = useState({q:"",country:"",breed:"",healthVerifiedOnly:false});
  const [healthVerifiedMap, setHealthVerifiedMap] = useState(new Map());
  const [toast, setToast]   = useState("");
  const [adminTab, setAdminTab] = useState("overview");
  const [paymentSuccessTier, setPaymentSuccessTier] = useState(null);
  const [transferToken, setTransferToken] = useState(null);
  const [transferPresetEmail, setTransferPresetEmail] = useState('');
  const fileRef = useRef();

  // ── Load from Supabase on mount ───────────────────────────────────────────────
  useEffect(()=>{
    const loadDB = async () => {
      try {
        const [bRes, dRes, lRes, brRes, bbRes, dbRes, bdbRes, ccRes, hrRes, lpRes] = await Promise.all([
          supabase.from('breeders').select('*'),
          supabase.from('dogs').select('*'),
          supabase.from('litters').select('*'),
          supabase.from('breeds').select('id,name').order('name'),
          supabase.from('breeder_breeds').select('breeder_id,breed_id,added_at'),
          supabase.from('designer_breeds').select('id,name,parent_breeds_text,parent_breed_ids').order('name'),
          supabase.from('breeder_designer_breeds').select('breeder_id,designer_breed_id,added_at'),
          supabase.from('breeder_custom_crosses').select('id,breeder_id,name,parent_breeds_text,parent_breed_ids,created_at'),
          supabase.from('breed_health_requirements').select('*'),
          supabase.from('litter_puppies').select('*'),
        ]);
        const breeders = {};
        (bRes.data||[]).forEach(b => { breeders[b.id] = SB.breederFromDB(b); });
        const dogs = {};
        (dRes.data||[]).forEach(d => { dogs[d.id] = SB.dogFromDB(d); });
        const litters = {};
        (lRes.data||[]).forEach(l => { litters[l.id] = SB.litterFromDB(l); });
        const bbMap = {};
        (bbRes.data||[]).forEach(row => {
          if (!bbMap[row.breeder_id]) bbMap[row.breeder_id] = [];
          bbMap[row.breeder_id].push({breed_id: row.breed_id, added_at: row.added_at});
        });
        const bdbMap = {};
        (bdbRes.data||[]).forEach(row => {
          if (!bdbMap[row.breeder_id]) bdbMap[row.breeder_id] = [];
          bdbMap[row.breeder_id].push({designer_breed_id: row.designer_breed_id, added_at: row.added_at});
        });
        const ccMap = {};
        (ccRes.data||[]).forEach(row => {
          if (!ccMap[row.breeder_id]) ccMap[row.breeder_id] = [];
          ccMap[row.breeder_id].push(row);
        });
        // Merge with seed — DB takes priority
        const hasBreeders = Object.keys(breeders).length > 0;
        const hasDogs = Object.keys(dogs).length > 0;
        setData({
          breeders: breeders,
          dogs: dogs,
          litters: litters,
          litterPuppies: lpRes.data || [],
        });
        setBreeds(brRes.data || []);
        setBreederBreeds(bbMap);
        setDesignerBreeds(dbRes.data || []);
        setBreederDesignerBreeds(bdbMap);
        setCustomCrosses(ccMap);
        setHealthRequirements(hrRes.data || []);
        setDbLoaded(true);
      } catch(e) {
        console.log('DB load error:', e);
        setDbLoaded(true);
      }
      setDbLoading(false);
    };
    loadDB();
  },[]);

  // ── Auth listener ──────────────────────────────────────────────────────────
  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      setUser(session?.user||null);
      if(session?.user) loadMember(session.user.id);
    });
    const {data:{subscription}} = supabase.auth.onAuthStateChange((_,session)=>{
      setUser(session?.user||null);
      if(session?.user) loadMember(session.user.id);
      else setMember(null);
    });
    return ()=>subscription.unsubscribe();
  },[]);

  const loadMember = async (uid) => {
    try {
      const {data, error} = await supabase.from('members').select('*').eq('id',uid).single();
      if (error) console.log('Member load error:', error);
      if (data) setMember(data);
    } catch(e) { console.log('loadMember exception:', e); }
  };

  const flash = msg=>{setToast(msg);setTimeout(()=>setToast(""),2800);};

  // Handle browser back button
  useEffect(()=>{
    const handlePop = ()=>{
      const params = new URLSearchParams(window.location.search);
      const v = params.get('view')||'home';
      const dog = params.get('dog')||null;
      setView(v);
      if(dog) setFocusDog(dog);
    };
    window.addEventListener('popstate', handlePop);
    return ()=>window.removeEventListener('popstate', handlePop);
  },[]);

  // Detect ?payment=success&tier=xxx on initial load (Stripe After Payment redirect)
  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    if(params.get('payment')==='success'){
      const tier = params.get('tier')||'foundation';
      setPaymentSuccessTier(tier);
      setView('payment-success');
      window.history.replaceState({},'','/');
    }
  },[]);

  // Detect ?view=accept-transfer&token=X&email=Y on initial load (the link
  // from a transfer invite email). email is only ever the address the
  // invite was already sent to — pulled straight from the URL to pre-fill
  // sign-in/signup, never fetched from the DB pre-auth (no anonymous
  // view-only path onto someone else's dog record, per the locked design).
  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    if(params.get('view')==='accept-transfer'){
      const token = params.get('token');
      if(token){
        setTransferToken(token);
        setTransferPresetEmail(params.get('email')||'');
        setView('accept-transfer');
      }
      window.history.replaceState({},'','/');
    }
  },[]);

  const navigate = (v, dogId=null)=>{
    const params = new URLSearchParams();
    params.set('view', v);
    if(dogId) params.set('dog', dogId);
    window.history.pushState({}, '', `?${params.toString()}`);
    setView(v);
    if(dogId) setFocusDog(dogId);
  };

  const isAdmin = member?.role==='admin';
  const isBreeder = member?.role==='breeder' || isAdmin;
  const tierRank = getTierRank(member, isAdmin);
  const isContributor = !!user && !isBreeder && !isAdmin && tierRank === 0;
  const canViewPedigree = !!user;
  const canDownloadCert = tierRank >= 1;
  const canBulkImport   = tierRank >= 2;
  const canBackOffice   = tierRank >= 2;

  const dogs     = Object.values(data.dogs);
  const breeders = Object.values(data.breeders);
  const litters  = Object.values(data.litters||{});
  const pureDogs = dogs.filter(d=>d.type==="purebred");

  // Directory Health-Verified badge/filter — one batched query for the whole list,
  // recomputed whenever the loaded dog set or breed panels change.
  useEffect(() => {
    if (!dbLoaded) return;
    let cancelled = false;
    isHealthVerifiedBatch(dogs, healthRequirements, breeds, designerBreeds, supabase, customCrosses)
      .then(map => { if (!cancelled) setHealthVerifiedMap(map); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbLoaded, data.dogs, healthRequirements, breeds, designerBreeds, customCrosses]);
  const heriDogs = dogs.filter(d=>d.type==="heritage");

  const ensureBreederRow = async () => {
    const existing = Object.values(data.breeders||{}).find(b => b.email === user?.email);
    if (existing) return existing;
    if (!user) return null;
    const newId = crypto.randomUUID();
    const newBreeder = {
      id: newId, name: member?.full_name||user.email, email: user.email,
      kennel: '', country: 'United States', region: '', website: '', bio: '',
      joined: new Date().toISOString().split('T')[0],
      tiers: [], isPublic: false, kennelCategories: ['purebred'],
      verified: false, blocked: false, photoUrl: '',
    };
    const {error} = await supabase.from('breeders').insert(SB.breederToDB(newBreeder));
    if (error) throw new Error(error.message);
    setData(prev=>({...prev, breeders:{...prev.breeders, [newId]: newBreeder}}));
    return newBreeder;
  };

  const filteredDogs = dogs.filter(d=>{
    if(sec==="pure"&&d.type!=="purebred")return false;
    if(sec==="heritage"&&d.type!=="heritage")return false;
    if(filters.country&&d.country!==filters.country)return false;
    if(filters.breed&&d.breed!==filters.breed)return false;
    if(filters.healthVerifiedOnly&&!healthVerifiedMap.get(d.id))return false;
    if(filters.q){const q=filters.q.toLowerCase();return[d.name,d.callName,d.breed,d.regNum,d.country].some(v=>v?.toLowerCase().includes(q));}
    return true;
  });

  // Designer-section breed dropdown: dogs-sourced (only breeds present on actual
  // heritage dogs), matching the Purebred toggle — "show what we have." Names that
  // are custom crosses get a "(Custom)" suffix for clarity.
  const heritageBreedOptions = (() => {
    if (sec !== "heritage") return null;
    const customNames = new Set(Object.values(customCrosses).flat().map(c=>c?.name).filter(Boolean));
    return [...new Set(heriDogs.map(d=>d.breed).filter(Boolean))]
      .sort((a,b)=>a.localeCompare(b))
      .map(name => ({name, custom: customNames.has(name)}));
  })();

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"sans-serif",color:C.cream,position:"relative"}}>
      <GS/>
      {toast&&<div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",background:C.goldL,color:C.bg,padding:"10px 28px",borderRadius:30,fontWeight:700,fontSize:13,zIndex:999,boxShadow:"0 4px 24px rgba(0,0,0,0.5)"}}>{toast}</div>}

      {/* ── HEADER ── */}
      <header style={{borderBottom:`1px solid ${C.line}`,padding:"0 40px",background:"rgba(5,7,13,0.97)",backdropFilter:"blur(12px)",position:"sticky",top:0,zIndex:50}}>
        <div style={{maxWidth:1280,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",height:66}}>
          <div onClick={()=>setView("home")} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:46,height:46,borderRadius:"50%",background:`linear-gradient(145deg,${C.goldL},${C.gold})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 0 0 1px rgba(201,146,42,0.4),0 4px 16px rgba(201,146,42,0.2)`}}>
              <span style={{fontFamily:C.fontSerif,fontWeight:700,fontSize:13,letterSpacing:'0.08em',color:C.ink,lineHeight:1}}>UPR</span>
            </div>
            <div>
              <div style={{fontFamily:C.fontSerif,fontSize:20,fontWeight:600,color:C.cream,letterSpacing:'0.01em',lineHeight:1}}>United Pedigree Registry</div>
              <div style={{fontSize:8.5,color:C.goldDim,letterSpacing:'0.22em',textTransform:"uppercase",marginTop:1}}>Global · Est. 2026</div>
            </div>
          </div>
          <nav style={{display:"flex",alignItems:"center",gap:6}}>
           {[["home","Home"],["registry","Registry"],["litters","Litters"],["breeders","Breeders"]].map(([v,l])=>(
              <button key={v} className={view===v?"na":"ni"} onClick={()=>navigate(v)}>{l}</button>
            ))}
            {isAdmin&&<button className={view==="admin"?"na":"ni"} onClick={()=>setView("admin")}>⚙ Admin</button>}
            <div style={{width:1,height:18,background:C.line,margin:"0 4px"}}/>
            {user ? (
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <button className="ni" onClick={()=>setView("member")}>My Account</button>
                <button className="bgs" onClick={async()=>{await supabase.auth.signOut();setView("home");flash("Signed out");}}>Sign Out</button>
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <button className="ni" onClick={()=>{setAuthView("login");setView("auth");}}>Sign In</button>
                <button className="breg" onClick={()=>{setAuthView("signup");setView("auth");}}>Join Registry</button>
              </div>
            )}
          </nav>
        </div>
      </header>

      <main style={{maxWidth:1280,margin:"0 auto",padding:"28px 24px"}}>

        {/* ══ AUTH PAGES ══ */}
        {view==="auth"&&<AuthPage authView={authView} setAuthView={setAuthView} setView={setView} flash={flash} supabase={supabase}/>}

        {/* ══ MEMBER DASHBOARD ══ */}
        {view==="member"&&user&&<MemberDashboard member={member} user={user} setView={setView} flash={flash} supabase={supabase} loadMember={loadMember} data={data} setData={setData} isAdmin={isAdmin} breeds={breeds} breederBreeds={breederBreeds} setBreederBreeds={setBreederBreeds} designerBreeds={designerBreeds} breederDesignerBreeds={breederDesignerBreeds} setBreederDesignerBreeds={setBreederDesignerBreeds} customCrosses={customCrosses} setCustomCrosses={setCustomCrosses} ensureBreeder={ensureBreederRow} healthRequirements={healthRequirements} navigate={navigate} onRespondToTransfer={(token,email)=>{setTransferToken(token);setTransferPresetEmail(email);setView('accept-transfer');}}/>}

        {/* ══ HOME ══ */}
        {view==="home"&&(
          <div>
            {/* Hero */}
            <div style={{position:"relative",overflow:"hidden",padding:"90px 40px 86px",background:`radial-gradient(ellipse 70% 60% at 50% -10%,rgba(201,146,42,0.10) 0%,transparent 70%),radial-gradient(ellipse 40% 40% at 80% 80%,rgba(124,58,237,0.05) 0%,transparent 60%),linear-gradient(180deg,#0C0F1A 0%,${C.ink} 100%)`}}>
              <div style={{position:"absolute",inset:0,backgroundImage:`radial-gradient(circle,rgba(201,146,42,0.08) 1px,transparent 1px)`,backgroundSize:"32px 32px",WebkitMaskImage:"radial-gradient(ellipse 80% 80% at 50% 50%,black 20%,transparent 80%)",maskImage:"radial-gradient(ellipse 80% 80% at 50% 50%,black 20%,transparent 80%)",pointerEvents:"none"}}/>
              <div style={ORN.frameTL}/><div style={ORN.frameTR}/>
              <div style={ORN.frameBL}/><div style={ORN.frameBR}/>
              <div style={{maxWidth:820,margin:"0 auto",display:"flex",flexDirection:"column",alignItems:"center",position:"relative",zIndex:2}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:22}}>
                  <div style={ORN.line}/>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={ORN.dot}/><div style={ORN.diamond}/><div style={ORN.dot}/>
                  </div>
                  <div style={ORN.lineR}/>
                </div>
                <div style={{fontSize:10.5,letterSpacing:'0.32em',color:C.gold,textTransform:"uppercase",textAlign:"center",marginBottom:20}}>The World's Independent Dog Registry</div>
                <div style={{fontFamily:C.fontSerif,fontSize:"clamp(44px,5.5vw,68px)",fontWeight:700,lineHeight:1.0,textAlign:"center",letterSpacing:"-1px",color:C.cream}}>
                  <span style={{display:"block"}}>United Pedigree</span>
                  <span style={{display:"inline-block",fontStyle:"italic",fontWeight:600,background:`linear-gradient(180deg,${C.goldL} 0%,${C.gold} 60%)`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",padding:'0 0.1em 0.08em 0.02em',lineHeight:1.1}}>Registry</span>
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,margin:"22px 0 26px"}}>
                  <div style={ORN.seg}/>
                  <span style={ORN.crest}>✦</span>
                  <div style={ORN.segR}/>
                </div>
                <p style={{fontSize:16,lineHeight:1.7,color:C.muted,textAlign:"center",maxWidth:500,marginBottom:36,fontWeight:300,letterSpacing:'0.01em'}}>One source for every breed, every line, every health record — worldwide.<br/>Built by breeders, for breeders.</p>
                <div style={{display:"flex",gap:14,flexWrap:"wrap",justifyContent:"center"}}>
                  <button onClick={()=>setView("registry")} style={{padding:"14px 26px",borderRadius:7,fontSize:13.5,fontWeight:500,letterSpacing:'0.03em',border:"none",cursor:"pointer",background:`linear-gradient(180deg,${C.goldL} 0%,${C.gold} 100%)`,color:C.ink,boxShadow:"0 6px 24px rgba(201,146,42,0.32)"}}>Search Registry</button>
                  <button onClick={()=>setView("litters")} style={{padding:"14px 26px",borderRadius:7,fontSize:13.5,fontWeight:500,letterSpacing:'0.03em',cursor:"pointer",background:"transparent",border:`1px solid ${C.gold}`,color:C.gold}}>Find a Puppy</button>
                  <button onClick={()=>{setAuthView("signup");setView("auth");}} style={{padding:"14px 26px",borderRadius:7,fontSize:13.5,fontWeight:500,letterSpacing:'0.03em',cursor:"pointer",background:"transparent",border:`1px solid ${C.line}`,color:C.muted}}>Register Your Dogs</button>
                </div>
              </div>
            </div>

            {/* Feature cards */}
            <div style={{padding:"52px 40px 60px",background:C.ink}}>
              <div style={{maxWidth:1100,margin:"0 auto"}}>
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:32,gap:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={ORN.line}/>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><div style={ORN.dot}/><div style={ORN.diamond}/><div style={ORN.dot}/></div>
                    <div style={ORN.lineR}/>
                  </div>
                  <div style={{fontSize:10.5,letterSpacing:'0.32em',color:C.gold,textTransform:"uppercase",textAlign:"center"}}>What's Included</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:18}}>
                  {[
                    {accent:C.gold,   rgb:"201,146,42",  icon:<svg viewBox="0 0 24 24" fill="none" stroke="#C9922A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="21" height="21"><circle cx="12" cy="20" r="2"/><circle cx="6" cy="12" r="2"/><circle cx="18" cy="12" r="2"/><circle cx="4" cy="5" r="2"/><circle cx="10" cy="5" r="2"/><circle cx="14" cy="5" r="2"/><circle cx="20" cy="5" r="2"/><line x1="12" y1="18" x2="6" y2="14"/><line x1="12" y1="18" x2="18" y2="14"/><line x1="6" y1="10" x2="4" y2="7"/><line x1="6" y1="10" x2="10" y2="7"/><line x1="18" y1="10" x2="14" y2="7"/><line x1="18" y1="10" x2="20" y2="7"/></svg>, title:"Pedigree Search",   body:"Trace any dog's full lineage, generations deep — across every registry."},
                    {accent:C.lime,   rgb:"132,204,22",   icon:<svg viewBox="0 0 24 24" fill="none" stroke="#84CC16" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="21" height="21"><path d="M3 12h4l2 7 4-14 2 7h6"/></svg>, title:"Health Records",   body:"OFA, CHIC, and DNA clearances stored and displayed on every dog record."},
                    {accent:"#E8A030",rgb:"232,160,48",   icon:<svg viewBox="0 0 24 24" fill="none" stroke="#E8A030" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="21" height="21"><path d="M12 3l7 3v6c0 5-3 8-7 9-4-1-7-4-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>, title:"Verified Breeders",body:"Every kennel checked, not just claimed. Gold-ribbon status visible to buyers."},
                    {accent:C.purple, rgb:"155,107,240",  icon:<svg viewBox="0 0 24 24" fill="none" stroke="#9B6BF0" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="21" height="21"><rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1"/><path d="M9 12l2 2 4-4"/></svg>, title:"Litter Tracking",  body:"Manage litters from whelping to placement. One record, every dog."},
                  ].map(c=>(
                    <div key={c.title} style={{background:C.card,borderRadius:12,border:`1px solid ${C.line}`,borderTop:`2px solid ${c.accent}`,padding:"28px 22px",boxShadow:"0 12px 32px rgba(0,0,0,0.3)",position:"relative",overflow:"hidden"}}>
                      <div style={{position:"absolute",top:0,left:0,right:0,height:80,background:`linear-gradient(180deg,rgba(${c.rgb},0.06) 0%,transparent 100%)`,pointerEvents:"none"}}/>
                      <div style={{width:46,height:46,borderRadius:"50%",background:`rgba(${c.rgb},0.12)`,border:`1px solid rgba(${c.rgb},0.25)`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:18}}>{c.icon}</div>
                      <div style={{fontFamily:C.fontSerif,fontSize:19,fontWeight:600,color:C.cream,marginBottom:8}}>{c.title}</div>
                      <div style={{fontSize:13,color:C.muted,lineHeight:1.6}}>{c.body}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{padding:"0 40px 64px",background:C.ink}}>
              <div style={{maxWidth:1100,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(5,1fr)",background:C.card,border:`1px solid ${C.line}`,borderRadius:12,overflow:"hidden",boxShadow:"0 8px 28px rgba(0,0,0,0.25)"}}>
                {[[pureDogs.length,"Purebred Dogs"],[heriDogs.length,"Designer Dogs"],[breeders.filter(b=>b.isPublic!==false).length,"Breeders"],[[...new Set(pureDogs.map(d=>d.breed))].length,"Breeds"],[[...new Set(dogs.map(d=>d.country))].length,"Countries"]].map(([n,l],i,arr)=>(
                  <div key={l} style={{padding:"26px 12px",textAlign:"center",borderRight:i<arr.length-1?`1px solid ${C.line}`:"none"}}>
                    <div style={{fontFamily:C.fontSerif,fontSize:32,fontWeight:700,color:C.gold,lineHeight:1}}>{n}</div>
                    <div style={{fontSize:9.5,color:C.muted,letterSpacing:'0.14em',textTransform:"uppercase",marginTop:5}}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div style={{marginBottom:8,fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase"}}>Membership Plans</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:14,marginBottom:40}}>
              {[
                {icon:"🐾",title:"Free Contributor",price:"Free",desc:"View pedigrees, add dogs, suggest edits — no credit card needed",color:C.emeraldL,action:()=>{setAuthView("signup");setView("auth");}},
                {icon:"⚜",title:"Foundation Breeder",price:"$29/mo",desc:"Everything in Free + pedigree certificate downloads",color:C.goldL,href:STRIPE.foundation_mo},
                {icon:"◆",title:"Professional Breeder",price:"$57/mo",desc:"Foundation + bulk import OCR, full back office, health record OCR",color:C.sapphireL,href:STRIPE.pro_mo},
                {icon:"👑",title:"Elite Breeder",price:"$97/mo",desc:"Professional + public kennel profile, litter listings, verified badge, featured placement",color:C.amber,href:STRIPE.elite_mo},
              ].map(t=>(
                <div key={t.title} style={{background:C.card,border:`1px solid ${C.border}`,borderTop:`3px solid ${t.color}`,borderRadius:8,padding:20}}>
                  <div style={{fontSize:24,marginBottom:8}}>{t.icon}</div>
                  <div style={{fontFamily:"serif",fontSize:17,fontWeight:700,color:C.cream,marginBottom:4}}>{t.title}</div>
                  <div style={{fontSize:22,fontWeight:700,color:t.color,marginBottom:8}}>{t.price}</div>
                  <div style={{fontSize:12,color:C.slate,lineHeight:1.6,marginBottom:16}}>{t.desc}</div>
                  {t.action
                    ? <button onClick={t.action} style={{display:"block",width:"100%",textAlign:"center",background:`linear-gradient(135deg,${C.emerald},${C.emeraldL})`,color:"#fff",fontWeight:700,fontSize:12,border:"none",padding:"9px 0",borderRadius:5,cursor:"pointer"}}>Sign Up Free</button>
                    : <a href={t.href} target="_blank" rel="noreferrer" style={{display:"block",textAlign:"center",background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:12,textDecoration:"none",padding:"9px 0",borderRadius:5}}>Subscribe</a>
                  }
                </div>
              ))}
            </div>

            {/* Featured Kennels — only renders when at least 1 elite kennel exists */}
            {(()=>{
              const elite=[...breeders].filter(b=>b.isElite&&b.isPublic!==false)
                .sort(()=>Math.random()-0.5).slice(0,3);
              if(!elite.length) return null;
              return(<>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12}}>
                  <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase"}}>Featured Kennels</div>
                  <button className="tl" onClick={()=>setView("breeders")}>View all →</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12,marginBottom:32}}>
                  {elite.map(b=><BreederCard key={b.id} breeder={b} dogCount={dogs.filter(d=>d.breederId===b.id).length} onView={()=>{setFocusBreeder(b.id);setView("breeder");}}/>)}
                </div>
              </>);
            })()}

            {/* Recent dogs */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12}}>
              <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase"}}>Recently Added</div>
              <button className="tl" onClick={()=>setView("registry")}>View all →</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
              {dogs.slice(0,6).map(d=><DogCard key={d.id} dog={d} breeders={data.breeders} canView={canViewPedigree} onView={()=>{if(canViewPedigree){navigate("pedigree", d.id);}else{setAuthView("signup");setView("auth");}}} />)}
            </div>

            {/* Auth prompt for non-members */}
            {!user&&(
              <div style={{background:`linear-gradient(135deg,${C.card},${C.bg2})`,border:`1px solid ${C.gold}44`,borderRadius:12,padding:40,textAlign:"center",marginTop:40}}>
                <div style={{fontFamily:"serif",fontSize:28,fontWeight:700,color:C.cream,marginBottom:8}}>Ready to Access Full Pedigrees?</div>
                <p style={{color:C.slate,marginBottom:24,fontSize:15,lineHeight:1.7}}>Create your account and purchase access to view complete 5-generation pedigrees with full health records.</p>
                <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
                  <button className="bgl" style={{padding:"12px 32px"}} onClick={()=>{setAuthView("signup");setView("auth");}}>Create Account</button>
                  <button className="bos" onClick={()=>{setAuthView("login");setView("auth");}}>Sign In</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ REGISTRY ══ */}
        {view==="registry"&&(
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
              <div>
                <h2 style={{fontFamily:"serif",fontSize:28,fontWeight:900,margin:"0 0 4px"}}>Dog Registry</h2>
                <div style={{fontSize:13,color:C.slate}}>{filteredDogs.length} dogs · {sec==="pure"?"Purebred":"Designer"} section</div>
              </div>
            </div>
            <div style={{display:"inline-flex",background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:16}}>
              {[["pure","Purebred"],["heritage","Designer"]].map(([v,l])=>(
                <button key={v} onClick={()=>setSec(v)} style={{padding:"8px 22px",fontSize:13,fontWeight:sec===v?700:400,cursor:"pointer",border:"none",background:sec===v?`linear-gradient(135deg,${C.gold},${C.goldL})`:"transparent",color:sec===v?C.bg:C.slate,transition:"all 0.2s",fontFamily:"sans-serif"}}>{l}</button>
              ))}
            </div>
            {/* Filters */}
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
              <input className="fi" style={{flex:"2 1 220px",background:"rgba(255,255,255,0.04)"}} placeholder="Search name, breed, reg #…" value={filters.q} onChange={e=>setFilters(p=>({...p,q:e.target.value}))}/>
              <select className="fi" style={{flex:"1 1 150px",background:C.bg2}} value={filters.breed} onChange={e=>setFilters(p=>({...p,breed:e.target.value}))}>
                <option value="">All Breeds</option>
                {sec==="heritage" && heritageBreedOptions ? (
                  heritageBreedOptions.map(o=><option key={o.name} value={o.name}>{o.name}{o.custom?" (Custom)":""}</option>)
                ) : (
                  [...new Set(dogs.map(d=>d.breed).filter(Boolean))].sort().map(b=><option key={b}>{b}</option>)
                )}
              </select>
              <select className="fi" style={{flex:"1 1 150px",background:C.bg2}} value={filters.country} onChange={e=>setFilters(p=>({...p,country:e.target.value}))}>
                <option value="">All Countries</option>
                {[...new Set(dogs.map(d=>d.country).filter(Boolean))].sort().map(c=><option key={c}>{c}</option>)}
              </select>
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.creamD,cursor:"pointer",padding:"0 4px"}}>
                <input type="checkbox" checked={filters.healthVerifiedOnly} onChange={e=>setFilters(p=>({...p,healthVerifiedOnly:e.target.checked}))}/>
                Health-Verified only
              </label>
            </div>
            {filteredDogs.length===0
              ?<div style={{textAlign:"center",padding:"50px",color:C.slate,border:`1px dashed ${C.border}`,borderRadius:12}}>No dogs match your filters.</div>
              :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
                {filteredDogs.map(d=><DogCard key={d.id} dog={d} breeders={data.breeders} canView={canViewPedigree} healthVerified={healthVerifiedMap.get(d.id)} onView={()=>{if(canViewPedigree){navigate("pedigree", d.id);}else{setAuthView("signup");setView("auth");}}}/>)}
              </div>
            }
          </div>
        )}

        {/* ══ LITTERS ══ */}
        {view==="litters"&&(
          <div>
            <h2 style={{fontFamily:"serif",fontSize:28,fontWeight:900,marginBottom:4}}>Available Litters</h2>
            <div style={{fontSize:13,color:C.slate,marginBottom:24}}>Puppies from registered breeders worldwide</div>
            {litters.length===0
              ?<div style={{textAlign:"center",padding:"50px",color:C.slate,border:`1px dashed ${C.border}`,borderRadius:12}}>No litters posted yet.</div>
              :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
                {litters.map(l=><LitterCard key={l.id} litter={l} dogs={data.dogs} breeders={data.breeders} puppies={(data.litterPuppies||[]).filter(p=>p.litter_id===l.id)} supabase={supabase} flash={flash}/>)}
              </div>
            }
          </div>
        )}

        {/* ══ BREEDERS ══ */}
        {view==="breeders"&&(
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
              <div>
                <h2 style={{fontFamily:"serif",fontSize:28,fontWeight:900,margin:"0 0 4px"}}>Registered Breeders</h2>
                <div style={{fontSize:13,color:C.slate}}>{breeders.length} kennels worldwide</div>
              </div>
              <button className="breg" onClick={()=>{setAuthView("signup");setView("auth");}}>Join Registry</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
              {[...breeders].filter(b=>b.isPublic!==false).sort((a,b)=>{
                if(a.isElite!==b.isElite) return a.isElite ? -1 : 1;
                const o={verified:0,paying:1,reference:2};
                const ta=o[a.kennelType]??2, tb=o[b.kennelType]??2;
                if(ta!==tb) return ta-tb;
                return (a.kennel||'').localeCompare(b.kennel||'');
              }).map(b=><BreederCard key={b.id} breeder={b} dogCount={dogs.filter(d=>d.breederId===b.id).length} onView={()=>{setFocusBreeder(b.id);setView("breeder");}}/>)}
            </div>
          </div>
        )}

        {/* ══ PEDIGREE ══ */}
       {view==="pedigree"&&focusDog&&data.dogs[focusDog]&&(
          !!user
            ? <PedigreeView
                dog={data.dogs[focusDog]}
                allDogs={data.dogs}
                breeders={data.breeders}
                breeds={breeds}
                designerBreeds={designerBreeds}
                customCrosses={customCrosses}
                healthRequirements={healthRequirements}
                onBack={()=>navigate("registry")}
onDogClick={id=>navigate("pedigree", id)}
                canEdit={isAdmin || data.dogs[focusDog]?.recordOwnerId === breeders.find(b=>b.email===user?.email)?.id || data.dogs[focusDog]?.currentOwnerMemberId === user?.id}
                canSuggest={!!(user && !isAdmin && data.dogs[focusDog]?.recordOwnerId !== breeders.find(b=>b.email===user?.email)?.id && data.dogs[focusDog]?.currentOwnerMemberId !== user?.id)}
                suggesterId={breeders.find(b=>b.email===user?.email)?.id}
                canViewPedigree={canDownloadCert}
                ensureBreeder={ensureBreederRow}
                supabase={supabase}
                data={data}
                setData={setData}
                flash={flash}
                onSaveDog={async(dog)=>{
                  setData(prev=>({...prev,dogs:{...prev.dogs,[dog.id]:dog}}));
                  try {
                    await supabase.from('dogs').upsert(SB.dogToDB(dog));
                  } catch(e) {
                    console.log('Save error:',e);
                  }
                }}
              />
            : <PaywallView onSignup={()=>{setAuthView("signup");setView("auth");}} onSignin={()=>{setAuthView("login");setView("auth");}}/>
        )}

        {/* ══ BREEDER PROFILE ══ */}
        {view==="breeder"&&focusBreeder&&data.breeders[focusBreeder]&&(
          <BreederProfile breeder={data.breeders[focusBreeder]} dogs={dogs.filter(d=>d.breederId===focusBreeder)} litters={litters.filter(l=>l.breederId===focusBreeder)} breeds={breeds} breederBreeds={breederBreeds} designerBreeds={designerBreeds} breederDesignerBreeds={breederDesignerBreeds} supabase={supabase} flash={flash} onBack={()=>setView("breeders")} onDogView={id=>navigate("pedigree", id)}/>
        )}

        {/* ══ ADMIN ══ */}
        {view==="admin"&&(
          isAdmin ? (
            <AdminPanel data={data} setData={setData} dogs={dogs} breeders={breeders} litters={litters} breeds={breeds} designerBreeds={designerBreeds} setDesignerBreeds={setDesignerBreeds} customCrosses={customCrosses} healthRequirements={healthRequirements} setHealthRequirements={setHealthRequirements} adminTab={adminTab} setAdminTab={setAdminTab} flash={flash} supabase={supabase}/>
          ) : (
            <div style={{maxWidth:400,margin:"80px auto",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:40,textAlign:"center"}}>
              <div style={{fontFamily:"serif",fontSize:24,fontWeight:900,color:C.goldL,marginBottom:8}}>Admin Access Required</div>
              <div style={{fontSize:13,color:C.slate,marginBottom:20,lineHeight:1.6}}>This area is restricted to admin accounts. Sign in with an admin account to continue.</div>
              <button className="bgl" onClick={()=>setView("home")}>← Back to Home</button>
            </div>
          )
        )}
{/* ══ TERMS ══ */}
        {view==="terms"&&<TermsOfService onBack={()=>setView("home")}/>}

        {/* ══ PRIVACY ══ */}
        {view==="privacy"&&<PrivacyPolicy onBack={()=>setView("home")}/>}

        {/* ══ COPYRIGHT ══ */}
        {view==="copyright"&&<CopyrightNotice onBack={()=>setView("home")}/>}

        {/* ══ PAYMENT SUCCESS ══ */}
        {view==='payment-success'&&paymentSuccessTier&&(
          <PaymentSuccessView
            tier={paymentSuccessTier}
            onSignIn={()=>{setAuthView('login');setView('auth');}}
            onHome={()=>{setPaymentSuccessTier(null);setView('home');}}
          />
        )}

        {/* ══ ACCEPT/DECLINE OWNERSHIP TRANSFER ══ */}
        {view==='accept-transfer'&&transferToken&&(
          <AcceptTransferView
            token={transferToken}
            presetEmail={transferPresetEmail}
            user={user}
            supabase={supabase}
            flash={flash}
            setData={setData}
            onDone={()=>{setTransferToken(null);setView(user?'member':'home');}}
          />
        )}
{payModal&&<PaymentModal item={payModal.item} price={payModal.price} stripeUrl={payModal.url} onClose={()=>setPayModal(null)}/>}
      </main>
{/* ── FOOTER ── */}
      <footer style={{borderTop:`1px solid ${C.border}`,marginTop:60,padding:"24px",textAlign:"center",background:C.bg2}}>
        <div style={{maxWidth:1280,margin:"0 auto"}}>
          <div style={{fontSize:11,color:C.slate,marginBottom:8}}>© 2026 United Pedigree Registry · 3R Ministries · TAGA Ranch · All Rights Reserved</div>
          <div style={{display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="tl" style={{fontSize:11}} onClick={()=>setView("terms")}>Terms of Service</button>
            <button className="tl" style={{fontSize:11}} onClick={()=>setView("privacy")}>Privacy Policy</button>
            <button className="tl" style={{fontSize:11}} onClick={()=>setView("copyright")}>Copyright Notice</button>
          </div>
        </div>
      </footer>
      {/* File input */}
      <input type="file" accept="image/*" ref={fileRef} style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setEditDog(p=>({...p,photoUrl:ev.target.result}));r.readAsDataURL(f);e.target.value="";}}/>
    </div>
  );
}

// ── ACCEPT/DECLINE OWNERSHIP TRANSFER VIEW ─────────────────────────────────────
// Reached via the token link in a transfer invite email. Never shows the
// dog's own details until the visitor is authenticated AND their session
// email matches the invite's to_email — dog_transfers' own SELECT RLS
// enforces this same rule server-side, this is just the UI reflecting it
// honestly rather than a separate trust boundary. presetEmail is only ever
// the address the invite was already sent to (from the URL, not fetched),
// used to pre-fill sign-in/signup — never a new disclosure.
function AcceptTransferView({token, presetEmail, user, supabase, flash, setData, onDone}){
  const [transfer, setTransfer] = useState(null);
  const [dogName, setDogName] = useState('');
  const [loadState, setLoadState] = useState('checking'); // checking | needs-auth | mismatched | ready | not-found | already-handled
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState(presetEmail || '');
  const [password, setPassword] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState('');
  const [responding, setResponding] = useState(false);
  // Persistent, not a 2.8s auto-dismissing toast — a silent, missed failure
  // on an ownership-transfer accept must be impossible, not just usually
  // fine. Stays on screen until the next attempt (cleared at the start of
  // respond()), not on a timer.
  const [respondErr, setRespondErr] = useState('');

  const tryLoad = async () => {
    const { data, error } = await supabase.from('dog_transfers').select('id, dog_id, to_email, status').eq('token', token).maybeSingle();
    if (error || !data) {
      // RLS returns nothing rather than an error for a logged-in user whose
      // email doesn't match to_email — distinguish that from "not signed in
      // at all" using the still-known presetEmail vs the current session.
      setLoadState(user ? 'mismatched' : 'needs-auth');
      return;
    }
    if (data.status !== 'pending') { setLoadState('already-handled'); setTransfer(data); return; }
    const { data: dog } = await supabase.from('dogs').select('name').eq('id', data.dog_id).maybeSingle();
    setTransfer(data);
    setDogName(dog?.name || 'this dog');
    setLoadState('ready');
  };

  useEffect(() => {
    if (user) tryLoad(); else setLoadState('needs-auth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const doAuth = async () => {
    if (!email.trim() || !password) { setAuthErr('Email and password required'); return; }
    setAuthBusy(true); setAuthErr('');
    try {
      const { error } = mode === 'signin'
        ? await supabase.auth.signInWithPassword({ email: email.trim(), password })
        : await supabase.auth.signUp({ email: email.trim(), password });
      if (error) { setAuthErr(error.message); return; }
      // onAuthStateChange elsewhere in the app updates `user`; the effect
      // above re-fires once that resolves and attempts tryLoad() again.
    } catch (e) {
      setAuthErr(e.message || 'Something went wrong — please try again.');
    } finally {
      setAuthBusy(false);
    }
  };

  // No un-surfaced failure paths: every exit either shows respondErr (which
  // stays on screen, not a timed toast) or calls onDone() on genuine success.
  // finally always clears the "Please wait…" state, even on a thrown
  // exception (e.g. session momentarily null right after signing in) that
  // would otherwise leave the button frozen with nothing ever shown.
  const respond = async (action) => {
    setResponding(true);
    setRespondErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setRespondErr('Your session expired — please sign in again and retry.');
        return;
      }
      const resp = await fetch('/api/dog-transfer-respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ token, action }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setRespondErr(json.error || `Request failed (${resp.status}) — please try again.`);
        return;
      }
      if (action === 'accept') {
        // Server already set dogs.current_owner_member_id = callerId (this
        // user) — patch it into shared state now so My Dogs reflects it the
        // instant we land back on the dashboard, no page reload required.
        setData(prev => prev.dogs[transfer.dog_id] ? {...prev, dogs:{...prev.dogs, [transfer.dog_id]:{...prev.dogs[transfer.dog_id], currentOwnerMemberId:user.id}}} : prev);
      }
      flash(action === 'accept' ? `You now own ${dogName} ⚜` : 'Transfer declined');
      onDone();
    } catch (e) {
      setRespondErr('Something went wrong sending your response — please try again. (' + (e.message || 'unknown error') + ')');
    } finally {
      setResponding(false);
    }
  };

  return (
    <div style={{maxWidth:440,margin:"60px auto",background:C.card,border:`1px solid ${C.gold}44`,borderRadius:12,padding:32}}>
      <div style={{fontFamily:'serif',fontSize:22,fontWeight:900,color:C.cream,marginBottom:16,textAlign:'center'}}>Ownership Transfer</div>

      {loadState === 'not-found' && <div style={{color:C.slate,textAlign:'center'}}>This invite link isn't valid — check that you copied the full link from the email.</div>}
      {loadState === 'already-handled' && <div style={{color:C.slate,textAlign:'center'}}>This transfer has already been {transfer?.status}. No further action is needed.</div>}
      {loadState === 'mismatched' && <div style={{color:C.slate,textAlign:'center'}}>This invite was sent to a different email address than the one you're currently signed in with. Sign out and sign in with the invited address to continue.</div>}

      {loadState === 'needs-auth' && (
        <div>
          <div style={{fontSize:13,color:C.slate,marginBottom:16,textAlign:'center'}}>Sign in or create a free account with the email this invite was sent to, to see the details and respond.</div>
          <div style={{display:'flex',gap:8,marginBottom:16,justifyContent:'center'}}>
            <button className={mode==='signin'?'bgl':'bgs'} style={{fontSize:12}} onClick={()=>setMode('signin')}>Sign In</button>
            <button className={mode==='signup'?'bgl':'bgs'} style={{fontSize:12}} onClick={()=>setMode('signup')}>Create Account</button>
          </div>
          <input className="fi" style={{marginBottom:8}} placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)}/>
          <input className="fi" style={{marginBottom:8}} placeholder="Password" type="password" value={password} onChange={e=>setPassword(e.target.value)}/>
          {authErr && <div style={{fontSize:12,color:'#e74c3c',marginBottom:8}}>{authErr}</div>}
          <button className="bgl" style={{width:'100%'}} disabled={authBusy} onClick={doAuth}>{authBusy?'Please wait…':(mode==='signin'?'Sign In →':'Create Account →')}</button>
        </div>
      )}

      {loadState === 'ready' && (
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:14,color:C.cream,marginBottom:20,lineHeight:1.6}}>You've been offered ownership of <strong>{dogName}</strong>'s record on United Pedigree Registry — verified pedigree, complete health history, everything documented, permanently on file.</div>
          {respondErr && (
            <div style={{background:'#e74c3c22',border:'1px solid #e74c3c66',borderRadius:8,padding:'10px 14px',marginBottom:16,color:'#e74c3c',fontSize:13,textAlign:'left'}}>
              ⚠ {respondErr}
            </div>
          )}
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button className="bgs" disabled={responding} onClick={()=>respond('decline')}>Decline</button>
            <button className="bgl" disabled={responding} onClick={()=>respond('accept')}>{responding?'Please wait…':`Accept Ownership of ${dogName}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PAYMENT SUCCESS VIEW ──────────────────────────────────────────────────────
function PaymentSuccessView({tier, onSignIn, onHome}){
  // Cumulative feature lists — each tier inherits everything above it.
  const FREE = [
    'View full pedigrees for all registered dogs',
    'Add unlimited dogs manually',
    'Suggest edits on any dog record',
    'Approve or reject edits on your own dogs',
  ];
  const FOUND_NEW = [
    'Pedigree certificate downloads',
  ];
  const PRO_NEW = [
    'Bulk import — photograph registration papers, OCR auto-extracts full pedigrees',
    'Full Back Office — per-dog health tests, vaccinations, vet visits, heat cycles, breeding records, weight log',
    'Health record OCR — photograph OFA certs, vaccination records, vet invoices → auto-filled records',
    {text:'Waitlist management',soon:true},
  ];
  const ELITE_NEW = [
    'Public kennel profile & listing',
    'Litter listings',
    'Verified breeder badge',
    'Featured kennel placement',
    'Priority search placement',
    'Custom branding on pedigree PDFs',
    {text:'Buyer leads',soon:true},
    'Priority support',
    {text:'Email & SMS reminders',soon:true},
  ];

  const TIERS = {
    foundation:{
      label:'Foundation Breeder', price:'$29/mo', accent:C.gold, rgb:'201,146,42', icon:'⚜', isSubscription:true,
      sections:[
        {heading:'Everything in Free',        hc:C.muted,     items:FREE,       muted:true},
        {heading:'New in Foundation',         hc:C.gold,      items:FOUND_NEW,  muted:false},
      ],
    },
    professional:{
      label:'Professional Breeder', price:'$57/mo', accent:C.sapphireL, rgb:'42,106,212', icon:'◆', isSubscription:true,
      sections:[
        {heading:'Everything in Foundation',  hc:C.muted,     items:[...FREE,...FOUND_NEW],  muted:true},
        {heading:'New in Professional',       hc:C.sapphireL, items:PRO_NEW,    muted:false},
      ],
    },
    elite:{
      label:'Elite Breeder', price:'$97/mo', accent:C.amber, rgb:'200,120,32', icon:'✦', isSubscription:true,
      sections:[
        {heading:'Everything in Professional',hc:C.muted,     items:[...FREE,...FOUND_NEW,...PRO_NEW], muted:true},
        {heading:'New in Elite',              hc:C.amber,     items:ELITE_NEW,  muted:false},
      ],
    },
    pedigree:{
      label:'Single Pedigree', price:'$27', accent:C.goldL, rgb:'232,185,90', icon:'📜', isSubscription:false,
      sections:[{heading:'What you unlocked', hc:C.goldL, muted:false, items:[
        '1 pedigree certificate download — use it on any registered dog',
        'Full 4-generation pedigree with registration details, health clearances, and titles',
        'Sign in, open any dog\'s pedigree, and click Download Certificate',
        'Credit never expires',
      ]}],
    },
    bundle:{
      label:'Pedigree Bundle', price:'$47', accent:C.goldL, rgb:'232,185,90', icon:'📚', isSubscription:false,
      sections:[{heading:'What you unlocked', hc:C.goldL, muted:false, items:[
        '3 pedigree certificate downloads — use each on any registered dog',
        'Full 4-generation pedigree with registration details, health clearances, and titles',
        'Sign in, open any dog\'s pedigree, and click Download Certificate',
        'Credits never expire — use them whenever you\'re ready',
      ]}],
    },
    registration:{
      label:'Single Registration', price:'$35', accent:C.emeraldL, rgb:'58,170,104', icon:'📋', isSubscription:false,
      sections:[{heading:'What you unlocked', hc:C.emeraldL, muted:false, items:[
        '1 registration certificate download for one dog',
        'Official United Pedigree Registry registration credential',
        'Sign in, open the dog\'s record, and download your certificate',
        'Credit never expires',
      ]}],
    },
  };

  const t = TIERS[tier] || TIERS.foundation;
  const Soon = ()=><span style={{fontSize:10,color:C.muted,background:'rgba(122,130,150,0.15)',borderRadius:4,padding:'1px 6px',marginLeft:6,letterSpacing:'0.04em',verticalAlign:'middle'}}>coming soon</span>;

  return (
    <div style={{padding:'60px 0',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{maxWidth:520,width:'100%',textAlign:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:28,justifyContent:'center'}}>
          <div style={ORN.line}/><div style={{display:'flex',alignItems:'center',gap:6}}><div style={ORN.dot}/><div style={ORN.diamond}/><div style={ORN.dot}/></div><div style={ORN.lineR}/>
        </div>
        <div style={{fontSize:10,letterSpacing:'0.32em',color:C.gold,textTransform:'uppercase',marginBottom:18,fontFamily:C.fontSans}}>United Pedigree Registry</div>
        <div style={{fontSize:42,marginBottom:12,lineHeight:1}}>{t.icon}</div>
        <div style={{fontFamily:C.fontSerif,fontSize:34,fontWeight:700,color:C.cream,lineHeight:1.15,marginBottom:6}}>
          Welcome to <span style={{color:t.accent,fontStyle:'italic'}}>{t.label}</span>
        </div>
        <div style={{fontSize:13,color:C.muted,marginBottom:26,fontWeight:300,fontFamily:C.fontSans}}>{t.price} · Payment confirmed</div>
        <div style={{marginBottom:20,textAlign:'left',display:'flex',flexDirection:'column',gap:10}}>
          {t.sections.map((sec,si)=>(
            <div key={si} style={{background:C.card,border:`1px solid ${C.line}`,borderTop:`2px solid ${sec.hc}`,borderRadius:12,padding:'20px 24px'}}>
              <div style={{fontSize:9.5,letterSpacing:'0.22em',color:sec.hc,textTransform:'uppercase',marginBottom:12,fontFamily:C.fontSans}}>{sec.heading}</div>
              {sec.items.map((item,ii)=>{
                const isObj = typeof item==='object';
                const text  = isObj ? item.text : item;
                const soon  = isObj && item.soon;
                return (
                  <div key={ii} style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:7}}>
                    <span style={{color:sec.hc,fontSize:12,flexShrink:0,marginTop:2}}>✓</span>
                    <span style={{fontSize:13,color:sec.muted?C.muted:C.cream,lineHeight:1.5,fontFamily:C.fontSans}}>
                      {text}{soon&&<Soon/>}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {t.isSubscription&&(
          <div style={{background:`rgba(${t.rgb},0.06)`,border:`1px solid rgba(${t.rgb},0.15)`,borderRadius:8,padding:'11px 16px',marginBottom:20,fontSize:11.5,color:C.muted,lineHeight:1.6,fontFamily:C.fontSans}}>
            Your account upgrades automatically within ~60 seconds. Sign in to access your new features.
          </div>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <button onClick={onSignIn} style={{width:'100%',padding:'14px',borderRadius:8,fontSize:14,fontWeight:600,border:'none',cursor:'pointer',background:`linear-gradient(180deg,${C.goldL} 0%,${C.gold} 100%)`,color:C.ink,boxShadow:'0 6px 24px rgba(201,146,42,0.28)',fontFamily:C.fontSans}}>
            Sign In to Your Account
          </button>
          <button onClick={onHome} style={{width:'100%',padding:'13px',borderRadius:8,fontSize:12.5,cursor:'pointer',background:'transparent',border:`1px solid ${C.line}`,color:C.muted,fontFamily:C.fontSans}}>
            Return to Registry
          </button>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8,marginTop:28,justifyContent:'center'}}>
          <div style={ORN.seg}/><span style={ORN.crest}>✦</span><div style={ORN.segR}/>
        </div>
      </div>
    </div>
  );
}

// ── AUTH PAGE ─────────────────────────────────────────────────────────────────
// ── KENNEL CATEGORIES ────────────────────────────────────────────────────────
// The three tiers a breeder can self-identify with. Drives which dashboard tabs
// (My Breeds / My Designer Breeds / My Custom Crosses) are shown.
const KENNEL_CATEGORIES = [['purebred','Purebred'],['designer','Designer'],['custom','Custom Crosses']];

// Reusable 3-checkbox group. value = array of category keys; onChange(nextArray).
// The "≥1 checked" rule is enforced by the caller at save time; this just hints.
function CategoryCheckboxes({value, onChange}){
  const cats = value || [];
  const toggle = (key) => onChange(cats.includes(key) ? cats.filter(c=>c!==key) : [...cats, key]);
  return (
    <div>
      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>What does your kennel breed?</label>
      <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
        {KENNEL_CATEGORIES.map(([key,label])=>(
          <label key={key} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:C.cream}}>
            <input type="checkbox" checked={cats.includes(key)} onChange={()=>toggle(key)}/>
            {label}
          </label>
        ))}
      </div>
      {cats.length===0 && <div style={{fontSize:11,color:C.red,marginTop:6}}>Select at least one category.</div>}
    </div>
  );
}

function AuthPage({authView,setAuthView,setView,flash,supabase}){
  const [email,setEmail]   = useState('');
  const [pass,setPass]     = useState('');
  const [name,setName]     = useState('');
  const [err,setErr]       = useState('');
  const [loading,setLoading] = useState(false);
  const [success,setSuccess] = useState('');

  const login = async()=>{
    setLoading(true);setErr('');
    const {error} = await supabase.auth.signInWithPassword({email,password:pass});
    if(error){setErr(error.message);}
    else{flash("Welcome back!");setView("home");}
    setLoading(false);
  };

  const signup = async()=>{
    if(!email||!pass){setErr("Email and password required.");return;}
    if(pass.length<8){setErr("Password must be at least 8 characters.");return;}
    setLoading(true);setErr('');
    const {error} = await supabase.auth.signUp({email,password:pass,options:{data:{full_name:name}}});
    if(error){setErr(error.message);}
    else{setSuccess("Account created! Check your email to confirm, then sign in.");}
    setLoading(false);
  };

  const resetPass = async()=>{
    if(!email){setErr("Enter your email first.");return;}
    setLoading(true);
   await supabase.auth.resetPasswordForEmail(email,{redirectTo:"https://www.unitedpedigreeregistry.com/auth/callback"});
    setSuccess("Password reset email sent!");
    setLoading(false);
  };

  return(
    <div style={{maxWidth:440,margin:"60px auto"}}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:40,boxShadow:`0 20px 60px rgba(0,0,0,0.4)`}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:56,height:56,borderRadius:"50%",background:`conic-gradient(${C.gold},${C.goldXL},${C.gold})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",boxShadow:`0 4px 16px rgba(184,146,42,0.4)`,fontSize:28}}>
            🐾
          </div>
          <div style={{fontFamily:"serif",fontSize:22,fontWeight:900,color:C.goldXL}}>United Pedigree Registry</div>
          <div style={{fontSize:12,color:C.slate,marginTop:4}}>{authView==="login"?"Sign in to your account":"Create your account"}</div>
        </div>

        {success ? (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:48,marginBottom:16}}>✉️</div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.cream,marginBottom:8}}>Check Your Email</div>
            <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:24}}>{success}</p>
            <button className="bgl" style={{width:"100%"}} onClick={()=>{setSuccess('');setAuthView("login");}}>Sign In</button>
          </div>
        ) : (
          <>
            {authView==="signup"&&(
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:4}}>Full Name</label>
                <input className="auth-input" placeholder="Your full name" value={name} onChange={e=>setName(e.target.value)}/>
              </div>
            )}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:4}}>Email Address</label>
              <input className="auth-input" type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)}/>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:11,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:4}}>Password</label>
              <input className="auth-input" type="password" placeholder={authView==="signup"?"Min. 8 characters":"Your password"} value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")authView==="login"?login():signup();}}/>
            </div>
            {err&&<div style={{color:C.red,fontSize:13,marginBottom:14,padding:"10px 14px",background:`${C.red}15`,borderRadius:6,border:`1px solid ${C.red}44`}}>{err}</div>}
            <button className="bgl" style={{width:"100%",padding:"13px",fontSize:15,marginBottom:14}} onClick={authView==="login"?login:signup} disabled={loading}>
              {loading?"Please wait…":authView==="login"?"Sign In":"Create Account"}
            </button>

            {authView==="login"&&(
              <div style={{textAlign:"center",marginBottom:14}}>
                <button className="tl" style={{fontSize:12}} onClick={resetPass}>Forgot password?</button>
              </div>
            )}

            <div style={{textAlign:"center",padding:"16px 0 0",borderTop:`1px solid ${C.border}`,fontSize:13,color:C.slate}}>
              {authView==="login"
                ? <>Don't have an account? <button className="tl" onClick={()=>{setAuthView("signup");setErr('');}}>Create one</button></>
                : <>Already have an account? <button className="tl" onClick={()=>{setAuthView("login");setErr('');}}>Sign in</button></>
              }
            </div>

            <div style={{marginTop:20,padding:"14px 16px",background:C.bg3,borderRadius:8,textAlign:"center",fontSize:12,color:C.slate,lineHeight:1.6}}>
              Free to join — view pedigrees and add dogs at no cost. <a href={STRIPE.foundation_mo} target="_blank" rel="noreferrer" style={{color:C.goldL,textDecoration:"none"}}>See paid plans →</a>
            </div>
          </>
        )}
      </div>
      <div style={{textAlign:"center",marginTop:16}}>
        <button className="bgs" onClick={()=>setView("home")}>← Back to Registry</button>
      </div>
    </div>
  );
}

// ── MEMBER DASHBOARD ──────────────────────────────────────────────────────────
function MemberDashboard({member,user,setView,flash,supabase,loadMember,data,setData,isAdmin,breeds,breederBreeds,setBreederBreeds,designerBreeds,breederDesignerBreeds,setBreederDesignerBreeds,customCrosses,setCustomCrosses,ensureBreeder,healthRequirements,navigate,onRespondToTransfer}){
  const [dashTab, setDashTab] = useState("account");
  const [editDog, setEditDog] = useState(null);
  const [editBreeder, setEditBreeder] = useState(null);
  const [editLitter, setEditLitter] = useState(null);
  const [inlineDog, setInlineDog] = useState(null);
  const [viewDogDetail, setViewDogDetail] = useState(null);
  const [viewDogReadOnly, setViewDogReadOnly] = useState(false);

  const isBreeder = isAdmin || member?.role==="breeder";
  const tierRank = getTierRank(member, isAdmin);
  const isContributor = !!user && !isBreeder && !isAdmin && tierRank === 0;
  const canBackOffice = tierRank >= 2;
  const myBreeder = Object.values(data.breeders||{}).find(b=>b.email===user?.email);
  // A buyer who accepted a dog transfer has no breeders row at all (Option B —
  // current_owner_member_id references members.id directly, never a breeders
  // row) — the currentOwnerMemberId clause is what makes their transferred
  // dog show up here. A dog I bred but transferred away must NOT show as
  // active just because recordOwnerId still points to me — recordOwnerId
  // never changes, so that's gated on currentOwnerMemberId being unset.
  const myDogs             = Object.values(data.dogs||{}).filter(d=>d.currentOwnerMemberId===user?.id || (d.recordOwnerId===myBreeder?.id && !d.currentOwnerMemberId));
  const previouslyOwnedDogs = Object.values(data.dogs||{}).filter(d=>d.recordOwnerId===myBreeder?.id && d.currentOwnerMemberId && d.currentOwnerMemberId!==user?.id);
  const recordedByMe = Object.values(data.dogs||{}).filter(d=>d.recordedById===myBreeder?.id && d.recordOwnerId!==myBreeder?.id);
  const myLitters = Object.values(data.litters||{}).filter(l=>l.breederId===myBreeder?.id);

  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistView, setWaitlistView] = useState('active');
  const [matchPickerFor, setMatchPickerFor] = useState(null);
  const [buyerSearch, setBuyerSearch] = useState('');
  const MAX_PUPPY_PHOTOS = 10;
  // litter_puppies is loaded once, app-wide, in the top-level loadDB effect
  // (public SELECT policy — every visitor sees the same table) and lives in
  // data.litterPuppies. Deriving from — and writing back through — that one
  // top-level store, instead of a separate breeder-scoped fetch/state here,
  // is what keeps the public Litters browse page in sync with puppies added
  // or edited from this dashboard without needing a full page reload. An
  // earlier version kept a second, independent copy here; it worked for the
  // dashboard itself but the public page's copy never learned about the
  // change, so newly-added puppies silently never appeared there.
  const litterPuppies = (data.litterPuppies||[]).filter(p=>p.breeder_id===myBreeder?.id);
  useEffect(() => {
    if (!myBreeder?.id || tierRank < 3) return;
    supabase.from('waitlist_entries').select('*').eq('breeder_id', myBreeder.id)
      .order('joined_at', { ascending: false })
      .then(({ data }) => setWaitlistEntries(data || []));
  }, [myBreeder?.id, tierRank]);

  const [contractTemplates, setContractTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  useEffect(() => {
    if (!myBreeder?.id || tierRank < 3) return;
    supabase.from('contract_templates').select('*').eq('breeder_id', myBreeder.id)
      .order('created_at', { ascending: false }).then(({ data }) => setContractTemplates(data || []));
    supabase.from('contracts').select('*').eq('breeder_id', myBreeder.id)
      .order('created_at', { ascending: false }).then(({ data }) => setContracts(data || []));
  }, [myBreeder?.id, tierRank]);
  const [sendContractCtx, setSendContractCtx] = useState(undefined);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [viewingMatchFor, setViewingMatchFor] = useState(null);
  const [exportingData, setExportingData] = useState(false);
  const [showPuppyHealthLog, setShowPuppyHealthLog] = useState(false);
  const [showDogBulkHealthLog, setShowDogBulkHealthLog] = useState(false);

  // Breeder Data Export — downloads a ZIP of everything api/export-data.js
  // can find for this breeder (dogs, litters, contracts, health records,
  // photos, etc). Auth via bearer token like every other endpoint here;
  // the blob-then-click-a-link dance is just how an authenticated binary
  // download works from fetch (a plain <a href> can't carry the header).
  const exportMyData = async () => {
    setExportingData(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/export-data', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(()=>({}));
        throw new Error(err.error || 'Export failed');
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const match = /filename="([^"]+)"/.exec(resp.headers.get('Content-Disposition') || '');
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'upr-export.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      flash('Export downloaded ✓');
    } catch (e) {
      flash('⚠ ' + e.message);
    } finally {
      setExportingData(false);
    }
  };

  const addPuppy = async (litterId) => {
    const id = crypto.randomUUID();
    const row = { id, litter_id: litterId, breeder_id: myBreeder.id, sex: '', color: '', price: '', label: '', status: 'available', photo_urls: [] };
    const { error } = await supabase.from('litter_puppies').insert(row);
    if (error) { flash('⚠ ' + error.message); return; }
    setData(prev => ({...prev, litterPuppies: [...(prev.litterPuppies||[]), { ...row, created_at: new Date().toISOString() }]}));
  };
  const updatePuppy = async (id, patch) => {
    setData(prev => ({...prev, litterPuppies: (prev.litterPuppies||[]).map(p => p.id===id ? {...p, ...patch} : p)}));
    const { error } = await supabase.from('litter_puppies').update(patch).eq('id', id);
    if (error) flash('⚠ ' + error.message);
  };
  // Manual status toggle is for walk-in buyers with no waitlist entry — a puppy
  // currently linked to a real match (isLinked) must be unmatched from the
  // Waitlist tab instead, so the waitlist entry's matched_puppy_id never points
  // at a puppy the entry doesn't actually know was released back.
  const togglePuppyStatus = (p, isLinked) => {
    if (p.status === 'matched' && isLinked) { flash('This puppy is matched to a waitlist entry — use Unmatch on the Waitlist tab.'); return; }
    updatePuppy(p.id, { status: p.status==='available' ? 'matched' : 'available' });
  };
  const removePuppy = async (id) => {
    const p = litterPuppies.find(x=>x.id===id);
    if (p?.status === 'matched') { flash("Matched puppies can't be removed — unmatch from the Waitlist tab first."); return; }
    if (!window.confirm('Remove this puppy record?')) return;
    setData(prev => ({...prev, litterPuppies: (prev.litterPuppies||[]).filter(x => x.id!==id)}));
    const { error } = await supabase.from('litter_puppies').delete().eq('id', id);
    if (error) flash('⚠ ' + error.message);
  };
  // Reuses the same 'dog-photos' bucket every other photo upload in this app
  // uses. Unlike the single-photo entities (dogs/breeders/litters, which
  // upsert one fixed path), each puppy can have a few photos, so the path is
  // unique per upload (puppyId + timestamp) rather than overwritten.
  const addPuppyPhoto = async (puppyId, file) => {
    const p = litterPuppies.find(x=>x.id===puppyId);
    if ((p?.photo_urls||[]).length >= MAX_PUPPY_PHOTOS) { flash(`Maximum ${MAX_PUPPY_PHOTOS} photos per puppy.`); return; }
    try {
      const ext = file.name.split('.').pop();
      const path = `litter-puppies/${puppyId}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('dog-photos').upload(path, file, {upsert:true});
      if (upErr) throw upErr;
      const { data: udata } = supabase.storage.from('dog-photos').getPublicUrl(path);
      await updatePuppy(puppyId, { photo_urls: [...(p?.photo_urls||[]), udata.publicUrl] });
    } catch (e) { flash('⚠ Photo upload failed: ' + e.message); }
  };
  const removePuppyPhoto = (puppyId, url) => {
    const p = litterPuppies.find(x=>x.id===puppyId);
    updatePuppy(puppyId, { photo_urls: (p?.photo_urls||[]).filter(u=>u!==url) });
  };

  const updateWaitlistStatus = async (id, status) => {
    const { error } = await supabase.from('waitlist_entries').update({ status }).eq('id', id);
    if (error) { flash('⚠ ' + error.message); return; }
    setWaitlistEntries(prev => prev.map(e => e.id===id ? {...e, status} : e));
  };
  const updateWaitlistNotes = async (id, breeder_notes) => {
    setWaitlistEntries(prev => prev.map(e => e.id===id ? {...e, breeder_notes} : e));
    const { error } = await supabase.from('waitlist_entries').update({ breeder_notes }).eq('id', id);
    if (error) flash('⚠ ' + error.message);
  };
  const matchToPuppy = async (entryId, puppyId) => {
    const matched_at = new Date().toISOString();
    const { error } = await supabase.from('waitlist_entries')
      .update({ status: 'converted', matched_puppy_id: puppyId, matched_at }).eq('id', entryId);
    if (error) { flash('⚠ ' + error.message); return; }
    await supabase.from('litter_puppies').update({ status: 'matched' }).eq('id', puppyId);
    setWaitlistEntries(prev => prev.map(e => e.id===entryId ? {...e, status:'converted', matched_puppy_id:puppyId, matched_at} : e));
    setData(prev => ({...prev, litterPuppies: (prev.litterPuppies||[]).map(p => p.id===puppyId ? {...p, status:'matched'} : p)}));
  };
  const unmatchEntry = async (entryId) => {
    const entry = waitlistEntries.find(e => e.id===entryId);
    const { error } = await supabase.from('waitlist_entries')
      .update({ status: 'active', matched_puppy_id: null, matched_at: null }).eq('id', entryId);
    if (error) { flash('⚠ ' + error.message); return; }
    if (entry?.matched_puppy_id) {
      await supabase.from('litter_puppies').update({ status: 'available' }).eq('id', entry.matched_puppy_id);
      setData(prev => ({...prev, litterPuppies: (prev.litterPuppies||[]).map(p => p.id===entry.matched_puppy_id ? {...p, status:'available'} : p)}));
    }
    setWaitlistEntries(prev => prev.map(e => e.id===entryId ? {...e, status:'active', matched_puppy_id:null, matched_at:null} : e));
  };

  const [backOfficeDog, setBackOfficeDog] = useState(null);
  const [pendingEditsCount, setPendingEditsCount] = useState(0);
  useEffect(() => {
    if (!myBreeder || !myDogs.length) return;
    supabase.from('pending_edits').select('id', {count:'exact',head:true})
      .in('dog_id', myDogs.map(d=>d.id)).eq('status','pending')
      .then(({count}) => setPendingEditsCount(count||0));
  }, [myBreeder?.id, myDogs.length]);
  const [pendingTransfersCount, setPendingTransfersCount] = useState(0);
  // Independent of whether the Transfers tab has ever been opened this
  // session — TransfersPanel only computes this count once it mounts, so
  // without this the nav badge stayed at 0 until the user visited the tab.
  useEffect(() => {
    if (!user?.id) { setPendingTransfersCount(0); return; }
    let cancelled = false;
    (async () => {
      const [transfersRes, returnsRes] = await Promise.all([
        supabase.from('dog_transfers').select('id', {count:'exact',head:true})
          .eq('status','pending').or(`initiated_by_member_id.eq.${user.id},to_email.eq.${user.email.toLowerCase()}`),
        supabase.from('dog_returns').select('id', {count:'exact',head:true}).eq('status','pending'),
      ]);
      if (cancelled) return;
      setPendingTransfersCount((transfersRes.count||0) + (returnsRes.count||0));
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.email, supabase]);

  const [uprRegs, setUprRegs] = useState({});
  const [uprRegDog, setUprRegDog] = useState(null);
  useEffect(() => {
    if (!myBreeder?.id || !myDogs.length) return;
    supabase.from('upr_registrations')
      .select('dog_id, status, upr_number, created_at')
      .eq('breeder_id', myBreeder.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        for (const row of data) {
          if (!map[row.dog_id]) map[row.dog_id] = row;
        }
        setUprRegs(map);
      });
  }, [myBreeder?.id, myDogs.length]);

const uploadPhoto = async (file, dogId) => {
    const ext = file.name.split('.').pop();
    const path = `dogs/${dogId}.${ext}`;
    const { error } = await supabase.storage
      .from('dog-photos')
      .upload(path, file, { upsert: true });
    if (error) throw error;
    const { data } = supabase.storage
      .from('dog-photos')
      .getPublicUrl(path);
    return data.publicUrl;
  };

  const saveDog = async (dog) => {
    let effectiveBreeder;
    try { effectiveBreeder = await ensureBreeder(); }
    catch(e) { flash('⚠ '+e.message); return; }
    let finalDog = {
      ...dog,
      breederId:     effectiveBreeder?.id || dog.breederId,
      recordOwnerId: dog.recordOwnerId  || effectiveBreeder?.id || null,
      recordedById:  dog.recordedById   || effectiveBreeder?.id || null,
      recordType:    dog.recordType     || 'owned',
    };
    // If there's a pending file upload, do that first
    if (dog._photoFile) {
      try {
        flash("Uploading photo…");
        const url = await uploadPhoto(dog._photoFile, dog.id);
        finalDog = { ...finalDog, photoUrl: url, _photoFile: undefined };
      } catch(e) {
        flash("⚠ Photo upload failed: " + e.message);
        return;
      }
    }
    // Remove internal-only fields before saving
    const { _photoFile, ...dogToSave } = finalDog;
    setData(prev=>({...prev, dogs:{...prev.dogs, [dogToSave.id]: dogToSave}}));
    setEditDog(null);
    flash("Saving…");
    try {
      const { error } = await supabase.from('dogs').upsert(SB.dogToDB(dogToSave));
      if (error) throw error;
      flash("Dog saved! ✓");
    } catch(e) {
      flash("⚠ Saved locally — sync failed: " + e.message);
    }
  };

  const downloadCert = async (dogId, uprNum) => {
    // Opens the freshly-generated PDF in a new tab (browsers show PDFs inline
    // in their built-in viewer) instead of forcing a save-file prompt — the
    // viewer's own controls still let someone download/print/save if they
    // actually want the file. The blank tab is opened synchronously, before
    // any await, so it stays inside this click's trusted user-gesture window
    // and popup blockers don't swallow it; we redirect that tab to the real
    // blob URL once the certificate is ready.
    const tab = window.open('', '_blank');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { flash('Not signed in'); if (tab) tab.close(); return; }
      flash('Generating certificate…');
      const resp = await fetch('/api/generate-cert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ dogId }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        flash(err.error || 'Certificate generation failed');
        if (tab) tab.close();
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank'); // popup was blocked before the fetch — best effort now
      // Revoked well after the tab has had time to load the PDF into its
      // viewer, not immediately — an immediate revoke can blank out a tab
      // that hasn't finished loading the blob yet.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      flash('Certificate ready ⚜');
    } catch (e) {
      if (tab) tab.close();
      flash('Certificate generation failed: ' + e.message);
    }
  };

  const deleteDog = async (id)=>{
    if(!window.confirm("Delete this dog?")) return;
    setData(prev=>{const d={...prev.dogs};delete d[id];return{...prev,dogs:d};});
    flash("Deleting…");
    await supabase.from('dogs').delete().eq('id',id);
    flash("Dog deleted.");
  };

  const saveLitter = async (litter)=>{
    const prevStatus = data.litters[litter.id]?.litterStatus || null; // before this save overwrites it — needed to detect a genuine transition into "current", not just an edit while already current
    let l = {...litter, breederId: myBreeder?.id||litter.breederId};
    if(litter._photoFile) {
      try {
        flash("Uploading photo…");
        const ext = litter._photoFile.name.split('.').pop();
        const path = `litters/${l.id}.${ext}`;
        await supabase.storage.from('dog-photos').upload(path, litter._photoFile, {upsert:true});
        const {data} = supabase.storage.from('dog-photos').getPublicUrl(path);
        l = {...l, photoUrl: data.publicUrl};
      } catch(e) { flash("⚠ Photo upload failed: " + e.message); return; }
    }
    const {_photoFile, _litterType, ...litterToSave} = l;
    setData(prev=>({...prev,litters:{...prev.litters,[litterToSave.id]:litterToSave}}));
    setEditLitter(null);
    flash("Saving…");
    await supabase.from('litters').upsert(SB.litterToDB(litterToSave));
    flash("Litter saved! ✓");
    // Fire-and-forget — a slow/unconfigured notify step should never block the save
    // the breeder is waiting on. Only when this save is a genuine transition INTO
    // "current" (new litter posted as current, or an existing one just went current).
    if (litterToSave.litterStatus==='current' && prevStatus!=='current') {
      supabase.auth.getSession().then(({data:{session}})=>{
        fetch('/api/notify-waitlist', {
          method:'POST',
          headers:{'Content-Type':'application/json',Authorization:`Bearer ${session.access_token}`},
          body:JSON.stringify({litterId:litterToSave.id}),
        })
          .then(r=>r.json().then(json=>({httpStatus:r.status, json})))
          .then(({httpStatus,json})=>console.log('[notify-waitlist]', httpStatus, json))
          .catch(e=>console.log('[notify-waitlist] call failed:',e.message));
      });
    }
  };

  const deleteLitter = async (id)=>{
    // Same protection removePuppy already gives individual matched puppies —
    // deleting the whole litter must not silently orphan a
    // waitlist_entries.matched_puppy_id reference.
    if ((data.litterPuppies||[]).some(p=>p.litter_id===id && p.status==='matched')) {
      flash("This litter has matched puppies on file — unmatch them from the Waitlist tab first.");
      return;
    }
    if(!window.confirm("Remove this litter listing?")) return;
    setData(prev=>{const l={...prev.litters};delete l[id];return{...prev,litters:l};});
    await supabase.from('litters').delete().eq('id',id);
    flash("Litter removed.");
  };

  const saveMyBreeder = async (b)=>{
    if(!(b.kennelCategories||[]).length){ flash("Pick at least one kennel category."); return; }
    let breederToSave = {...b};
    if(b._photoFile) {
      try {
        const ext = b._photoFile.name.split('.').pop();
        const path = `kennels/${b.id}.${ext}`;
        await supabase.storage.from('dog-photos').upload(path, b._photoFile, {upsert:true});
        const {data} = supabase.storage.from('dog-photos').getPublicUrl(path);
        breederToSave = {...breederToSave, photoUrl: data.publicUrl};
      } catch(e) { console.log('Photo error:', e); }
    }
    if(b._logoFile) {
      try {
        const ext = b._logoFile.name.split('.').pop();
        const path = `kennel-logos/${b.id}.${ext}`;
        await supabase.storage.from('dog-photos').upload(path, b._logoFile, {upsert:true});
        const {data} = supabase.storage.from('dog-photos').getPublicUrl(path);
        breederToSave = {...breederToSave, logoUrl: data.publicUrl};
      } catch(e) { console.log('Logo error:', e); }
    }
    const {_photoFile, _logoFile, ...finalBreeder} = breederToSave;
    setData(prev=>({...prev,breeders:{...prev.breeders,[finalBreeder.id]:finalBreeder}}));
    setEditBreeder(null);
    flash("Saving…");
    await supabase.from('breeders').upsert(SB.breederToDB(finalBreeder));
    flash("Profile saved! ✓");
  };

  const roleLabel = ()=>{
    if(isAdmin) return "Admin · Founder";
    if(member?.role==="breeder"){
      if(member?.tier==="elite")        return "Elite Breeder";
      if(member?.tier==="professional") return "Professional Breeder";
      if(member?.tier==="foundation")   return "Foundation Breeder";
      return "Breeder";
    }
    if(member?.role==="buyer_full") return "Full Registry Access";
    if(member?.role==="buyer_bundle") return `Pedigree Bundle`;
    if(member?.role==="buyer_single") return "Single Pedigree";
    return "Free Account";
  };

  return(
    <div style={{maxWidth:900,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:"serif",fontSize:28,fontWeight:900,margin:"0 0 4px"}}>{isBreeder?"Breeder Dashboard":"My Account"}</h2>
          <div style={{fontSize:13,color:C.slate}}>{user?.email}</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{background:`${C.gold}22`,border:`1px solid ${C.gold}44`,borderRadius:20,padding:"4px 14px",fontSize:12,color:C.goldL}}>{roleLabel()}</span>
          <button className="bgs" onClick={async()=>{await supabase.auth.signOut();setView("home");flash("Signed out");}}>Sign Out</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:2,marginBottom:24,borderBottom:`1px solid ${C.border}`,flexWrap:"wrap"}}>
        {[
          ["account","My Account"],
          ...(user?[["dogs","My Dogs"]]:[]),
          ...(tierRank>=3?[
            ["profile","Kennel Profile"],
            ...((myBreeder?.kennelCategories||['purebred','designer','custom']).includes('purebred')?[["breeds","My Breeds"]]:[]),
            ...((myBreeder?.kennelCategories||['purebred','designer','custom']).includes('designer')?[["designer","My Designer Breeds"]]:[]),
            ...((myBreeder?.kennelCategories||['purebred','designer','custom']).includes('custom')?[["crosses","My Custom Crosses"]]:[]),
            ["litters","Litter Listings"],
            ["waitlist","Waitlist"],
            ["planner","Breeding Planner"],
            ["contracts","Contracts"],
          ]:[]),
          ...(tierRank>=2?[["import","Bulk Import"]]:[]),
          ...(pendingEditsCount>0?[["edits",`Pending Edits (${pendingEditsCount})`]]:[]),
          ...(user?[["transfers",pendingTransfersCount>0?`Transfers (${pendingTransfersCount})`:"Transfers"]]:[]),
          ["upgrade","Upgrade"],
        ].map(([v,l])=>(
          <button key={v} onClick={()=>setDashTab(v)} style={{padding:"8px 18px",background:"transparent",border:"none",borderBottom:`2px solid ${dashTab===v?C.gold:"transparent"}`,color:dashTab===v?C.gold:C.slate,fontSize:13,cursor:"pointer",fontFamily:"sans-serif",fontWeight:dashTab===v?700:400,marginBottom:-1,transition:"all 0.15s"}}>{l}</button>
        ))}
      </div>

      {/* ── ACCOUNT TAB ── */}
      {dashTab==="account"&&(
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28}}>
          <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:16}}>Account Info</div>
          <div style={{fontSize:16,color:C.cream,marginBottom:4}}>{member?.full_name||user?.email}</div>
          <div style={{fontSize:13,color:C.slate,marginBottom:16}}>{user?.email}</div>
          {isContributor&&(
            <div style={{marginTop:16}}>
              <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:16,marginBottom:12}}>
                <div style={{fontSize:11,color:C.gold,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Free Contributor</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[["✅","View full pedigrees for any dog in the registry"],["✅","Add unlimited dogs manually, one at a time"],["✅","Suggest edits to any dog record"],["✅","Approve or reject suggestions on your own dogs"],["❌","Certificate downloads (Foundation $29/mo)"],["❌","Bulk import & back office (Professional $57/mo)"],["❌","Public kennel profile & litter listings (Elite $97/mo)"]].map(([icon,text])=>(
                    <div key={text} style={{display:"flex",gap:8,fontSize:13,color:icon==="✅"?C.cream:C.slate}}><span>{icon}</span><span>{text}</span></div>
                  ))}
                </div>
              </div>
              <div style={{fontSize:12,color:C.slate}}>Want certificate downloads or breeder tools? <button className="tl" onClick={()=>setDashTab("upgrade")}>See paid plans →</button></div>
            </div>
          )}
        </div>
      )}

      {/* ── KENNEL PROFILE TAB ── */}
      {dashTab==="profile"&&tierRank>=3&&(
        <div>
          {myBreeder ? (
            <div>
              <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:12,padding:24,marginBottom:16,display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
                <div style={{width:72,height:72,borderRadius:10,border:`2px solid ${C.gold}`,background:C.bg4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,flexShrink:0}}>
                  {myBreeder.photoUrl?<img src={myBreeder.photoUrl} alt={myBreeder.kennel} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:8}}/>:"🏡"}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"serif",fontSize:22,fontWeight:900,marginBottom:4}}>{myBreeder.kennel}</div>
                  <div style={{fontSize:13,color:C.slate,marginBottom:8}}>{myBreeder.name} · {myBreeder.country}</div>
                  {myBreeder.bio&&<div style={{fontSize:13,color:C.creamD,lineHeight:1.7}}>{myBreeder.bio}</div>}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end"}}>
                  <button className="bos" onClick={()=>setEditBreeder({...myBreeder})}>Edit Profile</button>
                  <button className="bgs" style={{fontSize:11}} disabled={exportingData} onClick={exportMyData} title="Download all your dogs, litters, contracts, health records, and photos as a ZIP">
                    {exportingData?'Preparing export…':'⬇ Export My Data'}
                  </button>
                </div>
              </div>
              {editBreeder&&(
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:16}}>
                  <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:16}}>Edit Kennel Profile</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    {[["Kennel Name","kennel"],["Contact Name","name"],["Email","email"],["Website","website"],["Region/State","region"]].map(([label,field])=>(
                      <div key={field}>
                        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                        <input className="fi" value={editBreeder[field]||""} onChange={e=>setEditBreeder(p=>({...p,[field]:e.target.value}))}/>
                      </div>
                    ))}
                    <div>
                      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                      <select className="fi" value={editBreeder.country||"United States"} onChange={e=>setEditBreeder(p=>({...p,country:e.target.value}))}>
                        {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{gridColumn:"1/-1"}}>
                      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Kennel Photo</label>
                      <input className="fi" placeholder="https://… or upload below" value={editBreeder.photoUrl||""} onChange={e=>setEditBreeder(p=>({...p,photoUrl:e.target.value}))}/>
                      <label style={{display:"inline-block",marginTop:6,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                        📷 Upload Kennel Photo
                        <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setEditBreeder(p=>({...p,_photoFile:f,photoUrl:URL.createObjectURL(f)}));e.target.value="";}}/>
                      </label>
                      {editBreeder.photoUrl&&<img src={editBreeder.photoUrl} alt="preview" style={{display:"block",marginTop:8,width:80,height:80,objectFit:"cover",borderRadius:"50%",border:`2px solid ${C.gold}66`}}/>}
                    </div>
                    {tierRank>=3&&(
                      <div style={{gridColumn:"1/-1",marginTop:4}}>
                        <label style={{fontSize:10,color:C.amber,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>
                          ⭐ Elite Kennel Logo <span style={{color:C.slate,fontWeight:400,textTransform:"none",letterSpacing:0}}>(appears on pedigree PDFs)</span>
                        </label>
                        {editBreeder.logoUrl&&(
                          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                            <img src={editBreeder.logoUrl} alt="logo" style={{width:60,height:60,objectFit:"contain",border:`1px solid ${C.gold}44`,borderRadius:6,background:"#fff",padding:4}}/>
                            <button className="bgs" style={{fontSize:11}} onClick={()=>setEditBreeder(p=>({...p,logoUrl:null,_logoFile:undefined}))}>Remove logo</button>
                          </div>
                        )}
                        <label style={{display:"inline-block",background:`linear-gradient(135deg,${C.amber},${C.gold})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                          🖼 Upload Logo (PNG/JPG/WebP · max 2 MB)
                          <input type="file" accept="image/png,image/jpeg,image/webp" style={{display:"none"}} onChange={e=>{
                            const f=e.target.files[0];if(!f)return;
                            if(f.size>2*1024*1024){flash("Logo must be under 2 MB");return;}
                            setEditBreeder(p=>({...p,_logoFile:f,logoUrl:URL.createObjectURL(f)}));
                            e.target.value="";
                          }}/>
                        </label>
                      </div>
                    )}
                    <div style={{gridColumn:"1/-1",marginTop:4,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                      <label style={{fontSize:10,color:C.gold,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Waitlist Setup</label>
                      <div style={{fontSize:11,color:C.slate,marginBottom:10,lineHeight:1.5}}>Already run a waitlist elsewhere? Paste its URL below and "Join Waitlist" sends buyers straight there. Leave blank to use UPR's free built-in waitlist instead.</div>
                      <div>
                        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>External Waitlist URL (optional)</label>
                        <input className="fi" placeholder="https://yoursite.com/waitlist" value={editBreeder.externalWaitlistUrl||""} onChange={e=>setEditBreeder(p=>({...p,externalWaitlistUrl:e.target.value}))}/>
                      </div>
                      {!editBreeder.externalWaitlistUrl&&(
                        <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:10}}>
                          <div>
                            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Waitlist Policy (optional)</label>
                            <textarea className="fi" rows={2} placeholder="e.g. $200 non-refundable deposit required, applied to purchase price" value={editBreeder.waitlistPolicy||""} onChange={e=>setEditBreeder(p=>({...p,waitlistPolicy:e.target.value}))} style={{resize:"vertical"}}/>
                          </div>
                          <div>
                            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Deposit Payment Link (optional)</label>
                            <input className="fi" placeholder="PayPal.me, Venmo, your own Stripe link, etc." value={editBreeder.waitlistPaymentLink||""} onChange={e=>setEditBreeder(p=>({...p,waitlistPaymentLink:e.target.value}))}/>
                          </div>
                          <div style={{fontSize:10,color:C.slate,lineHeight:1.5}}>UPR never processes payment or sees transaction data — this just shows buyers your link, same as the Inquire button.</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{marginTop:16}}>
                    <CategoryCheckboxes value={editBreeder.kennelCategories} onChange={cats=>setEditBreeder(p=>({...p,kennelCategories:cats}))}/>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
                    <button className="bgs" onClick={()=>setEditBreeder(null)}>Cancel</button>
                    <button className="bgl" disabled={!(editBreeder.kennelCategories||[]).length} onClick={()=>saveMyBreeder(editBreeder)}>Save Profile</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:32,textAlign:"center"}}>
              <div style={{fontSize:48,marginBottom:12}}>🏡</div>
              <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,marginBottom:8}}>Set Up Your Kennel Profile</div>
              <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:20}}>Your kennel profile is visible to puppy buyers worldwide. Add your kennel name, bio, and contact info.</p>
              <button className="bgl" onClick={()=>setEditBreeder({id:crypto.randomUUID(),kennel:"",name:"",email:user?.email||"",country:"United States",region:"",website:"",bio:"",joined:new Date().toISOString().split("T")[0],tiers:["foundation"],photoUrl:"",verified:false,blocked:false,kennelCategories:['purebred','designer','custom']})}>Create Kennel Profile</button>
              {editBreeder&&(
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginTop:16,textAlign:"left"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    {[["Kennel Name","kennel"],["Your Name","name"],["Website","website"],["Region/State","region"]].map(([label,field])=>(
                      <div key={field}>
                        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                        <input className="fi" value={editBreeder[field]||""} onChange={e=>setEditBreeder(p=>({...p,[field]:e.target.value}))}/>
                      </div>
                    ))}
                    <div>
                      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                      <select className="fi" value={editBreeder.country||"United States"} onChange={e=>setEditBreeder(p=>({...p,country:e.target.value}))}>
                        {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{gridColumn:"1/-1"}}>
                      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Bio</label>
                      <textarea className="fi" rows={3} value={editBreeder.bio||""} onChange={e=>setEditBreeder(p=>({...p,bio:e.target.value}))} style={{resize:"vertical"}}/>
                    </div>
                    <div style={{gridColumn:"1/-1"}}>
                      <CategoryCheckboxes value={editBreeder.kennelCategories} onChange={cats=>setEditBreeder(p=>({...p,kennelCategories:cats}))}/>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
                    <button className="bgs" onClick={()=>setEditBreeder(null)}>Cancel</button>
                    <button className="bgl" disabled={!(editBreeder.kennelCategories||[]).length} onClick={()=>saveMyBreeder(editBreeder)}>Save Profile</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── MY BREEDS TAB ── */}
      {dashTab==="breeds"&&tierRank>=3&&(
        myBreeder ? (
          <BreederBreedsManager
            breeder={myBreeder}
            breeds={breeds}
            breederBreeds={breederBreeds}
            setBreederBreeds={setBreederBreeds}
            supabase={supabase}
            flash={flash}
          />
        ) : (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:32,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>🐕</div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,marginBottom:8}}>Set Up Your Kennel First</div>
            <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:20}}>Before choosing breeds, create your kennel profile on the Kennel Profile tab.</p>
            <button className="bos" onClick={()=>setDashTab("profile")}>Go to Kennel Profile →</button>
          </div>
        )
      )}

      {/* ── MY DESIGNER BREEDS TAB ── */}
      {dashTab==="designer"&&tierRank>=3&&(
        myBreeder ? (
          <DesignerBreedsManager
            breeder={myBreeder}
            designerBreeds={designerBreeds}
            breederDesignerBreeds={breederDesignerBreeds}
            setBreederDesignerBreeds={setBreederDesignerBreeds}
            supabase={supabase}
            flash={flash}
          />
        ) : (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:32,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>🐕</div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,marginBottom:8}}>Set Up Your Kennel First</div>
            <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:20}}>Before choosing designer breeds, create your kennel profile on the Kennel Profile tab.</p>
            <button className="bos" onClick={()=>setDashTab("profile")}>Go to Kennel Profile →</button>
          </div>
        )
      )}

      {/* ── MY CUSTOM CROSSES TAB ── */}
      {dashTab==="crosses"&&tierRank>=3&&(
        myBreeder ? (
          <CustomCrossesManager
            breeder={myBreeder}
            customCrosses={customCrosses}
            setCustomCrosses={setCustomCrosses}
            breeds={breeds}
            supabase={supabase}
            flash={flash}
          />
        ) : (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:32,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>🐕</div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,marginBottom:8}}>Set Up Your Kennel First</div>
            <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:20}}>Before creating custom crosses, create your kennel profile on the Kennel Profile tab.</p>
            <button className="bos" onClick={()=>setDashTab("profile")}>Go to Kennel Profile →</button>
          </div>
        )
      )}

      {/* ── MY DOGS TAB ── */}
      {dashTab==="dogs"&&user&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:C.slate}}>{isBreeder?`${myDogs.length} owned · ${recordedByMe.length} recorded`:`${myDogs.length} dog${myDogs.length!==1?'s':''}`}</div>
            <div style={{display:"flex",gap:8}}>
              {canBackOffice&&<button className="bgs" style={{fontSize:11,color:C.emeraldL,borderColor:C.emeraldL+'44'}} onClick={()=>setShowDogBulkHealthLog(v=>!v)}>🩺 Bulk Health Log</button>}
              <button className="bos" onClick={()=>setEditDog({id:crypto.randomUUID(),type:"purebred",breed:"",name:"",callName:"",sex:"M",color:"",dob:"",country:"United States",regNum:"",regBody:"AKC",ofaHips:"",ofaElbows:"",ofaEyes:"",chicNum:"",mdr1:"",baer:"",titles:"",breederId:myBreeder?.id||"",sireId:"",damId:"",photoUrl:"",notes:"",hidden:false})}>+ Add Dog</button>
            </div>
          </div>

          {showDogBulkHealthLog && (
            <DogBulkHealthLog
              myDogs={myDogs}
              allDogs={data.dogs}
              breeders={data.breeders}
              supabase={supabase}
              flash={flash}
              breederId={myBreeder?.id||''}
              onDogsUpdate={(patched)=>setData(prev=>({...prev,dogs:{...prev.dogs,...Object.fromEntries(patched.map(d=>[d.id,d]))}}))}
              onClose={()=>setShowDogBulkHealthLog(false)}
            />
          )}

          {editDog&&!data.dogs[editDog.id]&&(
            <div style={{background:C.bg2,border:`1px solid ${C.gold}44`,borderRadius:12,padding:24,marginBottom:16}}>
              <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:16}}>{editDog.name?"Edit Dog":"Add New Dog"}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {[["Registered Name *","name","text"],["Call Name","callName","text"],["Color/Markings","color","text"],["Date of Birth","dob","date"],["Registration Number","regNum","text"],["DNA Number","dnaNum","text"],["CHIC Number","chicNum","text"],["BAER Hearing","baer","text"],["Titles & Awards","titles","text"]].map(([label,field,type])=>(
                  <div key={field}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                    <input className="fi" type={type} value={editDog[field]||""} onChange={e=>setEditDog(p=>({...p,[field]:e.target.value}))}/>
                  </div>
                ))}
                <OfaResultField testKey="ofa_hips" label="OFA Hips" value={editDog.ofaHips} onChange={v=>setEditDog(p=>({...p,ofaHips:v}))}
                  certValue={editDog.ofaHipsCert} onCertChange={v=>setEditDog(p=>({...p,ofaHipsCert:v}))}
                  registryValue={editDog.ofaHipsRegistry} onRegistryChange={v=>setEditDog(p=>({...p,ofaHipsRegistry:v}))} altRegistries={['PennHIP']}
                  diValue={editDog.ofaHipsDi} onDiChange={v=>setEditDog(p=>({...p,ofaHipsDi:v}))}/>
                <OfaResultField testKey="ofa_elbows" label="OFA Elbows" value={editDog.ofaElbows} onChange={v=>setEditDog(p=>({...p,ofaElbows:v}))}
                  certValue={editDog.ofaElbowsCert} onCertChange={v=>setEditDog(p=>({...p,ofaElbowsCert:v}))}/>
                <OfaResultField testKey="ofa_eyes" label="OFA Eyes" value={editDog.ofaEyes} onChange={v=>setEditDog(p=>({...p,ofaEyes:v}))}/>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breed *</label>
                  {editDog.type==="purebred" ? (
                    <BreedPicker breederId={myBreeder?.id} breeds={breeds} breederBreeds={breederBreeds}
                      selectedId={editDog.breedId} selectedName={editDog.breed}
                      onChange={b=>setEditDog(p=>({...p,breedId:b?.id||null,designerBreedId:null,customCrossId:null,breed:b?.name||""}))}
                      onGoToMyBreeds={()=>setDashTab("breeds")}/>
                  ) : (
                    <BreedPicker breederId={myBreeder?.id} breeds={designerBreeds} breederBreeds={breederDesignerBreeds}
                      rowKey="designer_breed_id" gateLabel="My Designer Breeds"
                      customCrosses={customCrosses[myBreeder?.id]||[]}
                      selectedId={editDog.designerBreedId} selectedCustomId={editDog.customCrossId} selectedName={editDog.breed}
                      onChange={b=>setEditDog(p=>({...p,designerBreedId:(b&&!b.isCustom)?b.id:null,customCrossId:(b&&b.isCustom)?b.id:null,breedId:null,breed:b?.name||""}))}
                      onGoToMyBreeds={()=>setDashTab("designer")}/>
                  )}
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sex</label>
                  <select className="fi" value={editDog.sex||"M"} onChange={e=>setEditDog(p=>({...p,sex:e.target.value}))}>
                    <option value="M">Male (Sire)</option>
                    <option value="F">Female (Dam)</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>MDR1 Status</label>
                  <select className="fi" value={editDog.mdr1||""} onChange={e=>setEditDog(p=>({...p,mdr1:e.target.value}))}>
                    <option value="">Unknown / Not Tested</option>
                    <option value="Clear">Clear (Normal/Normal)</option>
                    <option value="Carrier">Carrier (Normal/Mutant)</option>
                    <option value="Affected">Affected (Mutant/Mutant)</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registry Body</label>
                  <select className="fi" value={editDog.regBody||"AKC"} onChange={e=>setEditDog(p=>({...p,regBody:e.target.value}))}>
                    {REG_BODIES.map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Type</label>
                  <select className="fi" value={editDog.type||"purebred"} onChange={e=>setEditDog(p=>({...p,type:e.target.value}))}>
                    <option value="purebred">Purebred</option>
                    <option value="heritage">Designer</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                  <select className="fi" value={editDog.country||"United States"} onChange={e=>setEditDog(p=>({...p,country:e.target.value}))}>
                    {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                {editDog.breed==="Australian Shepherd"&&<div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>ASCA Number</label>
                  <input className="fi" value={editDog.ascaNum||""} onChange={e=>setEditDog(p=>({...p,ascaNum:e.target.value}))}/>
                </div>}
                {editDog.breed==="German Shepherd"&&<div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>SV Number</label>
                  <input className="fi" value={editDog.svNum||""} onChange={e=>setEditDog(p=>({...p,svNum:e.target.value}))}/>
                </div>}
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sire (Father)</label>
                  {!editDog.sireId && editDog.sireNameManual && (
                    <div style={{marginBottom:6,padding:"6px 10px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:6,fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                      <span style={{color:C.creamD}}>Existing manual entry: <strong>{editDog.sireNameManual}</strong></span>
                      <button type="button" onClick={()=>setInlineDog({sex:'M',initialName:editDog.sireNameManual,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,sireId:id,sireNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                    </div>
                  )}
                  <DogAutocomplete sex="M" addLabel="sire" value={editDog.sireId} onChange={v=>setEditDog(p=>({...p,sireId:v}))} dogs={data.dogs} breeders={data.breeders} currentDogId={editDog.id}
                    onAdd={(query)=>setInlineDog({sex:'M',initialName:query,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,sireId:id}));setInlineDog(null);}})}
                    placeholder="Search sires (3+ chars)…"/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Dam (Mother)</label>
                  {!editDog.damId && editDog.damNameManual && (
                    <div style={{marginBottom:6,padding:"6px 10px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:6,fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                      <span style={{color:C.creamD}}>Existing manual entry: <strong>{editDog.damNameManual}</strong></span>
                      <button type="button" onClick={()=>setInlineDog({sex:'F',initialName:editDog.damNameManual,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,damId:id,damNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                    </div>
                  )}
                  <DogAutocomplete sex="F" addLabel="dam" value={editDog.damId} onChange={v=>setEditDog(p=>({...p,damId:v}))} dogs={data.dogs} breeders={data.breeders} currentDogId={editDog.id}
                    onAdd={(query)=>setInlineDog({sex:'F',initialName:query,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,damId:id}));setInlineDog(null);}})}
                    placeholder="Search dams (3+ chars)…"/>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Photo</label>
                  <input className="fi" placeholder="https://… or upload below" value={editDog.photoUrl||""} onChange={e=>setEditDog(p=>({...p,photoUrl:e.target.value}))}/>
                  <label style={{display:"inline-block",marginTop:6,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                    📷 Upload Photo
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setEditDog(p=>({...p,_photoFile:f,photoUrl:URL.createObjectURL(f)}));e.target.value="";}}/>
                  </label>
                  {editDog.photoUrl&&<img src={editDog.photoUrl} alt="preview" style={{display:"block",marginTop:8,width:80,height:80,objectFit:"cover",borderRadius:6,border:`2px solid ${C.gold}66`}}/>}
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Notes</label>
                  <textarea className="fi" rows={3} value={editDog.notes||""} onChange={e=>setEditDog(p=>({...p,notes:e.target.value}))} style={{resize:"vertical"}}/>
                </div>
                <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:8}}>
                  <input type="checkbox" id="hidedog" checked={editDog.hidden||false} onChange={e=>setEditDog(p=>({...p,hidden:e.target.checked}))}/>
                  <label htmlFor="hidedog" style={{fontSize:13,color:C.slate,cursor:"pointer"}}>Hide from public view (only you and admins can see)</label>
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                <button className="bgs" onClick={()=>setEditDog(null)}>Cancel</button>
                <button className="bgl" onClick={()=>saveDog(editDog)}>Save Dog</button>
              </div>
            </div>
          )}

          {myDogs.length===0&&!editDog&&(
            <div style={{textAlign:"center",padding:"48px 20px",border:`1px dashed ${C.border}`,borderRadius:12,color:C.slate}}>
              <div style={{fontSize:48,marginBottom:12}}>🐾</div>
              <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.cream,marginBottom:8}}>No Dogs Yet</div>
              <p style={{fontSize:14,lineHeight:1.7,marginBottom:20}}>Add your first dog to the registry. Include health clearances, registration numbers, and photos.</p>
              <button className="bgl" onClick={()=>setEditDog({id:crypto.randomUUID(),type:"purebred",breed:"",name:"",callName:"",sex:"M",color:"",dob:"",country:"United States",regNum:"",regBody:"AKC",ofaHips:"",ofaElbows:"",ofaEyes:"",chicNum:"",mdr1:"",baer:"",titles:"",breederId:myBreeder?.id||"",sireId:"",damId:"",photoUrl:"",notes:"",hidden:false})}>Add Your First Dog</button>
            </div>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
            {myDogs.map(d=>(
              <div key={d.id}>
              <div style={{background:C.card,border:`1px solid ${d.hidden?"#444":C.border}`,borderRadius:8,padding:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",opacity:d.hidden?0.6:1}}>
                <div onClick={()=>{setViewDogDetail(d);setViewDogReadOnly(false);}} style={{width:48,height:48,borderRadius:7,border:`2px solid ${C.gold}66`,background:C.bg4,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,overflow:"hidden",cursor:"pointer"}}>
                  {d.photoUrl?<img src={d.photoUrl} alt={d.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:"🐾"}
                </div>
                <div style={{flex:1,minWidth:160}}>
                  <div onClick={()=>{setViewDogDetail(d);setViewDogReadOnly(false);}} style={{fontSize:14,fontWeight:600,cursor:"pointer",display:"inline-block"}}>{d.name||"Unnamed"} {d.hidden&&<span style={{fontSize:10,color:C.slate}}>(hidden)</span>}</div>
                  <div style={{fontSize:11,color:C.slate}}>{d.breed} · {d.sex==="M"?"Male":"Female"} · {d.color}</div>
                  <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
                    {d.mdr1&&<span style={{fontSize:9,color:mdr1C(d.mdr1)}}>MDR1: {d.mdr1}</span>}
                    {d.ofaHips&&<span style={{fontSize:9,color:C.gold}}>OFA: {d.ofaHips}</span>}
                    {d.chicNum&&<span style={{fontSize:9,color:C.sapphireL}}>CHIC: {d.chicNum}</span>}
                    {uprRegs[d.id]?.status==='approved'&&<span style={{fontSize:9,color:C.gold,fontWeight:700}}>⚜ {uprRegs[d.id].upr_number}</span>}
                    {uprRegs[d.id]?.status==='pending'&&<span style={{fontSize:9,color:C.slate}}>Registration pending…</span>}
                    {uprRegs[d.id]?.status==='rejected'&&<span style={{fontSize:9,color:'#e67e22'}}>Registration rejected</span>}
                    {uprRegs[d.id]?.status==='approved'&&(
                      <button className="bgs" style={{fontSize:9,color:C.gold,borderColor:C.gold+'44',padding:'1px 6px'}} onClick={()=>downloadCert(d.id,uprRegs[d.id].upr_number)}>📄 View Certificate</button>
                    )}
                  </div>
                </div>
                {d.callName&&<div style={{fontSize:12,color:C.slate,fontStyle:"italic",whiteSpace:"nowrap"}}>"{d.callName}"</div>}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button className="bgs" style={{fontSize:11}} onClick={async()=>{
 const updated = {...d, hidden:!d.hidden};
          setData(prev=>({...prev,dogs:{...prev.dogs,[d.id]:updated}}));
          await supabase.from('dogs').update({hidden:updated.hidden}).eq('id',d.id);
          flash(updated.hidden?"Dog hidden":"Dog visible");
        }}>
                        {d.hidden?"Show":"Hide"}
                  </button>
                  <button className="bos" style={{fontSize:11}} onClick={()=>{setBackOfficeDog(null);setEditDog({...d});}}>Edit</button>
                  {canBackOffice&&<button className="bgs" style={{fontSize:11,color:C.emeraldL,borderColor:C.emeraldL+'44'}} onClick={()=>{setEditDog(null);setBackOfficeDog(backOfficeDog===d.id?null:d.id);}}>Back Office</button>}
                  {(!uprRegs[d.id]||uprRegs[d.id].status==='rejected')&&(
                    <button className="bgs" style={{fontSize:11,color:C.gold,borderColor:C.gold+'44'}} onClick={()=>{setEditDog(null);setBackOfficeDog(null);setUprRegDog(d);}}>Register with UPR</button>
                  )}
                  <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:11}} onClick={()=>deleteDog(d.id)}>Delete</button>
                </div>
              </div>
              {backOfficeDog===d.id&&(
                <DogBackOffice
                  dog={data.dogs[d.id]}
                  dogs={data.dogs}
                  breeders={data.breeders}
                  supabase={supabase}
                  flash={flash}
                  breederId={myBreeder?.id||''}
                  user={user}
                  onClose={()=>setBackOfficeDog(null)}
                  onDogUpdate={upd=>setData(prev=>({...prev,dogs:{...prev.dogs,[upd.id]:upd}}))}
                />
              )}
              {editDog?.id===d.id&&(
              <div style={{background:C.bg2,border:`1px solid ${C.gold}44`,borderRadius:12,padding:24,marginTop:8}}>
              <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:16}}>{editDog.name?"Edit Dog":"Add New Dog"}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {[["Registered Name *","name","text"],["Call Name","callName","text"],["Color/Markings","color","text"],["Date of Birth","dob","date"],["Registration Number","regNum","text"],["DNA Number","dnaNum","text"],["CHIC Number","chicNum","text"],["BAER Hearing","baer","text"],["Titles & Awards","titles","text"]].map(([label,field,type])=>(
                  <div key={field}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                    <input className="fi" type={type} value={editDog[field]||""} onChange={e=>setEditDog(p=>({...p,[field]:e.target.value}))}/>
                  </div>
                ))}
                <OfaResultField testKey="ofa_hips" label="OFA Hips" value={editDog.ofaHips} onChange={v=>setEditDog(p=>({...p,ofaHips:v}))}
                  certValue={editDog.ofaHipsCert} onCertChange={v=>setEditDog(p=>({...p,ofaHipsCert:v}))}
                  registryValue={editDog.ofaHipsRegistry} onRegistryChange={v=>setEditDog(p=>({...p,ofaHipsRegistry:v}))} altRegistries={['PennHIP']}
                  diValue={editDog.ofaHipsDi} onDiChange={v=>setEditDog(p=>({...p,ofaHipsDi:v}))}/>
                <OfaResultField testKey="ofa_elbows" label="OFA Elbows" value={editDog.ofaElbows} onChange={v=>setEditDog(p=>({...p,ofaElbows:v}))}
                  certValue={editDog.ofaElbowsCert} onCertChange={v=>setEditDog(p=>({...p,ofaElbowsCert:v}))}/>
                <OfaResultField testKey="ofa_eyes" label="OFA Eyes" value={editDog.ofaEyes} onChange={v=>setEditDog(p=>({...p,ofaEyes:v}))}/>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breed *</label>
                  {editDog.type==="purebred" ? (
                    <BreedPicker breederId={myBreeder?.id} breeds={breeds} breederBreeds={breederBreeds}
                      selectedId={editDog.breedId} selectedName={editDog.breed}
                      onChange={b=>setEditDog(p=>({...p,breedId:b?.id||null,designerBreedId:null,customCrossId:null,breed:b?.name||""}))}
                      onGoToMyBreeds={()=>setDashTab("breeds")}/>
                  ) : (
                    <BreedPicker breederId={myBreeder?.id} breeds={designerBreeds} breederBreeds={breederDesignerBreeds}
                      rowKey="designer_breed_id" gateLabel="My Designer Breeds"
                      customCrosses={customCrosses[myBreeder?.id]||[]}
                      selectedId={editDog.designerBreedId} selectedCustomId={editDog.customCrossId} selectedName={editDog.breed}
                      onChange={b=>setEditDog(p=>({...p,designerBreedId:(b&&!b.isCustom)?b.id:null,customCrossId:(b&&b.isCustom)?b.id:null,breedId:null,breed:b?.name||""}))}
                      onGoToMyBreeds={()=>setDashTab("designer")}/>
                  )}
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sex</label>
                  <select className="fi" value={editDog.sex||"M"} onChange={e=>setEditDog(p=>({...p,sex:e.target.value}))}>
                    <option value="M">Male (Sire)</option>
                    <option value="F">Female (Dam)</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>MDR1 Status</label>
                  <select className="fi" value={editDog.mdr1||""} onChange={e=>setEditDog(p=>({...p,mdr1:e.target.value}))}>
                    <option value="">Unknown / Not Tested</option>
                    <option value="Clear">Clear (Normal/Normal)</option>
                    <option value="Carrier">Carrier (Normal/Mutant)</option>
                    <option value="Affected">Affected (Mutant/Mutant)</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registry Body</label>
                  <select className="fi" value={editDog.regBody||"AKC"} onChange={e=>setEditDog(p=>({...p,regBody:e.target.value}))}>
                    {REG_BODIES.map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Type</label>
                  <select className="fi" value={editDog.type||"purebred"} onChange={e=>setEditDog(p=>({...p,type:e.target.value}))}>
                    <option value="purebred">Purebred</option>
                    <option value="heritage">Designer</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                  <select className="fi" value={editDog.country||"United States"} onChange={e=>setEditDog(p=>({...p,country:e.target.value}))}>
                    {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                {editDog.breed==="Australian Shepherd"&&<div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>ASCA Number</label>
                  <input className="fi" value={editDog.ascaNum||""} onChange={e=>setEditDog(p=>({...p,ascaNum:e.target.value}))}/>
                </div>}
                {editDog.breed==="German Shepherd"&&<div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>SV Number</label>
                  <input className="fi" value={editDog.svNum||""} onChange={e=>setEditDog(p=>({...p,svNum:e.target.value}))}/>
                </div>}
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sire (Father)</label>
                  {!editDog.sireId && editDog.sireNameManual && (
                    <div style={{marginBottom:6,padding:"6px 10px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:6,fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                      <span style={{color:C.creamD}}>Existing manual entry: <strong>{editDog.sireNameManual}</strong></span>
                      <button type="button" onClick={()=>setInlineDog({sex:'M',initialName:editDog.sireNameManual,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,sireId:id,sireNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                    </div>
                  )}
                  <DogAutocomplete sex="M" addLabel="sire" value={editDog.sireId} onChange={v=>setEditDog(p=>({...p,sireId:v}))} dogs={data.dogs} breeders={data.breeders} currentDogId={editDog.id}
                    onAdd={(query)=>setInlineDog({sex:'M',initialName:query,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,sireId:id}));setInlineDog(null);}})}
                    placeholder="Search sires (3+ chars)…"/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Dam (Mother)</label>
                  {!editDog.damId && editDog.damNameManual && (
                    <div style={{marginBottom:6,padding:"6px 10px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:6,fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                      <span style={{color:C.creamD}}>Existing manual entry: <strong>{editDog.damNameManual}</strong></span>
                      <button type="button" onClick={()=>setInlineDog({sex:'F',initialName:editDog.damNameManual,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,damId:id,damNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                    </div>
                  )}
                  <DogAutocomplete sex="F" addLabel="dam" value={editDog.damId} onChange={v=>setEditDog(p=>({...p,damId:v}))} dogs={data.dogs} breeders={data.breeders} currentDogId={editDog.id}
                    onAdd={(query)=>setInlineDog({sex:'F',initialName:query,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,damId:id}));setInlineDog(null);}})}
                    placeholder="Search dams (3+ chars)…"/>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Photo</label>
                  <input className="fi" placeholder="https://… or upload below" value={editDog.photoUrl||""} onChange={e=>setEditDog(p=>({...p,photoUrl:e.target.value}))}/>
                  <label style={{display:"inline-block",marginTop:6,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                    📷 Upload Photo
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setEditDog(p=>({...p,_photoFile:f,photoUrl:URL.createObjectURL(f)}));e.target.value="";}}/>
                  </label>
                  {editDog.photoUrl&&<img src={editDog.photoUrl} alt="preview" style={{display:"block",marginTop:8,width:80,height:80,objectFit:"cover",borderRadius:6,border:`2px solid ${C.gold}66`}}/>}
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Notes</label>
                  <textarea className="fi" rows={3} value={editDog.notes||""} onChange={e=>setEditDog(p=>({...p,notes:e.target.value}))} style={{resize:"vertical"}}/>
                </div>
                <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:8}}>
                  <input type="checkbox" id="hidedog" checked={editDog.hidden||false} onChange={e=>setEditDog(p=>({...p,hidden:e.target.checked}))}/>
                  <label htmlFor="hidedog" style={{fontSize:13,color:C.slate,cursor:"pointer"}}>Hide from public view (only you and admins can see)</label>
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                <button className="bgs" onClick={()=>setEditDog(null)}>Cancel</button>
                <button className="bgl" onClick={()=>saveDog(editDog)}>Save Dog</button>
              </div>
            </div>
              )}
              </div>
            ))}
          </div>
        </div>
      )}

      {uprRegDog&&(
        <UprRegistrationModal
          dog={uprRegDog}
          allDogs={data.dogs}
          myBreeder={myBreeder}
          member={member}
          supabase={supabase}
          flash={flash}
          onClose={()=>setUprRegDog(null)}
          onSuccess={result=>{
            setUprRegs(prev=>({...prev,[uprRegDog.id]:{dog_id:uprRegDog.id,status:result.status,upr_number:result.upr_number,created_at:new Date().toISOString()}}));
            loadMember(user.id);
          }}
          onEditDog={d=>{setUprRegDog(null);setEditDog({...d});}}
          onGoToImport={()=>{setUprRegDog(null);setDashTab('import');}}
        />
      )}

      {viewDogDetail&&(
        <DogDetailModal
          dog={viewDogDetail}
          allDogs={data.dogs}
          uprReg={uprRegs[viewDogDetail.id]}
          healthRequirements={healthRequirements}
          breeds={breeds}
          designerBreeds={designerBreeds}
          customCrosses={customCrosses}
          breeders={data.breeders}
          myBreeder={myBreeder}
          supabase={supabase}
          user={user}
          flash={flash}
          canTransfer={viewDogDetail.recordOwnerId===myBreeder?.id || viewDogDetail.currentOwnerMemberId===user?.id}
          readOnly={viewDogReadOnly}
          contracts={contracts}
          onClose={()=>{setViewDogDetail(null);setViewDogReadOnly(false);}}
          onEdit={()=>{setEditDog({...viewDogDetail});setViewDogDetail(null);}}
          onBackOffice={()=>{setBackOfficeDog(viewDogDetail.id);setViewDogDetail(null);}}
          onRegister={()=>{setUprRegDog(viewDogDetail);setViewDogDetail(null);}}
          onViewPedigree={()=>{navigate("pedigree", viewDogDetail.id);setViewDogDetail(null);}}
        />
      )}

      {sendContractCtx!==undefined && myBreeder && (
        <SendContractModal
          context={sendContractCtx}
          breederId={myBreeder.id}
          contractTemplates={contractTemplates}
          setContractTemplates={setContractTemplates}
          setContracts={setContracts}
          supabase={supabase}
          flash={flash}
          onClose={()=>setSendContractCtx(undefined)}
        />
      )}

      {editingTemplate && (
        <FieldEditor
          template={editingTemplate}
          breederId={myBreeder?.id}
          supabase={supabase}
          flash={flash}
          onSave={(fields)=>setContractTemplates(prev=>prev.map(t=>t.id===editingTemplate.id?{...t,field_layout:fields}:t))}
          onClose={()=>setEditingTemplate(null)}
        />
      )}

      {viewingContract && (
        <ContractDetailModal
          contract={viewingContract}
          dog={data.dogs[viewingContract.dog_id]}
          litter={myLitters.find(l=>l.id===viewingContract.litter_id)}
          supabase={supabase}
          flash={flash}
          onClose={()=>setViewingContract(null)}
          onVoided={(id)=>setContracts(prev=>prev.map(c=>c.id===id?{...c,status:'voided'}:c))}
          onDeleted={(id)=>{setContracts(prev=>prev.filter(c=>c.id!==id));setViewingContract(null);}}
        />
      )}

      {viewingMatchFor && (() => {
        const matchEntry = waitlistEntries.find(e=>e.matched_puppy_id===viewingMatchFor && e.status==='converted');
        if (!matchEntry) return null;
        const matchPuppy = litterPuppies.find(p=>p.id===viewingMatchFor) || null;
        const matchLitter = matchPuppy ? myLitters.find(l=>l.id===matchPuppy.litter_id) : null;
        const matchContract = contracts
          .filter(c=>c.waitlist_entry_id===matchEntry.id)
          .sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0] || null;
        return (
          <PuppyMatchDetailsModal
            entry={matchEntry}
            puppy={matchPuppy}
            litter={matchLitter}
            contract={matchContract}
            updateWaitlistNotes={updateWaitlistNotes}
            onSendContract={setSendContractCtx}
            onViewContract={setViewingContract}
            onClose={()=>setViewingMatchFor(null)}
          />
        );
      })()}

      {/* ── LITTER LISTINGS TAB ── */}
      {dashTab==="litters"&&tierRank>=3&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:C.slate}}>{myLitters.length} litter listings</div>
            <button className="bos" onClick={()=>setEditLitter({id:crypto.randomUUID(),breederId:myBreeder?.id||"",customName:"",breed:"",breedId:null,designerBreedId:null,_litterType:"purebred",sireId:"",damId:"",whelped:"",count:0,available:0,colors:"",country:"United States",price:"",description:"",status:"active"})}>+ Add Litter</button>
          </div>
          {myLitters.length===0&&!editLitter&&(
            <div style={{textAlign:"center",padding:"48px 20px",border:`1px dashed ${C.border}`,borderRadius:12,color:C.slate}}>
              <div style={{fontSize:48,marginBottom:12}}>🐶</div>
              <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.cream,marginBottom:8}}>No Litters Posted</div>
              <p style={{fontSize:14,lineHeight:1.7,marginBottom:20}}>Post your available litters so puppy buyers can find you.</p>
              <button className="bgl" onClick={()=>setEditLitter({id:crypto.randomUUID(),breederId:myBreeder?.id||"",customName:"",breed:"",breedId:null,designerBreedId:null,_litterType:"purebred",sireId:"",damId:"",whelped:"",count:0,available:0,colors:"",country:"United States",price:"",description:"",status:"active"})}>Post a Litter</button>
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
            {myLitters.map(l=>(
              <div key={l.id} style={{background:C.card,border:`1px solid ${l.status==="inactive"?"#444":C.emeraldL+"44"}`,borderRadius:8,padding:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",opacity:l.status==="inactive"?0.6:1}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:600}}>{litterHeadline(l)} {l.status==="inactive"&&<span style={{fontSize:10,color:C.slate}}>(inactive)</span>}</div>
                  <div style={{fontSize:11,color:C.slate}}>{l.breed}{!l.whelped&&' · Whelp date TBD'} · {l.available}/{l.count} available · {l.price}</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="bgs" style={{fontSize:11}} onClick={()=>setData(prev=>({...prev,litters:{...prev.litters,[l.id]:{...l,status:l.status==="active"?"inactive":"active"}}}))}>
                    {l.status==="active"?"Deactivate":"Activate"}
                  </button>
                  <button className="bos" style={{fontSize:11}} onClick={()=>{setShowPuppyHealthLog(false);setEditLitter({...l, _litterType: l.designerBreedId?'designer':'purebred'});}}>Edit</button>
                  <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:11}} onClick={()=>deleteLitter(l.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          {editLitter&&(
            <div style={{background:C.bg2,border:`1px solid ${C.emeraldL}44`,borderRadius:12,padding:24}}>
              <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:16}}>Litter Details</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Litter Name (optional)</label>
                  <input className="fi" placeholder="e.g. Blue Collar Litter, Summer 2026 Litter" value={editLitter.customName||""} onChange={e=>setEditLitter(p=>({...p,customName:e.target.value}))}/>
                  <div style={{fontSize:10,color:C.slate,marginTop:3}}>Shown instead of the breed name wherever this litter is listed. Leave blank to just show the breed.</div>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Type</label>
                  <select className="fi" value={editLitter._litterType||"purebred"} onChange={e=>setEditLitter(p=>({...p,_litterType:e.target.value,breed:"",breedId:null,designerBreedId:null}))}>
                    <option value="purebred">Purebred</option>
                    <option value="designer">Designer</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breed</label>
                  {editLitter._litterType==="designer" ? (
                    <BreedPicker breederId={myBreeder?.id} breeds={designerBreeds} breederBreeds={breederDesignerBreeds}
                      rowKey="designer_breed_id" gateLabel="My Designer Breeds"
                      selectedId={editLitter.designerBreedId} selectedName={editLitter.breed}
                      onChange={b=>setEditLitter(p=>({...p,designerBreedId:b?.id||null,breedId:null,breed:b?.name||""}))}
                      onGoToMyBreeds={()=>setDashTab("designer")}/>
                  ) : (
                    <BreedPicker breederId={myBreeder?.id} breeds={breeds} breederBreeds={breederBreeds}
                      selectedId={editLitter.breedId} selectedName={editLitter.breed}
                      onChange={b=>setEditLitter(p=>({...p,breedId:b?.id||null,designerBreedId:null,breed:b?.name||""}))}
                      onGoToMyBreeds={()=>setDashTab("breeds")}/>
                  )}
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Date Whelped</label>
                  <input className="fi" type="date" value={editLitter.whelped||""} onChange={e=>setEditLitter(p=>({...p,whelped:e.target.value}))}/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Total Puppies</label>
                  <input className="fi" type="number" value={editLitter.count||0} onChange={e=>setEditLitter(p=>({...p,count:parseInt(e.target.value)||0}))}/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Available Now</label>
                  <input className="fi" type="number" value={editLitter.available||0} onChange={e=>setEditLitter(p=>({...p,available:parseInt(e.target.value)||0}))}/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Colors Available</label>
                  <input className="fi" placeholder="e.g. Blue Merle, Black Tri" value={editLitter.colors||""} onChange={e=>setEditLitter(p=>({...p,colors:e.target.value}))}/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Price</label>
                  <input className="fi" placeholder="e.g. $2,000 USD" value={editLitter.price||""} onChange={e=>setEditLitter(p=>({...p,price:e.target.value}))}/>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sire</label>
                  <select className="fi" value={editLitter.sireId||""} onChange={e=>setEditLitter(p=>({...p,sireId:e.target.value}))}>
                    <option value="">Select sire…</option>
                    {Object.values(data.dogs||{}).filter(d=>d.sex==="M").map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Dam</label>
                  <select className="fi" value={editLitter.damId||""} onChange={e=>setEditLitter(p=>({...p,damId:e.target.value}))}>
                    <option value="">Select dam…</option>
                    {Object.values(data.dogs||{}).filter(d=>d.sex==="F").map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Litter Status</label>
                  <select className="fi" value={editLitter.litterStatus||"current"} onChange={e=>setEditLitter(p=>({...p,litterStatus:e.target.value}))}>
                    <option value="current">🟢 Available Now</option>
                    <option value="upcoming">🔵 Upcoming Litter</option>
                    <option value="reserved">🟡 Taking Deposits</option>
                    <option value="complete">✅ All Placed</option>
                  </select>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Litter Photo</label>
                  <input className="fi" placeholder="https://… or upload below" value={editLitter.photoUrl||""} onChange={e=>setEditLitter(p=>({...p,photoUrl:e.target.value}))}/>
                  <label style={{display:"inline-block",marginTop:6,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                    📷 Upload Litter Photo
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setEditLitter(p=>({...p,_photoFile:f,photoUrl:URL.createObjectURL(f)}));e.target.value="";}}/>
                  </label>
                  {editLitter.photoUrl&&<img src={editLitter.photoUrl} alt="preview" style={{display:"block",marginTop:8,width:120,height:90,objectFit:"cover",borderRadius:6,border:`2px solid ${C.gold}66`}}/>}
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Description</label>
                  <textarea className="fi" rows={3} placeholder="Health testing, temperament, what makes this litter special…" value={editLitter.description||""} onChange={e=>setEditLitter(p=>({...p,description:e.target.value}))} style={{resize:"vertical"}}/>
                </div>
                {myLitters.some(l=>l.id===editLitter.id) ? (
                  <div style={{gridColumn:"1/-1",marginTop:4,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <label style={{fontSize:10,color:C.gold,letterSpacing:1,textTransform:"uppercase"}}>Puppies ({litterPuppies.filter(p=>p.litter_id===editLitter.id).length})</label>
                      <div style={{display:"flex",gap:8}}>
                        <button className="bgs" style={{fontSize:11,color:C.emeraldL,borderColor:C.emeraldL+'44'}} onClick={()=>setShowPuppyHealthLog(v=>!v)}>🩺 Puppy Health Log</button>
                        <button className="bos" style={{fontSize:11}} onClick={()=>addPuppy(editLitter.id)}>+ Add Puppy</button>
                      </div>
                    </div>
                    {showPuppyHealthLog && (
                      <LitterPuppyHealthLog
                        litter={editLitter}
                        puppies={litterPuppies.filter(p=>p.litter_id===editLitter.id)}
                        supabase={supabase}
                        flash={flash}
                        breederId={myBreeder?.id||''}
                        onClose={()=>setShowPuppyHealthLog(false)}
                      />
                    )}
                    {litterPuppies.filter(p=>p.litter_id===editLitter.id).length===0 ? (
                      <div style={{fontSize:12,color:C.slate,padding:"6px 0"}}>No individual puppies tracked yet — this litter still uses the Count/Available totals above.</div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {litterPuppies.filter(p=>p.litter_id===editLitter.id).map(p=>{
                          const isLinked = waitlistEntries.some(e=>e.matched_puppy_id===p.id && e.status==='converted');
                          return (
                            <div key={p.id} style={{background:C.bg4,borderRadius:6,padding:8}}>
                              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                                <select className="fi" style={{width:80}} value={p.sex||""} onChange={e=>updatePuppy(p.id,{sex:e.target.value})}>
                                  <option value="">Sex</option>
                                  <option value="M">Male</option>
                                  <option value="F">Female</option>
                                </select>
                                <input className="fi" style={{width:110}} placeholder="Color" defaultValue={p.color||""} onBlur={e=>updatePuppy(p.id,{color:e.target.value})}/>
                                <input className="fi" style={{width:90}} placeholder="Price" defaultValue={p.price||""} onBlur={e=>updatePuppy(p.id,{price:e.target.value})}/>
                                <input className="fi" style={{flex:1,minWidth:140}} placeholder="Label (e.g. Blue Collar Boy)" defaultValue={p.label||""} onBlur={e=>updatePuppy(p.id,{label:e.target.value})}/>
                                <button className="bgs" style={{fontSize:11,color:p.status==='matched'?C.slate:C.emeraldL}} onClick={()=>(p.status==='matched'&&isLinked)?setViewingMatchFor(p.id):togglePuppyStatus(p,isLinked)}>
                                  {p.status==='matched'?'✓ Matched':'○ Available'}
                                </button>
                                {p.status!=='matched'&&<button className="bgs" style={{fontSize:11,color:C.red}} onClick={()=>removePuppy(p.id)}>✕</button>}
                              </div>
                              <div style={{display:"flex",gap:6,alignItems:"center",marginTop:8,flexWrap:"wrap"}}>
                                {(p.photo_urls||[]).map(url=>(
                                  <div key={url} style={{position:"relative"}}>
                                    <img src={url} alt="" style={{width:44,height:44,objectFit:"cover",borderRadius:4,border:`1px solid ${C.border}`}}/>
                                    <button onClick={()=>removePuppyPhoto(p.id,url)} style={{position:"absolute",top:-6,right:-6,width:16,height:16,borderRadius:"50%",background:C.red,color:"#fff",border:"none",cursor:"pointer",fontSize:9,lineHeight:"16px",padding:0}}>✕</button>
                                  </div>
                                ))}
                                {(p.photo_urls||[]).length < MAX_PUPPY_PHOTOS ? (
                                  <label style={{display:"inline-block",background:C.bg3,border:`1px dashed ${C.border2}`,color:C.slate,fontSize:10,padding:"4px 10px",borderRadius:5,cursor:"pointer"}}>
                                    + Photo ({(p.photo_urls||[]).length}/{MAX_PUPPY_PHOTOS})
                                    <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)addPuppyPhoto(p.id,f);e.target.value="";}}/>
                                  </label>
                                ) : (
                                  <span style={{fontSize:10,color:C.slate}}>{MAX_PUPPY_PHOTOS}/{MAX_PUPPY_PHOTOS} photos (max)</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{gridColumn:"1/-1",marginTop:4,paddingTop:16,borderTop:`1px solid ${C.border}`,fontSize:12,color:C.slate}}>
                    Save this litter first, then individual puppies can be added here.
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                <button className="bgs" onClick={()=>{setShowPuppyHealthLog(false);setEditLitter(null);}}>Cancel</button>
                <button className="bgl" onClick={()=>saveLitter(editLitter)}>Save Litter</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── WAITLIST TAB ── */}
      {dashTab==="waitlist"&&tierRank>=3&&(()=>{
        const breedById = (breeds||[]).reduce((acc,b)=>{acc[b.id]=b;return acc;},{});
        const designerBreedById = (designerBreeds||[]).reduce((acc,b)=>{acc[b.id]=b;return acc;},{});
        const breedLabel = (e) => e.breed_id ? (breedById[e.breed_id]?.name||'Unknown breed') : (designerBreedById[e.designer_breed_id]?.name||'Unknown breed');
        const active = waitlistEntries.filter(e=>e.status==='active');
        const matched = waitlistEntries.filter(e=>e.status==='converted');
        // Buyer/Puppy History search — matched-only. Active entries have no
        // matched_puppy_id yet, so there's no litter/puppy tie to search for;
        // that list is short and already visible on its own tab.
        const q = buyerSearch.trim().toLowerCase();
        const searchedMatched = q ? matched.filter(e=>(e.buyer_name||'').toLowerCase().includes(q) || (e.buyer_email||'').toLowerCase().includes(q)) : matched;
        const list = waitlistView==='active' ? active : searchedMatched;
        const grouped = {};
        list.forEach(e => { const k = breedLabel(e); (grouped[k]=grouped[k]||[]).push(e); });
        const groupNames = Object.keys(grouped).sort();

        const puppyFor = (id) => litterPuppies.find(p=>p.id===id) || null;
        const litterFor = (puppy) => puppy ? myLitters.find(l=>l.id===puppy.litter_id) : null;
        const puppyLabel = (p) => !p ? null : [p.label, p.sex==='M'?'Male':p.sex==='F'?'Female':null, p.color, p.price].filter(Boolean).join(' · ');
        const eligiblePuppiesFor = (e) => litterPuppies.filter(p=>{
          if (p.status!=='available') return false;
          const litter = myLitters.find(l=>l.id===p.litter_id);
          if (!litter) return false;
          return e.breed_id ? litter.breedId===e.breed_id : litter.designerBreedId===e.designer_breed_id;
        });

        const NotesBox = ({e}) => (
          <div style={{marginTop:8}}>
            {e.notes&&<div style={{fontSize:11,color:C.creamD,marginBottom:4}}><span style={{color:C.slate}}>Buyer's note:</span> {e.notes}</div>}
            <label style={{fontSize:9,color:C.slate,letterSpacing:0.5,textTransform:"uppercase",display:"block",marginBottom:2}}>Your Notes</label>
            <textarea className="fi" rows={2} placeholder="Conversation history, follow-ups, which puppy they're interested in…" defaultValue={e.breeder_notes||""} onBlur={ev=>updateWaitlistNotes(e.id, ev.target.value)} style={{resize:"vertical",fontSize:12}}/>
          </div>
        );

        return (
          <div>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              <button className={waitlistView==='active'?"bgl":"bgs"} style={{fontSize:12}} onClick={()=>{setWaitlistView('active');setMatchPickerFor(null);}}>Active ({active.length})</button>
              <button className={waitlistView==='matched'?"bgl":"bgs"} style={{fontSize:12}} onClick={()=>{setWaitlistView('matched');setMatchPickerFor(null);}}>Matched ({matched.length})</button>
            </div>
            {waitlistView==='active' && (
              <div style={{fontSize:13,color:C.slate,marginBottom:16}}>
                {active.length} active waitlist {active.length===1?'entry':'entries'} across {groupNames.length} breed{groupNames.length===1?'':'s'}. Buyers join from your public kennel profile — no account required on their end.
              </div>
            )}
            {waitlistView==='matched' && (
              <div style={{fontSize:13,color:C.slate,marginBottom:16}}>
                {matched.length} matched {matched.length===1?'entry':'entries'} — a permanent record, not removable.
              </div>
            )}
            {waitlistView==='matched' && matched.length>0 && (
              <input className="fi" style={{marginBottom:16,maxWidth:320}} placeholder="Search buyer name or email…" value={buyerSearch} onChange={e=>setBuyerSearch(e.target.value)}/>
            )}
            {list.length===0 ? (
              <div style={{textAlign:"center",padding:"48px 20px",border:`1px dashed ${C.border}`,borderRadius:12,color:C.slate}}>
                <div style={{fontSize:48,marginBottom:12}}>📋</div>
                <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.cream,marginBottom:8}}>{waitlistView==='active'?'No Waitlist Entries Yet':q?'No Matches':'No Matched Buyers Yet'}</div>
                <p style={{fontSize:14,lineHeight:1.7}}>{waitlistView==='active'?"Buyers can join a waitlist for any breed listed on your public Kennel Profile. You'll see them here as they sign up.":q?`No matched buyers found for "${buyerSearch.trim()}".`:"Once you match an active entry to a specific puppy, it moves here permanently."}</p>
              </div>
            ) : groupNames.map(name => {
              const groupEntries = grouped[name];

              const renderEntry = (e) => {
                const puppy = puppyFor(e.matched_puppy_id);
                const litter = litterFor(puppy);
                // Requested puppy, if any, sorts to the top of its own picker
                // list with a badge — a convenience default, never an
                // auto-match. Matching stays a deliberate breeder click.
                const eligible = eligiblePuppiesFor(e);
                const sortedEligible = e.requested_puppy_id
                  ? [...eligible].sort((a,b) => (a.id===e.requested_puppy_id?-1:0) - (b.id===e.requested_puppy_id?-1:0))
                  : eligible;
                // Most recent contract tied to this waitlist entry, if any —
                // a re-attempt after a decline/void creates a new row, so
                // "most recent" is what actually reflects current status.
                const existingContract = contracts
                  .filter(c=>c.waitlist_entry_id===e.id)
                  .sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0];
                return (
                <div key={e.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
                    <div style={{flex:1,minWidth:200}}>
                      <div style={{fontSize:14,fontWeight:700,color:C.cream}}>{e.buyer_name}</div>
                      <div style={{fontSize:12,color:C.slate}}>{e.buyer_email}{e.buyer_phone?` · ${e.buyer_phone}`:''}</div>
                      <div style={{fontSize:10,color:C.slate,marginTop:4}}>Joined {e.joined_at?new Date(e.joined_at).toLocaleDateString():'—'}{e.notified_at?` · Notified ${new Date(e.notified_at).toLocaleDateString()}`:''}</div>
                      {waitlistView==='matched' && (
                        <div style={{fontSize:12,color:C.emeraldL,marginTop:6}}>
                          ✓ Matched{puppy?` to ${puppyLabel(puppy)}`:' (no specific puppy on file)'}{litter?` — ${litterHeadline(litter)}`:''}{e.matched_at?` · ${new Date(e.matched_at).toLocaleDateString()}`:''}
                        </div>
                      )}
                      <NotesBox e={e}/>
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      {waitlistView==='active' ? (
                        <>
                          <button className="bgs" style={{fontSize:11}} onClick={()=>setMatchPickerFor(matchPickerFor===e.id?null:e.id)}>Match to Puppy →</button>
                          <button className="bgs" style={{color:C.slate,fontSize:11}} onClick={()=>updateWaitlistStatus(e.id,'removed')}>Remove</button>
                        </>
                      ) : (
                        <>
                          {litter&&<button className="bos" style={{fontSize:11}} onClick={()=>{setDashTab('litters');setShowPuppyHealthLog(false);setEditLitter({...litter, _litterType: litter.designerBreedId?'designer':'purebred'});}}>View Litter →</button>}
                          {!existingContract && (
                            <button className="bos" style={{fontSize:11}} onClick={()=>setSendContractCtx({buyerEmail:e.buyer_email, waitlistEntryId:e.id, litterId:litter?.id||null, dogId:null})}>Send Contract →</button>
                          )}
                          {existingContract && existingContract.status==='signed' && (
                            <>
                              <span style={{fontSize:10,color:C.emeraldL,fontWeight:700,alignSelf:"center"}}>✓ Signed</span>
                              <button className="bos" style={{fontSize:11}} onClick={()=>setViewingContract(existingContract)}>View Contract →</button>
                            </>
                          )}
                          {existingContract && existingContract.status==='sent' && (
                            <>
                              <span style={{fontSize:10,color:C.amber,fontWeight:700,alignSelf:"center"}}>Contract sent — pending signature</span>
                              <button className="bos" style={{fontSize:11}} onClick={()=>setViewingContract(existingContract)}>View Contract →</button>
                            </>
                          )}
                          {existingContract && (existingContract.status==='declined'||existingContract.status==='voided') && (
                            <>
                              <span style={{fontSize:10,color:C.slate,fontWeight:700,alignSelf:"center",textTransform:"uppercase"}}>{existingContract.status}</span>
                              <button className="bos" style={{fontSize:11}} onClick={()=>setViewingContract(existingContract)}>View Contract →</button>
                              <button className="bos" style={{fontSize:11}} onClick={()=>setSendContractCtx({buyerEmail:e.buyer_email, waitlistEntryId:e.id, litterId:litter?.id||null, dogId:null})}>Send New Contract →</button>
                            </>
                          )}
                          <button className="bgs" style={{color:C.slate,fontSize:11}} onClick={()=>unmatchEntry(e.id)}>Unmatch</button>
                        </>
                      )}
                    </div>
                  </div>
                  {waitlistView==='active' && matchPickerFor===e.id && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                      {sortedEligible.length===0 ? (
                        <div style={{fontSize:12,color:C.slate}}>No available puppies on file for this breed yet — add them via the litter's Puppies section first.</div>
                      ) : (
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {sortedEligible.map(p=>(
                            <button key={p.id} className="bgs" style={{fontSize:12,textAlign:"left"}} onClick={()=>{matchToPuppy(e.id,p.id);setMatchPickerFor(null);}}>
                              {p.id===e.requested_puppy_id && <span style={{color:C.amber}}>★ Requested by this buyer — </span>}
                              {puppyLabel(p)||'(unlabeled puppy)'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              };

              // Active view only: sub-group by requested puppy so "N people
              // want puppy X" is visible at a glance. Matched view doesn't
              // need this — matched_puppy_id already says who got what.
              let puppyBuckets = null;
              if (waitlistView==='active') {
                const byPuppy = {};
                const general = [];
                groupEntries.forEach(e => {
                  if (e.requested_puppy_id) (byPuppy[e.requested_puppy_id] = byPuppy[e.requested_puppy_id] || []).push(e);
                  else general.push(e);
                });
                puppyBuckets = Object.entries(byPuppy)
                  .map(([puppyId, entries]) => ({ puppyId, label: puppyLabel(litterPuppies.find(p=>p.id===puppyId)) || 'a specific puppy', entries }))
                  .sort((a,b) => b.entries.length - a.entries.length);
                if (general.length) puppyBuckets.push({ puppyId: null, label: null, entries: general });
              }

              return (
                <div key={name} style={{marginBottom:24}}>
                  <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,marginBottom:8,color:C.gold}}>{name} <span style={{fontSize:12,color:C.slate,fontWeight:400}}>({groupEntries.length})</span></div>
                  {puppyBuckets ? (
                    <div style={{display:"flex",flexDirection:"column",gap:16}}>
                      {puppyBuckets.map(bucket => (
                        <div key={bucket.puppyId||'general'}>
                          {bucket.puppyId && (
                            <div style={{fontSize:11,color:C.amber,fontWeight:700,marginBottom:6}}>
                              🎯 {bucket.label} — {bucket.entries.length} request{bucket.entries.length!==1?'s':''}
                            </div>
                          )}
                          {!bucket.puppyId && puppyBuckets.length>1 && (
                            <div style={{fontSize:11,color:C.slate,fontWeight:700,marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>General Interest</div>
                          )}
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            {bucket.entries.map(renderEntry)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {groupEntries.map(renderEntry)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── BREEDING PLANNER TAB ── */}
      {dashTab==="planner"&&tierRank>=3&&(
        myBreeder ? (
          <BreedingPlanner
            allDogs={data.dogs}
            breeders={data.breeders}
            breeds={breeds}
            designerBreeds={designerBreeds}
            customCrosses={customCrosses}
            healthRequirements={healthRequirements}
            myLitters={myLitters}
          />
        ) : (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:32,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>🐕</div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,marginBottom:8}}>Set Up Your Kennel First</div>
            <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:20}}>Before previewing a pairing, create your kennel profile on the Kennel Profile tab.</p>
            <button className="bos" onClick={()=>setDashTab("profile")}>Go to Kennel Profile →</button>
          </div>
        )
      )}

      {/* ── CONTRACTS TAB ── */}
      {dashTab==="contracts"&&tierRank>=3&&(
        myBreeder ? (
          <ContractsPanel
            myBreeder={myBreeder}
            contracts={contracts}
            setContracts={setContracts}
            contractTemplates={contractTemplates}
            setContractTemplates={setContractTemplates}
            allDogs={data.dogs}
            myLitters={myLitters}
            supabase={supabase}
            flash={flash}
            onSendContract={setSendContractCtx}
            onEditTemplateFields={setEditingTemplate}
            onViewContract={setViewingContract}
          />
        ) : (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:32,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>🐕</div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,marginBottom:8}}>Set Up Your Kennel First</div>
            <p style={{color:C.slate,fontSize:14,lineHeight:1.7,marginBottom:20}}>Before sending contracts, create your kennel profile on the Kennel Profile tab.</p>
            <button className="bos" onClick={()=>setDashTab("profile")}>Go to Kennel Profile →</button>
          </div>
        )
      )}

      {/* ── BULK IMPORT TAB ── */}
      {dashTab==="import"&&tierRank>=2&&(
        <BulkImportManager breeder={myBreeder} user={user} supabase={supabase} flash={flash}
          dogs={data.dogs} breeders={data.breeders} designerBreeds={designerBreeds}
          onDogsImported={(rows)=>setData(prev=>({...prev,dogs:{...prev.dogs, ...Object.fromEntries(rows.map(r=>[r.id, SB.dogFromDB(r)]))}}))}/>
      )}

      {dashTab==="edits"&&user&&(
        <PendingEditsQueue
          myDogs={myDogs}
          breeders={data.breeders}
          supabase={supabase}
          flash={flash}
          onCountChange={setPendingEditsCount}
        />
      )}

      {dashTab==="transfers"&&user&&(
        <TransfersPanel
          user={user}
          supabase={supabase}
          data={data}
          setData={setData}
          previouslyOwnedDogs={previouslyOwnedDogs}
          flash={flash}
          onRespond={onRespondToTransfer}
          onCountChange={setPendingTransfersCount}
          onViewDog={d=>{setViewDogDetail(d);setViewDogReadOnly(true);}}
        />
      )}

      {/* ── UPGRADE TAB ── */}
      {dashTab==="upgrade"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10,marginBottom:24}}>

            {/* Tier 0 — Free Contributor */}
            <div style={{background:C.card,border:`1px solid ${isContributor?C.gold:C.border}`,borderRadius:10,padding:18,display:"flex",flexDirection:"column",gap:10,opacity:0.85}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.cream}}>Free Contributor</div>
                  {isContributor&&<span style={{fontSize:9,background:`${C.gold}22`,border:`1px solid ${C.gold}44`,color:C.goldL,borderRadius:10,padding:"2px 7px",letterSpacing:1}}>CURRENT</span>}
                </div>
                <div style={{fontSize:22,fontWeight:900,color:C.emeraldL,fontFamily:"serif"}}>$0</div>
                <div style={{fontSize:11,color:C.slate}}>always free</div>
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                {["Search registry & view pedigrees","Add unlimited dogs manually","Suggest edits to any dog","Approve/reject your own suggestions"].map(f=>(
                  <div key={f} style={{fontSize:11,color:C.cream,display:"flex",gap:5,alignItems:"flex-start"}}><span style={{color:C.emeraldL,flexShrink:0}}>✓</span>{f}</div>
                ))}
                {["Certificate downloads (Foundation $29/mo)","Bulk import & back office (Professional $57/mo)","Public kennel profile & litter listings (Elite $97/mo)"].map(f=>(
                  <div key={f} style={{fontSize:11,color:C.slate,display:"flex",gap:5,alignItems:"flex-start"}}><span style={{flexShrink:0}}>✗</span>{f}</div>
                ))}
              </div>
              <div style={{fontSize:11,color:C.slate,textAlign:"center",paddingTop:8,borderTop:`1px solid ${C.border}`}}>{isContributor?"Your current plan":"Sign up free — no credit card"}</div>
            </div>

            {/* Tier 1 — Foundation Breeder $29/mo */}
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:18,display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.cream,marginBottom:2}}>Foundation Breeder</div>
                <div style={{fontSize:22,fontWeight:900,color:C.goldL,fontFamily:"serif"}}>$29<span style={{fontSize:13,fontWeight:400,color:C.slate}}>/mo</span></div>
                <div style={{fontSize:11,color:C.slate}}>everything in Free, plus:</div>
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                {["Everything in Free","Pedigree certificate downloads"].map(f=>(
                  <div key={f} style={{fontSize:11,color:C.cream,display:"flex",gap:5,alignItems:"flex-start"}}><span style={{color:C.goldL,flexShrink:0}}>✓</span>{f}</div>
                ))}
              </div>
              <a href={STRIPE.foundation_mo} target="_blank" rel="noreferrer" style={{display:"block",textAlign:"center",background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:12,textDecoration:"none",padding:"9px 0",borderRadius:5}}>Subscribe — $29/mo</a>
            </div>

            {/* Tier 2 — Professional Breeder $57/mo */}
            <div style={{background:C.card,border:`1px solid ${C.sapphire}88`,borderRadius:10,padding:18,display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.cream,marginBottom:2}}>Professional Breeder</div>
                <div style={{fontSize:22,fontWeight:900,color:C.sapphireL,fontFamily:"serif"}}>$57<span style={{fontSize:13,fontWeight:400,color:C.slate}}>/mo</span></div>
                <div style={{fontSize:11,color:C.slate}}>everything in Foundation, plus:</div>
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                {["Everything in Foundation","Bulk import from papers (OCR)","Full breeder back office","Health record OCR (OFA certs, vaccine records, vet invoices)"].map(f=>(
                  <div key={f} style={{fontSize:11,color:C.cream,display:"flex",gap:5,alignItems:"flex-start"}}><span style={{color:C.sapphireL,flexShrink:0}}>✓</span>{f}</div>
                ))}
              </div>
              <a href={STRIPE.pro_mo} target="_blank" rel="noreferrer" style={{display:"block",textAlign:"center",background:`linear-gradient(135deg,${C.sapphire},${C.sapphireL})`,color:"#fff",fontWeight:700,fontSize:12,textDecoration:"none",padding:"9px 0",borderRadius:5}}>Subscribe — $57/mo</a>
            </div>

            {/* Tier 3 — Elite Breeder $97/mo */}
            <div style={{background:`linear-gradient(160deg,${C.bg4},${C.card})`,border:`2px solid ${C.amber}`,borderRadius:10,padding:18,display:"flex",flexDirection:"column",gap:10,position:"relative"}}>
              <div style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",background:`linear-gradient(135deg,${C.amber},${C.gold})`,color:C.bg,fontSize:9,fontWeight:700,letterSpacing:2,padding:"3px 12px",borderRadius:10,whiteSpace:"nowrap"}}>MOST POPULAR</div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.cream,marginBottom:2}}>Elite Breeder</div>
                <div style={{fontSize:22,fontWeight:900,color:C.amber,fontFamily:"serif"}}>$97<span style={{fontSize:13,fontWeight:400,color:C.slate}}>/mo</span></div>
                <div style={{fontSize:11,color:C.slate}}>everything in Professional, plus:</div>
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                {["Everything in Professional","Public kennel profile & listing","Litter listings","Verified breeder badge","Featured kennel placement","Priority search placement","Custom branding on pedigree PDFs","Buyer leads (coming soon)","Priority support","Email & SMS reminders (coming soon)"].map(f=>(
                  <div key={f} style={{fontSize:11,color:C.cream,display:"flex",gap:5,alignItems:"flex-start"}}><span style={{color:C.amber,flexShrink:0}}>✓</span>{f}</div>
                ))}
              </div>
              <a href={STRIPE.elite_mo} target="_blank" rel="noreferrer" style={{display:"block",textAlign:"center",background:`linear-gradient(135deg,${C.amber},${C.gold})`,color:C.bg,fontWeight:700,fontSize:12,textDecoration:"none",padding:"9px 0",borderRadius:5}}>Subscribe — $97/mo</a>
            </div>

          </div>

          {/* One-time purchases */}
          <div style={{fontSize:10,color:C.slate,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>One-Time Purchases</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {[
              {label:"Single Pedigree View",price:"$27",url:STRIPE.single},
              {label:"Pedigree Bundle (3)",price:"$47",url:STRIPE.bundle},
              {label:"Single Registration",price:"$35",url:STRIPE.single_reg},
            ].map(t=>(
              <a key={t.label} href={t.url} target="_blank" rel="noreferrer" style={{display:"flex",flexDirection:"column",gap:3,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",textDecoration:"none",minWidth:140}}>
                <div style={{fontSize:12,color:C.cream,fontWeight:600}}>{t.label}</div>
                <div style={{fontSize:14,color:C.goldL,fontWeight:700}}>{t.price}</div>
              </a>
            ))}
          </div>
        </div>
      )}

      {inlineDog && <InlineDogModal {...inlineDog} onClose={()=>setInlineDog(null)} breeders={data.breeders} setData={setData} supabase={supabase} flash={flash}/>}
    </div>
  );
}

// ── PAYWALL ───────────────────────────────────────────────────────────────────
function PaywallView({onSignup,onSignin}){
  return(
    <div style={{maxWidth:560,margin:"80px auto",textAlign:"center"}}>
      <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:14,padding:48}}>
        <div style={{fontSize:48,marginBottom:16}}>🐾</div>
        <div style={{fontFamily:"serif",fontSize:28,fontWeight:700,color:C.cream,marginBottom:8}}>Sign In to View Pedigrees</div>
        <p style={{color:C.slate,fontSize:15,lineHeight:1.75,marginBottom:28}}>United Pedigree Registry is free to join. Create an account to view full 5-generation pedigrees, add your own dogs, and contribute to the public registry.</p>
        <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:24}}>
          <button className="bgl" style={{padding:"12px 32px"}} onClick={onSignup}>Create Free Account</button>
          <button className="bos" onClick={onSignin}>Sign In</button>
        </div>
        <div style={{fontSize:12,color:C.slate,textAlign:"center"}}>Want certificates & bulk import? <a href={STRIPE.foundation_mo} target="_blank" rel="noreferrer" style={{color:C.goldL,textDecoration:"none"}}>See paid plans →</a></div>
      </div>
    </div>
  );
}

const SUGGESTABLE_FIELDS = ['name','callName','breed','color','dob','sex','country',
  'akcNum','ascaNum','ukcNum','regNum','regBody','ofaHips','ofaElbows','ofaEyes',
  'ofaCardiac','ofaThyroid','chicNum','cerfNum','baer','mdr1','vwd','dm','hsf4',
  'cea','brucellosis','titles','notes','healthNotes','originalKennel','dnaNum'];
const FIELD_MAP = {
  name:'name', callName:'call_name', breed:'breed', color:'color', dob:'dob',
  sex:'sex', country:'country', akcNum:'akc_num', ascaNum:'asca_num', ukcNum:'ukc_num',
  regNum:'reg_num', regBody:'reg_body', ofaHips:'ofa_hips', ofaElbows:'ofa_elbows',
  ofaHipsCert:'ofa_hips_cert', ofaElbowsCert:'ofa_elbows_cert',
  ofaHipsRegistry:'ofa_hips_registry', ofaHipsDi:'ofa_hips_di',
  ofaEyes:'ofa_eyes', ofaCardiac:'ofa_cardiac', ofaThyroid:'ofa_thyroid',
  chicNum:'chic_num', cerfNum:'cerf_num', baer:'baer', mdr1:'mdr1', vwd:'vwd',
  dm:'dm', hsf4:'hsf4', cea:'cea', brucellosis:'brucellosis', titles:'titles',
  notes:'notes', healthNotes:'health_notes', originalKennel:'original_kennel', dnaNum:'dna_num',
};

const HEALTH_TEST_TYPES = [
  'OFA Hips','OFA Elbows','OFA Eyes','OFA Cardiac','OFA Thyroid',
  'CHIC','CERF','BAER','MDR1','vWD','DM','HSF4','CEA','Brucellosis',
  'Embark DNA Panel','Other',
];
const HEALTH_SYNC_MAP = {
  'OFA Hips':   {col:'ofa_hips',    jsKey:'ofaHips',    useResult:true, certCol:'ofa_hips_cert',   certJsKey:'ofaHipsCert', registryCol:'ofa_hips_registry', registryJsKey:'ofaHipsRegistry', diCol:'ofa_hips_di', diJsKey:'ofaHipsDi'},
  'OFA Elbows': {col:'ofa_elbows',  jsKey:'ofaElbows',  useResult:true, certCol:'ofa_elbows_cert', certJsKey:'ofaElbowsCert'},
  'OFA Eyes':   {col:'ofa_eyes',    jsKey:'ofaEyes',    useResult:true},
  'OFA Cardiac':{col:'ofa_cardiac', jsKey:'ofaCardiac', useResult:true},
  'OFA Thyroid':{col:'ofa_thyroid', jsKey:'ofaThyroid', useResult:true},
  'CHIC':       {col:'chic_num',    jsKey:'chicNum',    useResult:false},
  'CERF':       {col:'cerf_num',    jsKey:'cerfNum',    useResult:false},
  'BAER':       {col:'baer',        jsKey:'baer',       useResult:true},
  'MDR1':       {col:'mdr1',        jsKey:'mdr1',       useResult:true},
  'vWD':        {col:'vwd',         jsKey:'vwd',        useResult:true},
  'DM':         {col:'dm',          jsKey:'dm',         useResult:true},
  'HSF4':       {col:'hsf4',        jsKey:'hsf4',       useResult:true},
  'CEA':        {col:'cea',         jsKey:'cea',        useResult:true},
  'Brucellosis':{col:'brucellosis', jsKey:'brucellosis',useResult:true},
};

// ── HEALTH-VERIFIED ELIGIBILITY ─────────────────────────────────────────────────
const COL_TO_JSKEY = Object.fromEntries(Object.entries(FIELD_MAP).map(([js,col])=>[col,js]));
// Canonical test_type string the Back Office actually stores in dog_records.data.test_type
// for a given breed_health_requirements.test_key — derived from HEALTH_SYNC_MAP, never
// from test_label (display-only text; matching on it silently breaks recency checks).
const COL_TO_TEST_TYPE = Object.fromEntries(Object.entries(HEALTH_SYNC_MAP).map(([label,{col}])=>[col,label]));

const TEST_RESULT_VOCAB = {
  ofa_hips:   ['Excellent','Good','Fair','Borderline','Mild','Moderate','Severe'],
  ofa_elbows: ['Normal','Grade I','Grade II','Grade III'],
  ofa_eyes:   ['Normal','Abnormal'],
};

// The only dogs columns a breed_health_requirements row is allowed to gate on — derived
// from HEALTH_SYNC_MAP (useResult:true means it's a graded result, not a cert number),
// so the admin "test_key" picker can never point at a column that doesn't really exist
// or can't carry a pass/fail result. Same bug class as the breed-name mismatch: a
// requirement pointing at a nonexistent/non-result field would silently never match.
const TEST_KEY_OPTIONS = Object.entries(HEALTH_SYNC_MAP)
  .filter(([,v]) => v.useResult)
  .map(([label,v]) => ({col: v.col, label}));

function resolveBreedId(dog, breeds) {
  if (dog.breedId) return dog.breedId;
  if (!dog.breed) return null;
  const target = dog.breed.trim().toUpperCase();
  const match = (breeds||[]).find(b => (b.name||'').trim().toUpperCase() === target);
  return match ? match.id : null;
}

// Shared by the designer and custom-cross branches below — both derive their
// required tests as the union of their real purebred parent breeds' own
// required rows, deduped by test_key.
function unionParentRequirements(parentIds, healthRequirements) {
  const seen = new Set(), rows = [];
  (parentIds||[]).forEach(pid => {
    healthRequirements.filter(r => r.breed_type === 'purebred' && r.breed_id === pid).forEach(r => {
      if (!seen.has(r.test_key)) { seen.add(r.test_key); rows.push(r); }
    });
  });
  return rows;
}

function requirementsForDog(dog, healthRequirements, breeds, designerBreeds, customCrosses) {
  if (dog.type === 'purebred') {
    const breedId = resolveBreedId(dog, breeds);
    if (!breedId) return [];
    return healthRequirements.filter(r => r.breed_type === 'purebred' && r.breed_id === breedId);
  }
  if (dog.designerBreedId) {
    const db = (designerBreeds||[]).find(d => d.id === dog.designerBreedId);
    return unionParentRequirements(db?.parent_breed_ids, healthRequirements);
  }
  if (dog.customCrossId) {
    // customCrosses is grouped {[breederId]: [...]} — flattened here since a
    // dog's own custom cross record may belong to a different breeder than
    // whoever currently owns the dog (e.g. an ancestor bred elsewhere).
    const cc = Object.values(customCrosses||{}).flat().find(c => c.id === dog.customCrossId);
    return unionParentRequirements(cc?.parent_breed_ids, healthRequirements);
  }
  return [];
}

const monthsSince = (dateStr) => {
  if (!dateStr) return Infinity;
  const then = new Date(dateStr+'T00:00:00'), now = new Date();
  return (now.getFullYear()-then.getFullYear())*12 + (now.getMonth()-then.getMonth());
};

// The pure "does the data qualify" computation — no override. Split out from
// isHealthVerified so the admin panel can show what the auto-check WOULD say next to a
// manually-overridden dog, instead of a manual override silently going stale.
// Shared by computeAutoEligibility and isHealthVerifiedBatch so the OFA-vs-alt-registry
// branching logic lives in exactly one place. registry defaults to 'OFA' for any dog that
// never set it (legacy dogs, elbows/eyes which have no registry-selector UI) — unchanged
// grade+cert behavior. A non-OFA registry (e.g. PennHIP) skips the grade comparison
// entirely — there's no DI-norms table to auto-grade against (intentional scope call) —
// and passes on a cert/report number alone, same trust level as everything else here.
function passesNonRecencyRow(dog, row, hasChic) {
  const jsKey = COL_TO_JSKEY[row.test_key];
  if (!jsKey) return false;
  const registry = dog[jsKey+'Registry'] || 'OFA';
  const certVal = (dog[jsKey+'Cert']||'').trim();
  if (registry === 'OFA') {
    if (!(row.pass_values||[]).includes(dog[jsKey])) return false;
    if (row.require_cert_number && !hasChic && !certVal) return false;
    return true;
  }
  const registryAllowed = (row.acceptable_registries||['OFA']).includes(registry);
  return registryAllowed && !!certVal;
}

async function computeAutoEligibility(dog, healthRequirements, breeds, designerBreeds, supabase, customCrosses) {
  const required = requirementsForDog(dog, healthRequirements, breeds, designerBreeds, customCrosses).filter(r => r.requirement === 'required');
  if (required.length === 0) return false;
  // A CHIC number is a dog-level verifiability shortcut only — it means the breed's
  // required tests are on file with OFA, NOT that they passed. It can satisfy the
  // "has a cert number" bar below but never substitutes for the pass_values check.
  const hasChic = !!(dog.chicNum||'').trim();
  for (const row of required) {
    if (row.valid_months) {
      const { data } = await supabase.from('dog_records').select('record_date,data')
        .eq('dog_id', dog.id).eq('record_type','health_test').order('record_date', {ascending:false});
      const latest = (data||[]).find(r => r.data?.test_type === COL_TO_TEST_TYPE[row.test_key]);
      if (!latest || monthsSince(latest.record_date) > row.valid_months) return false;
      if (!(row.pass_values||[]).includes(latest.data?.result)) return false;
      if (row.require_cert_number && !hasChic && !(latest.data?.cert_number||'').trim()) return false;
    } else {
      if (!passesNonRecencyRow(dog, row, hasChic)) return false;
    }
  }
  return true;
}

async function isHealthVerified(dog, healthRequirements, breeds, designerBreeds, supabase, customCrosses) {
  if (dog.healthVerifiedOverride === true) return true;
  if (dog.healthVerifiedOverride === false) return false;
  return computeAutoEligibility(dog, healthRequirements, breeds, designerBreeds, supabase, customCrosses);
}

// Directory/list-friendly version of isHealthVerified — one batched dog_records query
// for the whole visible list (not one query per dog), and skips the query entirely for
// dogs that already fail their non-recency required tests via the cached dogs.* columns.
async function isHealthVerifiedBatch(dogsList, healthRequirements, breeds, designerBreeds, supabase, customCrosses) {
  const results = new Map();
  const pending = [];
  for (const dog of dogsList) {
    if (dog.healthVerifiedOverride === true) { results.set(dog.id, true); continue; }
    if (dog.healthVerifiedOverride === false) { results.set(dog.id, false); continue; }
    const required = requirementsForDog(dog, healthRequirements, breeds, designerBreeds, customCrosses).filter(r => r.requirement === 'required');
    if (required.length === 0) { results.set(dog.id, false); continue; }
    const hasChic = !!(dog.chicNum||'').trim();
    const nonRecency = required.filter(r => !r.valid_months);
    const recency = required.filter(r => r.valid_months);
    const passesNonRecency = nonRecency.every(row => passesNonRecencyRow(dog, row, hasChic));
    if (!passesNonRecency) { results.set(dog.id, false); continue; }
    if (recency.length === 0) { results.set(dog.id, true); continue; }
    pending.push({dog, recency, hasChic});
  }
  if (pending.length > 0) {
    const { data } = await supabase.from('dog_records').select('dog_id,record_date,data')
      .in('dog_id', pending.map(p => p.dog.id)).eq('record_type','health_test').order('record_date',{ascending:false});
    const byDog = {};
    (data||[]).forEach(r => { (byDog[r.dog_id] = byDog[r.dog_id]||[]).push(r); });
    pending.forEach(({dog, recency, hasChic}) => {
      const rows = byDog[dog.id]||[];
      const ok = recency.every(row => {
        const latest = rows.find(r => r.data?.test_type === COL_TO_TEST_TYPE[row.test_key]);
        if (!latest || monthsSince(latest.record_date) > row.valid_months) return false;
        if (!(row.pass_values||[]).includes(latest.data?.result)) return false;
        if (row.require_cert_number && !hasChic && !(latest.data?.cert_number||'').trim()) return false;
        return true;
      });
      results.set(dog.id, ok);
    });
  }
  return results;
}

// ── OWNERSHIP TRAIL ───────────────────────────────────────────────────────────
// Bred-by-breeder + current-owner-member + the accepted-transfer chain between
// them. A separate axis from record_type/recordOwnerId (Bulk Import's
// breeder-level "who owns this DB record" model) — this one tracks the actual
// buyer handoffs from the Ownership Transfer feature. Self-contained: does its
// own on-demand fetch per dog rather than riding the bulk loader, since most
// dogs will never have transfer history. Both queries only ever touch views
// that resolve a display name server-side (dog_transfer_history,
// member_display_names) — never a raw email, even though members/breeders
// themselves are already public tables; this feature specifically never
// pulls a raw address into the client at all, by design.
function OwnershipTrail({dog, supabase}){
  const [bredByKennel, setBredByKennel] = useState(null);
  const [currentOwnerName, setCurrentOwnerName] = useState(null);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [breederRes, ownerRes, transferRes, returnRes] = await Promise.all([
        dog.bredByBreederId
          ? supabase.from('breeders').select('kennel').eq('id', dog.bredByBreederId).maybeSingle()
          : Promise.resolve({data:null}),
        dog.currentOwnerMemberId
          ? supabase.from('member_display_names').select('display_name').eq('id', dog.currentOwnerMemberId).maybeSingle()
          : Promise.resolve({data:null}),
        supabase.from('dog_transfer_history').select('accepted_at,owner_display_name').eq('dog_id', dog.id).order('accepted_at',{ascending:true}),
        supabase.from('dog_return_history').select('accepted_at').eq('dog_id', dog.id).order('accepted_at',{ascending:true}),
      ]);
      if (cancelled) return;
      const kennel = breederRes.data?.kennel || null;
      setBredByKennel(kennel);
      setCurrentOwnerName(ownerRes.data?.display_name || null);
      // Merged, chronological. A return always snaps ownership back to the
      // bred-by kennel — there's no separate destination field, that IS the
      // return — so its label reuses the already-resolved kennel name rather
      // than needing a second server-side name lookup.
      const combined = [
        ...(transferRes.data||[]).map(h => ({at: h.accepted_at, label: h.owner_display_name, returned: false})),
        ...(returnRes.data||[]).map(h => ({at: h.accepted_at, label: kennel || 'the breeder', returned: true})),
      ].sort((a,b) => new Date(a.at) - new Date(b.at));
      setEvents(combined);
      setLoaded(true);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dog.id, dog.bredByBreederId, dog.currentOwnerMemberId]);

  if (!loaded) return null;
  if (!bredByKennel && !currentOwnerName && events.length === 0) return null;

  const currentLabel = currentOwnerName || bredByKennel;
  const chain = [bredByKennel || 'Unknown breeder', ...events.map(e=>e.returned?`${e.label} (returned)`:e.label)];
  const chainDates = events.map(e=>e.at);
  const fmt = iso => iso ? new Date(iso).toLocaleDateString() : '';

  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:24}}>
      <div style={{fontSize:9,color:C.slate,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Ownership</div>
      <div style={{display:"flex",gap:24,flexWrap:"wrap",marginBottom:events.length?10:0}}>
        {bredByKennel&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>Bred by</div><div style={{fontSize:13,color:C.cream}}>{bredByKennel}</div></div>}
        {currentLabel&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>Currently owned by</div><div style={{fontSize:13,color:C.gold}}>{currentLabel}</div></div>}
      </div>
      {events.length>0&&(
        <div style={{fontSize:12,color:C.slate,lineHeight:1.8}}>
          {chain.map((name,i)=>(
            <span key={i}>
              {i>0&&<span style={{margin:'0 6px',color:C.slate}}>→</span>}
              <span style={{color:i===chain.length-1?C.gold:C.cream}}>{name}</span>
              {i>0&&chainDates[i-1]&&<span style={{color:C.slate}}> ({fmt(chainDates[i-1])})</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PEDIGREE VIEW ─────────────────────────────────────────────────────────────
function PedigreeView({dog,allDogs,breeders,breeds,designerBreeds,customCrosses,healthRequirements,onBack,onDogClick,canEdit,canSuggest,suggesterId,canViewPedigree,onSaveDog,setData,flash,supabase,data,ensureBreeder}){
  const b        = breeders[dog.breederId];
  const owner    = breeders[dog.recordOwnerId];
  const recorder = breeders[dog.recordedById];
 const [editAnc, setEditAnc] = useState(null);
  const [ancParent, setAncParent] = useState(null);
  const [showCert, setShowCert] = useState(false);
  const [inlineDog, setInlineDog] = useState(null);

  const openAdd = (parentDogId, field, sex) => {
    setAncParent({dogId: parentDogId, field});
    setEditAnc({
      id: crypto.randomUUID(), type:'purebred', breed: dog.breed||'', breedId: dog.breedId||null,
      name:'', callName:'', sex: sex||'M', color:'', dob:'',
      country: dog.country||'United States',
      akcNum:'', ascaNum:'', ukcNum:'', regNum:'', regBody:'AKC',
      ofaHips:'', ofaElbows:'', ofaEyes:'', ofaCardiac:'', ofaThyroid:'',
      chicNum:'', cerfNum:'', baer:'', mdr1:'', vwd:'', dm:'', hsf4:'',
      cea:'', brucellosis:'', otherHealth:'',
      titles:'', sireId:'', damId:'', sireNameManual:'', damNameManual:'',
      photoUrl:'', notes:'', hidden:false, breederId:'', dnaNum:'',
    });
  };

  const openEdit = (ancDog) => {
    setAncParent(null);
    setEditAnc({...ancDog});
  };

 const saveAncestor = async (ancDog) => {
    let {_photoFile, ...dogToSave} = ancDog;
    if(_photoFile) {
      try {
        const url = await uploadPhoto(_photoFile, dogToSave.id);
        dogToSave = {...dogToSave, photoUrl: url};
      } catch(e) {
        console.log('Photo upload error:', e);
      }
    }
    if(onSaveDog) await onSaveDog(dogToSave);
    if(ancParent) {
      const parentDog = allDogs[ancParent.dogId];
      if(parentDog) {
        const updatedParent = {...parentDog, [ancParent.field]: dogToSave.id};
        if(onSaveDog) await onSaveDog(updatedParent);
      }
    }
    setEditAnc(null);
    setAncParent(null);
  };

  const suggestMode = canSuggest && !ancParent;

  const handleSuggestSave = async (editedDog) => {
    const original = allDogs[editedDog.id] || {};
    const rawSuggestions = [];
    SUGGESTABLE_FIELDS.forEach(f => {
      const oldVal = String(original[f]||'').trim();
      const newVal = String(editedDog[f]||'').trim();
      if (!newVal || newVal===oldVal) return;
      rawSuggestions.push({dog_id:editedDog.id, field_name:f,
        old_value:oldVal, new_value:newVal, status:'pending'});
    });
    if (!rawSuggestions.length) { flash('No changes detected'); setEditAnc(null); return; }
    try {
      // Resolve suggester id — auto-creates hidden breeders row for contributors
      let effectiveSuggesterId = suggesterId;
      if (!effectiveSuggesterId && ensureBreeder) {
        const breeder = await ensureBreeder();
        if (!breeder) throw new Error('Could not establish contributor identity');
        effectiveSuggesterId = breeder.id;
      }
      const suggestions = rawSuggestions.map(s=>({...s, suggested_by_id: effectiveSuggesterId}));
      const {error} = await supabase.from('pending_edits').insert(suggestions);
      if (error) throw error;
      setEditAnc(null); setAncParent(null);
      flash(`${suggestions.length} suggestion${suggestions.length>1?'s':''} submitted for owner review ✓`);
    } catch(e) { flash('⚠ ' + e.message); }
  };

  const uploadPhoto = async (file, dogId) => {
    const ext = file.name.split('.').pop();
    const path = `dogs/${dogId}.${ext}`;
    const { error } = await supabase.storage
      .from('dog-photos')
      .upload(path, file, { upsert: true });
    if (error) throw error;
    const { data } = supabase.storage
      .from('dog-photos')
      .getPublicUrl(path);
    return data.publicUrl;
  };

  const get = (id) => id && allDogs[id] ? allDogs[id] : null;

  const s=get(dog.sireId); const d=get(dog.damId);
  const ss=get(s?.sireId); const sd=get(s?.damId);
  const ds=get(d?.sireId); const dd=get(d?.damId);
  const sss=get(ss?.sireId); const ssd=get(ss?.damId);
  const sds=get(sd?.sireId); const sdd=get(sd?.damId);
  const dss=get(ds?.sireId); const dsd=get(ds?.damId);
  const dds=get(dd?.sireId); const ddd=get(dd?.damId);
  const ssss=get(sss?.sireId); const sssd=get(sss?.damId);
  const ssds=get(ssd?.sireId); const ssdd=get(ssd?.damId);
  const sdss=get(sds?.sireId); const sdsd=get(sds?.damId);
  const sdds=get(sdd?.sireId); const sddd=get(sdd?.damId);
  const dsss=get(dss?.sireId); const dssd=get(dss?.damId);
  const dsds=get(dsd?.sireId); const dsdd=get(dsd?.damId);
  const ddss=get(dds?.sireId); const ddsd=get(dds?.damId);
  const ddds=get(ddd?.sireId); const dddd=get(ddd?.damId);

  const cols = [
    [dog],
    [s,d],
    [ss,sd,ds,dd],
    [sss,ssd,sds,sdd,dss,dsd,dds,ddd],
    [ssss,sssd,ssds,ssdd,sdss,sdsd,sdds,sddd,dsss,dssd,dsds,dsdd,ddss,ddsd,ddds,dddd],
  ];

  const allIds = cols.flat().filter(Boolean).map(x=>x.id);
  const counts = {};
  allIds.forEach(id=>{counts[id]=(counts[id]||0)+1;});
  const lb = (id) => id && counts[id]>1;

  const GL = ["Subject","Parents","Grandparents","Great-Grandparents","2x Great"];
  const GW = [200,175,150,135,118];

  const Cell = ({n, gen, parentDogId, field, sex}) => {
    if(!n) {
      if(canEdit && parentDogId) return (
        <div
          onClick={()=>openAdd(parentDogId, field, sex)}
          style={{flex:1,margin:"1px 2px",background:"rgba(255,255,255,0.01)",border:`1px dashed ${C.gold}44`,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all 0.2s"}}
          onMouseEnter={e=>{e.currentTarget.style.background=`${C.gold}10`;e.currentTarget.style.borderColor=C.gold;}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.01)";e.currentTarget.style.borderColor=`${C.gold}44`;}}>
          <span style={{fontSize:9,color:C.gold}}>+ Add</span>
        </div>
      );
      return (
        <div style={{flex:1,margin:"1px 2px",background:"rgba(255,255,255,0.01)",border:`1px dashed ${C.border}33`,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <span style={{fontSize:7,color:C.border}}>—</span>
        </div>
      );
    }
    const lineBred = lb(n.id);
    const male = n.sex==="M";
    return(
      <div
        onClick={()=>onDogClick(n.id)}
        style={{flex:1,margin:"1px 2px",borderRadius:4,padding:"3px 5px",cursor:"pointer",background:lineBred?`${C.amber}18`:male?C.bg3:C.bg2,border:`1px solid ${lineBred?C.amber+"77":male?C.gold+"55":C.goldL+"33"}`,display:"flex",flexDirection:"column",justifyContent:"center",overflow:"hidden",position:"relative",transition:"transform 0.1s"}}
        onMouseEnter={e=>e.currentTarget.style.transform="scale(1.02)"}
        onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
        {lineBred&&<div style={{position:"absolute",top:1,right:1,fontSize:6,color:C.amber,background:`${C.amber}22`,borderRadius:2,padding:"0 2px"}}>LB</div>}
        {n.photoUrl&&<img src={n.photoUrl} alt="" style={{width:gen===0?40:gen===1?28:gen===2?20:16,height:gen===0?40:gen===1?28:gen===2?20:16,objectFit:"cover",borderRadius:3,marginBottom:2}}/>}
        <div style={{fontFamily:"serif",fontSize:gen===0?13:gen===1?10:gen===2?9:8,fontWeight:700,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:C.cream}}>{n.name}</div>
        {n.titles&&gen<=2&&<div style={{fontSize:7,color:C.goldL,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.titles}</div>}
        {n.ofaHips&&<div style={{fontSize:7,color:C.gold,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>OFA: {n.ofaHips}</div>}
       {n.mdr1&&<div style={{fontSize:7,color:mdr1C(n.mdr1)}}>MDR1: {n.mdr1}</div>}
        {n.dnaNum&&gen<=2&&<div style={{fontSize:7,color:C.sapphireL}}>DNA: {n.dnaNum}</div>}
        {canEdit&&(
          <div
            onClick={e=>{e.stopPropagation();openEdit(n);}}
            style={{position:"absolute",top:1,left:1,fontSize:7,background:`${C.gold}cc`,color:C.bg,borderRadius:2,padding:"0 3px",opacity:0,transition:"opacity 0.15s",cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.opacity=1}
            onMouseLeave={e=>e.currentTarget.style.opacity=0}>✏</div>
        )}
      </div>
    );
  };

  return(
    <div>
      <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:20,flexWrap:"wrap"}}>
        <button className="bgs" onClick={onBack}>← Registry</button>
        <div style={{flex:1}}>
          <div style={{fontFamily:"serif",fontSize:22,fontWeight:900}}>{dog.name}</div>
          <div style={{fontSize:12,color:C.slate}}>5-Generation Pedigree · {dog.country}</div>
        </div>
      </div>

      <div style={{background:C.card,border:`1px solid ${C.gold}66`,borderRadius:10,padding:20,marginBottom:24,display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{width:80,height:80,borderRadius:10,overflow:"hidden",border:`3px solid ${C.gold}`,background:C.bg4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,flexShrink:0}}>
          {dog.photoUrl?<img src={dog.photoUrl} alt={dog.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:"🐾"}
        </div>
    <div style={{flex:1}}>
          {dog.titles&&<div style={{fontSize:10,color:C.goldL,letterSpacing:1,marginBottom:2}}>{dog.titles}</div>}
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
            <div style={{fontFamily:"serif",fontSize:22,fontWeight:900}}>{dog.name}{dog.callName?` "${dog.callName}"`:""}</div>
           {canEdit&&<button className="bos" style={{fontSize:11,padding:"4px 12px"}} onClick={()=>openEdit(dog)}>✏ Edit</button>}
           {canSuggest&&<button className="bos" style={{fontSize:11,padding:"4px 12px"}} onClick={()=>openEdit(dog)}>✏ Suggest Edit</button>}
        {canViewPedigree&&<button className="bgl" style={{padding:"9px 20px"}} onClick={()=>setShowCert(true)}>⬇ Certificate</button>}
          </div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            {dog.akcNum&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>AKC</div><div style={{fontSize:13,color:C.cream}}>{dog.akcNum}</div></div>}
            {dog.ascaNum&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>ASCA</div><div style={{fontSize:13,color:C.cream}}>{dog.ascaNum}</div></div>}
            {(!dog.akcNum&&!dog.ascaNum&&dog.regNum)&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>{dog.regBody||'Reg'} #</div><div style={{fontSize:13,color:C.cream}}>{dog.regNum}</div></div>}
            {dog.ofaHips&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>OFA Hips</div><div style={{fontSize:13,color:C.gold}}>{dog.ofaHips}</div></div>}
            {dog.chicNum&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>CHIC</div><div style={{fontSize:13,color:C.cream}}>{dog.chicNum}</div></div>}
          {dog.mdr1&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>MDR1</div><div style={{fontSize:13,color:mdr1C(dog.mdr1)}}>{dog.mdr1}</div></div>}
            {dog.dnaNum&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>DNA #</div><div style={{fontSize:13,color:C.sapphireL}}>{dog.dnaNum}</div></div>}
            {(dog.originalKennel||(dog.source!=='reference'&&b))&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>Kennel</div><div style={{fontSize:13,color:C.cream}}>{dog.originalKennel||b?.kennel}</div></div>}
            {dog.recordType==='owned'&&owner&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>Owned by</div><div style={{fontSize:13,color:C.gold}}>{owner.kennel}</div></div>}
            {dog.recordType==='referenced'&&recorder&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>Recorded by</div><div style={{fontSize:13,color:C.slate}}>{recorder.kennel}</div></div>}
            {dog.recordType==='unclaimed'&&<div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase"}}>Status</div><div style={{fontSize:13,color:C.slate}}>Unclaimed</div></div>}
          </div>
        </div>
      </div>

      <OwnershipTrail dog={dog} supabase={supabase}/>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:9,color:C.slate,letterSpacing:2,textTransform:"uppercase"}}>5-Generation Pedigree · Click any dog to view</div>
        <div style={{display:"flex",gap:10}}>
          <span style={{fontSize:9,color:C.gold}}>■ Sire</span>
          <span style={{fontSize:9,color:C.goldL}}>■ Dam</span>
          <span style={{fontSize:9,color:C.amber}}>■ LB = Line Bred</span>
          {canEdit&&<span style={{fontSize:9,color:C.gold}}>· Hover cells to edit · Click — to add</span>}
        </div>
      </div>

      <div style={{overflowX:"auto",paddingBottom:12}}>
        <div style={{display:"flex",minWidth:GW.reduce((a,b)=>a+b,0)+40,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:12}}>
          {cols.map((col,gen)=>(
            <div key={gen} style={{width:GW[gen],flexShrink:0,display:"flex",flexDirection:"column"}}>
              <div style={{fontSize:7,color:C.gold,letterSpacing:2,textTransform:"uppercase",textAlign:"center",marginBottom:4,height:14}}>{GL[gen]}</div>
              <div style={{display:"flex",flexDirection:"column",flex:1,gap:0}}>
                {col.map((n,i)=>{
                  const parentIdx = Math.floor(i/2);
                  const parentDog = gen>0 ? cols[gen-1][parentIdx] : null;
                  const field = i%2===0 ? 'sireId' : 'damId';
                  const sex = i%2===0 ? 'M' : 'F';
                  return <Cell key={`${gen}-${i}`} n={n} gen={gen} parentDogId={parentDog?.id} field={field} sex={sex}/>;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editAnc&&(
        <div
          onClick={e=>{if(e.target===e.currentTarget){setEditAnc(null);setAncParent(null);}}}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px",overflowY:"auto"}}>
          <div style={{background:C.bg2,border:`1px solid ${C.gold}66`,borderRadius:12,padding:28,width:"100%",maxWidth:640,margin:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.goldXL}}>{ancParent?"Add Ancestor":suggestMode?"Suggest Changes":"Edit Dog"}</div>
              <button className="bgs" onClick={()=>{setEditAnc(null);setAncParent(null);}}>✕ Close</button>
            </div>
            {ancParent&&(
              <div style={{background:`${C.gold}11`,border:`1px solid ${C.gold}33`,borderRadius:6,padding:"8px 12px",marginBottom:16,fontSize:12,color:C.goldL}}>
                Adding as {ancParent.field==='sireId'?'Sire (Father)':'Dam (Mother)'} of {allDogs[ancParent.dogId]?.name||'this dog'}
              </div>
            )}
            {suggestMode&&(
              <div style={{background:`${C.sapphireL}15`,border:`1px solid ${C.sapphireL}44`,borderRadius:6,padding:"8px 12px",marginBottom:16,fontSize:12,color:C.sapphireL,lineHeight:1.5}}>
                You're not the record owner. All changes will be submitted as suggestions for the owner to approve.
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[["Registered Name *","name","text"],["Call Name","callName","text"],["Color/Markings","color","text"],["Date of Birth","dob","date"],["AKC Number","akcNum","text"],["ASCA Number","ascaNum","text"],["UKC Number","ukcNum","text"],["Registration #","regNum","text"],["OFA Cardiac","ofaCardiac","text"],["OFA Thyroid","ofaThyroid","text"],["CHIC Number","chicNum","text"],["CERF Number","cerfNum","text"],["BAER Hearing","baer","text"],["Titles & Awards","titles","text"],["VWD","vwd","text"],["DM","dm","text"],["HSF4","hsf4","text"],["CEA","cea","text"]].map(([label,field,type])=>(
                <div key={field}>
                  <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                  <input className="fi" type={type} value={editAnc[field]||""} onChange={e=>setEditAnc(p=>({...p,[field]:e.target.value}))}/>
                </div>
              ))}
              <OfaResultField testKey="ofa_hips" label="OFA Hips" value={editAnc.ofaHips} onChange={v=>setEditAnc(p=>({...p,ofaHips:v}))}
                certValue={editAnc.ofaHipsCert} onCertChange={v=>setEditAnc(p=>({...p,ofaHipsCert:v}))}
                registryValue={editAnc.ofaHipsRegistry} onRegistryChange={v=>setEditAnc(p=>({...p,ofaHipsRegistry:v}))} altRegistries={['PennHIP']}
                diValue={editAnc.ofaHipsDi} onDiChange={v=>setEditAnc(p=>({...p,ofaHipsDi:v}))}/>
              <OfaResultField testKey="ofa_elbows" label="OFA Elbows" value={editAnc.ofaElbows} onChange={v=>setEditAnc(p=>({...p,ofaElbows:v}))}
                certValue={editAnc.ofaElbowsCert} onCertChange={v=>setEditAnc(p=>({...p,ofaElbowsCert:v}))}/>
              <OfaResultField testKey="ofa_eyes" label="OFA Eyes" value={editAnc.ofaEyes} onChange={v=>setEditAnc(p=>({...p,ofaEyes:v}))}/>
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breed</label>
                <BreedPicker breeds={breeds} selectedId={editAnc.breedId} selectedName={editAnc.breed}
                  onChange={b=>setEditAnc(p=>({...p,breedId:b?.id||null,breed:b?.name||""}))}/>
              </div>
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sex</label>
                <select className="fi" value={editAnc.sex||"M"} onChange={e=>setEditAnc(p=>({...p,sex:e.target.value}))}>
                  <option value="M">Male (Sire)</option>
                  <option value="F">Female (Dam)</option>
                </select>
              </div>
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>MDR1 Status</label>
                <select className="fi" value={editAnc.mdr1||""} onChange={e=>setEditAnc(p=>({...p,mdr1:e.target.value}))}>
                  <option value="">Unknown / Not Tested</option>
                  <option value="Clear">Clear</option>
                  <option value="Carrier">Carrier</option>
                  <option value="Affected">Affected</option>
                </select>
              </div>
             <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registry Body</label>
                <select className="fi" value={editAnc.regBody||"AKC"} onChange={e=>setEditAnc(p=>({...p,regBody:e.target.value}))}>
                  {REG_BODIES.map(r=><option key={r}>{r}</option>)}
                </select>
              </div>
              {editAnc.breed==="Australian Shepherd"&&(
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>ASCA Number</label>
                <input className="fi" value={editAnc.ascaNum||""} onChange={e=>setEditAnc(p=>({...p,ascaNum:e.target.value}))}/>
              </div>
              )}
              {editAnc.breed==="German Shepherd"&&(
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>SV Number</label>
                <input className="fi" value={editAnc.svNum||""} onChange={e=>setEditAnc(p=>({...p,svNum:e.target.value}))}/>
              </div>
              )}
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                <select className="fi" value={editAnc.country||"United States"} onChange={e=>setEditAnc(p=>({...p,country:e.target.value}))}>
                  {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Type</label>
                <select className="fi" value={editAnc.type||"purebred"} onChange={e=>setEditAnc(p=>({...p,type:e.target.value}))}>
                  <option value="purebred">Purebred</option>
                  <option value="heritage">Designer</option>
                </select>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sire</label>
                {!editAnc.sireId && editAnc.sireNameManual && (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:`${C.gold}11`,border:`1px solid ${C.gold}33`,borderRadius:6,marginBottom:6,fontSize:12}}>
                    <span style={{color:C.creamD}}>Existing manual entry: <strong>{editAnc.sireNameManual}</strong></span>
                    <button type="button" onClick={()=>setInlineDog({sex:'M',initialName:editAnc.sireNameManual,initialBreed:editAnc.breed,onSave:(id)=>{setEditAnc(p=>({...p,sireId:id,sireNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                  </div>
                )}
                <DogAutocomplete sex="M" addLabel="sire" value={editAnc.sireId} onChange={v=>setEditAnc(p=>({...p,sireId:v}))} dogs={allDogs} breeders={breeders} currentDogId={editAnc.id}
                  onAdd={(query)=>setInlineDog({sex:'M',initialName:query,initialBreed:editAnc.breed,onSave:(id)=>{setEditAnc(p=>({...p,sireId:id}));setInlineDog(null);}})}
                  placeholder="Type 3+ chars to search sires…"/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Dam</label>
                {!editAnc.damId && editAnc.damNameManual && (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:`${C.gold}11`,border:`1px solid ${C.gold}33`,borderRadius:6,marginBottom:6,fontSize:12}}>
                    <span style={{color:C.creamD}}>Existing manual entry: <strong>{editAnc.damNameManual}</strong></span>
                    <button type="button" onClick={()=>setInlineDog({sex:'F',initialName:editAnc.damNameManual,initialBreed:editAnc.breed,onSave:(id)=>{setEditAnc(p=>({...p,damId:id,damNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                  </div>
                )}
                <DogAutocomplete sex="F" addLabel="dam" value={editAnc.damId} onChange={v=>setEditAnc(p=>({...p,damId:v}))} dogs={allDogs} breeders={breeders} currentDogId={editAnc.id}
                  onAdd={(query)=>setInlineDog({sex:'F',initialName:query,initialBreed:editAnc.breed,onSave:(id)=>{setEditAnc(p=>({...p,damId:id}));setInlineDog(null);}})}
                  placeholder="Type 3+ chars to search dams…"/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Photo URL</label>
                <input className="fi" placeholder="https://…" value={editAnc.photoUrl||""} onChange={e=>setEditAnc(p=>({...p,photoUrl:e.target.value}))}/>
                <label style={{display:"inline-block",marginTop:6,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                  📷 Upload Photo
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setEditAnc(p=>({...p,_photoFile:f,photoUrl:URL.createObjectURL(f)}));e.target.value="";}}/>
                </label>
                {editAnc.photoUrl&&<img src={editAnc.photoUrl} alt="preview" style={{display:"block",marginTop:8,width:80,height:80,objectFit:"cover",borderRadius:6,border:`2px solid ${C.gold}66`}}/>}
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Notes</label>
                <textarea className="fi" rows={3} value={editAnc.notes||""} onChange={e=>setEditAnc(p=>({...p,notes:e.target.value}))} style={{resize:"vertical"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <button className="bgs" onClick={()=>{setEditAnc(null);setAncParent(null);}}>Cancel</button>
              <button className="bgl" onClick={()=>suggestMode?handleSuggestSave(editAnc):saveAncestor(editAnc)}>{suggestMode?"Submit Suggestions":"Save & Link to Pedigree"}</button>
            </div>
          </div>
        </div>
         )}
      {inlineDog && <InlineDogModal {...inlineDog} onClose={()=>setInlineDog(null)} breeders={breeders} setData={setData} supabase={supabase} flash={flash}/>}
      {showCert&&<PedigreeCertificate dog={dog} allDogs={allDogs} breeders={breeders} breeds={breeds} designerBreeds={designerBreeds} customCrosses={customCrosses} healthRequirements={healthRequirements} supabase={supabase} onClose={()=>setShowCert(false)}/>}
    </div>
  );
}

// ── PEDIGREE CERTIFICATE ──────────────────────────────────────────────────────
function PedigreeCertificate({dog, allDogs, breeders, breeds, designerBreeds, customCrosses, healthRequirements, supabase, onClose}){
  const b = breeders[dog.breederId];
  const get = (id) => id && allDogs[id] ? allDogs[id] : null;

  // Live eligibility check, not a cached flag — a dog reverts to Standard automatically
  // if a required field is later cleared/corrected. Computed on open, not per-render.
  const [healthVerified, setHealthVerified] = useState(false);
  useEffect(() => {
    let cancelled = false;
    isHealthVerified(dog, healthRequirements||[], breeds||[], designerBreeds||[], supabase, customCrosses)
      .then(v => { if (!cancelled) setHealthVerified(v); });
    return () => { cancelled = true; };
  }, [dog.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pedigreeTree = buildPedigreeTree(dog, get, { maxGenerations: PED_MAX_GEN });
  // Certificates always render the full 5-generation grid — unknown ancestors
  // show as a dim "(not on record)" placeholder box rather than the branch
  // simply ending early. realDeepestGen (real, on-record depth) still drives
  // the "N-Generation Pedigree" label so it never overclaims what's on file.
  const { nodes: pedNodes, edges: pedEdges, realDeepestGen } = flattenPedigreeTree(pedigreeTree, PED_MAX_GEN, { forceFull: true });

  const certNum = `UPR-${dog.id?.slice(0,8).toUpperCase()}`;
  const issued = new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});

  const genCount = realDeepestGen + 1;
  const pedLabel = realDeepestGen===0 ? 'Pedigree' : `${genCount}-Generation Pedigree`;
  const colLabels = {1:'Parents',2:'Grandparents',3:'Great-Grandparents',4:'2x Great-Grandparents'};

  const chartH = 300;
  const boxHByGen = {1:44,2:30,3:20,4:14};
  const nameFontByGen = {1:10,2:8.5,3:7.5,4:6};
  const subFontByGen = {1:6.5,2:6};
  const photoSizeByGen = {1:28,2:18,3:0,4:0};

  // Percent-based chart coordinate space, mirrors the PDF's pedLeft/colsStart/colW model:
  // gen 0 is a virtual point (the subject, shown in the header above — no box), gen
  // 1..(PED_MAX_GEN-1) are box columns whose right edges stay within the 0-100 chart width.
  const trunkGap = 4;
  const colW = (100 - trunkGap) / (PED_MAX_GEN - 1);
  const boxGapPct = 2.5;
  const boxWPct = colW - boxGapPct;
  const nodeX = (gen) => gen===0 ? 0 : trunkGap + (gen-1)*colW;

  return(
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:500,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px",overflowY:"auto"}}>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #upr-certificate, #upr-certificate * { visibility: visible !important; }
          #upr-certificate { position: fixed !important; left: 0 !important; top: 0 !important; width: 100% !important; box-shadow: none !important; padding: 10mm 12mm !important; }
          .cert-no-print { display: none !important; }
          @page { size: landscape; margin: 0; }
        }
      `}</style>

      {/* Controls */}
      <div className="cert-no-print" style={{position:"fixed",top:20,right:20,display:"flex",gap:10,zIndex:501}}>
        <button className="bgl" onClick={()=>window.print()}>⬇ Print / Save PDF</button>
        <button className="bgs" onClick={onClose}>✕ Close</button>
      </div>

      {/* Certificate */}
      <div id="upr-certificate" style={{
        background:"#fffff8", width:"279mm", minHeight:"210mm",
        padding:"12mm 14mm", boxShadow:"0 20px 60px rgba(0,0,0,0.6)",
        fontFamily:"Georgia,serif", position:"relative", overflow:"hidden",
        marginTop:60,
      }}>
        {/* Borders */}
        <div style={{position:"absolute",inset:8,border:"2px solid #b8922a",pointerEvents:"none",zIndex:0}}/>
        <div style={{position:"absolute",inset:13,border:"0.5px solid #d4af50",pointerEvents:"none",zIndex:0}}/>

        {/* Watermark */}
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:180,opacity:0.04,pointerEvents:"none",userSelect:"none",zIndex:0}}>🐾</div>

        {/* Health-Verified seal — distinct from the gold registration seal, only rendered
            when the dog currently passes its breed's required CHIC panel */}
        {healthVerified && (
          <div style={{position:"absolute",top:14,right:16,zIndex:2,width:66,height:66,borderRadius:"50%",background:"radial-gradient(circle at 35% 30%,#4fa87a,#1f6b45)",border:"2px solid #b8922a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.35)",textAlign:"center"}}>
            <div style={{fontSize:17,lineHeight:1,color:"#fff"}}>✚</div>
            <div style={{fontSize:6,color:"#fff",fontWeight:800,letterSpacing:0.5,marginTop:2}}>HEALTH</div>
            <div style={{fontSize:6,color:"#fff",fontWeight:800,letterSpacing:0.5}}>VERIFIED</div>
          </div>
        )}

        {/* Header */}
        <div style={{textAlign:"center",marginBottom:8,position:"relative",zIndex:1}}>
          {b?.logoUrl
            ? <img src={b.logoUrl} alt={b.kennel||"Kennel logo"} style={{height:56,maxWidth:160,objectFit:"contain",display:"block",margin:"0 auto 4px"}}/>
            : <div style={{fontSize:32,marginBottom:2}}>🐾</div>
          }
          <div style={{fontSize:20,fontWeight:700,color:"#1a1a2e",letterSpacing:3,textTransform:"uppercase"}}>United Pedigree Registry</div>
          <div style={{fontSize:9,color:"#b8922a",letterSpacing:2,textTransform:"uppercase",marginTop:2}}>Certificate of Pedigree</div>
          <div style={{width:120,height:1,background:"#b8922a",margin:"5px auto"}}/>
          <div style={{fontSize:7,color:"#999",letterSpacing:1}}>Issued in Accordance with UPR Standards · Est. 2026</div>
        </div>

        {/* Subject panel */}
        <div style={{display:"flex",gap:10,marginBottom:8,padding:"8px 10px",background:"#fffff0",border:"1px solid #d4af50",borderRadius:3,position:"relative",zIndex:1}}>
          {/* Dog photo */}
          <div style={{width:72,height:72,flexShrink:0,border:"2px solid #b8922a",borderRadius:3,overflow:"hidden",background:"#f0ebe0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>
            {dog.photoUrl
              ? <img src={dog.photoUrl} alt={dog.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : "🐾"
            }
          </div>
          {/* Dog info */}
          <div style={{flex:1}}>
            {dog.titles&&<div style={{fontSize:8,color:"#b8922a",marginBottom:2}}>{dog.titles}</div>}
            <div style={{fontSize:15,fontWeight:700,color:"#1a1a2e",marginBottom:4,lineHeight:1.2}}>{dog.name}{dog.callName?` "${dog.callName}"`:""}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"4px 12px"}}>
              {[
                ["Breed",dog.breed],
                ["Sex",dog.sex==="M"?"Male":"Female"],
                ["Color",dog.color],
                ["DOB",dog.dob],
                ["Type",dog.type==="purebred"?"Purebred":"Designer"],
                ["AKC",dog.akcNum],
                ["ASCA",dog.ascaNum],
                ["UKC",dog.ukcNum],
               ["DNA #",dog.dnaNum||null],
                ["Reg #",(!dog.akcNum&&!dog.ascaNum)?dog.regNum:null],
                ["OFA Hips",dog.ofaHips],
                ["OFA Elbows",dog.ofaElbows],
                ["OFA Eyes",dog.ofaEyes],
                ["OFA Cardiac",dog.ofaCardiac],
                ["CHIC",dog.chicNum],
                ["CERF",dog.cerfNum],
                ["MDR1",dog.mdr1],
                ["BAER",dog.baer],
                ["VWD",dog.vwd],
                ["DM",dog.dm],
                ["Country",dog.country],
                ["Kennel",dog.originalKennel||(dog.source!=='reference'?b?.kennel:null)],
                ["Breeder",b?.name],
              ].filter(([,v])=>v).map(([l,v])=>(
                <div key={l}>
                  <div style={{fontSize:6,color:"#999",textTransform:"uppercase",letterSpacing:0.5}}>{l}</div>
                  <div style={{fontSize:8,color:l==="MDR1"?(v==="Clear"?"#2a6a42":v==="Carrier"?"#c87820":"#c03838"):l.startsWith("OFA")||l==="BAER"?"#2a6a42":l==="DNA #"?"#1a4a8a":"#1a1a2e",fontWeight:600}}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Cert number */}
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:6.5,color:"#999"}}>Certificate No.</div>
            <div style={{fontSize:8,color:"#b8922a",fontWeight:700,fontFamily:"monospace"}}>{certNum}</div>
            <div style={{fontSize:6.5,color:"#999",marginTop:6}}>Date Issued</div>
            <div style={{fontSize:8,color:"#1a1a2e"}}>{issued}</div>
            <div style={{fontSize:6.5,color:"#999",marginTop:6}}>Registry</div>
            <div style={{fontSize:7,color:"#1a1a2e"}}>United Pedigree Registry</div>
          </div>
        </div>

        {/* Pedigree label */}
        <div style={{fontSize:7,color:"#b8922a",letterSpacing:2,textTransform:"uppercase",textAlign:"center",marginBottom:3,position:"relative",zIndex:1}}>{pedLabel}</div>

        {/* Pedigree chart — bracket tree with right-angle connectors, always full depth */}
        <div style={{position:"relative",height:chartH,border:"1px solid #d4af50",zIndex:1,background:"#fffff8"}}>
          <>
            {Array.from({length: PED_MAX_GEN-1}, (_,i)=>i+1).map(gen=>(
              <div key={gen} style={{position:"absolute",left:`${nodeX(gen)}%`,top:0,width:`${boxWPct}%`,fontSize:6,color:"#b8922a",letterSpacing:1,textTransform:"uppercase",textAlign:"center",padding:"2px 0",borderBottom:"0.5px solid #d4af50",background:"#fffff0",fontFamily:"Georgia,serif"}}>{colLabels[gen]}</div>
            ))}

            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{position:"absolute",inset:0,width:"100%",height:"100%"}}>
              {pedEdges.map((edge,i)=>{
                const fromX = nodeX(edge.from.gen) + (edge.from.gen===0?0:boxWPct);
                const fromY = edge.from.y*100;
                const toX = nodeX(edge.to.gen);
                const toY = edge.to.y*100;
                const xJoin = (fromX+toX)/2;
                return <polyline key={i} points={`${fromX},${fromY} ${xJoin},${fromY} ${xJoin},${toY} ${toX},${toY}`} fill="none" stroke="#b8922a" strokeWidth={0.3} opacity={0.75}/>;
              })}
            </svg>

            {pedNodes.filter(n=>n.gen>=1).map(n=>{
              const male = n.sex==="M";
              const h = boxHByGen[n.gen];
              const photoSize = photoSizeByGen[n.gen];
              const regLine = n.akcNum?`AKC ${n.akcNum}`:n.ascaNum?`ASCA ${n.ascaNum}`:n.regNum?`${n.regBody||"Reg"} ${n.regNum}`:"";
              const subLine = [n.titles, regLine].filter(Boolean).join(" · ");
              return (
                <div key={n.id||`${n.gen}-${n.slotIndex}`} style={{
                  position:"absolute", left:`${nodeX(n.gen)}%`, width:`${boxWPct}%`,
                  top:`${n.y*100}%`, height:h, transform:"translateY(-50%)",
                  border: n.isPlaceholder ? "1px dashed #ccc" : `1px solid ${male?"#b8922a":"#a5647e"}99`,
                  background: n.isPlaceholder ? "#fafaf8" : (male?"#f9f8f3":"#fdf6f5"),
                  borderRadius:2, padding:"2px 4px", display:"flex", gap:4, alignItems:"center",
                  overflow:"hidden",
                }}>
                  {photoSize>0&&!n.isPlaceholder&&(
                    <div style={{width:photoSize,height:photoSize,flexShrink:0,borderRadius:2,border:"1px solid #d4af50",overflow:"hidden",background:"#f0ebe0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:photoSize*0.4}}>
                      {n.photoUrl ? <img src={n.photoUrl} alt={n.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : "🐾"}
                    </div>
                  )}
                  <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                    <div style={{fontFamily:"Georgia,serif",fontSize:nameFontByGen[n.gen],fontWeight:700,fontStyle:n.isPlaceholder?"italic":"normal",color:n.isPlaceholder?"#aaa":"#1a1a2e",lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.name||"(not on record)"}</div>
                    {n.gen<=2&&subLine&&<div style={{fontSize:subFontByGen[n.gen],color:"#777",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{subLine}</div>}
                  </div>
                </div>
              );
            })}
          </>
        </div>

        {/* Footer */}
        <div style={{textAlign:"center",marginTop:7,position:"relative",zIndex:1}}>
          <div style={{width:"100%",height:"0.5px",background:"#b8922a",marginBottom:4}}/>
          <div style={{fontSize:8,color:"#b8922a",fontStyle:"italic"}}>"Preserving Every Breed's Heritage and Promoting Responsible Breeding Worldwide"</div>
          <div style={{fontSize:6.5,color:"#bbb",marginTop:2,letterSpacing:0.5}}>UNITEDPEDIGREEREGISTRY.COM · This certificate is issued based on information provided to UPR and does not constitute AKC or ASCA registration.</div>
        </div>
      </div>
    </div>
  );
}

// ── BREEDING PLANNER ───────────────────────────────────────────────────────────
// Hypothetical sire×dam pairing preview. Entirely read-only / client-side —
// no new tables, no writes. Reuses buildPedigreeTree/flattenPedigreeTree
// (same shared module the certificate uses) with a synthetic {sireId,damId}
// root, since there's no real puppy on file yet — those two functions only
// ever need an object with sireId/damId fields, never a saved dog row.

// depth 0 = the parent itself, depth 1 = the parent's own parents, etc. —
// relative to EACH parent, not the hypothetical puppy, which is what
// Wright's n1/n2 path-length terms need.
function collectAncestorDepths(dog, get, maxDepth) {
  const paths = {};
  function walk(row, depth) {
    if (!row) return;
    (paths[row.id] = paths[row.id] || []).push(depth);
    if (depth >= maxDepth) return;
    if (row.sireId) walk(get(row.sireId), depth + 1);
    if (row.damId) walk(get(row.damId), depth + 1);
  }
  walk(dog, 0);
  return paths;
}

// Wright's path-weighted COI, summed over every path-pair to each common
// ancestor: (0.5)^(n1+n2+1). Assumes each ancestor's own inbreeding = 0
// (historical F isn't tracked anywhere in this schema) and is bounded to
// PED_MAX_GEN generations of each parent — an honest "5-Generation COI",
// the same simplification most pedigree software uses when only ancestry,
// not full stud-book F values, is on file. Not the true whole-pedigree COI.
function calculatePreviewCOI(sire, dam, get, maxGenerations) {
  const maxDepth = maxGenerations - 1;
  const sirePaths = collectAncestorDepths(sire, get, maxDepth);
  const damPaths = collectAncestorDepths(dam, get, maxDepth);
  let coi = 0;
  const commonAncestorIds = [];
  Object.keys(sirePaths).forEach(id => {
    if (damPaths[id]) {
      commonAncestorIds.push(id);
      sirePaths[id].forEach(n1 => damPaths[id].forEach(n2 => { coi += Math.pow(0.5, n1 + n2 + 1); }));
    }
  });
  return { coiPercent: coi * 100, commonAncestorIds };
}

// A dog's own "founding purebred breed ids" — same branching requirementsForDog
// already uses (purebred → resolveBreedId; designer/custom → parent_breed_ids),
// just returning ids instead of health-requirement rows. Reused below to union
// both parents into the resulting cross's requirement set via the existing,
// unmodified unionParentRequirements — one path covers every combination of
// purebred/designer/custom on either side, with no per-type special-casing.
function parentBreedIdsForDog(dog, breeds, designerBreeds, customCrosses) {
  if (!dog) return [];
  if (dog.type === 'purebred') {
    const breedId = resolveBreedId(dog, breeds);
    return breedId ? [breedId] : [];
  }
  if (dog.designerBreedId) {
    const db = (designerBreeds||[]).find(d => d.id === dog.designerBreedId);
    return db?.parent_breed_ids || [];
  }
  if (dog.customCrossId) {
    const cc = Object.values(customCrosses||{}).flat().find(c => c.id === dog.customCrossId);
    return cc?.parent_breed_ids || [];
  }
  return [];
}

function describeCross(sire, dam, breeds, designerBreeds, customCrosses) {
  const breedById = Object.fromEntries((breeds||[]).map(b=>[b.id,b]));
  const combinedIds = [...new Set([
    ...parentBreedIdsForDog(sire, breeds, designerBreeds, customCrosses),
    ...parentBreedIdsForDog(dam, breeds, designerBreeds, customCrosses),
  ])];
  if (combinedIds.length === 0) return { label: 'Unknown breed mix', combinedIds };
  const names = combinedIds.map(id => breedById[id]?.name || 'Unknown breed').join(' × ');
  if (combinedIds.length === 1) return { label: `Purebred ${names}`, combinedIds };
  const sortedKey = [...combinedIds].sort().join(',');
  const catalogMatch = (designerBreeds||[]).find(db => {
    const ids = db.parent_breed_ids || [];
    return ids.length === combinedIds.length && [...ids].sort().join(',') === sortedKey;
  });
  return { label: catalogMatch ? `${catalogMatch.name} (${names})` : `${names} (uncataloged mix)`, combinedIds };
}

function BreedingPlanner({allDogs, breeders, breeds, designerBreeds, customCrosses, healthRequirements, myLitters}){
  const [sireId, setSireId] = useState('');
  const [damId, setDamId] = useState('');
  const get = (id) => id && allDogs[id] ? allDogs[id] : null;
  const sire = sireId ? allDogs[sireId] : null;
  const dam = damId ? allDogs[damId] : null;

  const preview = (sire && dam) ? (() => {
    const virtualRoot = { id: '__preview__', sireId, damId };
    const tree = buildPedigreeTree(virtualRoot, get, { maxGenerations: PED_MAX_GEN });
    const { nodes, edges } = flattenPedigreeTree(tree, PED_MAX_GEN, { forceFull: true });
    const { coiPercent, commonAncestorIds } = calculatePreviewCOI(sire, dam, get, PED_MAX_GEN);
    const cross = describeCross(sire, dam, breeds, designerBreeds, customCrosses);
    const requirements = unionParentRequirements(cross.combinedIds, healthRequirements);
    const existingLitters = (myLitters||[]).filter(l => l.sireId===sireId && l.damId===damId);
    return { nodes, edges, coiPercent, commonAncestorIds, cross, requirements, existingLitters };
  })() : null;

  const colLabels = {1:'Parents',2:'Grandparents',3:'Great-Grandparents',4:'2x Great-Grandparents'};
  const boxHByGen = {1:44,2:30,3:20,4:14};
  const nameFontByGen = {1:11,2:9,3:8,4:7};
  const trunkGap = 4;
  const colW = (100 - trunkGap) / (PED_MAX_GEN - 1);
  const boxGapPct = 2.5;
  const boxWPct = colW - boxGapPct;
  const nodeX = (gen) => trunkGap + (gen-1)*colW;

  return (
    <div>
      <div style={{fontSize:13,color:C.slate,marginBottom:16}}>
        Preview a hypothetical sire × dam pairing — pedigree, estimated COI, and health-test requirements — before committing to a real litter. Read-only; nothing here is saved.
      </div>

      <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:20}}>
        <div style={{flex:1,minWidth:220}}>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sire</label>
          <DogAutocomplete sex="M" value={sireId} onChange={setSireId} dogs={allDogs} breeders={breeders} placeholder="Search sires (3+ chars)…"/>
        </div>
        <div style={{flex:1,minWidth:220}}>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Dam</label>
          <DogAutocomplete sex="F" value={damId} onChange={setDamId} dogs={allDogs} breeders={breeders} placeholder="Search dams (3+ chars)…"/>
        </div>
      </div>

      {!preview && (
        <div style={{textAlign:"center",padding:"48px 20px",border:`1px dashed ${C.border}`,borderRadius:12,color:C.slate}}>
          <div style={{fontSize:48,marginBottom:12}}>🧬</div>
          <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.cream,marginBottom:8}}>Pick a Sire and Dam</div>
          <p style={{fontSize:14,lineHeight:1.7}}>Select any two dogs in the registry (not just your own) to preview what the pairing would produce.</p>
        </div>
      )}

      {preview && (
        <div>
          <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:10,padding:16,marginBottom:16}}>
            <div style={{fontFamily:"serif",fontSize:17,fontWeight:700,color:C.gold,marginBottom:6}}>{preview.cross.label}</div>
            {preview.existingLitters.length>0 && (
              <div style={{fontSize:12,color:C.amber,marginBottom:4}}>You already have {preview.existingLitters.length} litter{preview.existingLitters.length===1?'':'s'} on file for this exact pairing.</div>
            )}
            <div style={{fontSize:13,color:C.creamD}}>
              5-Gen COI estimate: <strong style={{color:preview.coiPercent>=6.25?C.red:preview.coiPercent>0?C.amber:C.emeraldL}}>{preview.coiPercent.toFixed(2)}%</strong>
              <span style={{color:C.slate}}> (Wright's coefficient, based on shared ancestors within {PED_MAX_GEN} generations of each parent; assumes each ancestor's own inbreeding is 0 — not the true whole-pedigree COI)</span>
            </div>
            {preview.commonAncestorIds.length>0 && (
              <div style={{fontSize:12,color:C.slate,marginTop:6}}>
                Common ancestor{preview.commonAncestorIds.length===1?'':'s'}: {preview.commonAncestorIds.map(id=>allDogs[id]?.name||'Unknown').join(', ')}
              </div>
            )}
          </div>

          <div style={{fontSize:10,color:C.slate,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Combined Pedigree Preview</div>
          <div style={{position:"relative",height:300,border:`1px solid ${C.border}`,borderRadius:8,background:C.bg2,marginBottom:20}}>
            {Array.from({length: PED_MAX_GEN-1}, (_,i)=>i+1).map(gen=>(
              <div key={gen} style={{position:"absolute",left:`${nodeX(gen)}%`,top:0,width:`${boxWPct}%`,fontSize:8,color:C.gold,letterSpacing:1,textTransform:"uppercase",textAlign:"center",padding:"3px 0",borderBottom:`1px solid ${C.border}`}}>{colLabels[gen]}</div>
            ))}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{position:"absolute",inset:0,width:"100%",height:"100%"}}>
              {preview.edges.filter(e=>e.from.gen>=1).map((edge,i)=>{
                const fromX = nodeX(edge.from.gen) + boxWPct;
                const fromY = edge.from.y*100;
                const toX = nodeX(edge.to.gen);
                const toY = edge.to.y*100;
                const xJoin = (fromX+toX)/2;
                return <polyline key={i} points={`${fromX},${fromY} ${xJoin},${fromY} ${xJoin},${toY} ${toX},${toY}`} fill="none" stroke={C.gold} strokeWidth={0.3} opacity={0.5}/>;
              })}
            </svg>
            {preview.nodes.filter(n=>n.gen>=1).map(n=>{
              const male = n.sex==="M";
              const isCommon = n.id && preview.commonAncestorIds.includes(n.id);
              return (
                <div key={n.id||`${n.gen}-${n.slotIndex}`} style={{
                  position:"absolute", left:`${nodeX(n.gen)}%`, width:`${boxWPct}%`,
                  top:`${n.y*100}%`, height:boxHByGen[n.gen], transform:"translateY(-50%)",
                  border: n.isPlaceholder ? `1px dashed ${C.border}` : `1px solid ${isCommon?C.amber:male?C.gold+"77":C.goldL+"55"}`,
                  background: n.isPlaceholder ? C.bg2 : isCommon ? `${C.amber}22` : male?C.bg3:C.bg2,
                  borderRadius:4, padding:"2px 5px", display:"flex", alignItems:"center", overflow:"hidden",
                }}>
                  <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                    <div style={{fontFamily:"serif",fontSize:nameFontByGen[n.gen],fontWeight:700,fontStyle:n.isPlaceholder?"italic":"normal",color:n.isPlaceholder?C.slate:C.cream,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.name||"(not on record)"}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{fontSize:10,color:C.slate,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Resulting Cross — Health Test Requirements</div>
          {preview.requirements.length===0 ? (
            <div style={{fontSize:13,color:C.slate}}>No health requirements on file for {preview.cross.label}.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {preview.requirements.map(r=>(
                <div key={r.test_key} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:13,color:C.cream}}>{r.test_label}</span>
                  <span style={{fontSize:10,color:r.requirement==='required'?C.gold:C.slate,textTransform:"uppercase",letterSpacing:1}}>{r.requirement}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── DOG CARD ──────────────────────────────────────────────────────────────────
function DogCard({dog,breeders,canView,healthVerified,onView}){
  const sm=dog.sex==="M";const hi=dog.type==="heritage";
  const b=breeders[dog.breederId];
  return(
    <div className="dcard" style={{borderColor:hi?`${C.emeraldL}66`:sm?`${C.gold}66`:`${C.goldL}33`,background:hi?"#0e1518":sm?C.bg3:C.bg2}}>
      <div style={{display:"flex",gap:10,marginBottom:10}}>
        <div style={{width:46,height:46,borderRadius:7,overflow:"hidden",border:`2px solid ${hi?C.emeraldL:sm?C.gold:C.goldL}66`,background:C.bg4,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
          {dog.photoUrl?<img src={dog.photoUrl} alt={dog.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(hi?"🌿":"🐾")}
        </div>
        <div style={{flex:1,minWidth:0}}>
          {dog.titles&&<div style={{fontSize:8,color:C.goldL,marginBottom:1}}>{dog.titles}</div>}
          <div style={{fontFamily:"serif",fontSize:13,fontWeight:700,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{dog.name||"Unnamed"}</div>
          {dog.callName&&<div style={{fontSize:10,color:C.slate}}>"{dog.callName}"</div>}
          <div style={{fontSize:10,color:hi?C.emeraldL:C.gold,marginTop:1}}>{dog.breed}</div>
          {healthVerified&&<div style={{fontSize:9,color:"#4fa87a",fontWeight:700,marginTop:2}}>✚ Health-Verified</div>}
        </div>
      </div>
      <div style={{marginBottom:10,display:"flex",flexDirection:"column",gap:3}}>
        {dog.ofaHips&&<MetaRow l="OFA Hips" v={dog.ofaHips}/>}
        {dog.mdr1&&<MetaRow l="MDR1" v={dog.mdr1} vc={mdr1C(dog.mdr1)}/>}
        {(dog.originalKennel||(dog.source!=='reference'&&b))&&<MetaRow l="Kennel" v={dog.originalKennel||b?.kennel}/>}
        {dog.recordType==='unclaimed'&&<MetaRow l="Status" v="Unclaimed" vc={C.slate}/>}
      </div>
      <button onClick={onView} style={{width:"100%",background:canView?`linear-gradient(135deg,${C.gold},${C.goldL})`:"rgba(255,255,255,0.05)",color:canView?C.bg:C.slate,border:canView?"none":`1px solid ${C.border}`,borderRadius:5,padding:"7px 0",cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"sans-serif"}}>
        {canView?"View Pedigree":"🔒 Members Only"}
      </button>
    </div>
  );
}

// ── BREEDER CARD ──────────────────────────────────────────────────────────────
// Tier-based visual styling for kennel cards/profiles.
// verified → gold border + ribbon badge; paying → gold border only;
// reference → muted gray border + dimmer text + "Reference" label on profiles.
const tierStyle = (t) => {
  if(t==='verified') return {
    border:C.gold, borderWidth:2,
    avatarBorder:C.gold, country:C.gold,
    text:C.cream, sub:C.slate, accent:C.goldL,
    showVerified:true, showRef:false,
  };
  if(t==='paying') return {
    border:C.gold, borderWidth:2,
    avatarBorder:C.gold, country:C.gold,
    text:C.cream, sub:C.slate, accent:C.goldL,
    showVerified:false, showRef:false,
  };
  return {
    border:C.slate, borderWidth:1,
    avatarBorder:C.slate, country:C.slateL,
    text:C.creamD, sub:C.slate, accent:C.slateL,
    showVerified:false, showRef:true,
  };
};

// ── DOG AUTOCOMPLETE ──────────────────────────────────────────────────────────
// Search-as-you-type sire/dam picker. Filters by sex, joins to breeders for
// tier color, two-pass match (starts-with then contains), capped at 10 results.
function dogPickerLabel(dog, breeders) {
  if (dog.originalKennel) return dog.originalKennel;
  if (dog.recordType === 'owned') return breeders[dog.breederId]?.kennel || '';
  if (dog.regNum) return (dog.regBody ? dog.regBody + ' ' : '') + '#' + dog.regNum;
  return '';
}

function DogAutocomplete({sex, value, onChange, dogs, breeders, currentDogId, onAdd, addLabel, placeholder}){
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const picked = value && dogs[value] ? dogs[value] : null;

  const matches = (() => {
    if (query.length < 3) return null;
    const q = query.toLowerCase();
    const tierRank = {verified:0, paying:1, reference:2};
    const annotate = (d) => {
      const b = breeders[d.breederId];
      const tier = b?.kennelType || 'reference';
      return {dog:d, breeder:b, tier, tr:tierRank[tier] ?? 2};
    };
    const starts = [], contains = [];
    Object.values(dogs).forEach(d => {
      if (sex && d.sex !== sex) return;
      if (d.id === currentDogId) return;
      const n = (d.name||'').toLowerCase();
      const c = (d.callName||'').toLowerCase();
      if (n.startsWith(q) || c.startsWith(q)) starts.push(annotate(d));
      else if (n.includes(q) || c.includes(q)) contains.push(annotate(d));
    });
    const cmp = (a,b) => a.tr !== b.tr ? a.tr - b.tr : (a.dog.name||'').localeCompare(b.dog.name||'');
    starts.sort(cmp); contains.sort(cmp);
    const all = [...starts, ...contains];
    return {items: all.slice(0,10), total: all.length};
  })();

  // Click-outside: close, and on hybrid Case A auto-link if exact match exists.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        if (query.trim() && !value) {
          const q = query.trim().toLowerCase();
          const exact = Object.values(dogs).find(d =>
            d.sex === sex && d.id !== currentDogId &&
            ((d.name||'').toLowerCase() === q || (d.callName||'').toLowerCase() === q)
          );
          if (exact) { onChange(exact.id); setQuery(''); }
        }
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, query, value, sex, currentDogId, dogs, onChange]);

  if (picked) {
    const b = breeders[picked.breederId];
    const tier = b?.kennelType || 'reference';
    const color = tier==='verified' ? C.gold : tier==='paying' ? C.cream : C.creamD;
    const pickedLbl = dogPickerLabel(picked, breeders);
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:C.bg3,border:`1px solid ${C.border}`,borderRadius:7,minHeight:38}}>
        <span style={{flex:1,color,fontSize:14,fontWeight:600}}>{picked.name}{pickedLbl ? ` · ${pickedLbl}` : ''}</span>
        <button type="button" onClick={()=>{onChange('');setQuery('');}} style={{background:"none",border:"none",color:C.slate,cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}>×</button>
      </div>
    );
  }

  const pick = (id) => { onChange(id); setQuery(''); setOpen(false); };
  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'Tab') { setOpen(false); return; }
    if (!matches || matches.items.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h+1, matches.items.length-1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h-1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = matches.items[hi]; if (r) pick(r.dog.id); }
  };

  const tierColor = (t) => t==='verified'?C.gold:t==='paying'?C.cream:C.creamD;
  const showHint = !focused && query.trim() && !value;

  return (
    <div ref={wrapRef} style={{position:"relative"}}>
      <input className="fi" value={query} placeholder={placeholder}
        onChange={e=>{setQuery(e.target.value); setOpen(true); setHi(0);}}
        onFocus={()=>{setFocused(true); setOpen(true);}}
        onBlur={()=>setFocused(false)}
        onKeyDown={onKey}/>
      {open && query.length >= 3 && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,marginTop:2,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:7,boxShadow:"0 6px 20px rgba(0,0,0,0.5)",zIndex:200,maxHeight:280,overflowY:"auto"}}>
          {matches && matches.items.length > 0 ? (
            <>
              {matches.items.map((r,i)=>{
                const d = r.dog;
                const tail = [];
                const lbl = dogPickerLabel(d, breeders);
                if (lbl) tail.push(lbl);
                return (
                  <div key={d.id}
                    onMouseDown={e=>{e.preventDefault(); pick(d.id);}}
                    onMouseEnter={()=>setHi(i)}
                    style={{padding:"8px 12px",cursor:"pointer",background:i===hi?C.bg3:"transparent",borderBottom:`1px solid ${C.border}33`,fontSize:13}}>
                    <span style={{color:tierColor(r.tier),fontWeight:600}}>{d.name}</span>
                    {tail.length>0 && <span style={{color:C.slate}}>{' · '}{tail.join(' · ')}</span>}
                  </div>
                );
              })}
              {matches.total > 10 && (
                <div style={{padding:"6px 12px",fontSize:11,fontStyle:"italic",color:C.slate}}>...more results, keep typing</div>
              )}
            </>
          ) : (
            <div style={{padding:"10px 12px",fontSize:12,fontStyle:"italic",color:C.slate}}>No matches</div>
          )}
          {onAdd && query.trim() && (
            <div style={{padding:"8px 12px",borderTop:`1px solid ${C.border}66`,background:`${C.gold}10`}}>
              <button type="button" onMouseDown={e=>{e.preventDefault(); setOpen(false); onAdd(query.trim());}} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>+ Add "{query.trim()}" as new {addLabel || 'dog'}</button>
            </div>
          )}
        </div>
      )}
      {showHint && (
        <div style={{fontSize:11,color:C.slate,fontStyle:"italic",marginTop:4}}>No dog selected — pick one from the dropdown or click + Add new</div>
      )}
    </div>
  );
}

// ── KENNEL AUTOCOMPLETE ──────────────────────────────────────────────────────
// Same UX shape as DogAutocomplete but for breeders. No sex filter, searches
// kennel name only, tier-colored rows, "+ Add" footer when onAdd is provided.
function KennelAutocomplete({value, onChange, breeders, currentKennelId, onAdd, placeholder}){
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const picked = value && breeders[value] ? breeders[value] : null;

  const matches = (() => {
    if (query.length < 3) return null;
    const q = query.toLowerCase();
    const tierRank = {verified:0, paying:1, reference:2};
    const annotate = (b) => {
      const tier = b.kennelType || 'reference';
      return {breeder:b, tier, tr:tierRank[tier] ?? 2};
    };
    const starts = [], contains = [];
    Object.values(breeders).forEach(b => {
      if (currentKennelId && b.id === currentKennelId) return;
      const k = (b.kennel||'').toLowerCase();
      if (k.startsWith(q)) starts.push(annotate(b));
      else if (k.includes(q)) contains.push(annotate(b));
    });
    const cmp = (a,b) => a.tr !== b.tr ? a.tr - b.tr : (a.breeder.kennel||'').localeCompare(b.breeder.kennel||'');
    starts.sort(cmp); contains.sort(cmp);
    const all = [...starts, ...contains];
    return {items: all.slice(0,10), total: all.length};
  })();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        if (query.trim() && !value) {
          const q = query.trim().toLowerCase();
          const exact = Object.values(breeders).find(b =>
            (!currentKennelId || b.id !== currentKennelId) &&
            (b.kennel||'').toLowerCase() === q
          );
          if (exact) { onChange(exact.id); setQuery(''); }
        }
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, query, value, currentKennelId, breeders, onChange]);

  if (picked) {
    const tier = picked.kennelType || 'reference';
    const color = tier==='verified' ? C.gold : tier==='paying' ? C.cream : C.creamD;
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:C.bg3,border:`1px solid ${C.border}`,borderRadius:7,minHeight:38}}>
        <span style={{flex:1,color,fontSize:14,fontWeight:600}}>{picked.kennel}{picked.country?` · ${picked.country}`:''}</span>
        <button type="button" onClick={()=>{onChange('');setQuery('');}} style={{background:"none",border:"none",color:C.slate,cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}>×</button>
      </div>
    );
  }

  const pick = (id) => { onChange(id); setQuery(''); setOpen(false); };
  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'Tab') { setOpen(false); return; }
    if (!matches || matches.items.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h+1, matches.items.length-1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h-1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = matches.items[hi]; if (r) pick(r.breeder.id); }
  };

  const tierColor = (t) => t==='verified'?C.gold:t==='paying'?C.cream:C.creamD;
  const showHint = !focused && query.trim() && !value;

  return (
    <div ref={wrapRef} style={{position:"relative"}}>
      <input className="fi" value={query} placeholder={placeholder}
        onChange={e=>{setQuery(e.target.value); setOpen(true); setHi(0);}}
        onFocus={()=>{setFocused(true); setOpen(true);}}
        onBlur={()=>setFocused(false)}
        onKeyDown={onKey}/>
      {open && query.length >= 3 && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,marginTop:2,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:7,boxShadow:"0 6px 20px rgba(0,0,0,0.5)",zIndex:200,maxHeight:280,overflowY:"auto"}}>
          {matches && matches.items.length > 0 ? (
            <>
              {matches.items.map((r,i)=>{
                const b = r.breeder;
                const tail = [];
                if (b.name) tail.push(b.name);
                if (b.country) tail.push(b.country);
                return (
                  <div key={b.id}
                    onMouseDown={e=>{e.preventDefault(); pick(b.id);}}
                    onMouseEnter={()=>setHi(i)}
                    style={{padding:"8px 12px",cursor:"pointer",background:i===hi?C.bg3:"transparent",borderBottom:`1px solid ${C.border}33`,fontSize:13}}>
                    <span style={{color:tierColor(r.tier),fontWeight:600}}>{b.kennel}</span>
                    {tail.length>0 && <span style={{color:C.slate}}>{' · '}{tail.join(' · ')}</span>}
                  </div>
                );
              })}
              {matches.total > 10 && (
                <div style={{padding:"6px 12px",fontSize:11,fontStyle:"italic",color:C.slate}}>...more results, keep typing</div>
              )}
            </>
          ) : (
            <div style={{padding:"10px 12px",fontSize:12,fontStyle:"italic",color:C.slate}}>No matches</div>
          )}
          {onAdd && query.trim() && (
            <div style={{padding:"8px 12px",borderTop:`1px solid ${C.border}66`,background:`${C.gold}10`}}>
              <button type="button" onMouseDown={e=>{e.preventDefault(); setOpen(false); onAdd(query.trim());}} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>+ Add "{query.trim()}" as new kennel</button>
            </div>
          )}
        </div>
      )}
      {showHint && (
        <div style={{fontSize:11,color:C.slate,fontStyle:"italic",marginTop:4}}>No kennel selected — pick one or click + Add new</div>
      )}
    </div>
  );
}

// ── BREEDER BREEDS MANAGER ───────────────────────────────────────────────────
// "My Breeds" tab body. Shows currently-selected breeds as removable chips,
// plus a BreedAutocomplete to add more. Optimistic local updates with rollback
// on error.
function BreederBreedsManager({breeder, breeds, breederBreeds, setBreederBreeds, supabase, flash}){
  const [saving, setSaving] = useState(false);
  const myRows = breederBreeds[breeder.id] || [];
  const myBreedIds = new Set(myRows.map(r => r.breed_id));
  const breedById = breeds.reduce((acc,b) => { acc[b.id] = b; return acc; }, {});
  const selectedSorted = myRows
    .map(r => breedById[r.breed_id])
    .filter(Boolean)
    .sort((a,b) => a.name.localeCompare(b.name));

  const addBreed = async (breed) => {
    if (saving || myBreedIds.has(breed.id)) return;
    setSaving(true);
    const optimistic = {breed_id: breed.id, added_at: new Date().toISOString()};
    setBreederBreeds(prev => ({
      ...prev,
      [breeder.id]: [...(prev[breeder.id]||[]), optimistic]
    }));
    const { error } = await supabase.from('breeder_breeds')
      .insert({ breeder_id: breeder.id, breed_id: breed.id });
    if (error) {
      setBreederBreeds(prev => ({
        ...prev,
        [breeder.id]: (prev[breeder.id]||[]).filter(r => r.breed_id !== breed.id)
      }));
      flash("⚠ " + error.message);
    } else {
      flash(`Added ${breed.name}`);
    }
    setSaving(false);
  };

  const removeBreed = async (breed) => {
    if (saving) return;
    setSaving(true);
    const snapshot = myRows;
    setBreederBreeds(prev => ({
      ...prev,
      [breeder.id]: (prev[breeder.id]||[]).filter(r => r.breed_id !== breed.id)
    }));
    const { error } = await supabase.from('breeder_breeds')
      .delete()
      .eq('breeder_id', breeder.id)
      .eq('breed_id', breed.id);
    if (error) {
      setBreederBreeds(prev => ({ ...prev, [breeder.id]: snapshot }));
      flash("⚠ " + error.message);
    }
    setSaving(false);
  };

  return (
    <div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:16}}>
        <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:8}}>My Breeds</div>
        <div style={{fontSize:13,color:C.slate,lineHeight:1.7,marginBottom:20}}>
          Choose the breeds your kennel works with. These will be shown on your public breeder profile and used to filter your dog and litter forms.
        </div>

        {selectedSorted.length === 0 ? (
          <div style={{padding:"20px",textAlign:"center",border:`1px dashed ${C.border}`,borderRadius:8,color:C.slate,fontSize:13,fontStyle:"italic",marginBottom:16}}>
            No breeds added yet. Search below to add your first breed.
          </div>
        ) : (
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
            {selectedSorted.map(b => (
              <span key={b.id} style={{display:"inline-flex",alignItems:"center",gap:6,background:`${C.gold}1a`,border:`1px solid ${C.gold}55`,borderRadius:20,padding:"5px 10px 5px 14px",fontSize:13,color:C.goldL}}>
                <span>{b.name}</span>
                <button
                  type="button"
                  onClick={()=>removeBreed(b)}
                  disabled={saving}
                  title={`Remove ${b.name}`}
                  style={{background:"none",border:"none",color:C.slate,cursor:saving?"default":"pointer",fontSize:16,padding:"0 2px",lineHeight:1,opacity:saving?0.5:1}}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Add a breed</label>
          <BreedAutocomplete
            breeds={breeds}
            excludeIds={myBreedIds}
            onPick={addBreed}
            placeholder="Type at least 3 letters…"
          />
        </div>
      </div>
    </div>
  );
}

// ── DESIGNER BREEDS MANAGER ──────────────────────────────────────────────────
// "My Designer Breeds" tab body. Parallel to BreederBreedsManager but backed by
// the breeder_designer_breeds join table + designer_breeds catalog. Reuses
// BreedAutocomplete (generic over {id,name}). Chips show the cross as a tooltip.
function DesignerBreedsManager({breeder, designerBreeds, breederDesignerBreeds, setBreederDesignerBreeds, supabase, flash}){
  const [saving, setSaving] = useState(false);
  const myRows = breederDesignerBreeds[breeder.id] || [];
  const myIds = new Set(myRows.map(r => r.designer_breed_id));
  const breedById = designerBreeds.reduce((acc,b) => { acc[b.id] = b; return acc; }, {});
  const selectedSorted = myRows
    .map(r => breedById[r.designer_breed_id])
    .filter(Boolean)
    .sort((a,b) => a.name.localeCompare(b.name));

  const addBreed = async (breed) => {
    if (saving || myIds.has(breed.id)) return;
    setSaving(true);
    const optimistic = {designer_breed_id: breed.id, added_at: new Date().toISOString()};
    setBreederDesignerBreeds(prev => ({
      ...prev,
      [breeder.id]: [...(prev[breeder.id]||[]), optimistic]
    }));
    const { error } = await supabase.from('breeder_designer_breeds')
      .insert({ breeder_id: breeder.id, designer_breed_id: breed.id });
    if (error) {
      setBreederDesignerBreeds(prev => ({
        ...prev,
        [breeder.id]: (prev[breeder.id]||[]).filter(r => r.designer_breed_id !== breed.id)
      }));
      flash("⚠ " + error.message);
    } else {
      flash(`Added ${breed.name}`);
    }
    setSaving(false);
  };

  const removeBreed = async (breed) => {
    if (saving) return;
    setSaving(true);
    const snapshot = myRows;
    setBreederDesignerBreeds(prev => ({
      ...prev,
      [breeder.id]: (prev[breeder.id]||[]).filter(r => r.designer_breed_id !== breed.id)
    }));
    const { error } = await supabase.from('breeder_designer_breeds')
      .delete()
      .eq('breeder_id', breeder.id)
      .eq('designer_breed_id', breed.id);
    if (error) {
      setBreederDesignerBreeds(prev => ({ ...prev, [breeder.id]: snapshot }));
      flash("⚠ " + error.message);
    }
    setSaving(false);
  };

  return (
    <div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:16}}>
        <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:8}}>My Designer Breeds</div>
        <div style={{fontSize:13,color:C.slate,lineHeight:1.7,marginBottom:20}}>
          Choose the designer / hybrid breeds your kennel works with. Hover a chip to see its parent breeds.
        </div>

        {selectedSorted.length === 0 ? (
          <div style={{padding:"20px",textAlign:"center",border:`1px dashed ${C.border}`,borderRadius:8,color:C.slate,fontSize:13,fontStyle:"italic",marginBottom:16}}>
            No designer breeds added yet. Search below to add your first one.
          </div>
        ) : (
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
            {selectedSorted.map(b => (
              <span key={b.id} title={b.parent_breeds_text||""} style={{display:"inline-flex",alignItems:"center",gap:6,background:`${C.gold}1a`,border:`1px solid ${C.gold}55`,borderRadius:20,padding:"5px 10px 5px 14px",fontSize:13,color:C.goldL}}>
                <span>{b.name}</span>
                <button
                  type="button"
                  onClick={()=>removeBreed(b)}
                  disabled={saving}
                  title={`Remove ${b.name}`}
                  style={{background:"none",border:"none",color:C.slate,cursor:saving?"default":"pointer",fontSize:16,padding:"0 2px",lineHeight:1,opacity:saving?0.5:1}}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Add a designer breed</label>
          <BreedAutocomplete
            breeds={designerBreeds}
            excludeIds={myIds}
            onPick={addBreed}
            placeholder="Type at least 3 letters…"
          />
        </div>
      </div>
    </div>
  );
}

// ── CUSTOM CROSSES MANAGER ───────────────────────────────────────────────────
// "My Custom Crosses" tab body (Tier C). Unlike the pick-from-list managers,
// this CREATES per-breeder PRIVATE entries (breeder_custom_crosses). name is
// required, parent_breeds_text optional; both trimmed. Client-generated uuid so
// the optimistic row's id is immediately usable as dogs.custom_cross_id.
// Duplicate names are rejected by the DB unique(breeder_id,name) -> friendly flash.
function CustomCrossesManager({breeder, customCrosses, setCustomCrosses, breeds, supabase, flash}){
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [parents, setParents] = useState('');
  const [parentBreedIds, setParentBreedIds] = useState([]);
  const myRows = (customCrosses[breeder.id] || [])
    .slice()
    .sort((a,b) => a.name.localeCompare(b.name));
  const purebredList = (breeds||[]).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const breedById = (breeds||[]).reduce((acc,b)=>{acc[b.id]=b;return acc;},{});

  const resetForm = () => { setName(''); setParents(''); setParentBreedIds([]); setCreating(false); setEditingId(null); };

  const startEdit = (cross) => {
    setName(cross.name);
    setParents(cross.parent_breeds_text || '');
    setParentBreedIds(cross.parent_breed_ids || []);
    setEditingId(cross.id);
    setCreating(true);
  };

  const toggleParentBreedId = (breedId) => {
    setParentBreedIds(prev => prev.includes(breedId) ? prev.filter(id=>id!==breedId) : [...prev, breedId]);
  };

  const createCross = async () => {
    const nm = name.trim();
    const pt = parents.trim();
    if (!nm || saving) return;
    setSaving(true);

    if (editingId) {
      const snapshot = customCrosses[breeder.id] || [];
      setCustomCrosses(prev => ({ ...prev, [breeder.id]: (prev[breeder.id]||[]).map(r => r.id===editingId ? {...r, name:nm, parent_breeds_text:pt, parent_breed_ids:parentBreedIds} : r) }));
      const { error } = await supabase.from('breeder_custom_crosses')
        .update({ name: nm, parent_breeds_text: pt, parent_breed_ids: parentBreedIds })
        .eq('id', editingId);
      if (error) {
        setCustomCrosses(prev => ({ ...prev, [breeder.id]: snapshot }));
        const dup = error.code === '23505' || /duplicate|already exists/i.test(error.message||'');
        flash(dup ? `⚠ You already have a cross named "${nm}"` : "⚠ " + error.message);
        setSaving(false);
        return;
      }
      flash(`Updated ${nm}`);
      setSaving(false);
      resetForm();
      return;
    }

    const id = crypto.randomUUID();
    const row = {id, name: nm, parent_breeds_text: pt, parent_breed_ids: parentBreedIds, created_at: new Date().toISOString()};
    setCustomCrosses(prev => ({ ...prev, [breeder.id]: [...(prev[breeder.id]||[]), row] }));
    const { error } = await supabase.from('breeder_custom_crosses')
      .insert({ id, breeder_id: breeder.id, name: nm, parent_breeds_text: pt, parent_breed_ids: parentBreedIds });
    if (error) {
      setCustomCrosses(prev => ({ ...prev, [breeder.id]: (prev[breeder.id]||[]).filter(r => r.id !== id) }));
      const dup = error.code === '23505' || /duplicate|already exists/i.test(error.message||'');
      flash(dup ? `⚠ You already have a cross named "${nm}"` : "⚠ " + error.message);
      setSaving(false);
      return;
    }
    flash(`Created ${nm}`);
    setSaving(false);
    resetForm();
  };

  const deleteCross = async (cross) => {
    if (saving) return;
    setSaving(true);
    const snapshot = customCrosses[breeder.id] || [];
    setCustomCrosses(prev => ({ ...prev, [breeder.id]: (prev[breeder.id]||[]).filter(r => r.id !== cross.id) }));
    const { error } = await supabase.from('breeder_custom_crosses')
      .delete()
      .eq('id', cross.id);
    if (error) {
      setCustomCrosses(prev => ({ ...prev, [breeder.id]: snapshot }));
      flash("⚠ " + error.message);
    }
    setSaving(false);
  };

  return (
    <div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:16}}>
        <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:8}}>My Custom Crosses</div>
        <div style={{fontSize:13,color:C.slate,lineHeight:1.7,marginBottom:20}}>
          Create your own cross names for combinations not in the recognized designer list. These are private to your kennel and appear in your dog form's Designer dropdown marked “(Custom).”
        </div>

        {myRows.length === 0 ? (
          <div style={{padding:"20px",textAlign:"center",border:`1px dashed ${C.border}`,borderRadius:8,color:C.slate,fontSize:13,fontStyle:"italic",marginBottom:16}}>
            No custom crosses yet. Create your first one below.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
            {myRows.map(c => (
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,background:C.bg4,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.cream}}>{c.name}</div>
                  {c.parent_breeds_text && <div style={{fontSize:12,color:C.slate}}>{c.parent_breeds_text}</div>}
                  {(c.parent_breed_ids||[]).length>0 ? (
                    <div style={{fontSize:11,color:C.emeraldL,marginTop:2}}>
                      Linked for Health-Verified: {c.parent_breed_ids.map(id=>breedById[id]?.name).filter(Boolean).join(', ')}
                    </div>
                  ) : (
                    <div style={{fontSize:11,color:C.slate,marginTop:2,fontStyle:"italic"}}>No real parent breeds linked yet — not eligible for Health-Verified</div>
                  )}
                </div>
                <button className="bos" style={{fontSize:11}} onClick={()=>startEdit(c)} disabled={saving}>Edit</button>
                <button type="button" onClick={()=>deleteCross(c)} disabled={saving} title={`Delete ${c.name}`}
                  style={{background:"none",border:"none",color:C.slate,cursor:saving?"default":"pointer",fontSize:18,lineHeight:1,opacity:saving?0.5:1}}>×</button>
              </div>
            ))}
          </div>
        )}

        {creating ? (
          <div style={{border:`1px solid ${C.gold}44`,borderRadius:8,padding:16,background:C.bg2}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Cross Name *</label>
                <input className="fi" value={name} autoFocus placeholder="e.g. Aussie Bernese" onChange={e=>setName(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')createCross();}}/>
              </div>
              <div>
                <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Parent Breeds (description)</label>
                <input className="fi" value={parents} placeholder="e.g. Australian Shepherd + Bernese Mountain Dog" onChange={e=>setParents(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')createCross();}}/>
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Real Parent Breeds (for Health-Verified)</label>
              <div style={{fontSize:11,color:C.slate,marginBottom:8}}>Optional, but required for this cross to be eligible for Health-Verified — pick the real purebred breeds behind it. Its required health panel becomes the union of theirs.</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8,maxHeight:180,overflowY:"auto"}}>
                {purebredList.map(b=>(
                  <label key={b.id} style={{fontSize:12,color:C.cream,display:"flex",alignItems:"center",gap:5,background:parentBreedIds.includes(b.id)?`${C.gold}22`:C.card,border:`1px solid ${parentBreedIds.includes(b.id)?C.gold+'66':C.border}`,borderRadius:16,padding:"5px 12px",cursor:"pointer"}}>
                    <input type="checkbox" checked={parentBreedIds.includes(b.id)} onChange={()=>toggleParentBreedId(b.id)}/>
                    {b.name}
                  </label>
                ))}
              </div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="bgs" onClick={resetForm} disabled={saving}>Cancel</button>
              <button className="bgl" onClick={createCross} disabled={saving||!name.trim()}>{editingId?'Save Changes':'Save'}</button>
            </div>
          </div>
        ) : (
          <button className="bos" onClick={()=>setCreating(true)}>+ Create Custom Cross</button>
        )}
      </div>
    </div>
  );
}

// ── BULK IMPORT MANAGER ──────────────────────────────────────────────────────
// Phase 11 Session 1: upload registration-paper photos into the PRIVATE
// "registration-papers" bucket + a dog_imports row (status 'uploaded'). OCR,
// review/edit, and save-to-dogs come in later sessions. Files live under
// "<auth.uid()>/…" to satisfy the storage RLS folder policy; private bucket →
// thumbnails use short-lived signed URLs. Imports list is component-local state.
// ── Import review field metadata (Phase 11 Session 3) ──
// The 30 ancestor paths (gens 2–5), generated the same way as the server's
// extraction schema — each a sequence of sire/dam steps from the dog.
const ANCESTOR_PATHS = (() => {
  const paths = [];
  let level = [''];
  for (let gen = 2; gen <= 5; gen++) {
    const next = [];
    for (const base of level)
      for (const side of ['sire', 'dam'])
        next.push(base ? `${base}_${side}` : side);
    paths.push(...next);
    level = next;
  }
  return paths;
})();

// "sire_dam_sire" → "Sire's Dam's Sire"
function ancestorLabel(path) {
  return path.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join("'s ");
}

// Path-derived sex: a "sire" leaf is Male, a "dam" leaf is Female. Definitional,
// not a guess — so it IS persisted on save (unlike the breed/DOB display defaults).
const impliedSex = (path) => {
  const last = path.split('_').pop();
  return last === 'sire' ? 'Male' : last === 'dam' ? 'Female' : '';
};

// Detects an OFA score token (e.g. "OFA43G") inside a color/markings string.
const OFA_RE = /OFA\d+[A-Z]?/i;

// Generation grouping for the review modal (segment count = gen − 1).
const GENS = [
  [2, 'Generation 2 — Parents'],
  [3, 'Generation 3 — Grandparents'],
  [4, 'Generation 4 — Great-Grandparents'],
  [5, 'Generation 5 — Great-Great-Grandparents'],
];

// Document-level terms for routing no-prefix other_fields keys: a key containing
// any of these is treated as generic (bottom section); otherwise it's a dog-level
// field shown on the Dog Info card. Extend as real papers reveal more terms.
const DOC_KEYWORDS = ['registry', 'registrar', 'certificate', 'pedigree', 'compiled', 'issued', 'stud book', 'studbook', 'kennel club', 'breed club', 'seal'];

// Compact preview of the OCR result for the import list. Shows key gen-1 fields
// plus a count of how many ancestor names were extracted (pedigree depth).
function importPreview(d) {
  if (!d || typeof d !== 'object') return [];
  if (d.error) return [['Error', String(d.error)]];
  const rows = [];
  if (d.dog_name) rows.push(['Name', d.dog_name]);
  if (d.breed) rows.push(['Breed', d.breed]);
  if (d.registration_number) rows.push(['Reg #', d.registration_number]);
  if (d.sex) rows.push(['Sex', d.sex]);
  if (d.date_of_birth) rows.push(['DOB', d.date_of_birth]);
  if (d.sire_name) rows.push(['Sire', d.sire_name]);
  if (d.dam_name) rows.push(['Dam', d.dam_name]);
  const ancestors = Object.keys(d).filter(k => k !== 'dog_name' && k.endsWith('_name') && d[k]).length;
  if (ancestors) rows.push(['Ancestors', String(ancestors)]);
  return rows;
}

// ── BULK IMPORT → DOGS (Phase 11 S4a) ────────────────────────────────────────
// Turn a reviewed import's extracted_data into dog rows ready for insert. S4a is
// the CREATE-only happy path: a position becomes a dog ONLY if it has BOTH a name
// and a reg# (Q1 — integrity over completeness). A reg# repeated within one paper
// maps to ONE record (line-breeding, decision 6). No cross-DB collision or parent
// conflict handling yet (S4b / S4c).

// Pull a position's six fields. path '' = the focus dog (keys differ: dog_name /
// registration_number); any other path = an ancestor (prefixed keys).
const POS_FIELDS = (ed, path) => path === '' ? {
  name: ed.dog_name, reg: ed.registration_number, breed: ed.breed,
  dob: ed.date_of_birth, sex: ed.sex, color: ed.color,
} : {
  name: ed[`${path}_name`], reg: ed[`${path}_registration_number`], breed: ed[`${path}_breed`],
  dob: ed[`${path}_date_of_birth`], sex: ed[`${path}_sex`], color: ed[`${path}_color`],
};

const sexToCode = (v) => { const s=(v||'').trim().toLowerCase(); return s.startsWith('m')?'M':s.startsWith('f')?'F':''; };

// DOB column is a DATE — accept only explicit calendar dates; never fabricate one
// from a bare year / season / date_code. Unparseable values fall back to a note so
// nothing is lost and the atomic insert can't fail on a bad date.
const parseDob = (v) => {
  const raw = (v==null?'':String(v)).trim();
  if (!raw) return { dob: null, note: '' };
  let iso = null, m;
  if ((m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) iso = `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`;
  else if ((m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) iso = `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}`;
  else if (/\b\d{1,2},\s*\d{4}\b/.test(raw)) { const d = new Date(raw); if (!isNaN(d.getTime())) iso = d.toISOString().slice(0,10); }
  if (iso && !isNaN(new Date(iso+'T00:00:00').getTime())) return { dob: iso, note: '' };
  return { dob: null, note: `DOB (as written): ${raw}` };
};

// Classify a dog's type from its own extracted breed name.
// Catalog match → type='heritage' + designerBreedId; breed present but no match → 'purebred';
// no breed → 'purebred' (safe default; user can correct post-import).
// Used identically for the focus dog and every ancestor, so F1/F2/F3/mixed papers all work.
function classifyBreed(breedName, designerBreeds) {
  const name = (breedName || '').trim();
  if (!name) return { type: 'purebred', designerBreedId: null };
  const match = (designerBreeds || []).find(db => db.name.toLowerCase() === name.toLowerCase());
  if (match) return { type: 'heritage', designerBreedId: match.id };
  return { type: 'purebred', designerBreedId: null };
}

function buildImportPlan(ed, breederId, { designerBreeds = [] } = {}) {
  ed = ed || {};
  const tv = (x) => (x==null?'':String(x)).trim();
  const depth = (p) => p === '' ? 0 : p.split('_').length;

  // Lookup for other_fields fallbacks (color_markings, date_code), keyed by the same
  // sire/dam path the S3 router uses — robust to '_' vs space vs case in the raw key.
  const of = ed.other_fields || {};
  const ofLookup = {};
  Object.keys(of).forEach(key => {
    const toks = key.trim().split(/[\s_]+/);
    let i = 0;
    while (i < toks.length && (toks[i].toLowerCase()==='sire' || toks[i].toLowerCase()==='dam')) i++;
    const path = toks.slice(0,i).map(s=>s.toLowerCase()).join('_');
    const prop = toks.slice(i).join('_').toLowerCase();
    ofLookup[`${path}|${prop}`] = of[key];
  });
  const ofGet = (path, prop) => tv(ofLookup[`${path}|${prop}`]);

  // _is_designer (set by ImportReviewModal toggle) controls the breed CASCADE only:
  // purebred import → blank ancestor breed inherits the focus breed (same breed throughout);
  // designer import → blank stays blank (each ancestor keeps its own extracted breed or nothing).
  // TYPE is always derived per-dog via classifyBreed — uniform for focus dog and all ancestors.
  const isDesigner = !!ed._is_designer;
  const focusBreed = tv(POS_FIELDS(ed, '').breed);

  const idByPath = {};            // path → dog id (created or deduped); absent = skipped
  const regToId = {};             // normalized reg# → id (line-breeding within this paper)
  const created = [];             // paths that get a NEW row
  ['', ...ANCESTOR_PATHS].forEach(path => {
    const f = POS_FIELDS(ed, path);
    const name = tv(f.name), reg = tv(f.reg);
    if (!name || !reg) return;                          // Q1: need BOTH name and reg#
    const key = reg.toLowerCase();
    if (regToId[key]) { idByPath[path] = regToId[key]; return; }   // dedup → same record
    const id = crypto.randomUUID();
    regToId[key] = id; idByPath[path] = id; created.push(path);
  });
  const childPath = (path, side) => path === '' ? side : `${path}_${side}`;
  created.sort((a,b) => depth(b) - depth(a));            // parent-first: deepest gen, focus last
  const rows = created.map(path => {
    const f = POS_FIELDS(ed, path);
    // Comprehensive split of the combined color/markings string:
    // OFA tokens → health_notes, DNA # → dna_num, (MM-YY) date code → DOB, remainder → color.
    const marks = ofGet(path, 'color_markings') || ofGet(path, 'color');
    const ofaMatches = marks.match(/OF[A-Z]+\d+[A-Z]?/gi) || [];
    const ofaScore = ofaMatches.join(' ');
    const dnaMatch = marks.match(/(?:AKC\s+)?DNA\s+#?(\w+)/i);
    const dnaFromMarks = dnaMatch ? dnaMatch[1] : '';
    const dateCodeMatch = marks.match(/\((\d{1,2}-\d{1,2})\)/);
    const dateCodeFromMarks = dateCodeMatch ? dateCodeMatch[1] : '';
    let colorFromMarks = marks;
    ofaMatches.forEach(m => { colorFromMarks = colorFromMarks.replace(m, ''); });
    if (dnaMatch) colorFromMarks = colorFromMarks.replace(dnaMatch[0], '');
    if (dateCodeMatch) colorFromMarks = colorFromMarks.replace(dateCodeMatch[0], '');
    colorFromMarks = colorFromMarks.replace(/\s+/g, ' ').trim();
    const color = tv(f.color) || colorFromMarks;
    // DOB: canonical field → date code from marks → other_fields date_code.
    const { dob, note } = parseDob(tv(f.dob) || dateCodeFromMarks || ofGet(path, 'date_code'));
    // Breed: extracted value always wins.
    // Purebred import: cascade focus breed to ancestors missing one (same breed throughout).
    // Designer import: no cascade — a blank ancestor breed is honest; a wrong one is a registry error.
    const breed = tv(f.breed) || (isDesigner ? '' : focusBreed);
    // New strict-schema fields take priority; marks parser stays as fallback for legacy data.
    const directOfa = tv(path === '' ? ed.ofa_health : ed[`${path}_ofa_health`]);
    const directDna = tv(path === '' ? ed.dna_number : ed[`${path}_dna_number`]);
    // Type derived from each dog's own breed — handles F1, F2+, and mixed parentage uniformly.
    const { type: dogType, designerBreedId: dogDesignerBreedId } = classifyBreed(breed, designerBreeds);
    const appDog = {
      id: idByPath[path], type: dogType, breed, name: tv(f.name),
      breedId: null,
      designerBreedId: dogDesignerBreedId,
      customCrossId: null,
      sex: sexToCode(f.sex), color, dob, country:'United States',
      regNum: tv(f.reg), regBody:'AKC', breederId,
      sireId: idByPath[childPath(path,'sire')] || '', damId: idByPath[childPath(path,'dam')] || '',
      notes: note, healthNotes: directOfa || ofaScore, dnaNum: directDna || dnaFromMarks,
      originalKennel: tv(ed[path ? `${path}_original_kennel` : 'original_kennel']),
      recordedById: breederId,
      recordOwnerId: (path === '' ? ed._own_focus !== false : !!ed[`_own_${path}`]) ? breederId : null,
      recordType:    (path === '' ? ed._own_focus !== false : !!ed[`_own_${path}`]) ? 'owned' : 'referenced',
    };
    return { ...SB.dogToDB(appDog), source: path === '' ? 'ocr_import' : 'reference' };
  });
  return { rows };
}

function applyCollisionDecisions(rows, decisions, existingMap, breederId) {
  const idRemap = {};
  const claimUpdates = [];
  rows.forEach(row => {
    const reg = (row.reg_num||'').toLowerCase();
    const ex = existingMap[reg];
    if (!ex) return;
    const dec = decisions[reg] || 'link';
    if (dec === 'link' || dec === 'claim') {
      idRemap[row.id] = ex.id;
      if (dec === 'claim') claimUpdates.push({id: ex.id});
    }
  });
  let finalRows = rows.filter(row => !idRemap[row.id]);
  finalRows = finalRows.map(row => ({
    ...row,
    sire_id: idRemap[row.sire_id] || row.sire_id || null,
    dam_id:  idRemap[row.dam_id]  || row.dam_id  || null,
  }));
  return { finalRows, claimUpdates };
}

function CollisionResolutionModal({collisions, existingMap, breeders, onResolve, onCancel}) {
  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries(collisions.map(r => [(r.reg_num||'').toLowerCase(), 'link']))
  );
  const OPTIONS = [
    {key:'link',  label:'Link to existing',       desc:'Use the existing registry record for pedigree connections. No duplicate created, no data modified.'},
    {key:'claim', label:'I own this now',          desc:'Link to the existing record and claim ownership. You become the record owner.'},
    {key:'new',   label:'This is a different dog', desc:'Create a new record with the same reg #. Only use if these are genuinely different dogs.'},
  ];
  return (
    <div onClick={onCancel} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,width:"100%",maxWidth:560,maxHeight:"85vh",overflowY:"auto",padding:28}}>
        <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:6}}>Registry Conflict{collisions.length>1?'s':''} Detected</div>
        <p style={{color:C.slate,fontSize:13,lineHeight:1.6,marginBottom:20}}>
          {collisions.length} dog{collisions.length>1?'s':''} on this paper already exist{collisions.length===1?'s':''} in the registry. Choose how to handle each:
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {collisions.map(row => {
            const reg = (row.reg_num||'').toLowerCase();
            const ex = existingMap[reg];
            const exOwner = breeders[ex.record_owner_id] || breeders[ex.breeder_id];
            const chosen = decisions[reg];
            return (
              <div key={reg} style={{border:`1px solid ${C.border}`,borderRadius:8,padding:14,background:C.bg4}}>
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:12,color:C.slate,marginBottom:2}}>Your paper</div>
                  <div style={{fontSize:14,color:C.cream,fontWeight:600}}>{row.name} <span style={{color:C.slate,fontWeight:400,fontSize:12}}>({row.reg_num})</span></div>
                </div>
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:12,color:C.slate,marginBottom:2}}>Existing registry record</div>
                  <div style={{fontSize:13,color:C.creamD}}>{ex.name} {exOwner?<span style={{color:C.gold}}>— {exOwner.kennel}</span>:<span style={{color:C.slate}}>(unclaimed)</span>}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {OPTIONS.map(opt => (
                    <label key={opt.key} style={{display:"flex",flexDirection:"column",gap:4,cursor:"pointer",padding:"8px 10px",borderRadius:6,border:`1px solid ${chosen===opt.key?C.gold:C.border}`,background:chosen===opt.key?"rgba(180,140,60,0.08)":"transparent"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <input type="radio" name={reg} checked={chosen===opt.key} onChange={()=>setDecisions(p=>({...p,[reg]:opt.key}))} style={{accentColor:C.gold}}/>
                        <span style={{fontSize:13,color:chosen===opt.key?C.cream:C.creamD,fontWeight:chosen===opt.key?600:400}}>{opt.label}</span>
                      </div>
                      {chosen===opt.key && <div style={{fontSize:11,color:C.slate,paddingLeft:22,lineHeight:1.5}}>{opt.desc}</div>}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:24}}>
          <button className="bgs" onClick={onCancel}>Cancel save</button>
          <button className="bgl" onClick={()=>onResolve(decisions)}>Confirm & save</button>
        </div>
      </div>
    </div>
  );
}

// ── DOG DETAIL MODAL ─────────────────────────────────────────────────────────
// Read-only summary reached by clicking a dog's name/photo in My Dogs — reuses
// UprRegistrationModal's exact overlay pattern rather than inventing new modal
// chrome. Keeps the breeder in their dashboard flow instead of navigating away
// to the public pedigree view just to see their own dog's details.
function DogDetailModal({dog, allDogs, uprReg, healthRequirements, breeds, designerBreeds, customCrosses, breeders, myBreeder, supabase, user, canTransfer, readOnly, contracts, flash, onClose, onEdit, onBackOffice, onRegister, onViewPedigree}){
  const sire = dog.sireId ? allDogs[dog.sireId] : null;
  const dam  = dog.damId  ? allDogs[dog.damId]  : null;
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const submitTransfer = async () => {
    const email = transferEmail.trim();
    if (!email) { flash('Enter the recipient\'s email'); return; }
    setTransferSubmitting(true);
    const { error } = await initiateDogTransfer(supabase, {dogId: dog.id, toEmail: email, memberId: user.id});
    setTransferSubmitting(false);
    if (error) { flash('⚠ ' + error.message); return; }
    flash(`Transfer invite sent to ${email}`);
    setTransferOpen(false);
    setTransferEmail('');
  };

  // Dog Return — only meaningful once a dog has actually left the recording
  // breeder's hands (currentOwnerMemberId set). Either the current buyer or
  // the record-owner breeder can request it back; the OTHER party then
  // confirms via the Transfers tab (see TransfersPanel), not here — this
  // modal only ever initiates.
  const amIBuyer = dog.currentOwnerMemberId === user?.id;
  const canReturn = !!dog.currentOwnerMemberId && (amIBuyer || dog.recordOwnerId === myBreeder?.id);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [pendingReturn, setPendingReturn] = useState(undefined); // undefined=loading, null=none, object=exists
  const [buyerDisplayName, setBuyerDisplayName] = useState('');
  useEffect(() => {
    if (!canReturn) { setPendingReturn(null); return; }
    let cancelled = false;
    const load = async () => {
      const [pendingRes, buyerRes] = await Promise.all([
        supabase.from('dog_returns').select('id').eq('dog_id', dog.id).eq('status', 'pending').maybeSingle(),
        (!amIBuyer && dog.currentOwnerMemberId)
          ? supabase.from('member_display_names').select('display_name').eq('id', dog.currentOwnerMemberId).maybeSingle()
          : Promise.resolve({data:null}),
      ]);
      if (cancelled) return;
      setPendingReturn(pendingRes.data || null);
      setBuyerDisplayName(buyerRes.data?.display_name || 'the current owner');
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dog.id, canReturn, amIBuyer]);

  const returnCounterpartyLabel = amIBuyer ? (breeders?.[dog.recordOwnerId]?.kennel || 'its breeder') : buyerDisplayName;
  const submitReturn = async () => {
    setReturnSubmitting(true);
    const { error } = await initiateDogReturn(supabase, {dogId: dog.id, memberId: user.id});
    setReturnSubmitting(false);
    if (error) { flash('⚠ ' + error.message); return; }
    flash(`Return request sent to ${returnCounterpartyLabel}`);
    setReturnOpen(false);
    setPendingReturn({});
  };

  // Live, not cached — same convention as the certificate seal and directory
  // badge elsewhere in the app, so this can never show a stale verified status.
  const [healthVerified, setHealthVerified] = useState(false);
  useEffect(() => {
    let cancelled = false;
    isHealthVerified(dog, healthRequirements||[], breeds||[], designerBreeds||[], supabase, customCrosses)
      .then(v => { if (!cancelled) setHealthVerified(v); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dog.id]);

  const Row = ({l,v}) => !v ? null : (
    <div>
      <div style={{fontSize:9,color:C.slate,textTransform:"uppercase",letterSpacing:0.5}}>{l}</div>
      <div style={{fontSize:13,color:C.cream}}>{v}</div>
    </div>
  );

  const hipsDetail = dog.ofaHips
    ? `${dog.ofaHips}${dog.ofaHipsRegistry==='PennHIP'?` (PennHIP${dog.ofaHipsDi?` DI ${dog.ofaHipsDi}`:''})`:''}${dog.ofaHipsCert?` · #${dog.ofaHipsCert}`:''}`
    : null;
  const elbowsDetail = dog.ofaElbows ? `${dog.ofaElbows}${dog.ofaElbowsCert?` · #${dog.ofaElbowsCert}`:''}` : null;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:C.bg2,border:`1px solid ${C.gold}44`,borderRadius:12,padding:28,maxWidth:560,width:'100%',maxHeight:'88vh',overflowY:'auto'}}>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20,gap:12}}>
          <div style={{display:'flex',gap:14,alignItems:'center'}}>
            <div style={{width:56,height:56,borderRadius:8,border:`2px solid ${C.gold}66`,background:C.bg4,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,overflow:'hidden'}}>
              {dog.photoUrl?<img src={dog.photoUrl} alt={dog.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>:'🐾'}
            </div>
            <div>
              <div style={{fontFamily:'serif',fontSize:19,fontWeight:700,color:C.cream}}>{dog.name}{dog.callName?` "${dog.callName}"`:''}</div>
              <div style={{fontSize:12,color:C.slate,marginTop:2}}>{dog.breed} · {dog.sex==='M'?'Male':'Female'}</div>
              {healthVerified&&<div style={{fontSize:10,color:'#4fa87a',fontWeight:700,marginTop:3}}>✚ Health-Verified</div>}
            </div>
          </div>
          <button className="bgs" style={{fontSize:11,flexShrink:0}} onClick={onClose}>✕ Close</button>
        </div>

        {dog.titles&&<div style={{fontSize:12,color:C.goldL,marginBottom:12}}>{dog.titles}</div>}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
          <Row l="Color / Markings" v={dog.color}/>
          <Row l="Date of Birth" v={dog.dob}/>
          <Row l="Country" v={dog.country}/>
          <Row l="Registration #" v={dog.regNum?`${dog.regBody||''} ${dog.regNum}`:null}/>
          <Row l="Sire" v={sire?.name}/>
          <Row l="Dam" v={dam?.name}/>
        </div>

        <OwnershipTrail dog={dog} supabase={supabase}/>

        {(contracts||[]).filter(c=>c.dog_id===dog.id).length>0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10,color:C.gold,letterSpacing:2,textTransform:'uppercase',marginBottom:6}}>Contracts</div>
            {contracts.filter(c=>c.dog_id===dog.id).map(c=>(
              <div key={c.id} style={{fontSize:12,color:C.creamD,marginBottom:4,display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                <span>{(c.contract_type||'').replace(/_/g,' ')} — {c.buyer_email}</span>
                <span style={{fontSize:10,color:c.status==='signed'?C.emeraldL:c.status==='declined'?C.red:C.amber,textTransform:'uppercase',letterSpacing:1}}>{c.status}</span>
              </div>
            ))}
          </div>
        )}

        {uprReg&&(
          <div style={{marginBottom:16}}>
            {uprReg.status==='approved'&&<div style={{fontSize:12,color:C.gold,fontWeight:700}}>⚜ UPR Registered — {uprReg.upr_number}</div>}
            {uprReg.status==='pending'&&<div style={{fontSize:12,color:C.slate}}>UPR registration pending…</div>}
            {uprReg.status==='rejected'&&<div style={{fontSize:12,color:'#e67e22'}}>UPR registration rejected</div>}
          </div>
        )}

        <div style={{fontSize:10,color:C.gold,letterSpacing:2,textTransform:'uppercase',marginBottom:8}}>Health</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
          <Row l="OFA Hips" v={hipsDetail}/>
          <Row l="OFA Elbows" v={elbowsDetail}/>
          <Row l="OFA Eyes" v={dog.ofaEyes}/>
          <Row l="MDR1" v={dog.mdr1}/>
          <Row l="DM" v={dog.dm}/>
          <Row l="HSF4" v={dog.hsf4}/>
          <Row l="CEA" v={dog.cea}/>
          <Row l="vWD" v={dog.vwd}/>
          <Row l="CHIC #" v={dog.chicNum}/>
          <Row l="BAER" v={dog.baer}/>
        </div>
        {dog.healthNotes&&<div style={{fontSize:12,color:C.creamD,lineHeight:1.6,marginBottom:16}}>{dog.healthNotes}</div>}

        {dog.notes&&<>
          <div style={{fontSize:10,color:C.gold,letterSpacing:2,textTransform:'uppercase',marginBottom:6}}>Notes</div>
          <div style={{fontSize:12,color:C.creamD,lineHeight:1.6,marginBottom:16}}>{dog.notes}</div>
        </>}

        <button className="bgs" style={{fontSize:11,marginBottom:16}} onClick={onViewPedigree}>View Full Pedigree →</button>

        <div style={{display:'flex',gap:8,flexWrap:'wrap',paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          {!readOnly&&<button className="bos" style={{fontSize:11}} onClick={onEdit}>Edit</button>}
          {!readOnly&&<button className="bgs" style={{fontSize:11,color:C.emeraldL,borderColor:C.emeraldL+'44'}} onClick={onBackOffice}>Back Office</button>}
          {(!uprReg||uprReg.status==='rejected')&&(
            <button className="bgs" style={{fontSize:11,color:C.gold,borderColor:C.gold+'44'}} onClick={onRegister}>Register with UPR</button>
          )}
          {!readOnly&&canTransfer&&(
            <button className="bgs" style={{fontSize:11,color:C.sapphireL,borderColor:C.sapphireL+'44'}} onClick={()=>setTransferOpen(o=>!o)}>
              {transferOpen?'✕ Cancel':'Transfer Ownership →'}
            </button>
          )}
          {!readOnly&&canReturn&&pendingReturn===null&&(
            <button className="bgs" style={{fontSize:11,color:C.amber,borderColor:C.amber+'44'}} onClick={()=>setReturnOpen(o=>!o)}>
              {returnOpen?'✕ Cancel':'Request Return →'}
            </button>
          )}
          {canReturn&&pendingReturn&&(
            <div style={{fontSize:11,color:C.slate,display:'flex',alignItems:'center'}}>Return already requested — see your Transfers tab</div>
          )}
        </div>
        {returnOpen&&(
          <div style={{marginTop:10,padding:12,background:C.bg3,borderRadius:8,display:'flex',flexDirection:'column',gap:8}}>
            <div style={{fontSize:11,color:C.slate}}>{amIBuyer
              ? `Request that ${dog.name} be returned to ${returnCounterpartyLabel}. They will need to confirm before ownership changes.`
              : `Request that ${returnCounterpartyLabel} return ${dog.name} to you. They will need to confirm before ownership changes.`}</div>
            <button className="bgl" style={{fontSize:12}} disabled={returnSubmitting} onClick={submitReturn}>{returnSubmitting?'Sending…':'Send Return Request'}</button>
          </div>
        )}
        {transferOpen&&(
          <div style={{marginTop:10,padding:12,background:C.bg3,borderRadius:8,display:'flex',flexDirection:'column',gap:8}}>
            <div style={{fontSize:11,color:C.slate}}>Enter the recipient's email — they'll get a link to accept ownership of {dog.name}. If they don't have a UPR account yet, they'll be prompted to create one free.</div>
            <input className="fi" placeholder="recipient@email.com" type="email" value={transferEmail} onChange={e=>setTransferEmail(e.target.value)}/>
            <button className="bgl" style={{fontSize:12}} disabled={transferSubmitting} onClick={submitTransfer}>{transferSubmitting?'Sending…':'Send Transfer Invite'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── UPR REGISTRATION MODAL ───────────────────────────────────────────────────
function UprRegistrationModal({ dog, allDogs, myBreeder, member, supabase, flash, onClose, onSuccess, onEditDog, onGoToImport }) {
  const [docFile, setDocFile]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr]           = useState('');
  const [result, setResult]     = useState(null);

  const get = id => id ? allDogs[id] : null;
  const sire = get(dog.sireId);
  const dam  = get(dog.damId);
  // dog.type is never literally 'designer' in this app — the real value is
  // 'heritage'. Checking the wrong string meant every non-purebred dog fell
  // into the purebred branch below and was asked for a club reg# on the dog
  // itself, which no mixed-breed dog would ever have — a dead end for every
  // designer (and custom-cross) registration attempt until this fix.
  const isDesigner = dog.type === 'heritage';

  // ── Pedigree check ──────────────────────────────────────────────────────────
  const pgMissing = [];
  if (!sire) pgMissing.push('Sire');
  if (!dam)  pgMissing.push('Dam');
  if (sire && !get(sire.sireId)) pgMissing.push(`${sire.name||'Sire'}'s Sire`);
  if (sire && !get(sire.damId))  pgMissing.push(`${sire.name||'Sire'}'s Dam`);
  if (dam  && !get(dam.sireId))  pgMissing.push(`${dam.name||'Dam'}'s Sire`);
  if (dam  && !get(dam.damId))   pgMissing.push(`${dam.name||'Dam'}'s Dam`);
  const pedigreeOk = pgMissing.length === 0;

  // ── Registration number check ───────────────────────────────────────────────
  // This client-side check only knows about club reg#s (sire.regNum/dam.regNum)
  // — it can't see whether a parent instead qualifies via its own prior UPR
  // approval, since that status isn't loaded for dogs owned by other breeders.
  // The server (api/upr-register.js) is the authoritative check and accepts
  // either path. So for heritage dogs, this checklist item is advisory, not a
  // hard gate — allOk below doesn't require clubOk for heritage, only for
  // purebred (where dog.regNum is the only real qualifying signal).
  let clubOk = false; let clubIssue = '';
  if (!isDesigner) {
    clubOk    = !!(dog.regNum);
    clubIssue = clubOk ? '' : 'No club registration number on this dog — edit the dog record to add it.';
  } else {
    const sireOk = !!(sire?.regNum);
    const damOk  = !!(dam?.regNum);
    clubOk = sireOk && damOk;
    if (!sireOk) clubIssue += (sire ? `${sire.name||'Sire'} needs a registration number. ` : 'Sire must be linked. ');
    if (!damOk)  clubIssue += (dam  ? `${dam.name||'Dam'} needs a registration number.`     : 'Dam must be linked.');
  }

  const isAdminMember = member?.role === 'admin';
  const credits  = member?.registration_credits || 0;
  const creditOk = isAdminMember || credits >= 1;
  const allOk    = pedigreeOk && creditOk && (isDesigner ? true : clubOk);

  async function handleSubmit() {
    if (!docFile) { setErr('Please select a document to upload.'); return; }
    setUploading(true); setErr('');
    try {
      const ext     = (docFile.name.split('.').pop()||'jpg').toLowerCase();
      const docPath = `${myBreeder.id}/${dog.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('club-documents').upload(docPath, docFile);
      if (upErr) throw new Error('Document upload failed: ' + upErr.message);

      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/upr-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          dogId:      dog.id,
          docPath,
          clubName:   isDesigner ? null : (dog.regBody || null),
          clubRegNum: isDesigner ? null : (dog.regNum  || null),
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Registration failed');
      setResult(json);
      onSuccess(json);
    } catch(e) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  }

  const Req = ({ ok, label, children }) => (
    <div style={{background:C.bg3,borderRadius:8,padding:12,border:`1px solid ${ok?C.emeraldL+'44':'#c0392b44'}`}}>
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:ok?0:8}}>
        <span style={{color:ok?C.emeraldL:'#e74c3c',fontSize:14,fontWeight:700}}>{ok?'✓':'✗'}</span>
        <span style={{fontSize:13,fontWeight:600,color:C.cream}}>{label}</span>
      </div>
      {!ok && <div style={{paddingLeft:22}}>{children}</div>}
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:C.bg2,border:`1px solid ${C.gold}44`,borderRadius:12,padding:28,maxWidth:520,width:'100%',maxHeight:'88vh',overflowY:'auto'}}>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
          <div>
            <div style={{fontFamily:'serif',fontSize:20,fontWeight:700,color:C.cream}}>Register with UPR</div>
            <div style={{fontSize:12,color:C.slate,marginTop:2}}>{dog.name} · {dog.breed}</div>
          </div>
          <button className="bgs" style={{fontSize:11,flexShrink:0}} onClick={onClose}>✕ Close</button>
        </div>

        {!result ? (
          <>
            <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:20}}>

              <Req ok={pedigreeOk} label="3 complete generations in registry">
                {pgMissing.map(m=><div key={m} style={{fontSize:11,color:'#aaa',marginBottom:2}}>Missing: {m}</div>)}
                <button className="bgs" style={{fontSize:10,marginTop:6,color:C.gold}} onClick={onGoToImport}>Add ancestors via Bulk Import →</button>
              </Req>

              <Req ok={clubOk} label={isDesigner ? 'Both parents registered with a kennel club' : 'Club registration number'}>
                <div style={{fontSize:11,color:'#aaa',marginBottom:6}}>{clubIssue}</div>
                {isDesigner && (
                  <div style={{fontSize:11,color:C.slate,marginBottom:6}}>
                    A parent that's already UPR-registered qualifies too, even without a club number — that's checked when you submit, so it's fine to continue.
                  </div>
                )}
                {!isDesigner && (
                  <button className="bgs" style={{fontSize:10,color:C.gold}} onClick={()=>onEditDog(dog)}>Edit Dog →</button>
                )}
                {isDesigner && sire && !sire.regNum && (
                  <button className="bgs" style={{fontSize:10,color:C.gold,marginRight:6}} onClick={()=>onEditDog(sire)}>Edit {sire.name||'Sire'} →</button>
                )}
                {isDesigner && dam && !dam.regNum && (
                  <button className="bgs" style={{fontSize:10,color:C.gold}} onClick={()=>onEditDog(dam)}>Edit {dam.name||'Dam'} →</button>
                )}
              </Req>

              <Req ok={creditOk} label={isAdminMember ? 'Registration credit (Admin — no credit required)' : `Registration credit (${credits} available)`}>
                <div style={{fontSize:11,color:'#aaa',marginBottom:8}}>Credit is only spent when your application is approved — never on rejection.</div>
                <a href={STRIPE.single_reg} target="_blank" rel="noreferrer"
                  style={{fontSize:11,color:C.gold,textDecoration:'none',border:`1px solid ${C.gold}44`,borderRadius:4,padding:'4px 10px',display:'inline-block'}}>
                  Purchase UPR Registration — $35 →
                </a>
              </Req>
            </div>

            {allOk && (
              <>
                <div style={{fontSize:12,color:C.slate,marginBottom:10}}>
                  {isDesigner
                    ? "Upload both parent dogs' registration certificates. Combine into one file if needed."
                    : 'Upload your club registration certificate (AKC, ACA, ASCA, CKC, etc.).'}
                </div>
                <input type="file" accept="image/*,.pdf"
                  onChange={e => setDocFile(e.target.files[0]||null)}
                  style={{display:'block',fontSize:12,color:C.cream,marginBottom:8,width:'100%'}}/>
                {docFile && <div style={{fontSize:11,color:C.emeraldL,marginBottom:10}}>✓ {docFile.name}</div>}
                {err && <div style={{fontSize:12,color:'#e74c3c',marginBottom:10}}>{err}</div>}
                <button className="bgl" disabled={!docFile||uploading} onClick={handleSubmit}
                  style={{width:'100%',opacity:(!docFile||uploading)?0.5:1}}>
                  {uploading ? 'Submitting…' : (myBreeder.trustedBreeder||isAdminMember) ? 'Submit & Register Instantly' : 'Submit Application'}
                </button>
                <div style={{fontSize:10,color:C.slate,marginTop:8,textAlign:'center'}}>
                  {(myBreeder.trustedBreeder||isAdminMember)
                    ? 'Your UPR number will be issued immediately.'
                    : 'Typically reviewed within 48 hours. Credit is not charged until approval.'}
                </div>
              </>
            )}
          </>
        ) : (
          <div style={{textAlign:'center',padding:'12px 0'}}>
            {result.status === 'approved' ? (
              <>
                <div style={{fontSize:40,marginBottom:12}}>✓</div>
                <div style={{fontFamily:'serif',fontSize:24,fontWeight:700,color:C.gold,marginBottom:6}}>{result.upr_number}</div>
                <div style={{fontSize:14,color:C.cream,marginBottom:4,fontWeight:600}}>Registration Complete</div>
                <div style={{fontSize:12,color:C.slate}}>{dog.name} is now officially registered with the United Pedigree Registry.</div>
              </>
            ) : (
              <>
                <div style={{fontSize:40,marginBottom:12}}>📋</div>
                <div style={{fontFamily:'serif',fontSize:18,fontWeight:700,color:C.cream,marginBottom:6}}>Application Submitted</div>
                <div style={{fontSize:12,color:C.slate,lineHeight:1.6}}>
                  <strong>{dog.name}</strong> is pending review. Your credit is reserved and will only be charged on approval.
                </div>
              </>
            )}
            <button className="bgl" style={{marginTop:20,minWidth:120}} onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function PendingEditsQueue({myDogs, breeders, supabase, flash, onCountChange, isAdmin, allDogs}) {
  const [edits, setEdits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const dogMap = isAdmin ? (allDogs||{}) : Object.fromEntries(myDogs.map(d=>[d.id,d]));

  useEffect(() => {
    if (!isAdmin && !myDogs.length) { setLoading(false); return; }
    const q = supabase.from('pending_edits').select('*').order('created_at', {ascending:false});
    if (!isAdmin) q.in('dog_id', myDogs.map(d=>d.id));
    q.then(({data}) => { setEdits(data||[]); setLoading(false); });
  }, [isAdmin]);

  const pending  = edits.filter(e=>e.status==='pending');
  const resolved = edits.filter(e=>e.status!=='pending');

  const approve = async (edit) => {
    const {error:de} = await supabase.from('dogs')
      .update({[FIELD_MAP[edit.field_name]]: edit.new_value}).eq('id', edit.dog_id);
    if (de) { flash('⚠ '+de.message); return; }
    await supabase.from('pending_edits')
      .update({status:'approved', resolved_at: new Date().toISOString()}).eq('id', edit.id);
    setEdits(prev=>prev.map(e=>e.id===edit.id?{...e,status:'approved'}:e));
    onCountChange(c=>Math.max(0,c-1));
    flash('Change approved ✓');
  };

  const reject = async (edit) => {
    await supabase.from('pending_edits')
      .update({status:'rejected', resolved_at: new Date().toISOString()}).eq('id', edit.id);
    setEdits(prev=>prev.map(e=>e.id===edit.id?{...e,status:'rejected'}:e));
    onCountChange(c=>Math.max(0,c-1));
    flash('Suggestion rejected');
  };

  const renderEdit = (edit, isHistory) => {
    const dog = dogMap[edit.dog_id];
    const suggester = Object.values(breeders).find(b=>b.id===edit.suggested_by_id);
    const ownerBreeder = isAdmin ? Object.values(breeders).find(b=>b.id===dog?.recordOwnerId) : null;
    return (
      <div key={edit.id} style={{background:C.bg4,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:C.slate,marginBottom:2}}>{dog?.name||'Unknown dog'}</div>
            {isAdmin&&<div style={{fontSize:11,color:C.sapphireL,marginBottom:4}}>Dog owned by: {ownerBreeder?.kennel||ownerBreeder?.name||'Unclaimed'}</div>}
            <div style={{fontSize:13,color:C.cream,fontWeight:600}}>{edit.field_name}</div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6,fontSize:12}}>
              <span style={{color:C.slate,textDecoration:'line-through'}}>{edit.old_value||'(blank)'}</span>
              <span style={{color:C.slate}}>→</span>
              <span style={{color:C.gold,fontWeight:600}}>{edit.new_value}</span>
            </div>
            <div style={{fontSize:11,color:C.slate,marginTop:6}}>
              {suggester?.kennel||'Unknown'} · {new Date(edit.created_at).toLocaleDateString()}
              {isHistory&&<span style={{marginLeft:8,color:edit.status==='approved'?C.emeraldL:C.red,fontWeight:600}}>{edit.status}</span>}
            </div>
          </div>
          {!isHistory&&(
            <div style={{display:'flex',gap:8,flexShrink:0}}>
              <button className="bgl" style={{fontSize:11,padding:'4px 12px'}} onClick={()=>approve(edit)}>Approve</button>
              <button className="bgs" style={{fontSize:11,padding:'4px 12px'}} onClick={()=>reject(edit)}>Reject</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <div style={{padding:40,textAlign:'center',color:C.slate,fontSize:13}}>Loading…</div>;

  return (
    <div>
      <div style={{marginBottom:16}}>
        <div style={{fontFamily:'serif',fontSize:20,fontWeight:700,marginBottom:4}}>{isAdmin?'All Pending Suggestions':'Pending Suggestions'}</div>
        <div style={{fontSize:13,color:C.slate}}>{pending.length} {isAdmin?'platform-wide awaiting review':'awaiting your review'}</div>
      </div>
      {pending.length ? (
        <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:24}}>
          {pending.map(e=>renderEdit(e,false))}
        </div>
      ) : (
        <div style={{padding:16,textAlign:'center',color:C.slate,fontSize:13,marginBottom:16}}>All caught up!</div>
      )}
      {resolved.length>0&&(
        <div>
          <button onClick={()=>setShowHistory(s=>!s)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:12,padding:0,marginBottom:10}}>
            {showHistory?'▾':'▸'} History ({resolved.length})
          </button>
          {showHistory&&(
            <div style={{display:'flex',flexDirection:'column',gap:8,opacity:0.7}}>
              {resolved.map(e=>renderEdit(e,true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── TRANSFERS PANEL ───────────────────────────────────────────────────────────
// Two independent lists, both scoped to the logged-in user: invites they sent
// (initiated_by_member_id = user.id) and invites addressed to them
// (to_email = user.email, the same RLS rule AcceptTransferView's tryLoad
// already relies on). "Respond" reuses that existing, hardened accept/decline
// flow via onRespond rather than duplicating it here. Sender names on the
// received side come from member_display_names — same no-raw-email-to-client
// view built for OwnershipTrail, reused as-is.
function TransfersPanel({user, supabase, data, setData, previouslyOwnedDogs, flash, onRespond, onCountChange, onViewDog}){
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [senderNames, setSenderNames] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [sentRes, receivedRes] = await Promise.all([
        supabase.from('dog_transfers').select('id,dog_id,to_email,created_at')
          .eq('initiated_by_member_id', user.id).eq('status','pending')
          .order('created_at',{ascending:false}),
        supabase.from('dog_transfers').select('id,dog_id,token,initiated_by_member_id,created_at')
          .eq('to_email', user.email.toLowerCase()).eq('status','pending')
          .order('created_at',{ascending:false}),
      ]);
      if (cancelled) return;
      const sentRows = sentRes.data || [];
      const receivedRows = receivedRes.data || [];
      setSent(sentRows);
      setReceived(receivedRows);
      const senderIds = [...new Set(receivedRows.map(r=>r.initiated_by_member_id).filter(Boolean))];
      if (senderIds.length) {
        const { data: nameRows } = await supabase.from('member_display_names').select('id,display_name').in('id', senderIds);
        if (!cancelled) setSenderNames(Object.fromEntries((nameRows||[]).map(n=>[n.id,n.display_name])));
      }
      setLoaded(true);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email]);

  const cancelTransfer = async (id) => {
    if (!window.confirm('Cancel this pending transfer invite?')) return;
    const { error } = await supabase.from('dog_transfers').update({status:'cancelled'}).eq('id', id);
    if (error) { flash('⚠ ' + error.message); return; }
    setSent(prev => prev.filter(t=>t.id!==id));
    flash('Transfer invite cancelled');
  };

  const resendTransfer = async (id) => {
    try {
      const resp = await fetch('/api/notify-transfer-invite', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({transferId:id})});
      const json = await resp.json().catch(()=>({}));
      if (!resp.ok || json.sent===false) { flash('⚠ ' + (json.error||json.reason||'Could not resend the invite')); return; }
      flash('Invite resent ✓');
    } catch(e) { flash('⚠ ' + (e.message||'Could not resend the invite')); }
  };

  // Dog Return — unlike dog_transfers, the SELECT RLS on dog_returns already
  // resolves "either party" itself (checking dogs.current_owner_member_id /
  // record_owner_id against auth.uid()), so one unfiltered pending-status
  // query returns every row I'm a party to in either direction; sent vs.
  // received is then just a client-side split on initiated_by === my id.
  const [returnsSent, setReturnsSent] = useState([]);
  const [returnsReceived, setReturnsReceived] = useState([]);
  const [buyerNames, setBuyerNames] = useState({});
  const [returnsLoaded, setReturnsLoaded] = useState(false);
  const [respondingReturnId, setRespondingReturnId] = useState(null);
  const [confirmReturn, setConfirmReturn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data: rows } = await supabase.from('dog_returns')
        .select('id,dog_id,initiated_by,created_at')
        .eq('status', 'pending')
        .order('created_at', {ascending:false});
      if (cancelled) return;
      const all = rows || [];
      setReturnsSent(all.filter(r => r.initiated_by === user.id));
      setReturnsReceived(all.filter(r => r.initiated_by !== user.id));
      // Buyer-side display names — the breeder side resolves for free via
      // the already-loaded data.breeders map, so only this side needs a fetch.
      const buyerIds = [...new Set(all.map(r => data.dogs[r.dog_id]?.currentOwnerMemberId).filter(Boolean))];
      if (buyerIds.length) {
        const { data: nameRows } = await supabase.from('member_display_names').select('id,display_name').in('id', buyerIds);
        if (!cancelled) setBuyerNames(Object.fromEntries((nameRows||[]).map(n=>[n.id,n.display_name])));
      }
      setReturnsLoaded(true);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (onCountChange) onCountChange(sent.length + received.length + returnsSent.length + returnsReceived.length);
  }, [sent.length, received.length, returnsSent.length, returnsReceived.length, onCountChange]);

  // Resolves both the initiator's and the counterparty's display label for a
  // return row — whichever side matches dogs.current_owner_member_id is the
  // buyer (name via member_display_names), the other is the record-owner
  // breeder (kennel via the already-loaded breeders map).
  const returnParties = (r) => {
    const dog = data.dogs[r.dog_id];
    const buyerLabel = buyerNames[dog?.currentOwnerMemberId] || 'the current owner';
    const breederLabel = data.breeders?.[dog?.recordOwnerId]?.kennel || 'the breeder';
    const initiatorIsBuyer = !!dog && r.initiated_by === dog.currentOwnerMemberId;
    return {
      dogName: dog?.name || 'A dog',
      breederLabel,
      initiatorLabel: initiatorIsBuyer ? buyerLabel : breederLabel,
      counterpartyLabel: initiatorIsBuyer ? breederLabel : buyerLabel,
    };
  };

  const acceptReturn = async (id) => {
    setRespondingReturnId(id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { flash('⚠ Your session expired — please sign in again and retry.'); return; }
      const resp = await fetch('/api/dog-return-respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ returnId: id, action: 'accept' }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) { flash('⚠ ' + (json.error || `Request failed (${resp.status})`)); return; }
      // Server already cleared dogs.current_owner_member_id back to null —
      // patch shared state now so the breeder's My Dogs reflects it
      // immediately, no page reload required.
      const returnedRow = returnsReceived.find(r=>r.id===id);
      if (returnedRow) {
        setData(prev => prev.dogs[returnedRow.dog_id] ? {...prev, dogs:{...prev.dogs, [returnedRow.dog_id]:{...prev.dogs[returnedRow.dog_id], currentOwnerMemberId:null}}} : prev);
      }
      setReturnsReceived(prev => prev.filter(r=>r.id!==id));
      flash(`${json.dogName || 'The dog'} returned ✓`);
    } catch (e) {
      flash('⚠ ' + (e.message || 'Something went wrong — please try again.'));
    } finally {
      setRespondingReturnId(null);
    }
  };

  const declineReturn = async (id) => {
    if (!window.confirm('Decline this return request?')) return;
    const { error } = await supabase.from('dog_returns').update({status:'declined'}).eq('id', id);
    if (error) { flash('⚠ ' + error.message); return; }
    setReturnsReceived(prev => prev.filter(r=>r.id!==id));
    flash('Return request declined');
  };

  const cancelReturn = async (id) => {
    if (!window.confirm('Cancel this return request?')) return;
    const { error } = await supabase.from('dog_returns').update({status:'cancelled'}).eq('id', id);
    if (error) { flash('⚠ ' + error.message); return; }
    setReturnsSent(prev => prev.filter(r=>r.id!==id));
    flash('Return request cancelled');
  };

  if (!loaded || !returnsLoaded) return <div style={{fontSize:12,color:C.slate}}>Loading…</div>;
  if (!sent.length && !received.length && !returnsSent.length && !returnsReceived.length && !(previouslyOwnedDogs||[]).length) return <div style={{fontSize:12,color:C.slate}}>No pending ownership transfers or return requests.</div>;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:24}}>
      {received.length>0&&(
        <div>
          <div style={{fontSize:10,color:C.gold,letterSpacing:2,textTransform:'uppercase',marginBottom:10}}>Waiting for Your Response</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {received.map(t => {
              const dog = data.dogs[t.dog_id];
              return (
                <div key={t.id} style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:8,padding:12,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                  <div>
                    <div style={{fontSize:13,color:C.cream}}>{dog?.name || 'A dog'}</div>
                    <div style={{fontSize:11,color:C.slate}}>from {senderNames[t.initiated_by_member_id]||'a UPR member'} · {new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                  <button className="bgl" style={{fontSize:11}} onClick={()=>onRespond(t.token, user.email)}>Respond →</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {sent.length>0&&(
        <div>
          <div style={{fontSize:10,color:C.gold,letterSpacing:2,textTransform:'uppercase',marginBottom:10}}>Sent by You</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {sent.map(t => {
              const dog = data.dogs[t.dog_id];
              return (
                <div key={t.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:12,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                  <div>
                    <div style={{fontSize:13,color:C.cream}}>{dog?.name || 'A dog'} → {t.to_email}</div>
                    <div style={{fontSize:11,color:C.slate}}>Sent {new Date(t.created_at).toLocaleDateString()} · Pending</div>
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="bos" style={{fontSize:11}} onClick={()=>resendTransfer(t.id)}>Resend</button>
                    <button className="bgs" style={{fontSize:11,color:'#e74c3c',borderColor:'#e74c3c44'}} onClick={()=>cancelTransfer(t.id)}>Cancel</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {returnsReceived.length>0&&(
        <div>
          <div style={{fontSize:10,color:C.amber,letterSpacing:2,textTransform:'uppercase',marginBottom:10}}>Return Requests Waiting for Your Response</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {returnsReceived.map(r => {
              const {dogName, initiatorLabel} = returnParties(r);
              return (
                <div key={r.id} style={{background:C.card,border:`1px solid ${C.amber}44`,borderRadius:8,padding:12,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                  <div>
                    <div style={{fontSize:13,color:C.cream}}>{dogName}</div>
                    <div style={{fontSize:11,color:C.slate}}>from {initiatorLabel} · {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="bgs" style={{fontSize:11}} disabled={respondingReturnId===r.id} onClick={()=>declineReturn(r.id)}>Decline</button>
                    <button className="bgl" style={{fontSize:11}} disabled={respondingReturnId===r.id} onClick={()=>setConfirmReturn(r)}>{respondingReturnId===r.id?'Please wait…':'Accept →'}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {returnsSent.length>0&&(
        <div>
          <div style={{fontSize:10,color:C.amber,letterSpacing:2,textTransform:'uppercase',marginBottom:10}}>Return Requests Sent by You</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {returnsSent.map(r => {
              const {dogName, counterpartyLabel} = returnParties(r);
              return (
                <div key={r.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:12,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                  <div>
                    <div style={{fontSize:13,color:C.cream}}>{dogName} → {counterpartyLabel}</div>
                    <div style={{fontSize:11,color:C.slate}}>Sent {new Date(r.created_at).toLocaleDateString()} · Pending</div>
                  </div>
                  <button className="bgs" style={{fontSize:11,color:'#e74c3c',borderColor:'#e74c3c44'}} onClick={()=>cancelReturn(r.id)}>Cancel</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {(previouslyOwnedDogs||[]).length>0&&(
        <div>
          <div style={{fontSize:10,color:C.amber,letterSpacing:2,textTransform:'uppercase',marginBottom:10}}>Previously Owned (Transferred Away)</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {previouslyOwnedDogs.map(d=>(
              <div key={d.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:12,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",opacity:0.7}}>
                <div onClick={()=>onViewDog(d)} style={{width:40,height:40,borderRadius:7,border:`2px solid ${C.border}`,background:C.bg4,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,overflow:"hidden",cursor:"pointer"}}>
                  {d.photoUrl?<img src={d.photoUrl} alt={d.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:"🐾"}
                </div>
                <div style={{flex:1,minWidth:160}}>
                  <div onClick={()=>onViewDog(d)} style={{fontSize:13,fontWeight:600,cursor:"pointer",display:"inline-block",color:C.cream}}>{d.name||"Unnamed"}</div>
                  <div style={{fontSize:11,color:C.slate}}>{d.breed} · {d.sex==="M"?"Male":"Female"} · {d.color}</div>
                </div>
                <button className="bos" style={{fontSize:11}} onClick={()=>onViewDog(d)}>View →</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {confirmReturn && (() => {
        const {dogName, breederLabel} = returnParties(confirmReturn);
        return (
          <div onClick={e=>{if(e.target===e.currentTarget) setConfirmReturn(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:C.bg2,border:`1px solid ${C.amber}44`,borderRadius:12,padding:24,width:"100%",maxWidth:420}}>
              <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:12,color:C.cream}}>Confirm Return</div>
              <p style={{fontSize:13,color:C.slate,lineHeight:1.6,marginBottom:20}}>
                Confirm return of <strong style={{color:C.cream}}>{dogName}</strong>? This dog will be returned to <strong style={{color:C.cream}}>{breederLabel}</strong>.
              </p>
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button className="bgs" onClick={()=>setConfirmReturn(null)}>Cancel</button>
                <button className="bgl" onClick={()=>{ const id = confirmReturn.id; setConfirmReturn(null); acceptReturn(id); }}>Accept</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── CONTRACTS ──────────────────────────────────────────────────────────────────
// Breeders upload their own contract PDF — UPR never writes or reviews contract
// language, just the send/sign/store infrastructure (Dropbox Sign, standard
// non-embedded flow — see api/contract-send.js for why). Read-only list here;
// all writes happen through contract-send.js / contract-webhook.js under
// service role, matching the deliberate no-client-write posture on `contracts`.
function ContractsPanel({myBreeder, contracts, setContracts, contractTemplates, setContractTemplates, allDogs, myLitters, supabase, flash, onSendContract, onEditTemplateFields, onViewContract}){
  const [deletingId, setDeletingId] = useState(null);
  const dogFor = (id) => id ? allDogs[id] : null;
  const litterFor = (id) => id ? myLitters.find(l=>l.id===id) : null;
  const statusColor = (s) => s==='signed'?C.emeraldL:s==='declined'?C.red:s==='voided'?C.slate:C.amber;

  const deleteContract = async (c) => {
    if (!window.confirm(`Permanently delete this contract? This can't be undone.`)) return;
    setDeletingId(c.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { flash('⚠ Your session expired — please sign in again.'); return; }
      const resp = await fetch('/api/contract-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ contractId: c.id }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) { flash('⚠ ' + (json.error || `Request failed (${resp.status})`)); return; }
      setContracts(prev => prev.filter(x=>x.id!==c.id));
      flash('Contract deleted');
    } catch (e) {
      flash('⚠ ' + (e.message || 'Something went wrong — please try again.'));
    } finally {
      setDeletingId(null);
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm('Delete this template? This won\'t affect contracts already sent from it.')) return;
    const { error: delErr } = await supabase.from('contract_templates').delete().eq('id', t.id);
    if (delErr) { flash('⚠ ' + delErr.message); return; }
    setContractTemplates(prev => prev.filter(x=>x.id!==t.id));

    // A used template's file is also every past contract's
    // original_document_url (same storage_path, template can be reused
    // across many sends) — deleting it would silently break historical
    // "view original" links for all of them, not just this cleanup.
    // template_id was set to null on those rows by the FK's ON DELETE SET
    // NULL, but the still-referenced file must survive regardless.
    const usedByContracts = contracts.some(c => c.template_id === t.id);
    if (usedByContracts) {
      flash('Template deleted — its file was kept since past contracts still reference it');
      return;
    }
    await supabase.storage.from('contracts').remove([t.storage_path]);
    flash('Template deleted');
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:13,color:C.slate}}>{contracts.length} contract{contracts.length!==1?'s':''} sent{contractTemplates.length>0?` · ${contractTemplates.length} saved template${contractTemplates.length!==1?'s':''}`:''}</div>
        <button className="bos" onClick={()=>onSendContract({})}>+ Send Contract</button>
      </div>

      {contractTemplates.length>0 && (
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,color:C.gold,letterSpacing:2,textTransform:'uppercase',marginBottom:8}}>My Contract Templates</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {contractTemplates.map(t=>(
              <div key={t.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:10,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                <div style={{fontSize:13,color:C.cream}}>
                  {t.name}
                  {(t.field_layout||[]).length>0 && <span style={{fontSize:10,color:C.emeraldL,marginLeft:8}}>{(t.field_layout||[]).length} field{(t.field_layout||[]).length!==1?'s':''} defined</span>}
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button className="bos" style={{fontSize:11}} onClick={()=>onEditTemplateFields(t)}>Edit Fields →</button>
                  <button className="bgs" style={{fontSize:11,color:C.red,borderColor:C.red+"44"}} onClick={()=>deleteTemplate(t)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {contracts.length===0 ? (
        <div style={{textAlign:"center",padding:"48px 20px",border:`1px dashed ${C.border}`,borderRadius:12,color:C.slate}}>
          <div style={{fontSize:48,marginBottom:12}}>📄</div>
          <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.cream,marginBottom:8}}>No Contracts Sent Yet</div>
          <p style={{fontSize:14,lineHeight:1.7}}>Send a puppy sale agreement, health guarantee, or stud service contract for e-signature.</p>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {contracts.map(c=>{
            const dog = dogFor(c.dog_id);
            const litter = litterFor(c.litter_id);
            return (
              <div key={c.id} onClick={()=>onViewContract(c)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:12,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,cursor:"pointer"}}>
                <div>
                  <div style={{fontSize:13,color:C.cream}}>{(c.contract_type||'').replace(/_/g,' ')} — {c.buyer_email}</div>
                  <div style={{fontSize:11,color:C.slate}}>
                    {dog?.name ? `Re: ${dog.name}` : litter ? `Re: ${litterHeadline(litter)}` : ''}{(dog?.name||litter)?' · ':''}Sent {c.sent_at?new Date(c.sent_at).toLocaleDateString():'—'}
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:10,color:statusColor(c.status),textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>{c.status}</span>
                  {['voided','declined'].includes(c.status) && (
                    <button className="bgs" style={{fontSize:11,color:C.red,borderColor:C.red+"44"}} disabled={deletingId===c.id} onClick={e=>{e.stopPropagation();deleteContract(c);}}>
                      {deletingId===c.id?'Deleting…':'Delete'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// The <input accept="application/pdf"> attribute is only a file-picker UI
// hint — it doesn't stop a user from actually selecting a non-PDF (e.g. a
// .docx, which the OS picker's "All Files" option lets through). Checking
// file.type alone is still just a browser-reported label; peeking at the
// real magic bytes (%PDF) is what actually confirms the content, not just
// what the OS claims it is.
async function isValidPdfFile(file) {
  if (file.type !== 'application/pdf') return false;
  try {
    const bytes = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    return bytes[0]===0x25 && bytes[1]===0x50 && bytes[2]===0x44 && bytes[3]===0x46; // %PDF
  } catch {
    return false;
  }
}

// Word docs get auto-converted server-side (api/convert-contract-file.js)
// rather than rejected outright — checked by extension since a browser's
// reported file.type for .doc/.docx varies by OS/browser and isn't reliable
// enough to gate on alone, unlike the %PDF magic-byte check above which
// reads real content.
function isWordDocFile(file) {
  return /\.docx?$/i.test(file.name);
}

function SendContractModal({context, breederId, contractTemplates, setContractTemplates, setContracts, supabase, flash, onClose}){
  const [templateId, setTemplateId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [needsConversion, setNeedsConversion] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState(context?.buyerEmail || '');
  const [contractType, setContractType] = useState('puppy_sale');
  const [mergeValues, setMergeValues] = useState({});
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);

  const selectedTemplate = contractTemplates.find(t=>t.id===templateId);
  const fieldLayout = selectedTemplate?.field_layout || null;
  // Merge-field names defined on the template's visual field layout, deduped —
  // one input per unique name, regardless of how many placed boxes share it.
  const mergeFieldNames = [...new Set((fieldLayout||[]).filter(f=>f.type==='text-merge').map(f=>f.name))];

  const submit = async () => {
    if (!buyerEmail.trim()) { flash('Enter the buyer\'s email'); return; }
    if (!templateId && !uploadFile) { flash('Pick a saved template or upload a PDF'); return; }
    if (mergeFieldNames.some(name => !(mergeValues[name]||'').trim())) { flash('Fill in all merge fields before sending'); return; }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { flash('⚠ Your session expired — please sign in again.'); return; }

      const contractId = crypto.randomUUID();
      let originalStoragePath = null;

      if (uploadFile) {
        // One-off upload lands under this contract's own folder; if "save as
        // template" is checked, a second copy also becomes a reusable template.
        // Path is always named ...pdf regardless of the source file's real
        // type — a Word doc uploaded here gets overwritten in place with
        // real PDF bytes by the conversion step below.
        originalStoragePath = `${breederId}/contracts/${contractId}/original.pdf`;
        // Real content-type of whatever was actually selected, not a
        // hardcoded 'application/pdf' — a docx sitting there mislabeled as
        // pdf is exactly the mismatch class of bug that broke the
        // conversion's own format auto-detection (see convert-contract-file.js).
        const { error: upErr } = await supabase.storage.from('contracts').upload(originalStoragePath, uploadFile, { contentType: uploadFile.type || 'application/octet-stream' });
        if (upErr) { flash('⚠ Upload failed: ' + upErr.message); return; }

        if (needsConversion) {
          setConverting(true);
          try {
            const convResp = await fetch('/api/convert-contract-file', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ storagePath: originalStoragePath, originalFilename: uploadFile.name }),
            });
            const convJson = await convResp.json().catch(() => ({}));
            if (!convResp.ok) {
              // Same safety net as the original PDF-only gate — a failed
              // conversion must reject clearly, never leave a broken (still
              // Word-format) file sitting at a .pdf-named path.
              flash('⚠ ' + (convJson.error || 'Could not convert this file to PDF. Please convert it manually and re-upload.'));
              return;
            }
            flash('Converted to PDF — please review the formatting before placing fields');
          } finally {
            setConverting(false);
          }
        }

        if (saveAsTemplate) {
          const newTemplateId = crypto.randomUUID();
          const templatePath = `${breederId}/templates/${newTemplateId}.pdf`;
          // Copy the (possibly just-converted) PDF already at
          // originalStoragePath rather than re-uploading uploadFile — avoids
          // converting the same source file twice.
          const { error: tErr } = await supabase.storage.from('contracts').copy(originalStoragePath, templatePath);
          if (!tErr) {
            const { data: inserted } = await supabase.from('contract_templates').insert({
              id: newTemplateId, breeder_id: breederId, name: templateName.trim() || 'Untitled template', storage_path: templatePath,
            }).select().single();
            if (inserted) setContractTemplates(prev => [inserted, ...prev]);
          }
        }
      }

      const customFields = mergeFieldNames.length ? mergeFieldNames.map(name => ({ name, value: mergeValues[name].trim() })) : null;

      const resp = await fetch('/api/contract-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          contractId,
          dogId: context?.dogId || null,
          litterId: context?.litterId || null,
          waitlistEntryId: context?.waitlistEntryId || null,
          buyerEmail: buyerEmail.trim(),
          contractType,
          templateId: templateId || null,
          originalStoragePath,
          formFieldsPerDocument: fieldLayout,
          customFields,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) { flash('⚠ ' + (json.error || `Request failed (${resp.status})`)); return; }

      setContracts(prev => [{
        id: contractId, breeder_id: breederId, dog_id: context?.dogId||null, litter_id: context?.litterId||null,
        waitlist_entry_id: context?.waitlistEntryId||null, template_id: templateId||null, contract_type: contractType,
        buyer_email: buyerEmail.trim(), status: 'sent', provider: 'dropbox_sign',
        sent_at: new Date().toISOString(), created_at: new Date().toISOString(),
      }, ...prev]);
      flash(json.testMode ? 'Contract sent (test mode — not legally binding yet) ✓' : 'Contract sent ✓');
      onClose();
    } catch (e) {
      flash('⚠ ' + (e.message || 'Something went wrong — please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget && !sending)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:"100%",maxWidth:480}}>
        <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:16}}>Send Contract for Signature</div>

        <div style={{marginBottom:12}}>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Buyer Email</label>
          <input className="fi" value={buyerEmail} onChange={e=>setBuyerEmail(e.target.value)}/>
        </div>

        <div style={{marginBottom:12}}>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Contract Type</label>
          <select className="fi" value={contractType} onChange={e=>setContractType(e.target.value)}>
            <option value="puppy_sale">Puppy Sale Agreement</option>
            <option value="health_guarantee">Health Guarantee</option>
            <option value="stud_service">Stud Service Agreement</option>
            <option value="co_ownership">Co-Ownership Agreement</option>
            <option value="other">Other</option>
          </select>
        </div>

        {contractTemplates.length>0 && (
          <div style={{marginBottom:12}}>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Use Saved Template</label>
            <select className="fi" value={templateId} onChange={e=>{setTemplateId(e.target.value); if(e.target.value) setUploadFile(null);}}>
              <option value="">— None, upload a PDF below —</option>
              {contractTemplates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        {mergeFieldNames.length>0 && (
          <div style={{marginBottom:12,padding:12,background:C.bg3,borderRadius:8}}>
            <div style={{fontSize:10,color:C.gold,letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Fill In Contract Fields</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {mergeFieldNames.map(name=>(
                <div key={name}>
                  <label style={{fontSize:10,color:C.slate,display:"block",marginBottom:2}}>{name.replace(/_/g,' ')}</label>
                  <input className="fi" value={mergeValues[name]||''} onChange={e=>setMergeValues(p=>({...p,[name]:e.target.value}))}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {!templateId && (
          <div style={{marginBottom:12}}>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Upload Contract (PDF or Word)</label>
            <input type="file" accept="application/pdf,.doc,.docx" onChange={async e=>{
              const f = e.target.files[0];
              if (!f) { setUploadFile(null); setNeedsConversion(false); return; }
              if (await isValidPdfFile(f)) { setUploadFile(f); setNeedsConversion(false); return; }
              if (isWordDocFile(f)) { setUploadFile(f); setNeedsConversion(true); return; }
              flash('⚠ Please upload a PDF or Word (.doc/.docx) file');
              e.target.value = '';
              setUploadFile(null);
              setNeedsConversion(false);
            }}/>
            {needsConversion && uploadFile && (
              <div style={{fontSize:11,color:C.amber,marginTop:6}}>Will be converted to PDF automatically when sent.</div>
            )}
            {uploadFile && (
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                <input type="checkbox" id="saveAsTemplate" checked={saveAsTemplate} onChange={e=>setSaveAsTemplate(e.target.checked)}/>
                <label htmlFor="saveAsTemplate" style={{fontSize:12,color:C.slate,cursor:"pointer"}}>Save as reusable template</label>
              </div>
            )}
            {uploadFile && saveAsTemplate && (
              <input className="fi" style={{marginTop:8}} placeholder="Template name (e.g. Standard Puppy Contract)" value={templateName} onChange={e=>setTemplateName(e.target.value)}/>
            )}
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
          <button className="bgs" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="bgl" onClick={submit} disabled={sending}>{converting?'Converting to PDF…':sending?'Sending…':'Send for Signature'}</button>
        </div>
      </div>
    </div>
  );
}

// Visual click-to-place field editor for a saved contract template. Coordinate
// origin confirmed empirically against a real Dropbox Sign test send (not
// assumed from docs alone): y=0 is the TOP of the page and increases
// downward, same as screen/canvas coordinates — so no axis flip is needed,
// only a uniform scale conversion between our fixed display width and each
// page's native point size (page.originalWidth/originalHeight from react-pdf,
// which is also the 72-DPI coordinate space form_fields_per_document expects).
// tool -> Dropbox Sign field type + who fills it in. 'merge' (text-merge) is
// filled in by the breeder (signer:'sender') before sending; 'buyerText'
// (plain text) is filled in by the buyer themselves at signing time
// (signer:0), same as signature/date.
const FIELD_DEFAULTS = {
  signature: { width: 160, height: 40, color: C.gold, type: 'signature', signer: 0, defaultName: 'Signature' },
  date:      { width: 100, height: 20, color: C.sapphireL, type: 'date', signer: 0, defaultName: 'Date' },
  merge:     { width: 150, height: 20, color: C.amber, type: 'text-merge', signer: 'sender', defaultName: '' },
  buyerText: { width: 150, height: 20, color: C.emeraldL, type: 'text', signer: 0, defaultName: '' },
};

function FieldEditor({template, breederId, supabase, flash, onSave, onClose}){
  const DISPLAY_WIDTH = 700;
  const [signedUrl, setSignedUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDims, setPageDims] = useState({});
  const [fields, setFields] = useState(template.field_layout || []);
  const [tool, setTool] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.storage.from('contracts').createSignedUrl(template.storage_path, 300).then(({data}) => {
      if (!cancelled) setSignedUrl(data?.signedUrl || null);
    });
    return () => { cancelled = true; };
  }, [template.storage_path, supabase]);

  const dims = pageDims[currentPage];
  const scale = dims ? DISPLAY_WIDTH / dims.originalWidth : null;

  const handlePageClick = (e) => {
    if (!tool || !scale) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;
    const def = FIELD_DEFAULTS[tool];
    setFields(prev => [...prev, {
      api_id: crypto.randomUUID(),
      type: def.type,
      x: Math.round(displayX / scale),
      y: Math.round(displayY / scale),
      width: Math.round(def.width),
      height: Math.round(def.height),
      page: currentPage,
      signer: def.signer,
      required: true,
      name: def.defaultName,
    }]);
    setTool(null);
  };

  const updateFieldName = (apiId, name) => setFields(prev => prev.map(f => f.api_id===apiId ? {...f, name} : f));
  const removeField = (apiId) => setFields(prev => prev.filter(f => f.api_id!==apiId));

  const save = async () => {
    const namedFields = fields.filter(f=>f.type==='text-merge'||f.type==='text');
    if (namedFields.some(f=>!f.name.trim())) { flash('Label every merge and buyer text field before saving'); return; }
    setSaving(true);
    const { error } = await supabase.from('contract_templates').update({ field_layout: fields }).eq('id', template.id);
    setSaving(false);
    if (error) { flash('⚠ ' + error.message); return; }
    onSave(fields);
    flash('Field layout saved ✓');
    onClose();
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget && !saving)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:400,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:20,overflowY:"auto"}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:"100%",maxWidth:1000,margin:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontFamily:"serif",fontSize:18,fontWeight:700}}>Edit Fields — {template.name}</div>
          <button className="bgs" onClick={onClose} disabled={saving}>✕ Close</button>
        </div>

        <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          <button className={tool==='signature'?"bgl":"bos"} style={{fontSize:12}} onClick={()=>setTool(tool==='signature'?null:'signature')}>+ Signature Field</button>
          <button className={tool==='date'?"bgl":"bos"} style={{fontSize:12}} onClick={()=>setTool(tool==='date'?null:'date')}>+ Date Field</button>
          <button className={tool==='merge'?"bgl":"bos"} style={{fontSize:12}} onClick={()=>setTool(tool==='merge'?null:'merge')}>+ Merge Text Field</button>
          <button className={tool==='buyerText'?"bgl":"bos"} style={{fontSize:12}} onClick={()=>setTool(tool==='buyerText'?null:'buyerText')}>+ Buyer Text Field</button>
          {tool && <span style={{fontSize:11,color:C.slate,alignSelf:"center"}}>Click on the document below to place it</span>}
        </div>

        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <div style={{flex:"0 0 auto"}}>
            {numPages>1 && (
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                <button className="bgs" style={{fontSize:11}} disabled={currentPage<=1} onClick={()=>setCurrentPage(p=>p-1)}>← Prev</button>
                <span style={{fontSize:12,color:C.slate}}>Page {currentPage} of {numPages}</span>
                <button className="bgs" style={{fontSize:11}} disabled={currentPage>=numPages} onClick={()=>setCurrentPage(p=>p+1)}>Next →</button>
              </div>
            )}
            <div style={{position:"relative",display:"inline-block",border:`1px solid ${C.border}`,cursor:tool?"crosshair":"default"}} onClick={handlePageClick}>
              {signedUrl && (
                <PdfDocument file={signedUrl} onLoadSuccess={({numPages})=>setNumPages(numPages)} onLoadError={(e)=>flash('⚠ Could not load PDF: '+e.message)}>
                  <PdfPage pageNumber={currentPage} width={DISPLAY_WIDTH} onLoadSuccess={(page)=>setPageDims(prev=>({...prev,[currentPage]:{originalWidth:page.originalWidth,originalHeight:page.originalHeight}}))}/>
                </PdfDocument>
              )}
              {scale && fields.filter(f=>f.page===currentPage).map(f=>{
                const color = f.type==='signature'?FIELD_DEFAULTS.signature.color:f.type==='date'?FIELD_DEFAULTS.date.color:f.type==='text'?FIELD_DEFAULTS.buyerText.color:FIELD_DEFAULTS.merge.color;
                const isNamed = f.type==='text-merge' || f.type==='text';
                return (
                  <div key={f.api_id} style={{position:"absolute",left:f.x*scale,top:f.y*scale,width:f.width*scale,height:f.height*scale,border:`2px solid ${color}`,background:`${color}22`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 4px",fontSize:10,color:C.cream,overflow:"hidden"}}>
                    {isNamed ? (
                      <input value={f.name} placeholder={f.type==='text-merge'?"field_name":"label for buyer"} onChange={e=>updateFieldName(f.api_id,e.target.value)} onClick={e=>e.stopPropagation()}
                        style={{background:"transparent",border:"none",color:C.cream,fontSize:10,width:"100%",outline:"none"}}/>
                    ) : (
                      <span>{f.name}</span>
                    )}
                    <span onClick={e=>{e.stopPropagation(); removeField(f.api_id);}} style={{cursor:"pointer",color:C.red,fontWeight:700,flexShrink:0}}>✕</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{flex:1,minWidth:220}}>
            <div style={{fontSize:10,color:C.gold,letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Placed Fields ({fields.length})</div>
            {fields.length===0 ? (
              <div style={{fontSize:12,color:C.slate}}>No fields placed yet. Pick a field type above, then click on the document.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {fields.map(f=>(
                  <div key={f.api_id} style={{fontSize:11,color:C.creamD,display:"flex",justifyContent:"space-between",gap:8}}>
                    <span>{(f.type==='text-merge'||f.type==='text')?(f.name||'(unnamed)'):f.name} · pg {f.page}</span>
                    <span onClick={()=>removeField(f.api_id)} style={{cursor:"pointer",color:C.red}}>✕</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          <button className="bgs" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="bgl" onClick={save} disabled={saving}>{saving?'Saving…':'Save Field Layout'}</button>
        </div>
      </div>
    </div>
  );
}

// Shared detail/view surface for a contract — reachable from both the
// Contracts tab list and a Matched Waitlist row, so status/document access
// isn't duplicated across two places. Signed URLs are generated client-side
// (storage RLS already scopes the breeder to their own path prefix) since
// this is a read-only view action, not a state change.
function ContractDetailModal({contract, dog, litter, supabase, flash, onClose, onVoided, onDeleted}){
  const [originalUrl, setOriginalUrl] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const statusColor = contract.status==='signed'?C.emeraldL:contract.status==='declined'?C.red:contract.status==='voided'?C.slate:C.amber;

  useEffect(() => {
    let cancelled = false;
    if (contract.original_document_url) {
      supabase.storage.from('contracts').createSignedUrl(contract.original_document_url, 300).then(({data}) => {
        if (!cancelled) setOriginalUrl(data?.signedUrl || null);
      });
    }
    if (contract.signed_document_url) {
      supabase.storage.from('contracts').createSignedUrl(contract.signed_document_url, 300).then(({data}) => {
        if (!cancelled) setSignedUrl(data?.signedUrl || null);
      });
    }
    return () => { cancelled = true; };
  }, [contract.original_document_url, contract.signed_document_url, supabase]);

  const voidContract = async () => {
    if (!window.confirm(`Cancel this contract? ${contract.buyer_email} will stop receiving signature reminders.`)) return;
    setVoiding(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { flash('⚠ Your session expired — please sign in again.'); return; }
      const resp = await fetch('/api/contract-void', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ contractId: contract.id }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) { flash('⚠ ' + (json.error || `Request failed (${resp.status})`)); return; }
      flash('Contract cancelled');
      onVoided(contract.id);
      onClose();
    } catch (e) {
      flash('⚠ ' + (e.message || 'Something went wrong — please try again.'));
    } finally {
      setVoiding(false);
    }
  };

  const deleteContract = async () => {
    if (!window.confirm(`Permanently delete this contract? This can't be undone.`)) return;
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { flash('⚠ Your session expired — please sign in again.'); return; }
      const resp = await fetch('/api/contract-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ contractId: contract.id }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) { flash('⚠ ' + (json.error || `Request failed (${resp.status})`)); return; }
      flash('Contract deleted');
      onDeleted(contract.id);
    } catch (e) {
      flash('⚠ ' + (e.message || 'Something went wrong — please try again.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget && !voiding && !deleting)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:"100%",maxWidth:440}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontFamily:"serif",fontSize:18,fontWeight:700}}>{(contract.contract_type||'').replace(/_/g,' ')}</div>
            <div style={{fontSize:12,color:C.slate,marginTop:2}}>{contract.buyer_email}</div>
          </div>
          <span style={{fontSize:10,color:statusColor,textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>{contract.status}</span>
        </div>

        {(dog?.name || litter) && (
          <div style={{fontSize:12,color:C.creamD,marginBottom:8}}>Re: {dog?.name || litterHeadline(litter)}</div>
        )}
        <div style={{fontSize:12,color:C.slate,marginBottom:16}}>
          Sent {contract.sent_at?new Date(contract.sent_at).toLocaleDateString():'—'}
          {contract.completed_at && ` · Signed ${new Date(contract.completed_at).toLocaleDateString()}`}
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {originalUrl && <a href={originalUrl} target="_blank" rel="noreferrer" className="bos" style={{fontSize:12,textAlign:"center",textDecoration:"none",display:"block",padding:"8px 0",borderRadius:5}}>View Original Document →</a>}
          {contract.status==='signed' && signedUrl && <a href={signedUrl} target="_blank" rel="noreferrer" className="bgl" style={{fontSize:12,textAlign:"center",textDecoration:"none",display:"block",padding:"8px 0",borderRadius:5,color:C.bg,fontWeight:700}}>View Signed Document →</a>}
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          {contract.status==='sent' && <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:12}} disabled={voiding} onClick={voidContract}>{voiding?'Cancelling…':'Cancel Contract'}</button>}
          {['voided','declined'].includes(contract.status) && <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:12}} disabled={deleting} onClick={deleteContract}>{deleting?'Deleting…':'Delete Contract'}</button>}
          <button className="bos" onClick={onClose} disabled={voiding || deleting}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Reachable from a matched puppy's "✓ Matched" indicator in the litter
// detail view — same buyer/contract info already shown in the Matched
// Waitlist view, just accessible without navigating away from the litter.
// Reuses updateWaitlistNotes, ContractDetailModal (via onViewContract), and
// SendContractModal (via onSendContract) wholesale — no new data-fetching,
// no duplicated contract/notes display.
function PuppyMatchDetailsModal({entry, puppy, litter, contract, updateWaitlistNotes, onSendContract, onViewContract, onClose}){
  const statusColor = contract && (contract.status==='signed'?C.emeraldL:contract.status==='declined'?C.red:contract.status==='voided'?C.slate:C.amber);
  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:"100%",maxWidth:440}}>
        <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,marginBottom:4}}>{entry.buyer_name}</div>
        <div style={{fontSize:12,color:C.slate,marginBottom:12}}>{entry.buyer_email}{entry.buyer_phone?` · ${entry.buyer_phone}`:''}</div>

        <div style={{fontSize:12,color:C.emeraldL,marginBottom:12}}>
          ✓ Matched{puppy?` to ${[puppy.label,puppy.sex==='M'?'Male':puppy.sex==='F'?'Female':null,puppy.color,puppy.price].filter(Boolean).join(' · ')}`:' (no specific puppy on file)'}{litter?` — ${litterHeadline(litter)}`:''}{entry.matched_at?` · ${new Date(entry.matched_at).toLocaleDateString()}`:''}
        </div>

        {entry.notes && (
          <div style={{fontSize:12,color:C.creamD,marginBottom:8}}><span style={{color:C.slate}}>Buyer's note:</span> {entry.notes}</div>
        )}
        <div style={{marginBottom:16}}>
          <label style={{fontSize:9,color:C.slate,letterSpacing:0.5,textTransform:"uppercase",display:"block",marginBottom:2}}>Your Notes</label>
          <textarea className="fi" rows={2} placeholder="Conversation history, follow-ups…" defaultValue={entry.breeder_notes||""} onBlur={ev=>updateWaitlistNotes(entry.id, ev.target.value)} style={{resize:"vertical",fontSize:12}}/>
        </div>

        <div style={{paddingTop:12,borderTop:`1px solid ${C.border}`}}>
          {contract ? (
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:12,color:C.slate}}>Contract: <span style={{color:statusColor,textTransform:"uppercase",fontWeight:700,fontSize:10}}>{contract.status}</span></div>
              <button className="bos" style={{fontSize:11}} onClick={()=>{onViewContract(contract);onClose();}}>View Contract →</button>
            </div>
          ) : (
            <button className="bos" style={{fontSize:11,width:"100%"}} onClick={()=>{onSendContract({buyerEmail:entry.buyer_email, waitlistEntryId:entry.id, litterId:litter?.id||null, dogId:null});onClose();}}>Send Contract →</button>
          )}
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
          <button className="bos" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── DOG BACK OFFICE ───────────────────────────────────────────────────────────
function DateInput({value, onChange}) {
  const [err, setErr] = useState(false);
  const handleBlur = () => {
    if (!value) { setErr(false); return; }
    const {dob} = parseDob(value);
    if (dob) { setErr(false); onChange(dob); }
    else setErr(true);
  };
  return (
    <div>
      <input className="fi" value={value}
        onChange={e=>{setErr(false); onChange(e.target.value);}}
        onBlur={handleBlur}
        style={err?{borderColor:C.red}:{}}/>
      <div style={{fontSize:10,color:err?C.red:C.slate,marginTop:2}}>
        {err?'Unrecognized format':'e.g. 8/5/2013 or 2013-08-05'}
      </div>
    </div>
  );
}

function DogBackOffice({dog, dogs, breeders, supabase, flash, breederId, user, onClose, onDogUpdate}) {
  const TYPES = [
    {key:'health_test', label:'Health Tests'},
    {key:'vaccination', label:'Vaccinations'},
    {key:'vet_visit',   label:'Vet Visits'},
    ...(dog.sex==='F'?[{key:'heat_cycle',label:'Heat Cycles'}]:[]),
    {key:'breeding',    label:'Breeding'},
    {key:'weight',      label:'Weights'},
  ];
  const EMPTY = {
    health_test: {date:'',testType:'OFA Hips',result:'',lab:'',certNumber:'',registry:'OFA',di:'',notes:''},
    vaccination:  {date:'',vaccine:'',expiryDate:'',vet:'',lotNumber:'',notes:''},
    vet_visit:    {date:'',reason:'',notes:'',costUsd:'',clinic:''},
    heat_cycle:   {startDate:'',endDate:'',notes:''},
    breeding:     {partnerDogId:'',date:'',method:'Natural',outcome:'TBD',notes:''},
    weight:       {date:'',weightLb:'',weightKg:'',notes:''},
  };

  const [activeType, setActiveType] = useState('health_test');
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY.health_test);
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString() : '';

  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);
  const lastUploadedPath = useRef(null);

  const records = allRecords.filter(r=>r.record_type===activeType);

  const prefillForm = (docType, fields) => {
    const type = ['health_test','vaccination','vet_visit'].includes(docType) ? docType : 'health_test';
    let mapped = {};
    if (type === 'health_test') mapped = {
      date: fields.test_date||'', testType: fields.test_type||'OFA Hips',
      result: fields.result||'', lab: fields.lab||'',
      certNumber: fields.cert_number||'', notes: fields.notes||'',
    };
    else if (type === 'vaccination') mapped = {
      date: fields.date_given||'', vaccine: fields.vaccine||'',
      expiryDate: fields.expiry_date||'', vet: fields.vet||'',
      lotNumber: fields.lot_number||'', notes: fields.notes||'',
    };
    else if (type === 'vet_visit') mapped = {
      date: fields.visit_date||'', reason: fields.reason||'',
      notes: [fields.diagnosis, fields.notes].filter(Boolean).join(' · '),
      costUsd: fields.cost||'', clinic: fields.clinic||'',
    };
    setActiveType(type);
    setForm({...EMPTY[type], ...mapped});
    setAdding(true);
  };

  const extractFromDoc = async (file) => {
    if (!file || !user) return;
    setExtracting(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('health-documents').upload(path, file);
      if (upErr) throw new Error('Upload failed: ' + upErr.message);

      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/extract-health', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ storagePath: path, dogId: dog.id, tabHint: activeType }),
      });
      const json = await resp.json();
      if (!resp.ok || json.error) throw new Error(json.error || 'Extraction failed');

      lastUploadedPath.current = path;
      prefillForm(json.extracted.doc_type, json.extracted);
      flash('Document extracted — review and save ✓');
    } catch(e) {
      lastUploadedPath.current = null;
      flash('⚠ ' + e.message);
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    supabase.from('dog_records').select('*').eq('dog_id', dog.id)
      .order('record_date',{ascending:false,nullsFirst:false})
      .then(({data})=>{ setAllRecords(data||[]); setLoading(false); });
  }, [dog.id]);

  const switchType = (t) => { setActiveType(t); setAdding(false); setEditingId(null); setForm(EMPTY[t]); lastUploadedPath.current = null; };

  const save = async () => {
    let row = {dog_id:dog.id, breeder_id:breederId, record_type:activeType, notes:form.notes||'', document_url:lastUploadedPath.current||null};
    switch(activeType) {
      case 'health_test':
        if (!form.testType) { flash('Select a test type'); return; }
        row.record_date = form.date||null;
        row.data = {test_type:form.testType,result:form.result,lab:form.lab,cert_number:form.certNumber,registry:form.registry||'OFA',di:form.di||''};
        break;
      case 'vaccination':
        if (!form.vaccine) { flash('Enter vaccine name'); return; }
        row.record_date = form.date||null;
        row.data = {vaccine:form.vaccine,expiry_date:form.expiryDate,vet:form.vet,lot_number:form.lotNumber};
        break;
      case 'vet_visit':
        row.record_date = form.date||null;
        row.data = {reason:form.reason,clinic:form.clinic,cost_usd:form.costUsd?parseFloat(form.costUsd):null};
        break;
      case 'heat_cycle':
        if (!form.startDate) { flash('Enter start date'); return; }
        row.record_date = form.startDate||null;
        row.data = {end_date:form.endDate||null};
        break;
      case 'breeding':
        if (!form.date) { flash('Enter breeding date'); return; }
        row.record_date = form.date||null;
        row.data = {partner_dog_id:form.partnerDogId||null,method:form.method,outcome:form.outcome};
        break;
      case 'weight':
        if (!form.date) { flash('Enter date'); return; }
        row.record_date = form.date||null;
        row.data = {weight_lb:form.weightLb?parseFloat(form.weightLb):null,weight_kg:form.weightKg?parseFloat(form.weightKg):null};
        break;
      default: return;
    }
    if (editingId) {
      const {data:updated,error} = await supabase.from('dog_records').update(row).eq('id',editingId).select().single();
      if (error) { flash('⚠ '+error.message); return; }
      setAllRecords(prev=>prev.map(r=>r.id===editingId?updated:r));
    } else {
      const {data:inserted,error} = await supabase.from('dog_records').insert(row).select().single();
      if (error) { flash('⚠ '+error.message); return; }
      setAllRecords(prev=>[inserted,...prev]);
    }
    if (activeType==='health_test') {
      const sync = HEALTH_SYNC_MAP[form.testType];
      if (sync) {
        const updates = {};
        const val = sync.useResult ? form.result : form.certNumber;
        if (val) updates[sync.col] = val;
        if (sync.certCol && form.certNumber) updates[sync.certCol] = form.certNumber;
        if (sync.registryCol && form.registry) updates[sync.registryCol] = form.registry;
        if (sync.diCol && form.di) updates[sync.diCol] = form.di;
        if (Object.keys(updates).length) {
          await supabase.from('dogs').update(updates).eq('id',dog.id);
          const jsUpdates = {};
          if (updates[sync.col]!==undefined) jsUpdates[sync.jsKey] = updates[sync.col];
          if (sync.certCol && updates[sync.certCol]!==undefined) jsUpdates[sync.certJsKey] = updates[sync.certCol];
          if (sync.registryCol && updates[sync.registryCol]!==undefined) jsUpdates[sync.registryJsKey] = updates[sync.registryCol];
          if (sync.diCol && updates[sync.diCol]!==undefined) jsUpdates[sync.diJsKey] = updates[sync.diCol];
          onDogUpdate({...dog, ...jsUpdates});
        }
      }
    }
    lastUploadedPath.current = null;
    setAdding(false); setForm(EMPTY[activeType]); setEditingId(null);
    flash(editingId ? 'Record updated ✓' : 'Record saved ✓');
  };

  const startEdit = (r) => {
    const d = r.data||{};
    let mapped = {};
    switch (r.record_type) {
      case 'health_test': mapped = {date:r.record_date||'', testType:d.test_type||'OFA Hips', result:d.result||'', lab:d.lab||'', certNumber:d.cert_number||'', registry:d.registry||'OFA', di:d.di||'', notes:r.notes||''}; break;
      case 'vaccination': mapped = {date:r.record_date||'', vaccine:d.vaccine||'', expiryDate:d.expiry_date||'', vet:d.vet||'', lotNumber:d.lot_number||'', notes:r.notes||''}; break;
      case 'vet_visit': mapped = {date:r.record_date||'', reason:d.reason||'', clinic:d.clinic||'', costUsd:d.cost_usd!=null?String(d.cost_usd):'', notes:r.notes||''}; break;
      case 'heat_cycle': mapped = {startDate:r.record_date||'', endDate:d.end_date||'', notes:r.notes||''}; break;
      case 'breeding': mapped = {date:r.record_date||'', partnerDogId:d.partner_dog_id||'', method:d.method||'Natural', outcome:d.outcome||'TBD', notes:r.notes||''}; break;
      case 'weight': mapped = {date:r.record_date||'', weightLb:d.weight_lb!=null?String(d.weight_lb):'', weightKg:d.weight_kg!=null?String(d.weight_kg):'', notes:r.notes||''}; break;
      default: return;
    }
    setActiveType(r.record_type);
    setForm({...EMPTY[r.record_type], ...mapped});
    setEditingId(r.id);
    setAdding(true);
  };

  const del = async (id) => {
    await supabase.from('dog_records').delete().eq('id',id);
    setAllRecords(prev=>prev.filter(r=>r.id!==id));
    flash('Record deleted');
  };

  const LBL = ({children}) => <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:'uppercase',display:'block',marginBottom:3}}>{children}</label>;

  const renderForm = () => {
    const grid = {display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10};
    switch(activeType) {
      case 'health_test': {
        const vocabKey = HEALTH_SYNC_MAP[form.testType]?.col;
        const vocab = TEST_RESULT_VOCAB[vocabKey];
        const isLegacyResult = form.result && vocab && !vocab.includes(form.result);
        const isHipsTest = form.testType === 'OFA Hips';
        const registry = form.registry || 'OFA';
        const isPennHip = isHipsTest && registry === 'PennHIP';
        return (
          <div style={grid}>
            <div><LBL>Test Type *</LBL><select className="fi" value={form.testType} onChange={e=>f('testType',e.target.value)}>{HEALTH_TEST_TYPES.map(t=><option key={t} value={t}>{t==='OFA Hips'?'Hips (OFA / PennHIP)':t}</option>)}</select></div>
            <div><LBL>Date</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
            {isHipsTest && (
              <div><LBL>Registry</LBL>
                <select className="fi" value={registry} onChange={e=>f('registry',e.target.value)}>
                  <option value="OFA">OFA</option>
                  <option value="PennHIP">PennHIP</option>
                </select>
              </div>
            )}
            <div><LBL>{isPennHip?'Distraction Index (reference only)':'Result'}</LBL>
              {isPennHip ? (
                <input className="fi" placeholder="e.g. 0.35 (not used for pass/fail)" value={form.di} onChange={e=>f('di',e.target.value)}/>
              ) : vocab ? (
                <select className="fi" value={form.result} onChange={e=>f('result',e.target.value)}>
                  <option value="">Select…</option>
                  {isLegacyResult && <option value={form.result}>{form.result} (legacy entry — re-select to update)</option>}
                  {vocab.map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input className="fi" placeholder="e.g. Excellent, Clear, Pass" value={form.result} onChange={e=>f('result',e.target.value)}/>
              )}
            </div>
            <div><LBL>{isPennHip?'PennHIP Report #':'Cert / Clearance #'}</LBL><input className="fi" placeholder={isPennHip?'e.g. PH-12345':'e.g. OFA12345E24M'} value={form.certNumber} onChange={e=>f('certNumber',e.target.value)}/></div>
            {!isHipsTest && (
              <div><LBL>Lab / Registry</LBL><input className="fi" placeholder="e.g. OFA, Embark, PennHIP" value={form.lab} onChange={e=>f('lab',e.target.value)}/></div>
            )}
            <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
            {HEALTH_SYNC_MAP[form.testType]&&(form.result||form.certNumber)&&(
              <div style={{gridColumn:'1/-1',fontSize:11,color:C.emeraldL}}>✓ Will also update the public dog card ({HEALTH_SYNC_MAP[form.testType].useResult?'result':'cert #'} → {HEALTH_SYNC_MAP[form.testType].jsKey})</div>
            )}
          </div>
        );
      }
      case 'vaccination': return (
        <div style={grid}>
          <div><LBL>Vaccine *</LBL><input className="fi" placeholder="e.g. DA2PP, Rabies" value={form.vaccine} onChange={e=>f('vaccine',e.target.value)}/></div>
          <div><LBL>Date Given</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Expiry Date</LBL><DateInput value={form.expiryDate} onChange={v=>f('expiryDate',v)}/></div>
          <div><LBL>Vet / Clinic</LBL><input className="fi" placeholder="e.g. Dr. Smith" value={form.vet} onChange={e=>f('vet',e.target.value)}/></div>
          <div><LBL>Lot #</LBL><input className="fi" placeholder="Vaccine lot number" value={form.lotNumber} onChange={e=>f('lotNumber',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'vet_visit': return (
        <div style={grid}>
          <div><LBL>Date</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Clinic</LBL><input className="fi" placeholder="Clinic name" value={form.clinic} onChange={e=>f('clinic',e.target.value)}/></div>
          <div style={{gridColumn:'1/-1'}}><LBL>Reason / Diagnosis</LBL><input className="fi" placeholder="e.g. Annual exam, Limping right front" value={form.reason} onChange={e=>f('reason',e.target.value)}/></div>
          <div><LBL>Cost ($)</LBL><input className="fi" type="number" min="0" step="0.01" placeholder="0.00" value={form.costUsd} onChange={e=>f('costUsd',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'heat_cycle': return (
        <div style={grid}>
          <div><LBL>Start Date *</LBL><DateInput value={form.startDate} onChange={v=>f('startDate',v)}/></div>
          <div><LBL>End Date</LBL><DateInput value={form.endDate} onChange={v=>f('endDate',v)}/></div>
          <div style={{gridColumn:'1/-1'}}><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'breeding': return (
        <div style={grid}>
          <div><LBL>Date *</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Method</LBL>
            <select className="fi" value={form.method} onChange={e=>f('method',e.target.value)}>
              {['Natural','AI','Surgical AI'].map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={{gridColumn:'1/-1'}}><LBL>Partner Dog</LBL>
            <DogAutocomplete value={form.partnerDogId} onChange={v=>f('partnerDogId',v)} dogs={dogs} breeders={breeders} currentDogId={dog.id} placeholder="Search all dogs…"/>
          </div>
          <div><LBL>Outcome</LBL>
            <select className="fi" value={form.outcome} onChange={e=>f('outcome',e.target.value)}>
              {['TBD','Pregnant','Whelped','Missed'].map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'weight': return (
        <div style={grid}>
          <div><LBL>Date *</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
          <div><LBL>Weight (lb)</LBL><input className="fi" type="number" min="0" step="0.1" placeholder="0.0" value={form.weightLb} onChange={e=>{f('weightLb',e.target.value); if(e.target.value) f('weightKg',(parseFloat(e.target.value)/2.20462).toFixed(1)); else f('weightKg','');}}/></div>
          <div><LBL>Weight (kg)</LBL><input className="fi" type="number" min="0" step="0.1" placeholder="0.0" value={form.weightKg} onChange={e=>{f('weightKg',e.target.value); if(e.target.value) f('weightLb',(parseFloat(e.target.value)*2.20462).toFixed(1)); else f('weightLb','');}}/></div>
        </div>
      );
      default: return null;
    }
  };

  const renderRecord = (r) => {
    const d = r.data||{};
    let primary='', secondary='', meta=[];
    switch(r.record_type) {
      case 'health_test':
        primary=d.test_type||'Health Test';
        secondary=d.result||(d.cert_number?`#${d.cert_number}`:'');
        if(d.cert_number&&d.result) meta.push(`#${d.cert_number}`);
        if(d.lab) meta.push(d.lab);
        break;
      case 'vaccination':
        primary=d.vaccine||'Vaccination';
        secondary=d.expiry_date?`Exp ${fmtDate(d.expiry_date)}`:'';
        if(d.vet) meta.push(d.vet);
        if(d.lot_number) meta.push(`Lot ${d.lot_number}`);
        break;
      case 'vet_visit':
        primary=d.reason||'Vet Visit';
        secondary=d.clinic||'';
        if(d.cost_usd!=null) meta.push(`$${d.cost_usd}`);
        break;
      case 'heat_cycle':
        primary='Heat Cycle';
        secondary=d.end_date?`${fmtDate(r.record_date)} – ${fmtDate(d.end_date)}`:fmtDate(r.record_date);
        break;
      case 'breeding': {
        const partner = d.partner_dog_id&&(dogs||{})[d.partner_dog_id];
        primary=partner?`× ${partner.name}`:'Breeding';
        secondary=d.outcome||'';
        if(d.method) meta.push(d.method);
        break;
      }
      case 'weight':
        primary=d.weight_lb?`${d.weight_lb} lb`:d.weight_kg?`${d.weight_kg} kg`:'Weight';
        secondary=d.weight_kg&&d.weight_lb?`${d.weight_kg} kg`:'';
        break;
      default: primary='Record';
    }
    return (
      <div key={r.id} style={{background:C.bg4,border:`1px solid ${C.border}`,borderRadius:7,padding:'10px 14px',display:'flex',alignItems:'flex-start',gap:12}}>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:700,color:C.cream}}>{primary}</div>
          {secondary&&<div style={{fontSize:12,color:C.gold,marginTop:2}}>{secondary}</div>}
          <div style={{fontSize:11,color:C.slate,marginTop:4,display:'flex',gap:8,flexWrap:'wrap'}}>
            {r.record_type!=='heat_cycle'&&r.record_date&&<span>{fmtDate(r.record_date)}</span>}
            {meta.map((m,i)=><span key={i}>· {m}</span>)}
            {r.notes&&<span>· {r.notes}</span>}
          </div>
        </div>
        <div style={{display:'flex',gap:4,flexShrink:0}}>
          <button onClick={()=>startEdit(r)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:11,padding:'0 4px'}}>Edit</button>
          <button onClick={()=>del(r.id)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:16,padding:'0 4px',lineHeight:1}}>×</button>
        </div>
      </div>
    );
  };

  const addLabel = {health_test:'Test',vaccination:'Vaccination',vet_visit:'Visit',heat_cycle:'Cycle',breeding:'Record',weight:'Entry'}[activeType]||'Record';

  return (
    <div style={{background:C.bg2,border:`1px solid ${C.emeraldL}44`,borderRadius:12,padding:24,marginBottom:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontSize:10,color:C.emeraldL,letterSpacing:3,textTransform:'uppercase',marginBottom:2}}>Back Office</div>
          <div style={{fontFamily:'serif',fontSize:20,fontWeight:700}}>{dog.name}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <input id="health-doc-input" ref={fileInputRef} type="file" accept="image/*,.pdf" style={{display:'none'}}
            onChange={e=>{ const f=e.target.files?.[0]; if(f) extractFromDoc(f); }}/>
          <label htmlFor="health-doc-input" className="bos" style={{fontSize:11,cursor:'pointer',display:'inline-block',opacity:extracting?0.6:1,pointerEvents:extracting?'none':'auto'}}>
            {extracting?'Extracting…':'📷 Auto-fill from Photo'}
          </label>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.slate,fontSize:22,cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
        </div>
      </div>

      {/* Type tabs */}
      <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:`1px solid ${C.border}`,paddingBottom:0,flexWrap:'wrap'}}>
        {TYPES.map(({key,label})=>(
          <button key={key} onClick={()=>switchType(key)} style={{padding:'6px 14px',background:'transparent',border:'none',borderBottom:`2px solid ${activeType===key?C.emeraldL:'transparent'}`,color:activeType===key?C.emeraldL:C.slate,fontSize:12,cursor:'pointer',fontFamily:'sans-serif',fontWeight:activeType===key?700:400,marginBottom:-1,transition:'all 0.15s'}}>
            {label}{allRecords.filter(r=>r.record_type===key).length>0&&<span style={{marginLeft:5,fontSize:10,color:activeType===key?C.emeraldL:C.slate}}>({allRecords.filter(r=>r.record_type===key).length})</span>}
          </button>
        ))}
      </div>

      {/* Add button + form */}
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:adding?12:8}}>
        {!adding&&<button className="bos" style={{fontSize:11}} onClick={()=>{setEditingId(null);setAdding(true);}}>+ Add {addLabel}</button>}
      </div>
      {adding&&(
        <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:16,marginBottom:16}}>
          {renderForm()}
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button className="bgl" style={{fontSize:12}} onClick={save}>{editingId?'Update':'Save'}</button>
            <button className="bgs" style={{fontSize:12}} onClick={()=>{setAdding(false);setEditingId(null);setForm(EMPTY[activeType]);}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Record list */}
      {loading?(
        <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16}}>Loading…</div>
      ):records.length===0?(
        <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16,border:`1px dashed ${C.border}`,borderRadius:8}}>No {TYPES.find(t=>t.key===activeType)?.label.toLowerCase()} recorded yet.</div>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:6}}>{records.map(r=>renderRecord(r))}</div>
      )}
    </div>
  );
}

// Same Back Office experience as DogBackOffice (tabs + inline add form +
// record list), scoped to a litter instead of one dog, with two structural
// differences: (1) fewer record types — heat_cycle/breeding are adult-dog
// concepts with no puppy equivalent, and there's no HEALTH_SYNC_MAP
// writeback since there's no dogs row yet to sync into; (2) a puppy
// checkbox selector, since one event usually applies to several puppies at
// once. Each save fans out into one litter_puppy_records row per selected
// puppy, sharing a batch_id — edits/deletes act on the whole batch
// (delete-then-reinsert on edit, simpler and just as correct as a
// diff-based reconcile since nothing else references individual row ids).
// litter-era records intentionally never migrate into dog_records — there's
// no link from a litter_puppies row to whatever dogs row a buyer eventually
// creates, so this stays a permanent historical log tied to the litter.
function LitterPuppyHealthLog({litter, puppies, supabase, flash, breederId, onClose}) {
  const TYPES = [
    {key:'health_test', label:'Health Tests'},
    {key:'vaccination', label:'Vaccinations'},
    {key:'vet_visit',   label:'Vet Visits'},
    {key:'weight',      label:'Weights'},
  ];
  const EMPTY = {
    health_test: {date:'',testType:'OFA Hips',result:'',lab:'',certNumber:'',registry:'OFA',di:'',notes:''},
    vaccination:  {date:'',vaccine:'',expiryDate:'',vet:'',lotNumber:'',notes:''},
    vet_visit:    {date:'',reason:'',notes:'',costUsd:'',clinic:''},
    weight:       {date:'',weightLb:'',weightKg:'',notes:''},
  };

  const [activeType, setActiveType] = useState('health_test');
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [form, setForm] = useState(EMPTY.health_test);
  const [selectedPuppyIds, setSelectedPuppyIds] = useState(puppies.map(p=>p.id));
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString() : '';
  const puppyShortLabel = (p) => p ? (p.label || [p.sex==='M'?'Male':p.sex==='F'?'Female':null, p.color].filter(Boolean).join(' ') || 'Puppy') : 'Puppy';

  useEffect(() => {
    let cancelled = false;
    supabase.from('litter_puppy_records').select('*').eq('litter_id', litter.id)
      .order('record_date', {ascending:false, nullsFirst:false})
      .then(({data,error}) => { if(cancelled) return; if(error) flash('⚠ '+error.message); setAllRecords(data||[]); setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [litter.id]);

  const records = allRecords.filter(r=>r.record_type===activeType);
  const groups = (() => {
    const map = {};
    records.forEach(r => { (map[r.batch_id] = map[r.batch_id] || []).push(r); });
    return Object.values(map);
  })();

  const switchType = (t) => { setActiveType(t); setAdding(false); setEditingBatchId(null); setForm(EMPTY[t]); setSelectedPuppyIds(puppies.map(p=>p.id)); };

  const save = async () => {
    if (selectedPuppyIds.length===0) { flash('Select at least one puppy'); return; }
    let shared = {litter_id: litter.id, breeder_id: breederId, record_type: activeType, notes: form.notes||''};
    switch(activeType) {
      case 'health_test':
        if (!form.testType) { flash('Select a test type'); return; }
        shared.record_date = form.date||null;
        shared.data = {test_type:form.testType,result:form.result,lab:form.lab,cert_number:form.certNumber,registry:form.registry||'OFA',di:form.di||''};
        break;
      case 'vaccination':
        if (!form.vaccine) { flash('Enter vaccine name'); return; }
        shared.record_date = form.date||null;
        shared.data = {vaccine:form.vaccine,expiry_date:form.expiryDate,vet:form.vet,lot_number:form.lotNumber};
        break;
      case 'vet_visit':
        shared.record_date = form.date||null;
        shared.data = {reason:form.reason,clinic:form.clinic,cost_usd:form.costUsd?parseFloat(form.costUsd):null};
        break;
      case 'weight':
        if (!form.date) { flash('Enter date'); return; }
        shared.record_date = form.date||null;
        shared.data = {weight_lb:form.weightLb?parseFloat(form.weightLb):null, weight_kg:form.weightKg?parseFloat(form.weightKg):null};
        break;
      default: return;
    }

    if (editingBatchId) {
      const { error: delErr } = await supabase.from('litter_puppy_records').delete().eq('batch_id', editingBatchId);
      if (delErr) { flash('⚠ '+delErr.message); return; }
      const rows = selectedPuppyIds.map(puppy_id => ({...shared, batch_id: editingBatchId, puppy_id}));
      const { data, error } = await supabase.from('litter_puppy_records').insert(rows).select();
      if (error) { flash('⚠ '+error.message); return; }
      setAllRecords(prev => [...(data||[]), ...prev.filter(r=>r.batch_id!==editingBatchId)]);
      flash('Health event updated ✓');
    } else {
      const batchId = crypto.randomUUID();
      const rows = selectedPuppyIds.map(puppy_id => ({...shared, batch_id: batchId, puppy_id}));
      const { data, error } = await supabase.from('litter_puppy_records').insert(rows).select();
      if (error) { flash('⚠ '+error.message); return; }
      setAllRecords(prev => [...(data||[]), ...prev]);
      flash('Health event logged ✓');
    }
    setAdding(false); setForm(EMPTY[activeType]); setEditingBatchId(null); setSelectedPuppyIds(puppies.map(p=>p.id));
  };

  const startEditBatch = (group) => {
    const r = group[0];
    const d = r.data||{};
    let mapped = {};
    switch(r.record_type) {
      case 'health_test': mapped = {date:r.record_date||'', testType:d.test_type||'OFA Hips', result:d.result||'', lab:d.lab||'', certNumber:d.cert_number||'', registry:d.registry||'OFA', di:d.di||'', notes:r.notes||''}; break;
      case 'vaccination': mapped = {date:r.record_date||'', vaccine:d.vaccine||'', expiryDate:d.expiry_date||'', vet:d.vet||'', lotNumber:d.lot_number||'', notes:r.notes||''}; break;
      case 'vet_visit': mapped = {date:r.record_date||'', reason:d.reason||'', clinic:d.clinic||'', costUsd:d.cost_usd!=null?String(d.cost_usd):'', notes:r.notes||''}; break;
      case 'weight': mapped = {date:r.record_date||'', weightLb:d.weight_lb!=null?String(d.weight_lb):'', weightKg:d.weight_kg!=null?String(d.weight_kg):'', notes:r.notes||''}; break;
      default: return;
    }
    setActiveType(r.record_type);
    setForm({...EMPTY[r.record_type], ...mapped});
    setSelectedPuppyIds(group.map(x=>x.puppy_id));
    setEditingBatchId(r.batch_id);
    setAdding(true);
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Delete this health event? This removes it for all puppies it applied to.')) return;
    const { error } = await supabase.from('litter_puppy_records').delete().eq('batch_id', batchId);
    if (error) { flash('⚠ '+error.message); return; }
    setAllRecords(prev => prev.filter(r=>r.batch_id!==batchId));
    flash('Health event deleted');
  };

  const LBL = ({children}) => <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:'uppercase',display:'block',marginBottom:3}}>{children}</label>;

  const renderForm = () => {
    const grid = {display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10};
    switch(activeType) {
      case 'health_test': return (
        <div style={grid}>
          <div><LBL>Test Type *</LBL><select className="fi" value={form.testType} onChange={e=>f('testType',e.target.value)}>{HEALTH_TEST_TYPES.map(t=><option key={t} value={t}>{t==='OFA Hips'?'Hips (OFA / PennHIP)':t}</option>)}</select></div>
          <div><LBL>Date</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Result</LBL><input className="fi" placeholder="e.g. Excellent, Clear, Pass" value={form.result} onChange={e=>f('result',e.target.value)}/></div>
          <div><LBL>Cert / Clearance #</LBL><input className="fi" placeholder="e.g. OFA12345E24M" value={form.certNumber} onChange={e=>f('certNumber',e.target.value)}/></div>
          <div><LBL>Lab / Registry</LBL><input className="fi" placeholder="e.g. OFA, Embark, PennHIP" value={form.lab} onChange={e=>f('lab',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'vaccination': return (
        <div style={grid}>
          <div><LBL>Vaccine *</LBL><input className="fi" placeholder="e.g. DA2PP, Rabies, Dewormer" value={form.vaccine} onChange={e=>f('vaccine',e.target.value)}/></div>
          <div><LBL>Date Given</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Expiry Date</LBL><DateInput value={form.expiryDate} onChange={v=>f('expiryDate',v)}/></div>
          <div><LBL>Vet / Clinic</LBL><input className="fi" placeholder="e.g. Dr. Smith" value={form.vet} onChange={e=>f('vet',e.target.value)}/></div>
          <div><LBL>Lot #</LBL><input className="fi" placeholder="Vaccine lot number" value={form.lotNumber} onChange={e=>f('lotNumber',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'vet_visit': return (
        <div style={grid}>
          <div><LBL>Date</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Clinic</LBL><input className="fi" placeholder="Clinic name" value={form.clinic} onChange={e=>f('clinic',e.target.value)}/></div>
          <div style={{gridColumn:'1/-1'}}><LBL>Reason / Diagnosis</LBL><input className="fi" placeholder="e.g. Wellness check" value={form.reason} onChange={e=>f('reason',e.target.value)}/></div>
          <div><LBL>Cost ($)</LBL><input className="fi" type="number" min="0" step="0.01" placeholder="0.00" value={form.costUsd} onChange={e=>f('costUsd',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'weight': return (
        <div style={grid}>
          <div><LBL>Date *</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
          <div><LBL>Weight (lb)</LBL><input className="fi" type="number" min="0" step="0.1" placeholder="0.0" value={form.weightLb} onChange={e=>{f('weightLb',e.target.value); if(e.target.value) f('weightKg',(parseFloat(e.target.value)/2.20462).toFixed(1)); else f('weightKg','');}}/></div>
          <div><LBL>Weight (kg)</LBL><input className="fi" type="number" min="0" step="0.1" placeholder="0.0" value={form.weightKg} onChange={e=>{f('weightKg',e.target.value); if(e.target.value) f('weightLb',(parseFloat(e.target.value)*2.20462).toFixed(1)); else f('weightLb','');}}/></div>
        </div>
      );
      default: return null;
    }
  };

  const renderBatch = (group) => {
    const r = group[0];
    const d = r.data||{};
    let primary='', secondary='', meta=[];
    switch(r.record_type) {
      case 'health_test':
        primary=d.test_type||'Health Test';
        secondary=d.result||(d.cert_number?`#${d.cert_number}`:'');
        if(d.cert_number&&d.result) meta.push(`#${d.cert_number}`);
        if(d.lab) meta.push(d.lab);
        break;
      case 'vaccination':
        primary=d.vaccine||'Vaccination';
        secondary=d.expiry_date?`Exp ${fmtDate(d.expiry_date)}`:'';
        if(d.vet) meta.push(d.vet);
        if(d.lot_number) meta.push(`Lot ${d.lot_number}`);
        break;
      case 'vet_visit':
        primary=d.reason||'Vet Visit';
        secondary=d.clinic||'';
        if(d.cost_usd!=null) meta.push(`$${d.cost_usd}`);
        break;
      case 'weight':
        primary=d.weight_lb?`${d.weight_lb} lb`:d.weight_kg?`${d.weight_kg} kg`:'Weight';
        secondary=d.weight_kg&&d.weight_lb?`${d.weight_kg} kg`:'';
        break;
      default: primary='Record';
    }
    const applyLabels = group.map(x=>puppyShortLabel(puppies.find(p=>p.id===x.puppy_id))).join(', ');
    return (
      <div key={r.batch_id} style={{background:C.bg4,border:`1px solid ${C.border}`,borderRadius:7,padding:'10px 14px',display:'flex',alignItems:'flex-start',gap:12}}>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:700,color:C.cream}}>{primary}</div>
          {secondary&&<div style={{fontSize:12,color:C.gold,marginTop:2}}>{secondary}</div>}
          <div style={{fontSize:11,color:C.slate,marginTop:4,display:'flex',gap:8,flexWrap:'wrap'}}>
            {r.record_date&&<span>{fmtDate(r.record_date)}</span>}
            {meta.map((m,i)=><span key={i}>· {m}</span>)}
            {r.notes&&<span>· {r.notes}</span>}
          </div>
          <div style={{fontSize:11,color:C.emeraldL,marginTop:4}}>Applies to: {applyLabels}</div>
        </div>
        <div style={{display:'flex',gap:4,flexShrink:0}}>
          <button onClick={()=>startEditBatch(group)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:11,padding:'0 4px'}}>Edit</button>
          <button onClick={()=>deleteBatch(r.batch_id)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:16,padding:'0 4px',lineHeight:1}}>×</button>
        </div>
      </div>
    );
  };

  const addLabel = {health_test:'Test',vaccination:'Vaccination',vet_visit:'Visit',weight:'Entry'}[activeType]||'Record';

  return (
    <div style={{background:C.bg2,border:`1px solid ${C.emeraldL}44`,borderRadius:12,padding:24,marginBottom:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontSize:10,color:C.emeraldL,letterSpacing:3,textTransform:'uppercase',marginBottom:2}}>Puppy Health Log</div>
          <div style={{fontFamily:'serif',fontSize:20,fontWeight:700}}>{litterHeadline(litter)}</div>
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',color:C.slate,fontSize:22,cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
      </div>

      <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:`1px solid ${C.border}`,paddingBottom:0,flexWrap:'wrap'}}>
        {TYPES.map(({key,label})=>(
          <button key={key} onClick={()=>switchType(key)} style={{padding:'6px 14px',background:'transparent',border:'none',borderBottom:`2px solid ${activeType===key?C.emeraldL:'transparent'}`,color:activeType===key?C.emeraldL:C.slate,fontSize:12,cursor:'pointer',fontFamily:'sans-serif',fontWeight:activeType===key?700:400,marginBottom:-1,transition:'all 0.15s'}}>
            {label}{allRecords.filter(r=>r.record_type===key).length>0&&<span style={{marginLeft:5,fontSize:10,color:activeType===key?C.emeraldL:C.slate}}>({new Set(allRecords.filter(r=>r.record_type===key).map(r=>r.batch_id)).size})</span>}
          </button>
        ))}
      </div>

      {puppies.length===0 ? (
        <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16,border:`1px dashed ${C.border}`,borderRadius:8}}>Add puppies to this litter first.</div>
      ) : (
        <>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:adding?12:8}}>
            {!adding&&<button className="bos" style={{fontSize:11}} onClick={()=>{setEditingBatchId(null);setSelectedPuppyIds(puppies.map(p=>p.id));setAdding(true);}}>+ Log {addLabel}</button>}
          </div>
          {adding&&(
            <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:16,marginBottom:16}}>
              {renderForm()}
              <div style={{marginTop:4}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <LBL>Applies To</LBL>
                  <button type="button" onClick={()=>setSelectedPuppyIds(selectedPuppyIds.length===puppies.length?[]:puppies.map(p=>p.id))} style={{background:'none',border:'none',color:C.emeraldL,fontSize:11,cursor:'pointer'}}>
                    {selectedPuppyIds.length===puppies.length?'Deselect All':'Select All'}
                  </button>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {puppies.map(p=>(
                    <label key={p.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:C.creamD,background:C.bg4,border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 8px',cursor:'pointer'}}>
                      <input type="checkbox" checked={selectedPuppyIds.includes(p.id)} onChange={()=>setSelectedPuppyIds(prev=>prev.includes(p.id)?prev.filter(id=>id!==p.id):[...prev,p.id])}/>
                      {puppyShortLabel(p)}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{display:'flex',gap:8,marginTop:16}}>
                <button className="bgl" style={{fontSize:12}} onClick={save}>{editingBatchId?'Update':'Save'}</button>
                <button className="bgs" style={{fontSize:12}} onClick={()=>{setAdding(false);setEditingBatchId(null);setForm(EMPTY[activeType]);setSelectedPuppyIds(puppies.map(p=>p.id));}}>Cancel</button>
              </div>
            </div>
          )}

          {loading?(
            <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16}}>Loading…</div>
          ):groups.length===0?(
            <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16,border:`1px dashed ${C.border}`,borderRadius:8}}>No {TYPES.find(t=>t.key===activeType)?.label.toLowerCase()} logged yet.</div>
          ):(
            <div style={{display:'flex',flexDirection:'column',gap:6}}>{groups.map(g=>renderBatch(g))}</div>
          )}
        </>
      )}
    </div>
  );
}

// Same batch-fan-out shape as LitterPuppyHealthLog, but writing into the
// EXISTING dog_records table for real registered dogs, with two things
// puppies don't have: all 6 record types (heat_cycle/breeding are real for
// adult dogs), and the HEALTH_SYNC_MAP writeback firing once per selected
// dog — same shared updates object as DogBackOffice's single-dog save,
// just looped, since the sync target is only ever "which dog", not
// anything about the event itself. dog_records.batch_id is nullable and
// only ever set here; ordinary single-dog Back Office saves stay null.
function DogBulkHealthLog({myDogs, allDogs, breeders, supabase, flash, breederId, onDogsUpdate, onClose}) {
  const TYPES = [
    {key:'health_test', label:'Health Tests'},
    {key:'vaccination', label:'Vaccinations'},
    {key:'vet_visit',   label:'Vet Visits'},
    {key:'heat_cycle',  label:'Heat Cycles'},
    {key:'breeding',    label:'Breeding'},
    {key:'weight',      label:'Weights'},
  ];
  const EMPTY = {
    health_test: {date:'',testType:'OFA Hips',result:'',lab:'',certNumber:'',registry:'OFA',di:'',notes:''},
    vaccination:  {date:'',vaccine:'',expiryDate:'',vet:'',lotNumber:'',notes:''},
    vet_visit:    {date:'',reason:'',notes:'',costUsd:'',clinic:''},
    heat_cycle:   {startDate:'',endDate:'',notes:''},
    breeding:     {partnerDogId:'',date:'',method:'Natural',outcome:'TBD',notes:''},
    weight:       {date:'',weightLb:'',weightKg:'',notes:''},
  };

  const [activeType, setActiveType] = useState('health_test');
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [form, setForm] = useState(EMPTY.health_test);
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString() : '';
  const dogShortLabel = (d) => d ? (d.callName || d.name || 'Dog') : 'Dog';

  // heat_cycle only makes sense for females — DogBackOffice already hides
  // that whole tab for a male dog; the bulk equivalent is filtering the
  // picker instead, since a batch can mix sexes for every other type.
  const selectableDogs = activeType==='heat_cycle' ? myDogs.filter(d=>d.sex==='F') : myDogs;
  const [selectedDogIds, setSelectedDogIds] = useState(selectableDogs.map(d=>d.id));

  useEffect(() => {
    let cancelled = false;
    const dogIds = myDogs.map(d=>d.id);
    if (dogIds.length===0) { setAllRecords([]); setLoading(false); return; }
    // Scoped by dog_id (ownership), not breeder_id — dog_records' own
    // breeder/current-owner authorship split means breeder_id on a given
    // row isn't reliably "me" even for a dog I currently own, but dog_id
    // membership in myDogs always is.
    supabase.from('dog_records').select('*').in('dog_id', dogIds).not('batch_id','is',null)
      .order('record_date', {ascending:false, nullsFirst:false})
      .then(({data,error}) => { if(cancelled) return; if(error) flash('⚠ '+error.message); setAllRecords(data||[]); setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDogs.map(d=>d.id).join(',')]);

  const records = allRecords.filter(r=>r.record_type===activeType);
  const groups = (() => {
    const map = {};
    records.forEach(r => { (map[r.batch_id] = map[r.batch_id] || []).push(r); });
    return Object.values(map);
  })();

  const switchType = (t) => {
    setActiveType(t); setAdding(false); setEditingBatchId(null); setForm(EMPTY[t]);
    setSelectedDogIds((t==='heat_cycle' ? myDogs.filter(d=>d.sex==='F') : myDogs).map(d=>d.id));
  };

  const save = async () => {
    if (selectedDogIds.length===0) { flash('Select at least one dog'); return; }
    let shared = {breeder_id: breederId, record_type: activeType, notes: form.notes||''};
    switch(activeType) {
      case 'health_test':
        if (!form.testType) { flash('Select a test type'); return; }
        shared.record_date = form.date||null;
        shared.data = {test_type:form.testType,result:form.result,lab:form.lab,cert_number:form.certNumber,registry:form.registry||'OFA',di:form.di||''};
        break;
      case 'vaccination':
        if (!form.vaccine) { flash('Enter vaccine name'); return; }
        shared.record_date = form.date||null;
        shared.data = {vaccine:form.vaccine,expiry_date:form.expiryDate,vet:form.vet,lot_number:form.lotNumber};
        break;
      case 'vet_visit':
        shared.record_date = form.date||null;
        shared.data = {reason:form.reason,clinic:form.clinic,cost_usd:form.costUsd?parseFloat(form.costUsd):null};
        break;
      case 'heat_cycle':
        if (!form.startDate) { flash('Enter start date'); return; }
        shared.record_date = form.startDate||null;
        shared.data = {end_date:form.endDate||null};
        break;
      case 'breeding':
        if (!form.date) { flash('Enter breeding date'); return; }
        shared.record_date = form.date||null;
        shared.data = {partner_dog_id:form.partnerDogId||null,method:form.method,outcome:form.outcome};
        break;
      case 'weight':
        if (!form.date) { flash('Enter date'); return; }
        shared.record_date = form.date||null;
        shared.data = {weight_lb:form.weightLb?parseFloat(form.weightLb):null, weight_kg:form.weightKg?parseFloat(form.weightKg):null};
        break;
      default: return;
    }

    if (editingBatchId) {
      const { error: delErr } = await supabase.from('dog_records').delete().eq('batch_id', editingBatchId);
      if (delErr) { flash('⚠ '+delErr.message); return; }
      const rows = selectedDogIds.map(dog_id => ({...shared, batch_id: editingBatchId, dog_id}));
      const { data, error } = await supabase.from('dog_records').insert(rows).select();
      if (error) { flash('⚠ '+error.message); return; }
      setAllRecords(prev => [...(data||[]), ...prev.filter(r=>r.batch_id!==editingBatchId)]);
    } else {
      const batchId = crypto.randomUUID();
      const rows = selectedDogIds.map(dog_id => ({...shared, batch_id: batchId, dog_id}));
      const { data, error } = await supabase.from('dog_records').insert(rows).select();
      if (error) { flash('⚠ '+error.message); return; }
      setAllRecords(prev => [...(data||[]), ...prev]);
    }

    // HEALTH_SYNC_MAP writeback — same shared updates object DogBackOffice
    // computes for one dog, applied once per selected dog here. Fires on
    // both create and edit, matching DogBackOffice's own save(). Removing a
    // dog from an edited batch never reverts its synced column — same as
    // deleting a record never reverts it today; not a new gap.
    if (activeType === 'health_test') {
      const sync = HEALTH_SYNC_MAP[form.testType];
      if (sync) {
        const updates = {};
        const val = sync.useResult ? form.result : form.certNumber;
        if (val) updates[sync.col] = val;
        if (sync.certCol && form.certNumber) updates[sync.certCol] = form.certNumber;
        if (sync.registryCol && form.registry) updates[sync.registryCol] = form.registry;
        if (sync.diCol && form.di) updates[sync.diCol] = form.di;
        if (Object.keys(updates).length) {
          const jsUpdates = {};
          if (updates[sync.col]!==undefined) jsUpdates[sync.jsKey] = updates[sync.col];
          if (sync.certCol && updates[sync.certCol]!==undefined) jsUpdates[sync.certJsKey] = updates[sync.certCol];
          if (sync.registryCol && updates[sync.registryCol]!==undefined) jsUpdates[sync.registryJsKey] = updates[sync.registryCol];
          if (sync.diCol && updates[sync.diCol]!==undefined) jsUpdates[sync.diJsKey] = updates[sync.diCol];
          const patched = [];
          for (const dogId of selectedDogIds) {
            await supabase.from('dogs').update(updates).eq('id', dogId);
            const dog = myDogs.find(d=>d.id===dogId);
            if (dog) patched.push({...dog, ...jsUpdates});
          }
          if (patched.length) onDogsUpdate(patched);
        }
      }
    }

    setAdding(false); setForm(EMPTY[activeType]); setEditingBatchId(null);
    setSelectedDogIds(selectableDogs.map(d=>d.id));
    flash(editingBatchId ? 'Health event updated ✓' : 'Health event logged ✓');
  };

  const startEditBatch = (group) => {
    const r = group[0];
    const d = r.data||{};
    let mapped = {};
    switch(r.record_type) {
      case 'health_test': mapped = {date:r.record_date||'', testType:d.test_type||'OFA Hips', result:d.result||'', lab:d.lab||'', certNumber:d.cert_number||'', registry:d.registry||'OFA', di:d.di||'', notes:r.notes||''}; break;
      case 'vaccination': mapped = {date:r.record_date||'', vaccine:d.vaccine||'', expiryDate:d.expiry_date||'', vet:d.vet||'', lotNumber:d.lot_number||'', notes:r.notes||''}; break;
      case 'vet_visit': mapped = {date:r.record_date||'', reason:d.reason||'', clinic:d.clinic||'', costUsd:d.cost_usd!=null?String(d.cost_usd):'', notes:r.notes||''}; break;
      case 'heat_cycle': mapped = {startDate:r.record_date||'', endDate:d.end_date||'', notes:r.notes||''}; break;
      case 'breeding': mapped = {date:r.record_date||'', partnerDogId:d.partner_dog_id||'', method:d.method||'Natural', outcome:d.outcome||'TBD', notes:r.notes||''}; break;
      case 'weight': mapped = {date:r.record_date||'', weightLb:d.weight_lb!=null?String(d.weight_lb):'', weightKg:d.weight_kg!=null?String(d.weight_kg):'', notes:r.notes||''}; break;
      default: return;
    }
    setActiveType(r.record_type);
    setForm({...EMPTY[r.record_type], ...mapped});
    setSelectedDogIds(group.map(x=>x.dog_id));
    setEditingBatchId(r.batch_id);
    setAdding(true);
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Delete this health event? This removes it for all dogs it applied to.')) return;
    const { error } = await supabase.from('dog_records').delete().eq('batch_id', batchId);
    if (error) { flash('⚠ '+error.message); return; }
    setAllRecords(prev => prev.filter(r=>r.batch_id!==batchId));
    flash('Health event deleted');
  };

  const LBL = ({children}) => <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:'uppercase',display:'block',marginBottom:3}}>{children}</label>;

  const renderForm = () => {
    const grid = {display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10};
    switch(activeType) {
      case 'health_test': {
        const vocabKey = HEALTH_SYNC_MAP[form.testType]?.col;
        const vocab = TEST_RESULT_VOCAB[vocabKey];
        const isLegacyResult = form.result && vocab && !vocab.includes(form.result);
        const isHipsTest = form.testType === 'OFA Hips';
        const registry = form.registry || 'OFA';
        const isPennHip = isHipsTest && registry === 'PennHIP';
        return (
          <div style={grid}>
            <div><LBL>Test Type *</LBL><select className="fi" value={form.testType} onChange={e=>f('testType',e.target.value)}>{HEALTH_TEST_TYPES.map(t=><option key={t} value={t}>{t==='OFA Hips'?'Hips (OFA / PennHIP)':t}</option>)}</select></div>
            <div><LBL>Date</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
            {isHipsTest && (
              <div><LBL>Registry</LBL>
                <select className="fi" value={registry} onChange={e=>f('registry',e.target.value)}>
                  <option value="OFA">OFA</option>
                  <option value="PennHIP">PennHIP</option>
                </select>
              </div>
            )}
            <div><LBL>{isPennHip?'Distraction Index (reference only)':'Result'}</LBL>
              {isPennHip ? (
                <input className="fi" placeholder="e.g. 0.35 (not used for pass/fail)" value={form.di} onChange={e=>f('di',e.target.value)}/>
              ) : vocab ? (
                <select className="fi" value={form.result} onChange={e=>f('result',e.target.value)}>
                  <option value="">Select…</option>
                  {isLegacyResult && <option value={form.result}>{form.result} (legacy entry — re-select to update)</option>}
                  {vocab.map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input className="fi" placeholder="e.g. Excellent, Clear, Pass" value={form.result} onChange={e=>f('result',e.target.value)}/>
              )}
            </div>
            <div><LBL>{isPennHip?'PennHIP Report #':'Cert / Clearance #'}</LBL><input className="fi" placeholder={isPennHip?'e.g. PH-12345':'e.g. OFA12345E24M'} value={form.certNumber} onChange={e=>f('certNumber',e.target.value)}/></div>
            {!isHipsTest && (
              <div><LBL>Lab / Registry</LBL><input className="fi" placeholder="e.g. OFA, Embark, PennHIP" value={form.lab} onChange={e=>f('lab',e.target.value)}/></div>
            )}
            <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
            {HEALTH_SYNC_MAP[form.testType]&&(form.result||form.certNumber)&&(
              <div style={{gridColumn:'1/-1',fontSize:11,color:C.emeraldL}}>✓ Will also update {selectedDogIds.length} dog record{selectedDogIds.length!==1?'s':''} ({HEALTH_SYNC_MAP[form.testType].useResult?'result':'cert #'} → {HEALTH_SYNC_MAP[form.testType].jsKey})</div>
            )}
          </div>
        );
      }
      case 'vaccination': return (
        <div style={grid}>
          <div><LBL>Vaccine *</LBL><input className="fi" placeholder="e.g. DA2PP, Rabies" value={form.vaccine} onChange={e=>f('vaccine',e.target.value)}/></div>
          <div><LBL>Date Given</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Expiry Date</LBL><DateInput value={form.expiryDate} onChange={v=>f('expiryDate',v)}/></div>
          <div><LBL>Vet / Clinic</LBL><input className="fi" placeholder="e.g. Dr. Smith" value={form.vet} onChange={e=>f('vet',e.target.value)}/></div>
          <div><LBL>Lot #</LBL><input className="fi" placeholder="Vaccine lot number" value={form.lotNumber} onChange={e=>f('lotNumber',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'vet_visit': return (
        <div style={grid}>
          <div><LBL>Date</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Clinic</LBL><input className="fi" placeholder="Clinic name" value={form.clinic} onChange={e=>f('clinic',e.target.value)}/></div>
          <div style={{gridColumn:'1/-1'}}><LBL>Reason / Diagnosis</LBL><input className="fi" placeholder="e.g. Annual exam" value={form.reason} onChange={e=>f('reason',e.target.value)}/></div>
          <div><LBL>Cost ($)</LBL><input className="fi" type="number" min="0" step="0.01" placeholder="0.00" value={form.costUsd} onChange={e=>f('costUsd',e.target.value)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'heat_cycle': return (
        <div style={grid}>
          <div><LBL>Start Date *</LBL><DateInput value={form.startDate} onChange={v=>f('startDate',v)}/></div>
          <div><LBL>End Date</LBL><DateInput value={form.endDate} onChange={v=>f('endDate',v)}/></div>
          <div style={{gridColumn:'1/-1'}}><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'breeding': return (
        <div style={grid}>
          <div><LBL>Date *</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Method</LBL>
            <select className="fi" value={form.method} onChange={e=>f('method',e.target.value)}>
              {['Natural','AI','Surgical AI'].map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={{gridColumn:'1/-1'}}><LBL>Partner Dog (same partner applied to every selected dog)</LBL>
            <DogAutocomplete value={form.partnerDogId} onChange={v=>f('partnerDogId',v)} dogs={allDogs} breeders={breeders} placeholder="Search all dogs…"/>
          </div>
          <div><LBL>Outcome</LBL>
            <select className="fi" value={form.outcome} onChange={e=>f('outcome',e.target.value)}>
              {['TBD','Pregnant','Whelped','Missed'].map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
        </div>
      );
      case 'weight': return (
        <div style={grid}>
          <div><LBL>Date *</LBL><DateInput value={form.date} onChange={v=>f('date',v)}/></div>
          <div><LBL>Notes</LBL><input className="fi" placeholder="Optional" value={form.notes} onChange={e=>f('notes',e.target.value)}/></div>
          <div><LBL>Weight (lb)</LBL><input className="fi" type="number" min="0" step="0.1" placeholder="0.0" value={form.weightLb} onChange={e=>{f('weightLb',e.target.value); if(e.target.value) f('weightKg',(parseFloat(e.target.value)/2.20462).toFixed(1)); else f('weightKg','');}}/></div>
          <div><LBL>Weight (kg)</LBL><input className="fi" type="number" min="0" step="0.1" placeholder="0.0" value={form.weightKg} onChange={e=>{f('weightKg',e.target.value); if(e.target.value) f('weightLb',(parseFloat(e.target.value)*2.20462).toFixed(1)); else f('weightLb','');}}/></div>
        </div>
      );
      default: return null;
    }
  };

  const renderBatch = (group) => {
    const r = group[0];
    const d = r.data||{};
    let primary='', secondary='', meta=[];
    switch(r.record_type) {
      case 'health_test':
        primary=d.test_type||'Health Test';
        secondary=d.result||(d.cert_number?`#${d.cert_number}`:'');
        if(d.cert_number&&d.result) meta.push(`#${d.cert_number}`);
        if(d.lab) meta.push(d.lab);
        break;
      case 'vaccination':
        primary=d.vaccine||'Vaccination';
        secondary=d.expiry_date?`Exp ${fmtDate(d.expiry_date)}`:'';
        if(d.vet) meta.push(d.vet);
        if(d.lot_number) meta.push(`Lot ${d.lot_number}`);
        break;
      case 'vet_visit':
        primary=d.reason||'Vet Visit';
        secondary=d.clinic||'';
        if(d.cost_usd!=null) meta.push(`$${d.cost_usd}`);
        break;
      case 'heat_cycle':
        primary='Heat Cycle';
        secondary=d.end_date?`${fmtDate(r.record_date)} – ${fmtDate(d.end_date)}`:fmtDate(r.record_date);
        break;
      case 'breeding': {
        const partner = d.partner_dog_id&&(allDogs||{})[d.partner_dog_id];
        primary=partner?`× ${partner.name}`:'Breeding';
        secondary=d.outcome||'';
        if(d.method) meta.push(d.method);
        break;
      }
      case 'weight':
        primary=d.weight_lb?`${d.weight_lb} lb`:d.weight_kg?`${d.weight_kg} kg`:'Weight';
        secondary=d.weight_kg&&d.weight_lb?`${d.weight_kg} kg`:'';
        break;
      default: primary='Record';
    }
    const applyLabels = group.map(x=>dogShortLabel(myDogs.find(d=>d.id===x.dog_id))).join(', ');
    return (
      <div key={r.batch_id} style={{background:C.bg4,border:`1px solid ${C.border}`,borderRadius:7,padding:'10px 14px',display:'flex',alignItems:'flex-start',gap:12}}>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:700,color:C.cream}}>{primary}</div>
          {secondary&&<div style={{fontSize:12,color:C.gold,marginTop:2}}>{secondary}</div>}
          <div style={{fontSize:11,color:C.slate,marginTop:4,display:'flex',gap:8,flexWrap:'wrap'}}>
            {r.record_type!=='heat_cycle'&&r.record_date&&<span>{fmtDate(r.record_date)}</span>}
            {meta.map((m,i)=><span key={i}>· {m}</span>)}
            {r.notes&&<span>· {r.notes}</span>}
          </div>
          <div style={{fontSize:11,color:C.emeraldL,marginTop:4}}>Applies to: {applyLabels}</div>
        </div>
        <div style={{display:'flex',gap:4,flexShrink:0}}>
          <button onClick={()=>startEditBatch(group)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:11,padding:'0 4px'}}>Edit</button>
          <button onClick={()=>deleteBatch(r.batch_id)} style={{background:'none',border:'none',color:C.slate,cursor:'pointer',fontSize:16,padding:'0 4px',lineHeight:1}}>×</button>
        </div>
      </div>
    );
  };

  const addLabel = {health_test:'Test',vaccination:'Vaccination',vet_visit:'Visit',heat_cycle:'Cycle',breeding:'Record',weight:'Entry'}[activeType]||'Record';

  return (
    <div style={{background:C.bg2,border:`1px solid ${C.emeraldL}44`,borderRadius:12,padding:24,marginBottom:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontSize:10,color:C.emeraldL,letterSpacing:3,textTransform:'uppercase',marginBottom:2}}>Bulk Health Log</div>
          <div style={{fontFamily:'serif',fontSize:20,fontWeight:700}}>Apply one event to multiple dogs</div>
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',color:C.slate,fontSize:22,cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
      </div>

      <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:`1px solid ${C.border}`,paddingBottom:0,flexWrap:'wrap'}}>
        {TYPES.map(({key,label})=>(
          <button key={key} onClick={()=>switchType(key)} style={{padding:'6px 14px',background:'transparent',border:'none',borderBottom:`2px solid ${activeType===key?C.emeraldL:'transparent'}`,color:activeType===key?C.emeraldL:C.slate,fontSize:12,cursor:'pointer',fontFamily:'sans-serif',fontWeight:activeType===key?700:400,marginBottom:-1,transition:'all 0.15s'}}>
            {label}{allRecords.filter(r=>r.record_type===key).length>0&&<span style={{marginLeft:5,fontSize:10,color:activeType===key?C.emeraldL:C.slate}}>({new Set(allRecords.filter(r=>r.record_type===key).map(r=>r.batch_id)).size})</span>}
          </button>
        ))}
      </div>

      {selectableDogs.length===0 ? (
        <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16,border:`1px dashed ${C.border}`,borderRadius:8}}>
          {activeType==='heat_cycle' ? 'No female dogs on file.' : 'Add dogs first.'}
        </div>
      ) : (
        <>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:adding?12:8}}>
            {!adding&&<button className="bos" style={{fontSize:11}} onClick={()=>{setEditingBatchId(null);setSelectedDogIds(selectableDogs.map(d=>d.id));setAdding(true);}}>+ Log {addLabel}</button>}
          </div>
          {adding&&(
            <div style={{background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:16,marginBottom:16}}>
              {renderForm()}
              <div style={{marginTop:4}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <LBL>Applies To</LBL>
                  <button type="button" onClick={()=>setSelectedDogIds(selectedDogIds.length===selectableDogs.length?[]:selectableDogs.map(d=>d.id))} style={{background:'none',border:'none',color:C.emeraldL,fontSize:11,cursor:'pointer'}}>
                    {selectedDogIds.length===selectableDogs.length?'Deselect All':'Select All'}
                  </button>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {selectableDogs.map(d=>(
                    <label key={d.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:C.creamD,background:C.bg4,border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 8px',cursor:'pointer'}}>
                      <input type="checkbox" checked={selectedDogIds.includes(d.id)} onChange={()=>setSelectedDogIds(prev=>prev.includes(d.id)?prev.filter(id=>id!==d.id):[...prev,d.id])}/>
                      {dogShortLabel(d)}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{display:'flex',gap:8,marginTop:16}}>
                <button className="bgl" style={{fontSize:12}} onClick={save}>{editingBatchId?'Update':'Save'}</button>
                <button className="bgs" style={{fontSize:12}} onClick={()=>{setAdding(false);setEditingBatchId(null);setForm(EMPTY[activeType]);setSelectedDogIds(selectableDogs.map(d=>d.id));}}>Cancel</button>
              </div>
            </div>
          )}

          {loading?(
            <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16}}>Loading…</div>
          ):groups.length===0?(
            <div style={{color:C.slate,fontSize:13,textAlign:'center',padding:16,border:`1px dashed ${C.border}`,borderRadius:8}}>No {TYPES.find(t=>t.key===activeType)?.label.toLowerCase()} logged yet.</div>
          ):(
            <div style={{display:'flex',flexDirection:'column',gap:6}}>{groups.map(g=>renderBatch(g))}</div>
          )}
        </>
      )}
    </div>
  );
}

function BulkImportManager({breeder, user, supabase, flash, dogs, breeders, onDogsImported, designerBreeds = []}){
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  // Load this breeder's imports + signed thumbnail URLs on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('dog_imports')
        .select('id,photo_url,status,created_at,extracted_data')
        .eq('breeder_id', breeder.id)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) { flash("⚠ " + error.message); setLoading(false); return; }
      const rows = data || [];
      const paths = rows.map(r => r.photo_url).filter(Boolean);
      const urlByPath = {};
      if (paths.length) {
        const { data: signed } = await supabase.storage
          .from('registration-papers')
          .createSignedUrls(paths, 3600);
        (signed || []).forEach(s => { if (s.path && s.signedUrl) urlByPath[s.path] = s.signedUrl; });
      }
      if (cancelled) return;
      setImports(rows.map(r => ({ ...r, thumb: urlByPath[r.photo_url] || null })));
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breeder.id]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || uploading) return;
    setUploading(true);
    for (const file of files) {
      const id = crypto.randomUUID();
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `${user.id}/${id}.${ext}`;          // first folder MUST be auth.uid() (storage RLS)
      const localThumb = URL.createObjectURL(file);
      try {
        const { error: upErr } = await supabase.storage
          .from('registration-papers')
          .upload(path, file, { upsert: true });
        if (upErr) throw upErr;
        const { error: insErr } = await supabase
          .from('dog_imports')
          .insert({ id, breeder_id: breeder.id, photo_url: path, status: 'uploaded' });
        if (insErr) throw insErr;
        setImports(prev => [{ id, photo_url: path, status: 'uploaded', created_at: new Date().toISOString(), thumb: localThumb }, ...prev]);
      } catch (e) {
        flash("⚠ Upload failed: " + e.message);
      }
    }
    setUploading(false);
    flash("Upload complete ✓");
  };

  const removeImport = async (imp) => {
    if (!window.confirm("Remove this upload?")) return;
    setImports(prev => prev.filter(i => i.id !== imp.id));
    if (imp.photo_url) await supabase.storage.from('registration-papers').remove([imp.photo_url]);
    const { error } = await supabase.from('dog_imports').delete().eq('id', imp.id);
    if (error) flash("⚠ " + error.message);
  };

  // OCR a single paper via the secure /api/extract serverless proxy (the
  // Anthropic key lives there, not in the client). We send the Supabase JWT so
  // the function can verify ownership before spending API credits.
  const processImport = async (imp) => {
    setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'processing'} : i));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session && session.access_token;
      if (!token) throw new Error('Not signed in');
      const resp = await fetch('/api/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ importId: imp.id }),
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || ('HTTP ' + resp.status));
      if (result.status === 'extracted') {
        setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'extracted', extracted_data: result.extracted_data} : i));
        flash('Extracted ✓');
      } else {
        setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'error', extracted_data: {error: result.error || 'Extraction failed'}} : i));
        flash('⚠ ' + (result.error || 'Extraction failed'));
      }
    } catch (e) {
      setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'error'} : i));
      flash('⚠ ' + e.message);
    }
  };

  // S4a: turn a reviewed import into dog rows and insert them in ONE atomic call
  // (all-or-nothing per paper). On success flip the import to 'saved' and merge the
  // new dogs into the parent's list so they show immediately.
  const [savingAll, setSavingAll] = useState(false);
  const [pendingCollision, setPendingCollision] = useState(null);

  const saveImport = async (imp) => {
    const plan = buildImportPlan(imp.extracted_data || {}, breeder.id, { designerBreeds });
    if (!plan.rows.length) { flash('⚠ Nothing to save — needs at least one dog with both a name and a reg #'); return; }

    let finalRows = plan.rows, claimUpdates = [];
    const regNums = plan.rows.map(r => r.reg_num).filter(Boolean);
    if (regNums.length) {
      const { data: existing } = await supabase.from('dogs').select('id,name,reg_num,breeder_id,record_owner_id').in('reg_num', regNums);
      const existingMap = {};
      (existing||[]).forEach(d => { if (d.reg_num) existingMap[d.reg_num.toLowerCase()] = d; });
      const collisions = plan.rows.filter(r => r.reg_num && existingMap[r.reg_num.toLowerCase()]);
      if (collisions.length) {
        const decisions = await new Promise(resolve => setPendingCollision({collisions, existingMap, resolve}));
        setPendingCollision(null);
        if (!decisions) { flash('Save cancelled'); return; }
        ({finalRows, claimUpdates} = applyCollisionDecisions(plan.rows, decisions, existingMap, breeder.id));
      }
    }

    setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'saving'} : i));
    try {
      if (finalRows.length) {
        const { error } = await supabase.from('dogs').insert(finalRows);
        if (error) throw error;
      }
      const { data: { session } } = await supabase.auth.getSession();
      for (const upd of claimUpdates) {
        const resp = await fetch('/api/claim-dog', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ dogId: upd.id }),
        });
        if (!resp.ok) { const e = await resp.json(); throw new Error(e.error || 'Claim failed'); }
      }
      const { error: stErr } = await supabase.from('dog_imports').update({ status:'saved' }).eq('id', imp.id);
      if (stErr) throw stErr;
      setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'saved'} : i));
      if (onDogsImported) onDogsImported(finalRows);
      flash(`Imported ${finalRows.length} dog${finalRows.length===1?'':'s'} ✓`);
    } catch (e) {
      setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'reviewed'} : i));
      flash('⚠ Save failed: ' + e.message);
    }
  };

  const saveAllReviewed = async () => {
    const list = imports.filter(i => i.status === 'reviewed');
    if (!list.length || savingAll) return;
    setSavingAll(true);
    let papers = 0, total = 0, failed = 0, skipped = 0;
    for (const imp of list) {
      const plan = buildImportPlan(imp.extracted_data || {}, breeder.id, { designerBreeds });
      if (!plan.rows.length) continue;

      const regNums = plan.rows.map(r => r.reg_num).filter(Boolean);
      if (regNums.length) {
        const { data: existing } = await supabase.from('dogs').select('id,reg_num').in('reg_num', regNums);
        if ((existing||[]).length > 0) { skipped++; continue; }
      }

      setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'saving'} : i));
      try {
        const { error } = await supabase.from('dogs').insert(plan.rows);
        if (error) throw error;
        await supabase.from('dog_imports').update({ status:'saved' }).eq('id', imp.id);
        setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'saved'} : i));
        if (onDogsImported) onDogsImported(plan.rows);
        papers++; total += plan.rows.length;
      } catch (e) {
        setImports(prev => prev.map(i => i.id === imp.id ? {...i, status:'reviewed'} : i));
        failed++;
      }
    }
    setSavingAll(false);
    flash(`Imported ${papers} paper${papers===1?'':'s'} — ${total} dogs${failed?`; ${failed} failed`:''}${skipped?`; ${skipped} skipped (reg# conflicts — save individually to resolve)`:''} ✓`);
  };

  return (
    <div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:16}}>
        <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:8}}>Bulk Import</div>
        <div style={{fontSize:13,color:C.slate,lineHeight:1.7,marginBottom:20}}>
          Upload photos of registration papers, then click <strong>Process</strong> to extract the details. Works with single registration certificates and multi-generation pedigrees. Once extracted, <strong>click an item</strong> to review &amp; edit the fields.
        </div>

        <label style={{display:"block",border:`2px dashed ${C.gold}55`,borderRadius:10,padding:"28px 16px",textAlign:"center",cursor:uploading?"default":"pointer",background:C.bg4,opacity:uploading?0.6:1}}>
          <div style={{fontSize:32,marginBottom:8}}>📄</div>
          <div style={{fontSize:14,color:C.cream,fontWeight:600,marginBottom:4}}>{uploading?"Uploading…":"Click to upload registration papers"}</div>
          <div style={{fontSize:12,color:C.slate}}>JPG or PNG · select multiple at once</div>
          <input type="file" accept="image/*" multiple style={{display:"none"}} disabled={uploading}
            onChange={e=>{onFiles(e.target.files); e.target.value="";}}/>
        </label>
      </div>

      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:12}}>
          <div style={{fontSize:13,color:C.slate}}>{imports.length} uploaded</div>
          {imports.some(i=>i.status==='reviewed') && (
            <button type="button" className="bgl" style={{fontSize:12}} disabled={savingAll} onClick={saveAllReviewed}>
              {savingAll ? 'Saving…' : `Save All Reviewed (${imports.filter(i=>i.status==='reviewed').length})`}
            </button>
          )}
        </div>
        {loading ? (
          <div style={{padding:"20px",textAlign:"center",color:C.slate,fontSize:13,fontStyle:"italic"}}>Loading…</div>
        ) : imports.length === 0 ? (
          <div style={{padding:"20px",textAlign:"center",border:`1px dashed ${C.border}`,borderRadius:8,color:C.slate,fontSize:13,fontStyle:"italic"}}>
            No imports yet. Upload registration papers above to get started.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {imports.map(imp => {
              const processing = imp.status === 'processing';
              const saving = imp.status === 'saving';
              const reviewable = imp.status==='extracted' || imp.status==='reviewed';
              const badgeColor = imp.status==='error' ? C.red : imp.status==='extracted' ? C.emeraldL : imp.status==='reviewed' ? C.sapphireL : imp.status==='saved' ? C.violetL : C.goldL;
              const preview = importPreview(imp.extracted_data);
              return (
              <div key={imp.id} onClick={()=>{ if(reviewable) setReviewing(imp); }} title={reviewable?"Click to review & edit":undefined} style={{background:C.bg4,border:`1px solid ${C.border}`,borderRadius:8,padding:10,cursor:reviewable?"pointer":"default"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:48,height:48,borderRadius:6,overflow:"hidden",background:C.bg2,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>
                    {imp.thumb ? <img src={imp.thumb} alt="registration paper" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : "📄"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,color:C.cream,fontWeight:600}}>Registration paper</div>
                    <div style={{fontSize:11,color:C.slate}}>{new Date(imp.created_at).toLocaleDateString()}</div>
                  </div>
                  <span style={{fontSize:10,letterSpacing:1,textTransform:"uppercase",color:badgeColor,border:`1px solid ${badgeColor}55`,borderRadius:20,padding:"3px 10px"}}>{processing?"processing…":saving?"saving…":imp.status}</span>
                  {(imp.status==='uploaded' || imp.status==='error') && (
                    <button type="button" className="bos" style={{fontSize:11}} disabled={processing} onClick={(e)=>{e.stopPropagation(); processImport(imp);}}>
                      {imp.status==='error' ? 'Retry' : 'Process'}
                    </button>
                  )}
                  {imp.status==='reviewed' && (
                    <button type="button" className="bgl" style={{fontSize:11}} disabled={savingAll} onClick={(e)=>{e.stopPropagation(); saveImport(imp);}}>
                      Save to Registry
                    </button>
                  )}
                  <button type="button" onClick={(e)=>{e.stopPropagation(); removeImport(imp);}} title="Remove" disabled={processing} style={{background:"none",border:"none",color:C.slate,cursor:processing?"default":"pointer",fontSize:18,lineHeight:1,opacity:processing?0.5:1}}>×</button>
                </div>
                {preview.length > 0 && (
                  <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`,display:"flex",flexWrap:"wrap",gap:"4px 16px"}}>
                    {preview.map(([k,v]) => (
                      <div key={k} style={{fontSize:11}}><span style={{color:C.slate,opacity:0.75}}>{k}:</span> <span style={{color:imp.status==='error'?C.red:C.creamD}}>{v}</span></div>
                    ))}
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
      </div>
      {reviewing && (
        <ImportReviewModal
          imp={reviewing}
          supabase={supabase}
          flash={flash}
          designerBreeds={designerBreeds}
          onClose={()=>setReviewing(null)}
          onSaved={(u)=>setImports(prev=>prev.map(i=>i.id===u.id?{...i,...u}:i))}
        />
      )}
      {pendingCollision && (
        <CollisionResolutionModal
          collisions={pendingCollision.collisions}
          existingMap={pendingCollision.existingMap}
          breeders={breeders||{}}
          onResolve={d=>pendingCollision.resolve(d)}
          onCancel={()=>pendingCollision.resolve(null)}
        />
      )}
    </div>
  );
}

// ── IMPORT REVIEW MODAL (Phase 11 Session 3) ─────────────────────────────────
// Side-by-side review of an extracted paper: photo (click to zoom) on the left,
// scrollable editable form (dog + 30 path-notation ancestors + other_fields) on
// the right. Save writes the cleaned extracted_data back and flips status to
// 'reviewed'. Empty fields are shown but dimmed ("not detected").
function ImportReviewModal({imp, supabase, flash, onClose, onSaved, designerBreeds = []}){
  const ed = imp.extracted_data || {};
  const { other_fields, ...flat } = ed;
  const ownFlags = Object.fromEntries(Object.entries(flat).filter(([k]) => k.startsWith('_own')));
  // Exclude all private _-prefixed keys (includes _own_* and _is_designer) from editable fields.
  const fieldsClean = Object.fromEntries(Object.entries(flat).filter(([k]) => !k.startsWith('_')));
  const [fields, setFields] = useState(fieldsClean);
  const [ownFocusDog, setOwnFocusDog] = useState(ownFlags._own_focus !== false);
  const [ownedAncestors, setOwnedAncestors] = useState(
    () => new Set(ANCESTOR_PATHS.filter(p => ownFlags[`_own_${p}`]))
  );
  const [otherFields, setOtherFields] = useState(other_fields || {});
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [showOther, setShowOther] = useState(false);
  // Auto-seed from prior save (_is_designer flag) or by matching focus breed against the catalog.
  // User can override with the toggle before saving.
  const [isDesignerImport, setIsDesignerImport] = useState(() => {
    if (ed._is_designer === true)  return true;
    if (ed._is_designer === false) return false;
    const focusBreed = (flat.breed || '').toLowerCase().trim();
    return focusBreed.length > 0 && designerBreeds.some(db => db.name.toLowerCase() === focusBreed);
  });

  // FIX 4: an OFA score inside an ancestor's "*_color_markings" gets its own input,
  // split from the remaining color text. Each half is held in local state per the
  // original key, then recombined into that one key at save (Option B — the raw
  // other_fields key stays the single source of truth).
  const [colorSplits, setColorSplits] = useState(() => {
    const m = {};
    Object.entries(other_fields||{}).forEach(([k,v]) => {
      if (!/^(sire|dam)[_\s]/i.test(k)) return;          // ancestor keys only
      if (!/color[_\s]*markings/i.test(k)) return;
      const mm = String(v).match(OFA_RE);
      if (mm) m[k] = { ofa: mm[0], rest: String(v).replace(mm[0],'').replace(/\s+/g,' ').trim() };
    });
    return m;
  });

  const set = (k, v) => setFields(p => ({...p, [k]: v}));
  const setOther = (k, v) => setOtherFields(p => ({...p, [k]: v}));
  const inputStyle = (v) => ({ borderColor: (v && String(v).trim()) ? C.border : `${C.border}55`, opacity: (v && String(v).trim()) ? 1 : 0.6 });
  const labelStyle = {fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3};
  const sectionHead = {fontSize:11,color:C.gold,letterSpacing:2,textTransform:"uppercase",margin:"18px 0 10px",fontWeight:700};

  const save = async () => {
    setSaving(true);
    const out = {};
    Object.entries(fields).forEach(([k, v]) => { const t = (v == null ? '' : String(v)).trim(); if (t) out[k] = t; });
    // FIX 1: persist path-derived sex for any real ancestor (has a name) left blank.
    // Definitional, not a guess. Breed/DOB defaults stay display-only (not written).
    ANCESTOR_PATHS.forEach(p => { const k = `${p}_sex`, s = impliedSex(p); if (s && !out[k] && out[`${p}_name`]) out[k] = s; });
    const of = {};
    Object.entries(otherFields).forEach(([k, v]) => { const t = (v == null ? '' : String(v)).trim(); if (t) of[k] = t; });
    // FIX 4: rejoin each split OFA + color back into its single original key.
    Object.entries(colorSplits).forEach(([k, {ofa, rest}]) => {
      const joined = `${rest} ${ofa}`.replace(/\s+/g,' ').trim();
      if (joined) of[k] = joined; else delete of[k];
    });
    if (Object.keys(of).length) out.other_fields = of;
    out._own_focus = ownFocusDog;
    out._is_designer = isDesignerImport;
    [...ownedAncestors].forEach(p => { out[`_own_${p}`] = true; });
    const { error } = await supabase.from('dog_imports').update({ extracted_data: out, status: 'reviewed' }).eq('id', imp.id);
    setSaving(false);
    if (error) { flash('⚠ ' + error.message); return; }
    flash('Saved ✓');
    onSaved({ ...imp, extracted_data: out, status: 'reviewed' });
    onClose();
  };

  // Render one labeled field (input, or a Sex select). Works for the dog (key
  // 'sex') and ancestors (key '<path>_sex'). A plain helper, not a component, so
  // the inputs don't remount/lose focus on each keystroke.
  const field = (key, label, displayDefault='') => {
    const isSex = key === 'sex' || key.endsWith('_sex');
    const shown = (fields[key]||'') || displayDefault;   // default shows only while state is empty
    return (
      <div key={key}>
        <label style={labelStyle}>{label}</label>
        {isSex ? (
          <select className="fi" value={shown} onChange={e=>set(key,e.target.value)} style={inputStyle(fields[key])}>
            <option value="">—</option><option value="Male">Male</option><option value="Female">Female</option>
          </select>
        ) : (
          <input className="fi" value={shown} placeholder="—" onChange={e=>set(key,e.target.value)} style={inputStyle(fields[key])}/>
        )}
      </div>
    );
  };

  // Free-form other_fields input (no Sex select), bound to otherFields/setOther.
  const extraField = (key, label) => (
    <div key={key}>
      <label style={labelStyle}>{label}</label>
      <input className="fi" value={otherFields[key]||''} placeholder="—" onChange={e=>setOther(key,e.target.value)} style={inputStyle(otherFields[key])}/>
    </div>
  );

  // FIX 4: two inputs (OFA + remaining color) backed by colorSplits[key]. Each half
  // owns its own state so typing/spaces behave; they recombine only at save().
  const colorSplitField = (key) => ([
    <div key={`${key}__ofa`}>
      <label style={labelStyle}>OFA</label>
      <input className="fi" value={colorSplits[key].ofa} placeholder="—"
        onChange={e=>setColorSplits(p=>({...p,[key]:{...p[key],ofa:e.target.value}}))} style={inputStyle(colorSplits[key].ofa)}/>
    </div>,
    <div key={`${key}__rest`}>
      <label style={labelStyle}>Color / Markings</label>
      <input className="fi" value={colorSplits[key].rest} placeholder="—"
        onChange={e=>setColorSplits(p=>({...p,[key]:{...p[key],rest:e.target.value}}))} style={inputStyle(colorSplits[key].rest)}/>
    </div>,
  ]);

  // Route each other_fields key (render-time only; keys/save unchanged):
  //  • leading sire/dam tokens → that ancestor's card (byPath)
  //  • no prefix + a document keyword → generic (bottom)
  //  • no prefix otherwise → dog-level (Dog Info card)
  //  • prefix too deep / no property → generic
  const routed = (() => {
    const byPath = {}, dogExtras = [], generic = [];
    Object.keys(otherFields).forEach(key => {
      const toks = key.trim().split(/[\s_]+/);
      let i = 0;
      while (i < toks.length && (toks[i].toLowerCase() === 'sire' || toks[i].toLowerCase() === 'dam')) i++;
      const segs = toks.slice(0, i);
      const property = toks.slice(i).join(' ');
      if (segs.length >= 1 && segs.length <= 4 && property) {
        const path = segs.map(s => s.toLowerCase()).join('_');
        if (ANCESTOR_PATHS.includes(path)) { (byPath[path] = byPath[path] || []).push({key, label: property, prop: property.replace(/\s+/g,'_').toLowerCase()}); return; }
      }
      if (segs.length === 0) {
        const lk = key.toLowerCase();
        if (DOC_KEYWORDS.some(w => lk.includes(w))) generic.push({key}); else dogExtras.push({key, label: key.replace(/_/g, ' ')});
        return;
      }
      generic.push({key});
    });
    return {byPath, dogExtras, generic};
  })();

  // One ancestor card — same compact layout as Dog Info. OCR only fills _name and
  // _registration_number; the other fields start empty for the user to fill.
  const ancestorCard = (path) => {
    const extras = routed.byPath[path] || [];
    const dobKey = `${path}_date_of_birth`;
    const dobEmpty = !((fields[dobKey]||'').trim());
    // FIX 3: surface a date_code as the DOB default when DOB is empty, and suppress
    // it as a duplicate extra in that case only (keep it visible if DOB was OCR'd).
    const dateCodeEntry = extras.find(e => e.prop === 'date_code');
    const dateCodeVal = (dobEmpty && dateCodeEntry) ? (otherFields[dateCodeEntry.key]||'') : '';
    // FIX 4: color_markings keys carrying an OFA score render as split inputs.
    const splitKeys = extras.filter(e => colorSplits[e.key]).map(e => e.key);
    const consumed = new Set(splitKeys);
    if (dobEmpty && dateCodeEntry) consumed.add(dateCodeEntry.key);
    const visibleExtras = extras.filter(e => !consumed.has(e.key));
    return (
    <div key={path} style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 12px",background:C.bg4}}>
      <div style={{fontSize:11,color:C.creamD,fontWeight:700,marginBottom:6}}>{ancestorLabel(path)}</div>
      {field(`${path}_name`, 'Registered Name')}
      <div style={{display:"grid",gridTemplateColumns:"1.6fr 1.2fr 1fr 0.9fr 1fr",gap:8,marginTop:8}}>
        {field(`${path}_breed`,'Breed', isDesignerImport ? '' : fields.breed||'')}
        {field(`${path}_registration_number`,'Reg #')}
        {field(dobKey,'DOB', dateCodeVal)}
        {field(`${path}_sex`,'Sex', impliedSex(path))}
        {field(`${path}_color`,'Color')}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:8}}>
        {field(`${path}_original_kennel`,'Original Kennel')}
        {field(`${path}_dna_number`,'DNA #')}
        {field(`${path}_ofa_health`,'OFA Health')}
      </div>
      {(visibleExtras.length > 0 || splitKeys.length > 0) && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
          {splitKeys.map(colorSplitField)}
          {visibleExtras.map(({key,label}) => extraField(key,label))}
        </div>
      )}
      <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer",fontSize:11,color:C.slate}}>
        <input type="checkbox" checked={ownedAncestors.has(path)}
          onChange={e=>setOwnedAncestors(prev=>{const s=new Set(prev);e.target.checked?s.add(path):s.delete(path);return s;})}
          style={{width:12,height:12,accentColor:C.gold}}/>
        I own this dog
      </label>
    </div>
    );
  };

  return (
    <div onClick={()=>{ if(!saving) onClose(); }} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,width:"100%",maxWidth:1100,height:"88vh",display:"flex",overflow:"hidden"}}>
        {/* Left — photo */}
        <div style={{width:"42%",background:C.bg,borderRight:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",padding:16,position:"relative"}}>
          {imp.thumb
            ? <img src={imp.thumb} alt="registration paper" onClick={()=>setZoom(true)} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",cursor:"zoom-in",borderRadius:6}}/>
            : <div style={{color:C.slate,fontSize:13}}>No image</div>}
          {imp.thumb && <div style={{position:"absolute",bottom:8,left:0,right:0,textAlign:"center",fontSize:10,color:C.slate}}>click image to zoom</div>}
        </div>
        {/* Right — form */}
        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
          <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontFamily:"serif",fontSize:18,fontWeight:700}}>Review extracted data</div>
            <button className="bgs" onClick={onClose} disabled={saving}>✕</button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"4px 20px 20px"}}>
            <div style={sectionHead}>Dog Info</div>
            {field('dog_name','Registered Name')}
            <div style={{display:"grid",gridTemplateColumns:"1.6fr 1.2fr 1fr 0.9fr 1fr",gap:8,marginTop:10}}>
              {field('breed','Breed')}
              {field('registration_number','Reg #')}
              {field('date_of_birth','DOB')}
              {field('sex','Sex')}
              {field('color','Color')}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:10}}>
              {field('breeder_name','Breeder')}
              {field('owner_name','Owner')}
              {field('original_kennel','Original Kennel')}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
              {field('dna_number','DNA #')}
              {field('ofa_health','OFA Health')}
            </div>
            <label style={{display:"flex",alignItems:"center",gap:8,marginTop:10,cursor:"pointer",fontSize:12,color:C.cream}}>
              <input type="checkbox" checked={ownFocusDog} onChange={e=>setOwnFocusDog(e.target.checked)} style={{width:14,height:14,accentColor:C.gold}}/>
              I own this dog (will appear under My Dogs)
            </label>
            <label style={{display:"flex",alignItems:"center",gap:8,marginTop:6,cursor:"pointer",fontSize:12,color:isDesignerImport?C.gold:C.slate}}>
              <input type="checkbox" checked={isDesignerImport} onChange={e=>setIsDesignerImport(e.target.checked)} style={{width:14,height:14,accentColor:C.gold}}/>
              Designer breed paper — each ancestor keeps its own breed (no cascade)
            </label>
            {routed.dogExtras.length > 0 && (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:10}}>
                {routed.dogExtras.map(({key,label}) => extraField(key,label))}
              </div>
            )}

            {GENS.map(([gen, title]) => (
              <div key={gen}>
                <div style={sectionHead}>{title}</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {ANCESTOR_PATHS.filter(p => p.split('_').length === gen - 1).map(ancestorCard)}
                </div>
              </div>
            ))}

            {routed.generic.length > 0 && (
              <div>
                <div onClick={()=>setShowOther(s=>!s)} style={{...sectionHead, cursor:"pointer", display:"flex", alignItems:"center", gap:6, userSelect:"none"}}>
                  <span style={{fontSize:10}}>{showOther ? '▾' : '▸'}</span>
                  <span>Other fields ({routed.generic.length})</span>
                </div>
                {showOther && (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                    {routed.generic.map(({key}) => extraField(key, key.replace(/_/g, ' ')))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{padding:"14px 20px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end",gap:10}}>
            <button className="bgs" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="bgl" onClick={save} disabled={saving}>{saving?'Saving…':'Save Review'}</button>
          </div>
        </div>
      </div>

      {zoom && imp.thumb && (
        <div onClick={(e)=>{e.stopPropagation(); setZoom(false);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:24,cursor:"zoom-out"}}>
          <img src={imp.thumb} alt="registration paper full" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
        </div>
      )}
    </div>
  );
}

// ── BREED AUTOCOMPLETE ───────────────────────────────────────────────────────
// Same UX shape as DogAutocomplete: 3-char min, starts-with then contains,
// ↑/↓/Enter, click-outside closes, capped at 10. Names come straight from the
// canonical breeds table (FCI verbatim ALL-CAPS, AKC proper case) — display
// untouched, only search comparison is lowercased.
// ── BREED PICKER (personal list) ──
// Purebred breed selector backed by the breeder's PERSONAL breeder_breeds list
// (Phase 6b Session 3). Used in the My Dogs and Litter forms.
//  • 0 personal breeds → "Set up My Breeds first" gate.
//  • 1+ personal breeds → <select> of those breeds (option value = breed.id).
//  • Legacy preservation: when editing a record whose current breed isn't in the
//    personal list, the existing value shows as a "{name} (current)" option (and
//    the gate is bypassed) so swapping the picker in never wipes saved data.
// onChange receives the picked breed {id,name}, {id:null,name} for legacy, or null.
function BreedPicker({breederId, breeds, breederBreeds, selectedId, selectedName, onChange, onGoToMyBreeds, rowKey='breed_id', gateLabel='My Breeds', customCrosses=[], selectedCustomId}){
  const breedById = (breeds||[]).reduce((acc,b)=>{acc[b.id]=b;return acc;},{});
  const myBreeds = ((breederBreeds||{})[breederId] || [])
    .map(r => breedById[r[rowKey]])
    .filter(Boolean)
    .sort((a,b) => a.name.localeCompare(b.name));
  // Tier C: per-breeder custom crosses, appended to the same dropdown with a
  // "(Custom)" suffix and a custom:<id> value so onChange can disambiguate tier.
  const crosses = (customCrosses||[]).slice().sort((a,b) => a.name.localeCompare(b.name));

  // A record can have a breed NAME (free text, possibly legacy) with no id yet.
  // If that name exactly matches a real breed already in the rendered list,
  // resolve the id automatically — both for display AND by firing onChange once,
  // so simply opening an edit form heals the missing id instead of silently
  // requiring the breeder to notice a blank-looking dropdown and manually
  // reselect a breed that's already right there in the list.
  const nameMatch = (!selectedId && !selectedCustomId && selectedName)
    ? myBreeds.find(b => b.name === selectedName) : null;
  useEffect(() => {
    if (nameMatch) onChange(nameMatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameMatch?.id]);

  const inList = (selectedId && breedById[selectedId]) ||
                 (selectedCustomId && crosses.some(c => c.id === selectedCustomId)) ||
                 !!nameMatch ||
                 (selectedName && crosses.some(c => c.name === selectedName));
  const legacyName = (!inList && selectedName) ? selectedName : '';

  if (myBreeds.length === 0 && crosses.length === 0 && !legacyName) {
    // No configured breeds — fall back to the full list so contributors and
    // new breeders who haven't set up My Breeds yet can still add dogs.
    const allSorted = (breeds||[]).slice().sort((a,b) => a.name.localeCompare(b.name));
    return (
      <select className="fi" value={selectedId||''} onChange={e=>{
        const v = e.target.value;
        if (!v) return onChange(null);
        onChange(breedById[v] || null);
      }}>
        <option value="">Select breed…</option>
        {allSorted.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    );
  }

  const currentValue = selectedCustomId ? `custom:${selectedCustomId}`
                     : selectedId ? selectedId
                     : nameMatch ? nameMatch.id
                     : (legacyName ? `legacy:${legacyName}` : '');

  return (
    <select className="fi" value={currentValue} onChange={e=>{
      const v = e.target.value;
      if (!v) return onChange(null);
      if (v.startsWith('legacy:')) return onChange({id:null, name:legacyName});
      if (v.startsWith('custom:')) {
        const cc = crosses.find(c => c.id === v.slice(7));
        return onChange(cc ? {id:cc.id, name:cc.name, isCustom:true} : null);
      }
      onChange(breedById[v] || null);
    }}>
      <option value="">Select breed…</option>
      {legacyName && <option value={`legacy:${legacyName}`}>{legacyName} (current)</option>}
      {myBreeds.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      {crosses.map(c => <option key={`custom:${c.id}`} value={`custom:${c.id}`}>{c.name} (Custom)</option>)}
    </select>
  );
}

// OFA-graded result field (hips/elbows/eyes). Constrained to TEST_RESULT_VOCAB so
// breed_health_requirements.pass_values can match exactly. A pre-existing value that
// doesn't match the vocab (legacy free text) is preserved as a pinned option — never
// blanked, never auto-guessed — until the breeder deliberately re-selects a grade.
function OfaResultField({testKey, label, value, onChange, certValue, onCertChange, registryValue, onRegistryChange, altRegistries, diValue, onDiChange}){
  const vocab = TEST_RESULT_VOCAB[testKey] || [];
  const isLegacy = value && !vocab.includes(value);
  const showRegistryToggle = !!onRegistryChange && altRegistries && altRegistries.length > 0;
  const registry = registryValue || 'OFA';
  return (
    <div>
      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
      {showRegistryToggle && (
        <select className="fi" style={{marginBottom:4}} value={registry} onChange={e=>onRegistryChange(e.target.value)}>
          <option value="OFA">OFA</option>
          {altRegistries.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
      )}
      {registry === 'OFA' ? (
        <select className="fi" value={value||""} onChange={e=>onChange(e.target.value)}>
          <option value="">Not tested / Unknown</option>
          {isLegacy && <option value={value}>{value} (legacy entry — re-select to update)</option>}
          {vocab.map(v=><option key={v} value={v}>{v}</option>)}
        </select>
      ) : (
        onDiChange && <input className="fi" type="text" placeholder={`${registry} Distraction Index (reference only — not used for pass/fail)`} value={diValue||""} onChange={e=>onDiChange(e.target.value)}/>
      )}
      {onCertChange && (
        <input className="fi" style={{marginTop:4}} placeholder={registry==='OFA'?"Cert / registry #":`${registry} report / cert #`} value={certValue||""} onChange={e=>onCertChange(e.target.value)}/>
      )}
    </div>
  );
}

function BreedAutocomplete({breeds, excludeIds, onPick, placeholder}){
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);

  const matches = (() => {
    if (query.length < 3) return null;
    const q = query.toLowerCase();
    const starts = [], contains = [];
    breeds.forEach(b => {
      if (excludeIds.has(b.id)) return;
      const n = (b.name||'').toLowerCase();
      if (n.startsWith(q)) starts.push(b);
      else if (n.includes(q)) contains.push(b);
    });
    const cmp = (a,b) => (a.name||'').localeCompare(b.name||'');
    starts.sort(cmp); contains.sort(cmp);
    const all = [...starts, ...contains];
    return {items: all.slice(0,10), total: all.length};
  })();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (b) => { onPick(b); setQuery(''); setOpen(false); setHi(0); };

  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'Tab') { setOpen(false); return; }
    if (!matches || matches.items.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h+1, matches.items.length-1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h-1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = matches.items[hi]; if (r) pick(r); }
  };

  return (
    <div ref={wrapRef} style={{position:"relative"}}>
      <input className="fi" value={query} placeholder={placeholder}
        onChange={e=>{setQuery(e.target.value); setOpen(true); setHi(0);}}
        onFocus={()=>setOpen(true)}
        onKeyDown={onKey}/>
      {open && query.length >= 3 && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,marginTop:2,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:7,boxShadow:"0 6px 20px rgba(0,0,0,0.5)",zIndex:200,maxHeight:280,overflowY:"auto"}}>
          {matches && matches.items.length > 0 ? (
            <>
              {matches.items.map((b,i)=>(
                <div key={b.id}
                  onMouseDown={e=>{e.preventDefault(); pick(b);}}
                  onMouseEnter={()=>setHi(i)}
                  style={{padding:"8px 12px",cursor:"pointer",background:i===hi?C.bg3:"transparent",borderBottom:`1px solid ${C.border}33`,fontSize:13,color:C.cream}}>
                  {b.name}
                </div>
              ))}
              {matches.total > 10 && (
                <div style={{padding:"6px 12px",fontSize:11,fontStyle:"italic",color:C.slate}}>...more results, keep typing</div>
              )}
            </>
          ) : (
            <div style={{padding:"10px 12px",fontSize:12,fontStyle:"italic",color:C.slate}}>No matches</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── INLINE KENNEL MODAL ──────────────────────────────────────────────────────
// Minimal-fields modal. Locked to kennel_type='reference'. Esc closes (top-most
// only). setData runs after the upsert resolves so failed saves can't leave a
// ghost row in the UI.
function InlineKennelModal({initialName, onSave, onClose, setData, supabase, flash}){
  const [kennel, setKennel] = useState(initialName || '');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('United States');
  const [saving, setSaving] = useState(false);

  const valid = kennel.trim().length > 0 && !saving;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    const newBreeder = {
      id: crypto.randomUUID(),
      kennel: kennel.trim(), name: name.trim(), email: '',
      country, region: '', website: '', bio: '',
      joined: new Date().toISOString().split('T')[0],
      tiers: ['foundation'],
      photoUrl: '', verified: false, blocked: false,
      kennelType: 'reference',
    };
    try {
      await supabase.from('breeders').upsert(SB.breederToDB(newBreeder));
      setData(prev => ({...prev, breeders: {...prev.breeders, [newBreeder.id]: newBreeder}}));
      flash('Kennel added! ✓');
      onSave(newBreeder.id);
    } catch(e) {
      setSaving(false);
      flash('Save failed: ' + e.message);
    }
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget && !saving)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:300,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:20,overflowY:"auto"}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:"100%",maxWidth:420,margin:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontFamily:"serif",fontSize:18,fontWeight:700}}>+ Add new kennel</div>
          <button className="bgs" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:12}}>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Kennel Name *</label>
            <input className="fi" value={kennel} onChange={e=>setKennel(e.target.value)} autoFocus/>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Owner Name (optional)</label>
            <input className="fi" value={name} onChange={e=>setName(e.target.value)}/>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
            <select className="fi" value={country} onChange={e=>setCountry(e.target.value)}>
              {COUNTRIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{fontSize:11,color:C.slate,fontStyle:"italic"}}>Will be saved as a Reference Kennel — community-attributed, can be filled in later.</div>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          <button className="bgs" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="bgl" onClick={handleSave} disabled={!valid} style={!valid?{opacity:0.5,cursor:"not-allowed"}:{}}>{saving?'Saving…':'Save Kennel'}</button>
        </div>
      </div>
    </div>
  );
}

// ── INLINE DOG MODAL ─────────────────────────────────────────────────────────
// Minimal-fields modal. Sex locked from caller (sire/dam context). Breeder
// kennel via KennelAutocomplete with inline-add. Esc only closes the dog modal
// when its inner kennel modal isn't open (no cascade). setData runs after the
// upsert resolves.
function InlineDogModal({sex, initialName, initialBreed, onSave, onClose, breeders, setData, supabase, flash}){
  const [name, setName] = useState(initialName || '');
  const [callName, setCallName] = useState('');
  const [breed, setBreed] = useState(initialBreed || '');
  const [country, setCountry] = useState('United States');
  const [regBody, setRegBody] = useState('AKC');
  const [regNum, setRegNum] = useState('');
  const [breederId, setBreederId] = useState('');
  const [inlineKennel, setInlineKennel] = useState(null);
  const [saving, setSaving] = useState(false);

  const valid = name.trim().length > 0 && !saving;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving && !inlineKennel) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving, inlineKennel]);

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    const newDog = {
      id: crypto.randomUUID(),
      type: 'purebred',
      breed: breed.trim(), name: name.trim(), callName: callName.trim(),
      sex, color: '', dob: '',
      country, regNum: regNum.trim(), regBody,
      breederId: breederId || null,
      sireId: null, damId: null,
      sireNameManual: '', damNameManual: '',
      photoUrl: '', notes: '', hidden: false, dnaNum: '',
      akcNum:'', ascaNum:'', ukcNum:'', fciNum:'', kcNum:'', ckcNum:'',
      ankcNum:'', nzkcNum:'', svNum:'', arbaNum:'', naprNum:'', otherRegNum:'',
      ofaHips:'', ofaElbows:'', ofaEyes:'', ofaCardiac:'', ofaThyroid:'',
      chicNum:'', cerfNum:'', baer:'', mdr1:'', vwd:'', dm:'', hsf4:'',
      cea:'', brucellosis:'', otherHealth:'', titles:'',
    };
    try {
      await supabase.from('dogs').upsert(SB.dogToDB(newDog));
      setData(prev => ({...prev, dogs: {...prev.dogs, [newDog.id]: newDog}}));
      flash('Dog added! ✓');
      onSave(newDog.id);
    } catch(e) {
      setSaving(false);
      flash('Save failed: ' + e.message);
    }
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget && !saving)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:20,overflowY:"auto"}}>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:"100%",maxWidth:480,margin:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontFamily:"serif",fontSize:18,fontWeight:700}}>+ Add new {sex==='M'?'sire':'dam'}</div>
          <button className="bgs" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div style={{gridColumn:"1/-1"}}>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registered Name *</label>
            <input className="fi" value={name} onChange={e=>setName(e.target.value)} autoFocus/>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Call Name</label>
            <input className="fi" value={callName} onChange={e=>setCallName(e.target.value)}/>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sex (locked)</label>
            <input className="fi" value={sex==='M'?'Male':'Female'} disabled style={{opacity:0.6}}/>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breed</label>
            <select className="fi" value={breed} onChange={e=>setBreed(e.target.value)}>
              <option value="">—</option>
              {BREEDS.map(b=><option key={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
            <select className="fi" value={country} onChange={e=>setCountry(e.target.value)}>
              {COUNTRIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Reg Body</label>
            <select className="fi" value={regBody} onChange={e=>setRegBody(e.target.value)}>
              {REG_BODIES.map(r=><option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registration #</label>
            <input className="fi" value={regNum} onChange={e=>setRegNum(e.target.value)}/>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breeder Kennel</label>
            <KennelAutocomplete value={breederId} onChange={setBreederId} breeders={breeders}
              onAdd={(q) => setInlineKennel({initialName: q})}
              placeholder="Search kennels (3+ chars) or leave blank…"/>
          </div>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          <button className="bgs" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="bgl" onClick={handleSave} disabled={!valid} style={!valid?{opacity:0.5,cursor:"not-allowed"}:{}}>{saving?'Saving…':'Save Dog'}</button>
        </div>
      </div>
      {inlineKennel && (
        <InlineKennelModal initialName={inlineKennel.initialName}
          onSave={(newKennelId) => { setBreederId(newKennelId); setInlineKennel(null); }}
          onClose={() => setInlineKennel(null)}
          setData={setData} supabase={supabase} flash={flash}/>
      )}
    </div>
  );
}

function BreederCard({breeder,dogCount,onView}){
  const ts = tierStyle(breeder.kennelType);
  return(
    <div className="dcard" style={{borderColor:ts.border,borderWidth:ts.borderWidth,background:C.card,position:"relative"}}>
      <div style={{position:"absolute",top:10,right:10,display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end"}}>
        {breeder.verified&&(
          <div style={{background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontSize:9,fontWeight:800,letterSpacing:1.2,padding:"3px 8px",borderRadius:4,boxShadow:"0 2px 6px rgba(0,0,0,0.4)"}}>✓ VERIFIED</div>
        )}
        {breeder.isElite&&(
          <div style={{background:`linear-gradient(135deg,${C.amber},${C.gold})`,color:C.bg,fontSize:9,fontWeight:700,letterSpacing:1,padding:"2px 7px",borderRadius:3}}>⭐ ELITE</div>
        )}
      </div>
      <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12}}>
        <div style={{width:50,height:50,borderRadius:"50%",overflow:"hidden",border:`2px solid ${ts.avatarBorder}88`,background:C.bg4,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>
          {breeder.photoUrl?<img src={breeder.photoUrl} alt={breeder.kennel} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:"🏡"}
        </div>
        <div>
          <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,color:ts.text}}>{breeder.kennel}</div>
          <div style={{fontSize:12,color:ts.sub}}>{breeder.name}</div>
          <div style={{fontSize:11,color:ts.country}}>{breeder.country}</div>
        </div>
      </div>
      {breeder.bio&&<div style={{fontSize:12,color:ts.text,lineHeight:1.6,marginBottom:10,display:"-webkit-box",overflow:"hidden",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{breeder.bio}</div>}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:11,color:ts.sub}}>{dogCount} dogs registered</span>
        <button className="bgs" onClick={onView}>View Kennel →</button>
      </div>
    </div>
  );
}

// Single shared insert path — every "Join Waitlist" entry point (BreederProfile
// chips, litter cards) calls this same function, so there is exactly one place
// that writes to waitlist_entries and the payload shape can't drift between them.
// Also the one place that triggers the breeder's new-signup email, so that
// trigger can't drift between call sites either.
//
// Client generates the row's own id rather than chaining .select().single() to
// read it back — an anonymous buyer's insert satisfies the INSERT policy's
// WITH CHECK, but a RETURNING read-back is governed by the SELECT policy
// instead, which (correctly) has no public-read rule for buyer contact info.
// .single() then throws on the empty read-back (PGRST116), which supabase-js
// surfaces as an error even though the row was actually written — so the
// buyer saw a false failure on their own successful join. Generating the id
// client-side avoids any RETURNING clause entirely; same pattern already used
// for custom crosses elsewhere in this file.
// Ownership Transfer — client does the direct insert (dog_transfers' own RLS
// INSERT policy already verified the caller currently owns dogId, via either
// record_owner_id or current_owner_member_id — either party can initiate),
// then fires the invite email as a side effect. Client-generated id, same
// pattern as joinWaitlist/custom crosses — avoids any RETURNING clause.
async function initiateDogTransfer(supabase, {dogId, toEmail, memberId}) {
  const id = crypto.randomUUID();
  const { error } = await supabase.from('dog_transfers').insert({
    id,
    dog_id: dogId,
    initiated_by_member_id: memberId,
    to_email: toEmail.trim().toLowerCase(),
  });
  if (!error) {
    fetch('/api/notify-transfer-invite', {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ transferId: id }),
    }).catch(e => console.log('[notify-transfer-invite] call failed:', e.message));
  }
  return { id, error };
}

// Dog Return — same client-does-the-direct-insert pattern as
// initiateDogTransfer (dog_returns' own INSERT RLS already verified the
// caller is a valid party for this dog, via either current_owner_member_id
// or record_owner_id), then fires a courtesy email as a side effect. No
// to_email — the recipient is derived server-side in notify-return-request.
async function initiateDogReturn(supabase, {dogId, memberId}) {
  const id = crypto.randomUUID();
  const { error } = await supabase.from('dog_returns').insert({
    id,
    dog_id: dogId,
    initiated_by: memberId,
  });
  if (!error) {
    fetch('/api/notify-return-request', {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ returnId: id }),
    }).catch(e => console.log('[notify-return-request] call failed:', e.message));
  }
  return { id, error };
}

// requestedPuppyId is the buyer's stated interest in a specific puppy at
// application time — distinct from matched_puppy_id, which stays reserved
// for the breeder's own finalized decision (see matchToPuppy). Multiple
// buyers can request the same available puppy; nothing here reserves it.
async function joinWaitlist(supabase, {breederId, breedId, designerBreedId, requestedPuppyId, name, email, phone, notes}) {
  const id = crypto.randomUUID();
  const { error } = await supabase.from('waitlist_entries').insert({
    id,
    breeder_id: breederId,
    breed_id: breedId || null,
    designer_breed_id: designerBreedId || null,
    requested_puppy_id: requestedPuppyId || null,
    buyer_name: name.trim(),
    buyer_email: email.trim(),
    buyer_phone: phone?.trim() || null,
    notes: notes?.trim() || null,
  });
  if (!error) {
    // Fire-and-forget, not awaited — callers show the join confirmation based
    // on `error` alone, before this even starts, so a slow/failed breeder
    // notification can never affect what the buyer sees. No auth header: the
    // buyer has no session (this registry never requires buyers to log in) —
    // the endpoint only ever trusts this id, re-fetching the real stored row
    // itself rather than any buyer-supplied content.
    fetch('/api/notify-breeder-signup', {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ waitlistEntryId: id }),
    }).catch(e => console.log('[notify-breeder-signup] call failed:', e.message));
  }
  return { error };
}

function openExternal(url) {
  window.open(url.startsWith('http') ? url : 'https://' + url, '_blank');
}

// Shared headline everywhere a litter is displayed — a breeder's own custom
// name (e.g. "Blue Collar Litter") if set, falling back to the original
// "{breed} Litter" behavior when it isn't, so every existing litter with no
// custom_name keeps looking exactly as it did before this feature existed.
// Whelp date folds in alongside the name rather than a separate no-context
// "TBD" segment when it isn't set yet (e.g. an upcoming litter).
function litterHeadline(l) {
  const name = l.customName || (l.breed ? `${l.breed} Litter` : 'Litter');
  if (!l.whelped) return name;
  return `${name} — Whelped ${new Date(l.whelped+'T00:00:00').toLocaleDateString()}`;
}

// Shown inline above the join form, Mode A only (Mode B redirects entirely, so a
// breeder's own external system handles policy/payment on its own). UPR never
// processes payment or sees transaction data here — stored text plus a link the
// buyer opens themselves, identical risk profile to the existing Inquire button.
function WaitlistPolicyBlock({policy, paymentLink}){
  if (!policy && !paymentLink) return null;
  return (
    <div style={{marginBottom:2}}>
      {policy && <div style={{fontSize:11,color:C.creamD,lineHeight:1.5,marginBottom:6}}>{policy}</div>}
      {paymentLink && (
        <button type="button" onClick={()=>openExternal(paymentLink)}
          style={{fontSize:11,color:C.emeraldL,background:"none",border:`1px solid ${C.emeraldL}44`,borderRadius:4,padding:"4px 10px",cursor:"pointer",marginBottom:6}}>
          💳 Pay Deposit →
        </button>
      )}
    </div>
  );
}

// Per-breed "Join Waitlist" chip — no login required (buyers never have an account
// in this registry), matching the frictionless "Inquire About Puppies" precedent.
// Exactly one of breedId/designerBreedId is passed by the caller.
// Mode B (externalWaitlistUrl set): just a link to the breeder's own system.
// Mode A (blank): UPR's built-in inline form, optionally preceded by the
// breeder's policy text + deposit payment link.
function WaitlistChip({breederId, breedId, designerBreedId, label, externalWaitlistUrl, waitlistPolicy, waitlistPaymentLink, supabase, flash}){
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({name:'',email:'',phone:'',notes:''});

  if (externalWaitlistUrl) {
    return (
      <div style={{background:`${C.gold}1a`,border:`1px solid ${C.gold}55`,borderRadius:12,padding:"8px 14px",fontSize:13,color:C.goldL,display:"flex",alignItems:"center",gap:10,justifyContent:"space-between"}}>
        <span>{label}</span>
        <button onClick={()=>openExternal(externalWaitlistUrl)} style={{background:"none",border:"none",color:C.emeraldL,cursor:"pointer",fontSize:11,fontWeight:700,padding:0}}>Join Waitlist →</button>
      </div>
    );
  }

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { flash('Name and email required'); return; }
    setSubmitting(true);
    const { error } = await joinWaitlist(supabase, {breederId, breedId, designerBreedId, ...form});
    setSubmitting(false);
    if (error) { flash('⚠ ' + error.message); return; }
    setSubmitted(true);
  };

  return (
    <div style={{background:`${C.gold}1a`,border:`1px solid ${C.gold}55`,borderRadius:12,padding:"8px 14px",fontSize:13,color:C.goldL}}>
      <div style={{display:"flex",alignItems:"center",gap:10,justifyContent:"space-between"}}>
        <span>{label}</span>
        {!submitted && (
          <button onClick={()=>setOpen(o=>!o)} style={{background:"none",border:"none",color:C.emeraldL,cursor:"pointer",fontSize:11,fontWeight:700,padding:0}}>
            {open?'✕':'+ Join Waitlist'}
          </button>
        )}
        {submitted && <span style={{fontSize:11,color:C.emeraldL,fontWeight:700}}>✓ Joined</span>}
      </div>
      {open && !submitted && (
        <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:6,minWidth:220}}>
          <WaitlistPolicyBlock policy={waitlistPolicy} paymentLink={waitlistPaymentLink}/>
          <input className="fi" placeholder="Your name" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/>
          <input className="fi" placeholder="Email" type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/>
          <input className="fi" placeholder="Phone (optional)" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/>
          <textarea className="fi" rows={2} placeholder="Anything else? (optional)" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={{resize:"vertical"}}/>
          <button className="bgl" style={{fontSize:12}} disabled={submitting} onClick={submit}>{submitting?'Joining…':'Join Waitlist'}</button>
        </div>
      )}
    </div>
  );
}

// ── BREEDER PROFILE ───────────────────────────────────────────────────────────
function BreederProfile({breeder,dogs,litters,breeds,breederBreeds,designerBreeds,breederDesignerBreeds,supabase,flash,onBack,onDogView}){
  const ts = tierStyle(breeder.kennelType);
  const breedById = (breeds||[]).reduce((acc,b)=>{acc[b.id]=b;return acc;},{});
  const myBreedRows = (breederBreeds||{})[breeder.id] || [];
  const selectedBreeds = myBreedRows
    .map(r => breedById[r.breed_id])
    .filter(Boolean)
    .sort((a,b) => a.name.localeCompare(b.name));
  const designerBreedById = (designerBreeds||[]).reduce((acc,b)=>{acc[b.id]=b;return acc;},{});
  const myDesignerBreedRows = (breederDesignerBreeds||{})[breeder.id] || [];
  const selectedDesignerBreeds = myDesignerBreedRows
    .map(r => designerBreedById[r.designer_breed_id])
    .filter(Boolean)
    .sort((a,b) => a.name.localeCompare(b.name));
  return(
    <div>
      <button className="bgs" onClick={onBack} style={{marginBottom:20}}>← Breeders</button>
      <div style={{background:C.card,border:`${ts.borderWidth}px solid ${ts.border}`,borderRadius:12,padding:24,marginBottom:24,display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap",position:"relative"}}>
        {ts.showVerified&&(
          <div style={{position:"absolute",top:14,right:14,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontSize:11,fontWeight:800,letterSpacing:1.5,padding:"5px 12px",borderRadius:5,boxShadow:"0 2px 8px rgba(0,0,0,0.45)"}}>✓ VERIFIED KENNEL</div>
        )}
        <div style={{width:80,height:80,borderRadius:10,overflow:"hidden",border:`3px solid ${ts.avatarBorder}`,background:C.bg4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,flexShrink:0}}>
          {breeder.photoUrl?<img src={breeder.photoUrl} alt={breeder.kennel} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:"🏡"}
        </div>
        <div style={{flex:1}}>
          <div style={{fontFamily:"serif",fontSize:24,fontWeight:900,marginBottom:4,color:ts.text}}>
            {breeder.kennel}
            {ts.showRef&&<span style={{fontSize:10,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",color:C.slate,border:`1px solid ${C.slate}`,padding:"3px 9px",borderRadius:3,marginLeft:12,verticalAlign:"middle",fontFamily:"sans-serif"}}>Reference Kennel</span>}
          </div>
          <div style={{fontSize:13,color:ts.sub,marginBottom:8}}>{breeder.name} · {breeder.country}</div>
          {breeder.bio&&<div style={{fontSize:14,color:ts.text,lineHeight:1.7}}>{breeder.bio}</div>}
        </div>
      </div>
      {(selectedBreeds.length>0||selectedDesignerBreeds.length>0)&&<>
        <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:12}}>Breeds</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:24}}>
          {selectedBreeds.map(b=>(
            <WaitlistChip key={b.id} breederId={breeder.id} breedId={b.id} label={b.name}
              externalWaitlistUrl={breeder.externalWaitlistUrl} waitlistPolicy={breeder.waitlistPolicy} waitlistPaymentLink={breeder.waitlistPaymentLink}
              supabase={supabase} flash={flash}/>
          ))}
          {selectedDesignerBreeds.map(b=>(
            <WaitlistChip key={b.id} breederId={breeder.id} designerBreedId={b.id} label={b.name}
              externalWaitlistUrl={breeder.externalWaitlistUrl} waitlistPolicy={breeder.waitlistPolicy} waitlistPaymentLink={breeder.waitlistPaymentLink}
              supabase={supabase} flash={flash}/>
          ))}
        </div>
      </>}
      {dogs.length>0&&<>
        <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:12}}>{dogs.length} Dogs</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12,marginBottom:24}}>
          {dogs.map(d=><DogCard key={d.id} dog={d} breeders={{[breeder.id]:breeder}} canView={true} onView={()=>onDogView(d.id)}/>)}
        </div>
      </>}
    </div>
  );
}

// ── LITTER CARD ───────────────────────────────────────────────────────────────
// Buyer-facing puppy row — only litter_puppies' own fields (sex/color/price/
// label/photos) plus, for available puppies, a direct "Apply" path into
// joinWaitlist with requested_puppy_id set. Never displays waitlist_entries
// data, so buyer contact info can never leak here — this only ever writes.
// showPrice is false for the "What We've Produced" showcase of already-placed
// puppies, where a price tag (and an apply option) reads oddly.
function PuppyRow({p, showPrice, litter, supabase, flash, externalWaitlistUrl}){
  const label = [p.label, p.sex==='M'?'Male':p.sex==='F'?'Female':null, p.color, showPrice?p.price:null].filter(Boolean).join(' · ');
  const canApply = p.status==='available' && litter && supabase;
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({name:'',email:'',phone:'',notes:''});

  const submitApply = async () => {
    if (!form.name.trim() || !form.email.trim()) { flash('Name and email required'); return; }
    setSubmitting(true);
    const { error } = await joinWaitlist(supabase, {
      breederId: litter.breederId, breedId: litter.breedId, designerBreedId: litter.designerBreedId,
      requestedPuppyId: p.id, ...form,
    });
    setSubmitting(false);
    if (error) { flash('⚠ ' + error.message); return; }
    setApplied(true);
    setApplyOpen(false);
  };

  // Mode B, same as WaitlistChip/LitterCard's own waitlist CTA: an external
  // link sends the buyer straight there instead of the inline UPR form —
  // no separate "application" link, breeders' one external intake system
  // already covers both breed-level and per-puppy interest in practice.
  const applyClick = () => externalWaitlistUrl ? openExternal(externalWaitlistUrl) : setApplyOpen(o=>!o);

  return (
    <div style={{background:C.bg3,borderRadius:6,padding:8}}>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        {(p.photo_urls||[]).length>0 && (
          <div style={{display:"flex",gap:4,flexShrink:0}}>
            {p.photo_urls.slice(0,3).map((url,i)=><img key={i} src={url} alt="" style={{width:36,height:36,objectFit:"cover",borderRadius:4}}/>)}
          </div>
        )}
        <div style={{fontSize:12,color:C.creamD,flex:1}}>{label||'(no details on file)'}</div>
        {canApply && !applied && (
          <button onClick={applyClick} style={{background:"none",border:"none",color:C.emeraldL,cursor:"pointer",fontSize:11,fontWeight:700,padding:0,flexShrink:0,whiteSpace:"nowrap"}}>
            {(!externalWaitlistUrl && applyOpen)?'✕ Cancel':'Apply for This Puppy →'}
          </button>
        )}
        {applied && <span style={{fontSize:11,color:C.emeraldL,fontWeight:700,flexShrink:0}}>✓ Applied</span>}
      </div>
      {!externalWaitlistUrl && canApply && applyOpen && !applied && (
        <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:6}}>
          <input className="fi" placeholder="Your name" value={form.name} onChange={e=>setForm(fp=>({...fp,name:e.target.value}))}/>
          <input className="fi" placeholder="Email" value={form.email} onChange={e=>setForm(fp=>({...fp,email:e.target.value}))}/>
          <input className="fi" placeholder="Phone (optional)" value={form.phone} onChange={e=>setForm(fp=>({...fp,phone:e.target.value}))}/>
          <textarea className="fi" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={e=>setForm(fp=>({...fp,notes:e.target.value}))} style={{resize:"vertical"}}/>
          <button className="bgl" style={{fontSize:12}} disabled={submitting} onClick={submitApply}>{submitting?'Applying…':'Submit Application'}</button>
        </div>
      )}
    </div>
  );
}

function LitterCard({litter,dogs,breeders,puppies,supabase,flash}){
  const sire=dogs[litter.sireId];const dam=dogs[litter.damId];
  const b=breeders[litter.breederId];
  const [joinOpen, setJoinOpen] = useState(false);
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({name:'',email:'',phone:'',notes:''});
  const [showPuppies, setShowPuppies] = useState(false);

  // Once a litter has real litter_puppies rows, the aggregate count derives
  // from them instead of the breeder's manually-typed count/available ints —
  // both numbers are visible on the same card now (header + expanded list),
  // so a mismatch between them would read as broken. Litters with no puppy
  // rows are completely untouched — same manual ints as always.
  const hasPuppyRecords = (puppies||[]).length > 0;
  const availablePuppies = (puppies||[]).filter(p=>p.status==='available');
  const matchedPuppies = (puppies||[]).filter(p=>p.status==='matched');
  const displayAvailable = hasPuppyRecords ? availablePuppies.length : litter.available;
  const displayCount = hasPuppyRecords ? puppies.length : litter.count;

  // Both actions are always offered when a breed is linked — only which one is
  // PRIMARY changes with status. A 'current' litter still has open puppies (see
  // "N of M available" above) and a serious buyer may want a deposit-backed
  // reservation, not just a no-record mailto inquiry — so Inquire leads but
  // Join Waitlist/Pay Deposit stays available as a secondary option, not
  // hidden. For upcoming/reserved/complete, there's nothing to inquire about
  // yet, so Join Waitlist leads instead. With no linked breed (a legacy litter
  // never resaved through the fixed form), there's nothing to scope a waitlist
  // match to — Inquire is the only option regardless of status.
  const isAvailableNow = litter.litterStatus === 'current';
  const hasBreedRef = !!(litter.breedId || litter.designerBreedId);
  const waitlistIsPrimary = !isAvailableNow && hasBreedRef;
  const externalUrl = b?.externalWaitlistUrl;

  const inquire = () => {
    if (b?.website) window.open(b.website.startsWith('http') ? b.website : 'https://' + b.website, '_blank');
    else if (b?.email) window.open(`mailto:${b.email}?subject=Inquiry about ${litterHeadline(litter)}`, '_blank');
  };

  const submitWaitlist = async () => {
    if (!form.name.trim() || !form.email.trim()) { flash('Name and email required'); return; }
    setSubmitting(true);
    const { error } = await joinWaitlist(supabase, {breederId: litter.breederId, breedId: litter.breedId, designerBreedId: litter.designerBreedId, ...form});
    setSubmitting(false);
    if (error) { flash('⚠ ' + error.message); return; }
    setJoined(true);
  };

  const primaryBtnStyle = {width:"100%",border:"none",borderRadius:5,padding:"8px 0",cursor:"pointer",fontWeight:700,fontSize:12};

  return(
    <div className="dcard" style={{borderColor:`${C.emeraldL}55`,background:C.card}}>
     {litter.photoUrl&&<img src={litter.photoUrl} alt={litter.breed} style={{width:"100%",height:140,objectFit:"cover",borderRadius:6,marginBottom:12}}/>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
        <div>
          <div style={{fontFamily:"serif",fontSize:17,fontWeight:700}}>{litterHeadline(litter)}</div>
          <div style={{fontSize:11,color:C.slate}}>{litter.breed} · {litter.country}</div>
          {litter.litterStatus&&<span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,marginTop:4,display:"inline-block",background:litter.litterStatus==="current"?`${C.emerald}33`:litter.litterStatus==="upcoming"?`${C.sapphire}33`:`${C.amber}33`,color:litter.litterStatus==="current"?C.emeraldL:litter.litterStatus==="upcoming"?C.sapphireL:C.amber}}>
            {litter.litterStatus==="current"?"🟢 Available Now":litter.litterStatus==="upcoming"?"🔵 Upcoming":litter.litterStatus==="reserved"?"🟡 Taking Deposits":"✅ Complete"}
          </span>}
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"serif",fontSize:18,fontWeight:700,color:C.goldL}}>{litter.price}</div>
          <div style={{fontSize:11,color:C.emeraldL}}>{displayAvailable} of {displayCount} available</div>
        </div>
      </div>
      {(sire||dam)&&<div style={{background:C.bg3,borderRadius:6,padding:10,marginBottom:10,fontSize:11}}>
        {sire&&<div style={{color:C.creamD}}>♂ <strong>{sire.name}</strong></div>}
        {dam&&<div style={{color:C.creamD,marginTop:3}}>♀ <strong>{dam.name}</strong></div>}
      </div>}
      {litter.description&&<div style={{fontSize:12,color:C.creamD,lineHeight:1.6,marginBottom:10}}>{litter.description}</div>}
      {b&&<div style={{fontSize:11,color:C.slate,marginBottom:10}}>🏡 {b.kennel}</div>}

      {hasPuppyRecords && (
        <div style={{marginBottom:10}}>
          <button onClick={()=>setShowPuppies(s=>!s)} style={{background:"none",border:"none",color:C.emeraldL,cursor:"pointer",fontSize:12,fontWeight:700,padding:0}}>
            {showPuppies?'✕ Hide Puppies':'See Available Puppies →'}
          </button>
          {showPuppies && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:10,color:C.gold,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Available ({availablePuppies.length})</div>
              {availablePuppies.length===0 ? (
                <div style={{fontSize:12,color:C.slate,marginBottom:12}}>None currently available.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                  {availablePuppies.map(p=><PuppyRow key={p.id} p={p} showPrice litter={litter} supabase={supabase} flash={flash} externalWaitlistUrl={externalUrl}/>)}
                </div>
              )}
              {matchedPuppies.length>0 && (
                <>
                  <div style={{fontSize:10,color:C.gold,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>What We've Produced ({matchedPuppies.length})</div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {matchedPuppies.map(p=><PuppyRow key={p.id} p={p}/>)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {(() => {
        const secondaryLinkStyle = {width:"100%",marginTop:6,background:"none",border:"none",color:C.slate,cursor:"pointer",fontSize:11,textDecoration:"underline"};
        const inquireBtn = (primary) => (
          <button onClick={inquire} style={primary
            ? {...primaryBtnStyle,background:`linear-gradient(135deg,${C.emerald},${C.emeraldL})`,color:"#fff"}
            : secondaryLinkStyle}>
            {primary ? "Inquire About Puppies" : "Or inquire directly"}
          </button>
        );
        const waitlistBlock = (primary) => {
          const label = primary ? "Join Waitlist for This Breed" : "Or join the waitlist / pay a deposit";
          if (externalUrl) {
            return (
              <button onClick={()=>openExternal(externalUrl)} style={primary
                ? {...primaryBtnStyle,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg}
                : secondaryLinkStyle}>
                {label}
              </button>
            );
          }
          if (joined) {
            return <div style={{textAlign:"center",padding:"8px 0",color:C.emeraldL,fontWeight:700,fontSize:12}}>✓ You're on the waitlist</div>;
          }
          return (
            <>
              <button onClick={()=>setJoinOpen(o=>!o)} style={primary
                ? {...primaryBtnStyle,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg}
                : secondaryLinkStyle}>
                {joinOpen ? '✕ Cancel' : label}
              </button>
              {joinOpen && (
                <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
                  <WaitlistPolicyBlock policy={b?.waitlistPolicy} paymentLink={b?.waitlistPaymentLink}/>
                  <input className="fi" placeholder="Your name" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/>
                  <input className="fi" placeholder="Email" type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/>
                  <input className="fi" placeholder="Phone (optional)" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/>
                  <textarea className="fi" rows={2} placeholder="Anything else? (optional)" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={{resize:"vertical"}}/>
                  <button className="bgl" style={{fontSize:12}} disabled={submitting} onClick={submitWaitlist}>{submitting?'Joining…':'Join Waitlist'}</button>
                </div>
              )}
            </>
          );
        };

        if (!hasBreedRef) return inquireBtn(true);
        return waitlistIsPrimary
          ? <>{waitlistBlock(true)}{inquireBtn(false)}</>
          : <>{inquireBtn(true)}{waitlistBlock(false)}</>;
      })()}
    </div>
  );
}

// ── HEALTH-VERIFIED PANEL ADMIN ─────────────────────────────────────────────────
// Admin-only for now (redefining a breed's Health-Verified panel is high-power).
// Kept as plain direct-write CRUD against breed_health_requirements with no baked-in
// single-author assumption, so a future "trusted breed expert proposes, admin approves"
// layer can be added later (e.g. a status column + filter) without restructuring this.
function RequirementRowForm({draft, setDraft, onSave, onCancel}){
  const vocab = TEST_RESULT_VOCAB[draft.test_key] || [];
  return (
    <div style={{background:C.bg2,border:`1px solid ${C.gold}44`,borderRadius:8,padding:14,marginBottom:16,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
      <div>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Test</label>
        <select className="fi" value={draft.test_key} onChange={e=>setDraft(p=>({...p,test_key:e.target.value,pass_values:[]}))}>
          <option value="">Select test…</option>
          {TEST_KEY_OPTIONS.map(t=><option key={t.col} value={t.col}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Display Label</label>
        <input className="fi" value={draft.test_label||""} onChange={e=>setDraft(p=>({...p,test_label:e.target.value}))}/>
      </div>
      <div>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registry</label>
        <input className="fi" value={draft.registry||""} onChange={e=>setDraft(p=>({...p,registry:e.target.value}))}/>
      </div>
      <div>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Requirement</label>
        <select className="fi" value={draft.requirement} onChange={e=>setDraft(p=>({...p,requirement:e.target.value}))}>
          <option value="required">Required</option>
          <option value="recommended">Recommended</option>
        </select>
      </div>
      {draft.requirement==='required' && (
        <div style={{gridColumn:"1/-1"}}>
          <label style={{fontSize:12,color:C.cream,display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
            <input type="checkbox" checked={draft.require_cert_number!==false}
              onChange={e=>setDraft(p=>({...p,require_cert_number:e.target.checked}))}/>
            Require a certificate/registry number to count (a CHIC number on file also satisfies this)
          </label>
          <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Passing Values{!vocab.length&&" (no fixed scale for this test — leave blank)"}</label>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {vocab.map(v=>(
              <label key={v} style={{fontSize:12,color:C.cream,display:"flex",alignItems:"center",gap:4}}>
                <input type="checkbox" checked={(draft.pass_values||[]).includes(v)}
                  onChange={e=>setDraft(p=>({...p,pass_values: e.target.checked ? [...(p.pass_values||[]),v] : (p.pass_values||[]).filter(x=>x!==v)}))}/>
                {v}
              </label>
            ))}
          </div>
        </div>
      )}
      <div>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Valid Months (recency; blank = never expires)</label>
        <input className="fi" type="number" value={draft.valid_months||""} onChange={e=>setDraft(p=>({...p,valid_months:e.target.value?parseInt(e.target.value):null}))}/>
      </div>
      <div>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Acceptable Registries (comma-separated)</label>
        <input className="fi" value={(draft.acceptable_registries||[]).join(', ')}
          onChange={e=>setDraft(p=>({...p,acceptable_registries:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))}/>
      </div>
      <div style={{gridColumn:"1/-1"}}>
        <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Notes</label>
        <input className="fi" value={draft.notes||""} onChange={e=>setDraft(p=>({...p,notes:e.target.value}))}/>
      </div>
      <div style={{gridColumn:"1/-1",display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="bgs" onClick={onCancel}>Cancel</button>
        <button className="bgl" onClick={onSave} disabled={!draft.test_key||!draft.test_label}>Save</button>
      </div>
    </div>
  );
}

function HealthPanelsAdmin({breeds, designerBreeds, setDesignerBreeds, customCrosses, healthRequirements, setHealthRequirements, dogs, data, setData, supabase, flash}){
  const [selType, setSelType] = useState('purebred');
  const [selId, setSelId] = useState('');
  const [draft, setDraft] = useState(null);
  const [autoResults, setAutoResults] = useState({});

  const purebredList = (breeds||[]).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const designerList = (designerBreeds||[]).slice().sort((a,b)=>a.name.localeCompare(b.name));

  const rowsForBreed = healthRequirements.filter(r => r.breed_type==='purebred' && r.breed_id===selId);
  const required = rowsForBreed.filter(r=>r.requirement==='required').sort((a,b)=>(a.sort_order||0)-(b.sort_order||0));
  const recommended = rowsForBreed.filter(r=>r.requirement==='recommended').sort((a,b)=>(a.sort_order||0)-(b.sort_order||0));

  const selectedDesigner = designerList.find(d=>d.id===selId);
  const parentIds = selectedDesigner?.parent_breed_ids || [];
  const unionRequired = (() => {
    const seen = new Set(), rows = [];
    parentIds.forEach(pid => {
      healthRequirements.filter(r=>r.breed_type==='purebred'&&r.breed_id===pid&&r.requirement==='required').forEach(r=>{
        if(!seen.has(r.test_key)){seen.add(r.test_key);rows.push(r);}
      });
    });
    return rows;
  })();

  const startAdd = (requirement) => setDraft({test_key:'',test_label:'',registry:'',acceptable_registries:[],requirement,pass_values:[],valid_months:null,require_cert_number:true,notes:''});
  const startEdit = (row) => setDraft({...row});

  const saveDraft = async () => {
    const payload = {
      breed_type:'purebred', breed_id:selId, designer_breed_id:null,
      test_key:draft.test_key, test_label:draft.test_label, registry:draft.registry||'',
      acceptable_registries:(draft.acceptable_registries&&draft.acceptable_registries.length)?draft.acceptable_registries:null,
      requirement:draft.requirement,
      pass_values:(draft.pass_values&&draft.pass_values.length)?draft.pass_values:null,
      require_cert_number: draft.requirement==='required' ? (draft.require_cert_number!==false) : true,
      valid_months:draft.valid_months||null, min_age_months:draft.min_age_months||null,
      sort_order:draft.sort_order||0, notes:draft.notes||null,
    };
    if(draft.id){
      const {error} = await supabase.from('breed_health_requirements').update(payload).eq('id',draft.id);
      if(error){flash('⚠ '+error.message);return;}
      setHealthRequirements(prev=>prev.map(r=>r.id===draft.id?{...r,...payload}:r));
    } else {
      const {data:inserted,error} = await supabase.from('breed_health_requirements').insert(payload).select().single();
      if(error){flash('⚠ '+error.message);return;}
      setHealthRequirements(prev=>[...prev,inserted]);
    }
    setDraft(null);
    flash('Saved ✓');
  };

  const deleteRow = async (id) => {
    const {error} = await supabase.from('breed_health_requirements').delete().eq('id',id);
    if(error){flash('⚠ '+error.message);return;}
    setHealthRequirements(prev=>prev.filter(r=>r.id!==id));
  };

  const toggleParentBreed = async (breedId) => {
    const current = selectedDesigner?.parent_breed_ids || [];
    const next = current.includes(breedId) ? current.filter(id=>id!==breedId) : [...current,breedId];
    const {error} = await supabase.from('designer_breeds').update({parent_breed_ids:next}).eq('id',selId);
    if(error){flash('⚠ '+error.message);return;}
    setDesignerBreeds(prev=>prev.map(d=>d.id===selId?{...d,parent_breed_ids:next}:d));
  };

  const overriddenDogs = (dogs||[]).filter(d=>d.healthVerifiedOverride===true||d.healthVerifiedOverride===false);
  useEffect(()=>{
    let cancelled=false;
    (async ()=>{
      const results={};
      for(const d of overriddenDogs){
        results[d.id] = await computeAutoEligibility(d, healthRequirements, breeds, designerBreeds, supabase, customCrosses);
      }
      if(!cancelled) setAutoResults(results);
    })();
    return ()=>{cancelled=true};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dogs, healthRequirements, designerBreeds, customCrosses]);

  const clearOverride = async (dog) => {
    const {error} = await supabase.from('dogs').update({health_verified_override:null}).eq('id',dog.id);
    if(error){flash('⚠ '+error.message);return;}
    setData(prev=>({...prev, dogs:{...prev.dogs, [dog.id]:{...prev.dogs[dog.id], healthVerifiedOverride:null}}}));
    flash('Override cleared ✓');
  };

  return (
    <div>
      <div style={{fontSize:13,color:C.slate,marginBottom:16,lineHeight:1.6}}>
        Defines which health clearances a breed needs for the Health-Verified badge. Purebred panels are edited directly here; designer-breed panels are computed automatically as the union of their linked foundation breeds' required tests.
      </div>
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <select className="fi" style={{maxWidth:160}} value={selType} onChange={e=>{setSelType(e.target.value);setSelId('');setDraft(null);}}>
          <option value="purebred">Purebred</option>
          <option value="designer">Designer</option>
        </select>
        <select className="fi" style={{maxWidth:280}} value={selId} onChange={e=>{setSelId(e.target.value);setDraft(null);}}>
          <option value="">Select breed…</option>
          {(selType==='purebred'?purebredList:designerList).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {selType==='purebred' && selId && (
        <div>
          <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,marginBottom:8}}>Required</div>
          {required.map(r=>(
            <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",marginBottom:6}}>
              <div>
                <div style={{fontSize:14,color:C.cream,fontWeight:600}}>{r.test_label} <span style={{fontSize:11,color:C.slate}}>({r.registry})</span></div>
                <div style={{fontSize:11,color:C.slate}}>Pass: {(r.pass_values||[]).join(', ')||'—'}{r.valid_months?` · valid ${r.valid_months}mo`:''}{r.require_cert_number!==false?' · cert # required (or CHIC)':''}</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button className="bgs" style={{fontSize:11}} onClick={()=>startEdit(r)}>Edit</button>
                <button className="bgs" style={{fontSize:11,color:"#e74c3c"}} onClick={()=>deleteRow(r.id)}>Delete</button>
              </div>
            </div>
          ))}
          <button className="bos" style={{fontSize:12,marginBottom:20}} onClick={()=>startAdd('required')}>+ Add Required Test</button>

          <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,marginBottom:8}}>Recommended</div>
          {recommended.map(r=>(
            <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",marginBottom:6}}>
              <div style={{fontSize:14,color:C.cream,fontWeight:600}}>{r.test_label} <span style={{fontSize:11,color:C.slate}}>({r.registry})</span></div>
              <div style={{display:"flex",gap:8}}>
                <button className="bgs" style={{fontSize:11}} onClick={()=>startEdit(r)}>Edit</button>
                <button className="bgs" style={{fontSize:11,color:"#e74c3c"}} onClick={()=>deleteRow(r.id)}>Delete</button>
              </div>
            </div>
          ))}
          <button className="bos" style={{fontSize:12}} onClick={()=>startAdd('recommended')}>+ Add Recommended Test</button>

          {draft && <RequirementRowForm draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={()=>setDraft(null)}/>}
        </div>
      )}

      {selType==='designer' && selId && (
        <div>
          <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,marginBottom:8}}>Foundation Breeds</div>
          <div style={{fontSize:12,color:C.slate,marginBottom:10}}>Health-Verified for this designer breed = union of required tests from its linked foundation breeds. Link breeds that already have a panel to make this designer breed eligible.</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:20}}>
            {purebredList.map(b=>(
              <label key={b.id} style={{fontSize:12,color:C.cream,display:"flex",alignItems:"center",gap:5,background:parentIds.includes(b.id)?`${C.gold}22`:C.card,border:`1px solid ${parentIds.includes(b.id)?C.gold+'66':C.border}`,borderRadius:16,padding:"5px 12px",cursor:"pointer"}}>
                <input type="checkbox" checked={parentIds.includes(b.id)} onChange={()=>toggleParentBreed(b.id)}/>
                {b.name}
              </label>
            ))}
          </div>
          <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,marginBottom:8}}>Computed Required Panel (read-only)</div>
          {unionRequired.length===0 && <div style={{fontSize:13,color:C.slate}}>No linked foundation breed has a seeded panel yet — this designer breed isn't eligible for Health-Verified until one does.</div>}
          {unionRequired.map(r=>(
            <div key={r.test_key} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",marginBottom:6}}>
              <div style={{fontSize:14,color:C.cream,fontWeight:600}}>{r.test_label} <span style={{fontSize:11,color:C.slate}}>({r.registry})</span></div>
              <div style={{fontSize:11,color:C.slate}}>Pass: {(r.pass_values||[]).join(', ')||'—'}{r.valid_months?` · valid ${r.valid_months}mo`:''}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{marginTop:32,paddingTop:20,borderTop:`1px solid ${C.border}`}}>
        <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,marginBottom:4}}>Manual Overrides</div>
        <div style={{fontSize:12,color:C.slate,marginBottom:10}}>Dogs with a manual Health-Verified override. "Auto-check" shows what the automatic panel check would say right now — if it disagrees with the override, the override may be stale.</div>
        {overriddenDogs.length===0 && <div style={{fontSize:13,color:C.slate}}>No dogs currently have a manual override.</div>}
        {overriddenDogs.map(d=>(
          <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",marginBottom:6}}>
            <div>
              <div style={{fontSize:14,color:C.cream,fontWeight:600}}>{d.name} <span style={{fontSize:11,color:C.slate}}>({d.breed})</span></div>
              <div style={{fontSize:11,color:d.healthVerifiedOverride?C.emeraldL:'#e74c3c'}}>
                Override: {d.healthVerifiedOverride?'Force Verified':'Force Denied'} · Auto-check: {autoResults[d.id]===undefined?'…':(autoResults[d.id]?'PASS':'FAIL')}
                {autoResults[d.id]!==undefined && autoResults[d.id]!==d.healthVerifiedOverride && <span style={{color:"#e6b800"}}> ⚠ disagrees</span>}
              </div>
            </div>
            <button className="bgs" style={{fontSize:11}} onClick={()=>clearOverride(d)}>Clear Override</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ADMIN PANEL ─────────────────────────────────────────────────────────────
function AdminPanel({data,setData,dogs,breeders,litters,breeds,designerBreeds,setDesignerBreeds,customCrosses,healthRequirements,setHealthRequirements,adminTab,setAdminTab,flash,supabase}){
  const [editDog, setEditDog] = useState(null);
  const [inlineDog, setInlineDog] = useState(null);
  const [inlineKennel, setInlineKennel] = useState(null);
  const [editBreeder, setEditBreeder] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [uprQueue, setUprQueue] = useState([]);
  const [uprSubTab, setUprSubTab] = useState('pending');
  const [rejectTarget, setRejectTarget] = useState(null); // { regId, dogName }
  const [rejectReason, setRejectReason] = useState('');

  useEffect(()=>{
    if(adminTab==="members") loadMembers();
    if(adminTab==="upr") loadUprQueue();
  },[adminTab]);

  const loadMembers = async()=>{
    setLoadingMembers(true);
    const {data:m} = await supabase.from('members').select('*').order('created_at',{ascending:false});
    setMembers(m||[]);
    setLoadingMembers(false);
  };

  const loadUprQueue = async()=>{
    const {data,error} = await supabase
      .from('upr_registrations')
      .select('*, dogs(name,breed,type), breeders(name,kennel,email)')
      .order('created_at',{ascending:false});
    if(error) console.log('loadUprQueue error:',error.message);
    setUprQueue(data||[]);
  };

  const approveReg = async(regId)=>{
    const {data:{session}} = await supabase.auth.getSession();
    const resp = await fetch('/api/upr-review',{
      method:'POST',
      headers:{'Content-Type':'application/json',Authorization:`Bearer ${session.access_token}`},
      body:JSON.stringify({regId,action:'approve'}),
    });
    const json = await resp.json();
    if(!resp.ok){flash('Approve failed: '+(json.error||resp.status));return;}
    flash(`Approved — ${json.upr_number} ✓`);
    setUprQueue(prev=>prev.map(r=>r.id===regId?{...r,status:'approved',upr_number:json.upr_number,reviewed_at:new Date().toISOString()}:r));
  };

  const rejectReg = async(regId,reason)=>{
    const {data:{session}} = await supabase.auth.getSession();
    const resp = await fetch('/api/upr-review',{
      method:'POST',
      headers:{'Content-Type':'application/json',Authorization:`Bearer ${session.access_token}`},
      body:JSON.stringify({regId,action:'reject',adminNotes:reason}),
    });
    const json = await resp.json();
    if(!resp.ok){flash('Reject failed: '+(json.error||resp.status));return;}
    flash('Application rejected.');
    setUprQueue(prev=>prev.map(r=>r.id===regId?{...r,status:'rejected',admin_notes:reason,reviewed_at:new Date().toISOString()}:r));
    setRejectTarget(null);
    setRejectReason('');
  };

  const openDocUrl = async(path)=>{
    const {data,error} = await supabase.storage.from('club-documents').createSignedUrl(path,3600);
    if(error||!data?.signedUrl){flash('Could not load document: '+(error?.message||'unknown'));return;}
    window.open(data.signedUrl,'_blank');
  };

  const updateMemberRole = async(id, role)=>{
    await supabase.from('members').update({role}).eq('id',id);
    setMembers(prev=>prev.map(m=>m.id===id?{...m,role}:m));
    flash("Member role updated!");
  };

  const deleteMember = async(id)=>{
    if(!window.confirm("Delete this member? This cannot be undone.")) return;
    await supabase.from('members').delete().eq('id',id);
    setMembers(prev=>prev.filter(m=>m.id!==id));
    flash("Member deleted.");
  };

  const saveDog = async (dog)=>{
    setData(prev=>({...prev,dogs:{...prev.dogs,[dog.id]:dog}}));
    setEditDog(null);
    flash("Saving…");
    try {
      await supabase.from('dogs').upsert(SB.dogToDB(dog));
      flash("Dog saved! ✓");
    } catch(e) {
      flash("Saved locally — sync error: " + e.message);
    }
  };

  const deleteDog = async (id)=>{
    if(!window.confirm("Delete this dog?")) return;
    setData(prev=>{const d={...prev.dogs};delete d[id];return{...prev,dogs:d};});
    flash("Deleting…");
    await supabase.from('dogs').delete().eq('id',id);
    flash("Dog deleted.");
  };

 const saveBreeder = async (breeder)=>{
    setData(prev=>({...prev,breeders:{...prev.breeders,[breeder.id]:breeder}}));
    setEditBreeder(null);
    flash("Saving…");
    try {
      await supabase.from('breeders').upsert(SB.breederToDB(breeder));
      flash("Breeder saved! ✓");
    } catch(e) {
      flash("Saved locally — sync error: " + e.message);
    }
  };

  const deleteBreeder = (id)=>{
    if(!window.confirm("Delete this breeder?")) return;
    setData(prev=>{const b={...prev.breeders};delete b[id];return{...prev,breeders:b};});
    flash("Breeder deleted.");
  };

  const ROLE_OPTIONS = ["public","buyer_single","buyer_bundle","buyer_full","breeder","admin","blocked"];

  return(
    <div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:22}}>
        <h2 style={{fontFamily:"serif",fontSize:26,fontWeight:900,margin:0}}>Admin Panel</h2>
        <span style={{fontSize:11,color:C.goldL,background:`${C.gold}18`,border:`1px solid ${C.gold}44`,borderRadius:20,padding:"3px 12px",letterSpacing:1}}>FOUNDER</span>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:2,marginBottom:24,borderBottom:`1px solid ${C.border}`,paddingBottom:0,flexWrap:"wrap"}}>
        {[["overview","Overview"],["members","Members"],["dogs","Dogs"],["breeders","Breeders"],["revenue","Revenue"],["edits","Pending Edits"],["upr","UPR Registrations"],["health","Health Panels"]].map(([v,l])=>(
          <button key={v} onClick={()=>setAdminTab(v)} style={{padding:"8px 18px",background:"transparent",border:"none",borderBottom:`2px solid ${adminTab===v?C.gold:"transparent"}`,color:adminTab===v?C.gold:C.slate,fontSize:13,cursor:"pointer",fontFamily:"sans-serif",fontWeight:adminTab===v?700:400,marginBottom:-1,transition:"all 0.15s"}}>{l}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {adminTab==="overview"&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
          {[[dogs.length,"Total Dogs",C.goldL],[breeders.length,"Breeders",C.sapphireL],[litters.length,"Active Litters",C.emeraldL],[[...new Set(dogs.map(d=>d.country))].length,"Countries",C.amber]].map(([n,l,c])=>(
            <div key={l} style={{background:C.card,border:`1px solid ${C.border}`,borderTop:`3px solid ${c}`,borderRadius:8,padding:"18px 14px",textAlign:"center"}}>
              <div style={{fontFamily:"serif",fontSize:36,fontWeight:900,color:c,lineHeight:1}}>{n}</div>
              <div style={{fontSize:9,color:C.slate,letterSpacing:2,textTransform:"uppercase",marginTop:4}}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── MEMBERS ── */}
      {adminTab==="members"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:C.slate}}>{members.length} registered members</div>
            <button className="bgs" onClick={loadMembers}>↻ Refresh</button>
          </div>
          {loadingMembers
            ?<div style={{textAlign:"center",padding:40,color:C.slate}}>Loading members…</div>
            :<div style={{display:"flex",flexDirection:"column",gap:8}}>
              {members.map(m=>(
                <div key={m.id} style={{background:C.card,border:`1px solid ${m.role==="blocked"?C.red:C.border}`,borderRadius:8,padding:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    <div style={{fontSize:14,fontWeight:600,color:C.cream}}>{m.email}</div>
                    <div style={{fontSize:11,color:C.slate,marginTop:2}}>
                      Joined: {m.created_at?new Date(m.created_at).toLocaleDateString():"Unknown"}
                      {m.pedigree_credits>0&&` · ${m.pedigree_credits} credits`}
                    </div>
                  </div>
                  <select
                    value={m.role||"public"}
                    onChange={e=>updateMemberRole(m.id,e.target.value)}
                    style={{background:C.bg3,border:`1px solid ${C.border}`,color:m.role==="blocked"?C.red:m.role==="admin"?C.goldL:m.role==="breeder"?C.emeraldL:C.slate,borderRadius:5,padding:"5px 10px",fontFamily:"sans-serif",fontSize:12,cursor:"pointer"}}>
                    {ROLE_OPTIONS.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                  <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:11}} onClick={()=>deleteMember(m.id)}>Delete</button>
                </div>
              ))}
              {members.length===0&&<div style={{textAlign:"center",padding:40,color:C.slate,border:`1px dashed ${C.border}`,borderRadius:8}}>No members yet.</div>}
            </div>
          }
        </div>
      )}

      {/* ── DOGS ── */}
      {adminTab==="dogs"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:C.slate}}>{dogs.length} dogs in registry</div>
            <button className="bos" onClick={()=>setEditDog({id:crypto.randomUUID(),type:"purebred",breed:"",breedId:null,name:"",callName:"",sex:"M",color:"",dob:"",country:"United States",regNum:"",regBody:"AKC",ofaHips:"",ofaElbows:"",ofaEyes:"",chicNum:"",mdr1:"",baer:"",titles:"",breederId:"",sireId:"",damId:"",photoUrl:"",notes:"",hidden:false})}>+ Add Dog</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {dogs.map(d=>(
              <div key={d.id} style={{background:d.hidden?C.bg2:C.card,border:`1px solid ${d.hidden?"#444":C.border}`,borderRadius:8,padding:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",opacity:d.hidden?0.6:1}}>
                <div style={{flex:1,minWidth:160}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.cream}}>{d.name||"Unnamed"} {d.hidden&&<span style={{fontSize:10,color:C.slate}}>(hidden)</span>}</div>
                  <div style={{fontSize:11,color:C.slate}}>{d.breed} · {d.type} · {d.sex==="M"?"Male":"Female"} · {d.country}</div>
                  {d.regNum&&<div style={{fontSize:11,color:C.gold}}>{d.regBody}: {d.regNum}</div>}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {d.mdr1&&<span style={{fontSize:10,color:mdr1C(d.mdr1),background:`${mdr1C(d.mdr1)}18`,border:`1px solid ${mdr1C(d.mdr1)}44`,borderRadius:10,padding:"2px 8px"}}>MDR1: {d.mdr1}</span>}
                  {d.ofaHips&&<span style={{fontSize:10,color:C.gold,background:`${C.gold}18`,border:`1px solid ${C.gold}44`,borderRadius:10,padding:"2px 8px"}}>OFA: {d.ofaHips}</span>}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="bgs" style={{fontSize:11}} onClick={async()=>{
  const updated = {...d, hidden:!d.hidden};
  setData(prev=>({...prev,dogs:{...prev.dogs,[d.id]:updated}}));
  await supabase.from('dogs').update({hidden:updated.hidden}).eq('id',d.id);
  flash(updated.hidden?"Dog hidden":"Dog visible");
}}>
                        {d.hidden?"Show":"Hide"}
                  </button>
                  <button className="bos" style={{fontSize:11}} onClick={()=>setEditDog({...d})}>Edit</button>
                  <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:11}} onClick={()=>deleteDog(d.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>

          {/* Dog Edit Modal */}
          {editDog&&(
            <div onClick={e=>{if(e.target===e.currentTarget)setEditDog(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:100,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px",overflowY:"auto"}}>
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:28,width:"100%",maxWidth:640,margin:"auto"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                  <div style={{fontFamily:"serif",fontSize:20,fontWeight:700}}>{editDog.id?"Edit Dog":"Add Dog"}</div>
                  <button className="bgs" onClick={()=>setEditDog(null)}>✕ Close</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {[
                   ["Registered Name","name","text"],
                    ["Call Name","callName","text"],
                    ["Color","color","text"],
                    ["Date of Birth","dob","date"],
                    ["Registration #","regNum","text"],
                    ["DNA Number","dnaNum","text"],
                    ["CHIC Number","chicNum","text"],
                    ["BAER","baer","text"],
                    ["Titles","titles","text"],
                  ].map(([label,field,type])=>(
                    <div key={field}>
                      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                      <input className="fi" type={type} value={editDog[field]||""} onChange={e=>setEditDog(p=>({...p,[field]:e.target.value}))}/>
                    </div>
                  ))}
                  <OfaResultField testKey="ofa_hips" label="OFA Hips" value={editDog.ofaHips} onChange={v=>setEditDog(p=>({...p,ofaHips:v}))}
                    certValue={editDog.ofaHipsCert} onCertChange={v=>setEditDog(p=>({...p,ofaHipsCert:v}))}
                    registryValue={editDog.ofaHipsRegistry} onRegistryChange={v=>setEditDog(p=>({...p,ofaHipsRegistry:v}))} altRegistries={['PennHIP']}
                    diValue={editDog.ofaHipsDi} onDiChange={v=>setEditDog(p=>({...p,ofaHipsDi:v}))}/>
                  <OfaResultField testKey="ofa_elbows" label="OFA Elbows" value={editDog.ofaElbows} onChange={v=>setEditDog(p=>({...p,ofaElbows:v}))}
                    certValue={editDog.ofaElbowsCert} onCertChange={v=>setEditDog(p=>({...p,ofaElbowsCert:v}))}/>
                  <OfaResultField testKey="ofa_eyes" label="OFA Eyes" value={editDog.ofaEyes} onChange={v=>setEditDog(p=>({...p,ofaEyes:v}))}/>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Breed</label>
                    <BreedPicker breeds={breeds} selectedId={editDog.breedId} selectedName={editDog.breed}
                      onChange={b=>setEditDog(p=>({...p,breedId:b?.id||null,breed:b?.name||""}))}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Sex</label>
                    <select className="fi" value={editDog.sex} onChange={e=>setEditDog(p=>({...p,sex:e.target.value}))}>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>MDR1 Status</label>
                    <select className="fi" value={editDog.mdr1||""} onChange={e=>setEditDog(p=>({...p,mdr1:e.target.value}))}>
                      <option value="">Unknown</option>
                      <option value="Clear">Clear</option>
                      <option value="Carrier">Carrier</option>
                      <option value="Affected">Affected</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Type</label>
                    <select className="fi" value={editDog.type} onChange={e=>setEditDog(p=>({...p,type:e.target.value}))}>
                      <option value="purebred">Purebred</option>
                      <option value="heritage">Designer</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                    <select className="fi" value={editDog.country||"United States"} onChange={e=>setEditDog(p=>({...p,country:e.target.value}))}>
                      {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Registry Body</label>
                    <select className="fi" value={editDog.regBody||"AKC"} onChange={e=>setEditDog(p=>({...p,regBody:e.target.value}))}>
                      {REG_BODIES.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>AKC Number</label>
                    <input className="fi" value={editDog.akcNum||""} onChange={e=>setEditDog(p=>({...p,akcNum:e.target.value}))}/>
                  </div>
                  <div>
                    {editDog.breed==="Australian Shepherd"&&(
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>ASCA Number</label>
                    <input className="fi" value={editDog.ascaNum||""} onChange={e=>setEditDog(p=>({...p,ascaNum:e.target.value}))}/>
                  </div>
                  )}
                  {editDog.breed==="German Shepherd"&&(
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>SV Number</label>
                    <input className="fi" value={editDog.svNum||""} onChange={e=>setEditDog(p=>({...p,svNum:e.target.value}))}/>
                  </div>
                  )}
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>UKC Number</label>
                    <input className="fi" value={editDog.ukcNum||""} onChange={e=>setEditDog(p=>({...p,ukcNum:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>MDR1 Status</label>
                    <select className="fi" value={editDog.mdr1||""} onChange={e=>setEditDog(p=>({...p,mdr1:e.target.value}))}>
                      <option value="">Unknown</option>
                      <option value="Clear">Clear</option>
                      <option value="Carrier">Carrier</option>
                      <option value="Affected">Affected</option>
                    </select>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Breeder Kennel</label>
                    <KennelAutocomplete value={editDog.breederId} onChange={v=>setEditDog(p=>({...p,breederId:v}))} breeders={data.breeders}
                      onAdd={(q)=>setInlineKennel({initialName:q,onSave:(id)=>{setEditDog(p=>({...p,breederId:id})); setInlineKennel(null);}})}
                      placeholder="Search kennels (3+ chars) or leave blank for orphan…"/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Sire (Father)</label>
                    {!editDog.sireId && editDog.sireNameManual && (
                      <div style={{marginBottom:6,padding:"6px 10px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:6,fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                        <span style={{color:C.creamD}}>Existing manual entry: <strong>{editDog.sireNameManual}</strong></span>
                        <button type="button" onClick={()=>setInlineDog({sex:'M',initialName:editDog.sireNameManual,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,sireId:id,sireNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                      </div>
                    )}
                    <DogAutocomplete sex="M" addLabel="sire" value={editDog.sireId} onChange={v=>setEditDog(p=>({...p,sireId:v}))} dogs={data.dogs} breeders={data.breeders} currentDogId={editDog.id}
                      onAdd={(query)=>setInlineDog({sex:'M',initialName:query,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,sireId:id}));setInlineDog(null);}})}
                      placeholder="Search sires (3+ chars)…"/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Dam (Mother)</label>
                    {!editDog.damId && editDog.damNameManual && (
                      <div style={{marginBottom:6,padding:"6px 10px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:6,fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                        <span style={{color:C.creamD}}>Existing manual entry: <strong>{editDog.damNameManual}</strong></span>
                        <button type="button" onClick={()=>setInlineDog({sex:'F',initialName:editDog.damNameManual,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,damId:id,damNameManual:''}));setInlineDog(null);}})} style={{background:"none",border:"none",color:C.goldL,cursor:"pointer",fontWeight:700,fontSize:12,padding:0}}>Convert to dog record →</button>
                      </div>
                    )}
                    <DogAutocomplete sex="F" addLabel="dam" value={editDog.damId} onChange={v=>setEditDog(p=>({...p,damId:v}))} dogs={data.dogs} breeders={data.breeders} currentDogId={editDog.id}
                      onAdd={(query)=>setInlineDog({sex:'F',initialName:query,initialBreed:editDog.breed,onSave:(id)=>{setEditDog(p=>({...p,damId:id}));setInlineDog(null);}})}
                      placeholder="Search dams (3+ chars)…"/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Photo URL</label>
                    <input className="fi" placeholder="https://… or upload below" value={editDog.photoUrl||""} onChange={e=>setEditDog(p=>({...p,photoUrl:e.target.value}))}/>
                    <label style={{display:"inline-block",marginTop:6,background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:11,padding:"6px 14px",borderRadius:5,cursor:"pointer"}}>
                      📷 Upload Photo
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setEditDog(p=>({...p,photoUrl:ev.target.result}));r.readAsDataURL(f);e.target.value="";}}/>
                    </label>
                    {editDog.photoUrl&&<img src={editDog.photoUrl} alt="preview" style={{display:"block",marginTop:8,width:80,height:80,objectFit:"cover",borderRadius:6,border:`2px solid ${C.gold}66`}}/>}
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Notes</label>
                    <textarea className="fi" rows={3} value={editDog.notes||""} onChange={e=>setEditDog(p=>({...p,notes:e.target.value}))} style={{resize:"vertical"}}/>
                  </div>
                  <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:8}}>
                    <input type="checkbox" id="hidden" checked={editDog.hidden||false} onChange={e=>setEditDog(p=>({...p,hidden:e.target.checked}))}/>
                    <label htmlFor="hidden" style={{fontSize:13,color:C.slate,cursor:"pointer"}}>Hide this dog from public view</label>
                  </div>
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                  <button className="bgs" onClick={()=>setEditDog(null)}>Cancel</button>
                  <button className="bgl" onClick={()=>saveDog(editDog)}>Save Dog</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── BREEDERS ── */}
      {adminTab==="breeders"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:C.slate}}>{breeders.length} breeders registered</div>
            <button className="bos" onClick={()=>setEditBreeder({id:crypto.randomUUID(),kennel:"",name:"",email:"",country:"United States",region:"",website:"",bio:"",joined:new Date().toISOString().split("T")[0],tiers:["foundation"],photoUrl:"",verified:false,blocked:false})}>+ Add Breeder</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {breeders.map(b=>(
              <div key={b.id} style={{background:b.blocked?C.bg2:C.card,border:`1px solid ${b.blocked?C.red:C.border}`,borderRadius:8,padding:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",opacity:b.blocked?0.6:1}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:15,fontWeight:700,color:C.cream}}>{b.kennel} {b.blocked&&<span style={{fontSize:10,color:C.red}}>(blocked)</span>}</div>
                  <div style={{fontSize:11,color:C.slate}}>{b.name} · {b.country} · Joined: {b.joined||"Unknown"}</div>
                  <div style={{fontSize:11,color:C.slate}}>{b.email}</div>
                  <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}}>
                    {b.tiers?.map(t=><span key={t} style={{fontSize:9,color:C.goldL,background:`${C.gold}18`,border:`1px solid ${C.gold}44`,borderRadius:10,padding:"1px 8px"}}>{t}</span>)}
                    {b.verified&&<span style={{fontSize:9,color:C.emeraldL,background:`${C.emerald}18`,border:`1px solid ${C.emerald}44`,borderRadius:10,padding:"1px 8px"}}>✓ verified</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button className="bgs" style={{fontSize:11,color:b.blocked?C.emeraldL:C.red,borderColor:b.blocked?`${C.emerald}44`:`${C.red}44`}}
                    onClick={async()=>{
  const updated = {...b, blocked:!b.blocked};
  setData(prev=>({...prev,breeders:{...prev.breeders,[b.id]:updated}}));
  await supabase.from('breeders').update({blocked:updated.blocked}).eq('id',b.id);
  flash(updated.blocked?"Breeder blocked":"Breeder unblocked");
}}>
                    {b.blocked?"Unblock":"Block"}
                  </button>
                  <button className="bgs" style={{fontSize:11,color:b.verified?C.slate:C.emeraldL,borderColor:b.verified?`${C.slate}44`:`${C.emerald}44`}}
                  onClick={async()=>{
  const updated = {...b, verified:!b.verified};
  setData(prev=>({...prev,breeders:{...prev.breeders,[b.id]:updated}}));
  await supabase.from('breeders').update({verified:updated.verified}).eq('id',b.id);
  flash(updated.verified?"Breeder verified ✓":"Breeder unverified");
}}>
                    {b.verified?"Unverify":"Verify"}
                  </button>
                  <button className="bgs" style={{fontSize:11,color:b.trustedBreeder?C.gold:C.slate,borderColor:b.trustedBreeder?`${C.gold}44`:`${C.slate}44`}}
                    onClick={async()=>{
  const updated={...b,trustedBreeder:!b.trustedBreeder};
  setData(prev=>({...prev,breeders:{...prev.breeders,[b.id]:updated}}));
  await supabase.from('breeders').update({trusted_breeder:!b.trustedBreeder}).eq('id',b.id);
  flash(updated.trustedBreeder?'Trusted breeder granted ⚜':'Trusted status revoked');
}}>
                    {b.trustedBreeder?'Trusted ⚜':'Trust'}
                  </button>
                  <button className="bos" style={{fontSize:11}} onClick={()=>setEditBreeder({...b})}>Edit</button>
                  <button className="bgs" style={{color:C.red,borderColor:C.red+"44",fontSize:11}} onClick={()=>deleteBreeder(b.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>

          {/* Breeder Edit Modal */}
          {editBreeder&&(
            <div onClick={e=>{if(e.target===e.currentTarget)setEditBreeder(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:100,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px",overflowY:"auto"}}>
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:28,width:"100%",maxWidth:560,margin:"auto"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                  <div style={{fontFamily:"serif",fontSize:20,fontWeight:700}}>{data.breeders[editBreeder.id] ? "Edit Breeder" : "Add Breeder"}</div>
                  <button className="bgs" onClick={()=>setEditBreeder(null)}>✕ Close</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {[["Kennel Name","kennel"],["Contact Name","name"],["Email","email"],["Website","website"],["Region/State","region"]].map(([label,field])=>(
                    <div key={field}>
                      <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>{label}</label>
                      <input className="fi" value={editBreeder[field]||""} onChange={e=>setEditBreeder(p=>({...p,[field]:e.target.value}))}/>
                    </div>
                  ))}
                  <div>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Country</label>
                    <select className="fi" value={editBreeder.country||"United States"} onChange={e=>setEditBreeder(p=>({...p,country:e.target.value}))}>
                      {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:3}}>Bio / Description</label>
                    <textarea className="fi" rows={3} value={editBreeder.bio||""} onChange={e=>setEditBreeder(p=>({...p,bio:e.target.value}))} style={{resize:"vertical"}}/>
                  </div>
                  <div style={{gridColumn:"1/-1",display:"flex",gap:16,flexWrap:"wrap"}}>
                    <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:C.slate}}>
                      <input type="checkbox" checked={editBreeder.verified||false} onChange={e=>setEditBreeder(p=>({...p,verified:e.target.checked}))}/>
                      Verified Breeder
                    </label>
                    <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:C.slate}}>
                      <input type="checkbox" checked={editBreeder.blocked||false} onChange={e=>setEditBreeder(p=>({...p,blocked:e.target.checked}))}/>
                      Blocked
                    </label>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:10,color:C.slate,letterSpacing:1,textTransform:"uppercase",display:"block",marginBottom:6}}>Membership Tiers</label>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {["foundation","litter","verified","kennel"].map(t=>(
                        <label key={t} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12,color:C.creamD}}>
                          <input type="checkbox" checked={editBreeder.tiers?.includes(t)||false} onChange={e=>{
                            const tiers = editBreeder.tiers||[];
                            setEditBreeder(p=>({...p,tiers:e.target.checked?[...tiers,t]:tiers.filter(x=>x!==t)}));
                          }}/>
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                  <button className="bgs" onClick={()=>setEditBreeder(null)}>Cancel</button>
                  <button className="bgl" onClick={()=>saveBreeder(editBreeder)}>Save Breeder</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── REVENUE ── */}
      {adminTab==="revenue"&&(
        <div>
          {/* Summary cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:24}}>
            {[
              {label:"Foundation Breeder",color:C.goldL,count:members.filter(m=>m.role==="breeder"||m.role==="admin").length,price:"$29/mo",total:(members.filter(m=>m.role==="breeder"||m.role==="admin").length*29)},
              {label:"Legacy Full Access",color:C.sapphireL,count:members.filter(m=>m.role==="buyer_full").length,price:"$97/yr",total:(members.filter(m=>m.role==="buyer_full").length*97)},
              {label:"Pedigree Bundle",color:C.goldL,count:members.filter(m=>m.role==="buyer_bundle").length,price:"$47",total:(members.filter(m=>m.role==="buyer_bundle").length*47)},
              {label:"Single Pedigree",color:C.amber,count:members.filter(m=>m.role==="buyer_single").length,price:"$27",total:(members.filter(m=>m.role==="buyer_single").length*27)},
            ].map(t=>(
              <div key={t.label} style={{background:C.card,border:`1px solid ${C.border}`,borderTop:`3px solid ${t.color}`,borderRadius:8,padding:"16px 14px",textAlign:"center"}}>
                <div style={{fontFamily:"serif",fontSize:28,fontWeight:900,color:t.color,lineHeight:1}}>{t.count}</div>
                <div style={{fontSize:9,color:C.slate,letterSpacing:1.5,textTransform:"uppercase",margin:"4px 0"}}>{t.label}</div>
                <div style={{fontSize:11,color:C.creamD}}>{t.price} · <span style={{color:t.color}}>${t.total}</span></div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:10,padding:20,textAlign:"center",marginBottom:24}}>
            <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:6}}>Total Estimated Revenue</div>
            <div style={{fontFamily:"serif",fontSize:44,fontWeight:900,color:C.goldL}}>
              ${members.reduce((sum,m)=>{
                if(m.role==="breeder"||m.role==="admin") return sum+29;
                if(m.role==="buyer_full") return sum+97;
                if(m.role==="buyer_bundle") return sum+47;
                if(m.role==="buyer_single") return sum+27;
                return sum;
              },0).toLocaleString()}
            </div>
            <div style={{fontSize:12,color:C.slate,marginTop:4}}>{members.filter(m=>m.role!=="public").length} paying members</div>
          </div>

          {/* Member breakdown by tier */}
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:20}}>
            <div style={{fontSize:10,color:C.gold,letterSpacing:3,textTransform:"uppercase",marginBottom:14}}>Member Breakdown by Tier</div>
            {[
              {role:"admin",label:"Admin / Founder",color:C.goldL},
              {role:"breeder",label:"Foundation Breeder ($29/mo)",color:C.emeraldL},
              {role:"buyer_full",label:"Legacy Full Access ($97/yr)",color:C.sapphireL},
              {role:"buyer_bundle",label:"Pedigree Bundle ($47)",color:C.goldL},
              {role:"buyer_single",label:"Single Pedigree ($27)",color:C.amber},
              {role:"public",label:"Free Account",color:C.slate},
              {role:"blocked",label:"Blocked",color:C.red},
            ].map(tier=>{
              const tierMembers = members.filter(m=>m.role===tier.role);
              if(tierMembers.length===0) return null;
              return(
                <div key={tier.role} style={{marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:12,color:tier.color,fontWeight:600}}>{tier.label}</div>
                    <div style={{fontSize:11,color:C.slate}}>{tierMembers.length} member{tierMembers.length!==1?"s":""}</div>
                  </div>
                  {tierMembers.map(m=>(
                    <div key={m.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 10px",background:C.bg3,borderRadius:5,marginBottom:4}}>
                      <span style={{fontSize:12,color:C.creamD}}>{m.email}</span>
                      <span style={{fontSize:11,color:C.slate}}>{m.created_at?new Date(m.created_at).toLocaleDateString():"Unknown"}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            {members.length===0&&<div style={{textAlign:"center",color:C.slate,fontSize:13,padding:20}}>No members yet. Load Members tab first.</div>}
          </div>
        </div>
      )}
      {adminTab==="edits"&&(
        <PendingEditsQueue
          myDogs={[]}
          breeders={data.breeders}
          supabase={supabase}
          flash={flash}
          onCountChange={()=>{}}
          isAdmin={true}
          allDogs={data.dogs}
        />
      )}

      {adminTab==="upr"&&(()=>{
        const pending  = uprQueue.filter(r=>r.status==='pending');
        const approved = uprQueue.filter(r=>r.status==='approved');
        const rejected = uprQueue.filter(r=>r.status==='rejected');
        // Spot-check lens, not a replacement for "Approved" — OCR-auto-approved rows
        // still show there too. This exists so a systematic OCR misread (e.g. one
        // club's certificate format consistently confusing the model) surfaces
        // before it silently approves many bad records, not because of distrust in
        // the per-registration logic itself.
        const THIRTY_DAYS_MS = 30*24*60*60*1000;
        const autoApproved = uprQueue.filter(r=>r.ocr_check_result==='auto_approved' &&
          r.created_at && (Date.now()-new Date(r.created_at).getTime())<=THIRTY_DAYS_MS);
        const lists    = {pending,approved,rejected,autoApproved};
        const counts   = {pending:pending.length,approved:approved.length,rejected:rejected.length,autoApproved:autoApproved.length};
        const rows     = lists[uprSubTab]||[];
        return(
          <div>
            <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:`1px solid ${C.border}`,flexWrap:'wrap'}}>
              {[['pending','Pending'],['approved','Approved'],['rejected','Rejected'],['autoApproved','Auto-Approved (30d)']].map(([v,l])=>(
                <button key={v} onClick={()=>setUprSubTab(v)}
                  style={{padding:'6px 16px',background:'transparent',border:'none',borderBottom:`2px solid ${uprSubTab===v?C.gold:'transparent'}`,color:uprSubTab===v?C.gold:C.slate,fontSize:13,cursor:'pointer',fontWeight:uprSubTab===v?700:400,marginBottom:-1}}>
                  {l}{counts[v]>0?` (${counts[v]})`:''}
                </button>
              ))}
              <button className="bgs" style={{fontSize:11,marginLeft:'auto',alignSelf:'center'}} onClick={loadUprQueue}>↻ Refresh</button>
            </div>
            {rows.length===0&&<div style={{textAlign:'center',color:C.slate,fontSize:13,padding:'40px 0'}}>No {uprSubTab} applications.</div>}
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {rows.map(r=>{
                const dog=r.dogs||{}; const breeder=r.breeders||{};
                const isTargeted=rejectTarget?.regId===r.id;
                return(
                  <div key={r.id}>
                    <div style={{background:C.card,border:`1px solid ${r.status==='approved'?C.gold+'44':r.status==='rejected'?'#c0392b44':C.border}`,borderRadius:isTargeted?'8px 8px 0 0':8,padding:14,display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-start'}}>
                      <div style={{flex:1,minWidth:220}}>
                        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4,flexWrap:'wrap'}}>
                          <span style={{fontSize:14,fontWeight:700,color:C.cream}}>{dog.name||'Unknown Dog'}</span>
                          <span style={{fontSize:9,color:dog.type==='heritage'?C.sapphireL:C.goldL,background:dog.type==='heritage'?`${C.sapphireL}22`:`${C.gold}18`,border:`1px solid ${dog.type==='heritage'?C.sapphireL+'44':C.gold+'33'}`,borderRadius:8,padding:'1px 7px'}}>{dog.type||'purebred'}</span>
                          {r.status==='approved'&&<span style={{fontSize:12,color:C.gold,fontWeight:700}}>{r.upr_number}</span>}
                        </div>
                        <div style={{fontSize:11,color:C.slate,marginBottom:2}}>{dog.breed}</div>
                        <div style={{fontSize:11,color:C.slate,marginBottom:2}}>{breeder.kennel?`${breeder.kennel} · `:''}{breeder.name||''}{breeder.email?` · ${breeder.email}`:''}</div>
                        {r.club_name&&<div style={{fontSize:11,color:C.slate,marginBottom:2}}>{r.club_name}{r.club_registration_number?` · ${r.club_registration_number}`:''}</div>}
                        {dog.type==='heritage'&&!r.club_registration_number&&<div style={{fontSize:11,color:C.slate,marginBottom:2}}>Designer — parent registrations on file</div>}
                        {r.ocr_check_result&&(()=>{
                          const OCR_LABELS = {
                            auto_approved:          ['OCR: Auto-Approved ✓', C.emeraldL],
                            reg_num_mismatch:        ['OCR: Reg # mismatch', '#e74c3c'],
                            name_mismatch:           ['OCR: Name mismatch', '#e74c3c'],
                            breed_mismatch:          ['OCR: Breed mismatch', '#e74c3c'],
                            on_file_reg_num_blank:   ['OCR: No reg # on file to verify against', '#e67e22'],
                            no_reg_num_to_verify:    ['OCR: Neither parent has a reg # to verify (UPR-approval based)', '#e67e22'],
                            not_a_certificate:       ["OCR: Document doesn't look like a certificate", '#e67e22'],
                            ocr_error:               ['OCR: Extraction failed', '#e67e22'],
                          };
                          const [label,color] = OCR_LABELS[r.ocr_check_result]||[`OCR: ${r.ocr_check_result}`,C.slate];
                          return <div style={{fontSize:10,color,marginBottom:2,fontWeight:600}}>{label}</div>;
                        })()}
                        <div style={{display:'flex',gap:12,marginTop:4,flexWrap:'wrap'}}>
                          <span style={{fontSize:10,color:C.slate}}>Pedigree depth: {r.pedigree_depth} gen</span>
                          <span style={{fontSize:10,color:C.slate}}>{r.created_at?new Date(r.created_at).toLocaleDateString():'—'}</span>
                          {r.reviewed_at&&<span style={{fontSize:10,color:C.slate}}>Reviewed {new Date(r.reviewed_at).toLocaleDateString()}{r.reviewed_by?` by ${r.reviewed_by}`:''}</span>}
                        </div>
                        {r.admin_notes&&<div style={{fontSize:11,color:'#e67e22',marginTop:4}}>Reason: {r.admin_notes}</div>}
                      </div>
                      <div style={{display:'flex',gap:6,flexShrink:0,flexWrap:'wrap'}}>
                        {r.document_url&&<button className="bgs" style={{fontSize:11}} onClick={()=>openDocUrl(r.document_url)}>View Doc</button>}
                        {r.status==='pending'&&(
                          <>
                            <button className="bgl" style={{fontSize:11}} onClick={()=>approveReg(r.id)}>Approve</button>
                            <button style={{fontSize:11,color:'#e74c3c',border:'1px solid #c0392b44',background:'transparent',borderRadius:5,padding:'5px 12px',cursor:'pointer'}}
                              onClick={()=>{setRejectTarget({regId:r.id,dogName:dog.name||'this dog'});setRejectReason('');}}>Reject</button>
                          </>
                        )}
                      </div>
                    </div>
                    {isTargeted&&(
                      <div style={{background:C.bg3,border:'1px solid #c0392b44',borderTop:'none',borderRadius:'0 0 8px 8px',padding:14}}>
                        <div style={{fontSize:12,color:C.cream,marginBottom:8}}>Rejection reason for <strong>{rejectTarget.dogName}</strong> (required):</div>
                        <textarea className="fi" rows={2} value={rejectReason} onChange={e=>setRejectReason(e.target.value)}
                          placeholder="e.g. Pedigree incomplete — grandparents missing on dam side"
                          style={{width:'100%',resize:'vertical',marginBottom:10,boxSizing:'border-box'}}/>
                        <div style={{display:'flex',gap:8}}>
                          <button onClick={()=>rejectReg(r.id,rejectReason)}
                            disabled={!rejectReason.trim()}
                            style={{fontSize:11,opacity:rejectReason.trim()?1:0.4,color:'#e74c3c',border:'1px solid #c0392b44',background:'transparent',borderRadius:5,padding:'5px 12px',cursor:rejectReason.trim()?'pointer':'default'}}>
                            Confirm Reject
                          </button>
                          <button className="bgs" style={{fontSize:11}} onClick={()=>{setRejectTarget(null);setRejectReason('');}}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {adminTab==="health"&&(
        <HealthPanelsAdmin breeds={breeds} designerBreeds={designerBreeds} setDesignerBreeds={setDesignerBreeds}
          customCrosses={customCrosses}
          healthRequirements={healthRequirements} setHealthRequirements={setHealthRequirements}
          dogs={dogs} data={data} setData={setData} supabase={supabase} flash={flash}/>
      )}

      {inlineDog && <InlineDogModal {...inlineDog} onClose={()=>setInlineDog(null)} breeders={data.breeders} setData={setData} supabase={supabase} flash={flash}/>}
      {inlineKennel && <InlineKennelModal initialName={inlineKennel.initialName} onSave={inlineKennel.onSave} onClose={()=>setInlineKennel(null)} setData={setData} supabase={supabase} flash={flash}/>}
    </div>
  );
}

// ── TERMS OF SERVICE ─────────────────────────────────────────────────────────
function TermsOfService({onBack}){
  return(
    <div style={{maxWidth:860,margin:"0 auto"}}>
      <button className="bgs" onClick={onBack} style={{marginBottom:24}}>← Back</button>
      <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:12,padding:"40px 48px"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:28,marginBottom:8}}>🐾</div>
          <div style={{fontFamily:"serif",fontSize:28,fontWeight:900,color:C.goldXL}}>Terms of Service</div>
          <div style={{fontSize:12,color:C.slate,marginTop:4}}>United Pedigree Registry · Effective May 1, 2026</div>
          <div style={{width:80,height:1,background:C.gold,margin:"16px auto 0"}}/>
        </div>

        {[
          ["1. Acceptance of Terms",
           "By accessing or using the United Pedigree Registry ('UPR', 'we', 'us', or 'our') website at unitedpedigreeregistry.com, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services. UPR is operated by 3R Ministries / TAGA Ranch, Coon Rapids, Iowa, United States."],
          ["2. Description of Service",
           "United Pedigree Registry is an independent global dog registry providing pedigree documentation, health record tracking, breeder profiles, and litter listings for all dog breeds worldwide. UPR is not affiliated with, endorsed by, or a substitute for AKC, ASCA, UKC, or any other official breed registry. Certificates issued by UPR are based solely on information provided by registered breeders and members."],
          ["3. User Accounts",
           "You must create an account to access certain features. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate, current, and complete information. UPR reserves the right to suspend or terminate accounts that violate these terms."],
          ["4. Paid Services & Refunds",
           "UPR offers paid membership tiers and pedigree access credits processed through Stripe. All sales are final. No refunds will be issued except at the sole discretion of UPR management. Pedigree credits do not expire. By purchasing, you authorize UPR to charge your payment method for the selected service."],
          ["5. Intellectual Property & Copyright",
           "All content on this website — including but not limited to the UPR name, logo, pedigree certificate design, software, text, graphics, and the database structure — is the exclusive intellectual property of United Pedigree Registry / 3R Ministries / TAGA Ranch. All rights reserved. Copyright © 2026 United Pedigree Registry. Unauthorized reproduction, distribution, modification, or use of any content without express written permission is strictly prohibited."],
          ["6. Breeder Responsibilities",
           "Registered breeders are solely responsible for the accuracy of all information submitted including registration numbers, health testing results, DNA numbers, parentage, and photos. UPR does not independently verify submitted information. Breeders agree not to submit false, misleading, or fraudulent data. UPR reserves the right to remove any listing at its sole discretion."],
          ["7. Prohibited Uses",
           "You may not use UPR to: (a) submit false or fraudulent pedigree information; (b) scrape, copy, or redistribute database content; (c) attempt to gain unauthorized access to any part of the system; (d) harass other users; (e) violate any applicable law or regulation. Violations may result in immediate account termination without refund."],
          ["8. Disclaimer of Warranties",
           "UPR is provided 'as is' without warranties of any kind, express or implied. We do not warrant that pedigree information is accurate, complete, or free from error. We do not guarantee uninterrupted service availability. Use of UPR is at your own risk."],
          ["9. Limitation of Liability",
           "To the maximum extent permitted by law, UPR, 3R Ministries, and TAGA Ranch shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service, including but not limited to loss of profits, data, or goodwill."],
          ["10. Governing Law",
           "These Terms shall be governed by the laws of the State of Iowa, United States, without regard to conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of Carroll County, Iowa."],
          ["11. Faith-Based Values",
           "United Pedigree Registry operates under the values of 3R Ministries, a faith-based organization. We reserve the right to decline service to any individual or entity whose activities conflict with our organizational values, including but not limited to animal cruelty, fraudulent breeding practices, or unethical treatment of animals."],
          ["12. Changes to Terms",
           "UPR reserves the right to modify these Terms at any time. Changes will be posted on this page with an updated effective date. Continued use of UPR after changes constitutes acceptance of the new Terms."],
          ["13. Contact",
           "For questions about these Terms, contact us at: United Pedigree Registry / 3R Ministries · TAGA Ranch · Coon Rapids, Iowa · unitedpedigreeregistry.com"],
        ].map(([title, body])=>(
          <div key={title} style={{marginBottom:24}}>
            <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,color:C.goldL,marginBottom:6}}>{title}</div>
            <div style={{fontSize:14,color:C.creamD,lineHeight:1.8}}>{body}</div>
          </div>
        ))}

        <div style={{marginTop:32,padding:"20px",background:C.bg3,borderRadius:8,textAlign:"center",border:`1px solid ${C.gold}33`}}>
          <div style={{fontSize:12,color:C.gold,letterSpacing:1}}>© 2026 United Pedigree Registry · All Rights Reserved</div>
          <div style={{fontSize:11,color:C.slate,marginTop:4}}>3R Ministries / TAGA Ranch · Coon Rapids, Iowa · Preserving Every Breed's Heritage</div>
        </div>
      </div>
    </div>
  );
}

// ── PRIVACY POLICY ────────────────────────────────────────────────────────────
function PrivacyPolicy({onBack}){
  return(
    <div style={{maxWidth:860,margin:"0 auto"}}>
      <button className="bgs" onClick={onBack} style={{marginBottom:24}}>← Back</button>
      <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:12,padding:"40px 48px"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:28,marginBottom:8}}>🔒</div>
          <div style={{fontFamily:"serif",fontSize:28,fontWeight:900,color:C.goldXL}}>Privacy Policy</div>
          <div style={{fontSize:12,color:C.slate,marginTop:4}}>United Pedigree Registry · Effective May 1, 2026</div>
          <div style={{width:80,height:1,background:C.gold,margin:"16px auto 0"}}/>
        </div>

        {[
          ["1. Who We Are",
           "United Pedigree Registry ('UPR') is operated by 3R Ministries / TAGA Ranch, Coon Rapids, Iowa, USA. We are committed to protecting your privacy and handling your data with integrity and transparency."],
          ["2. Information We Collect",
           "We collect information you provide directly: name, email address, payment information (processed securely by Stripe — we never store card numbers), kennel information, dog registration data, health records, and photos. We also collect usage data including IP addresses, browser type, and pages visited through standard web analytics."],
          ["3. How We Use Your Information",
           "We use your information to: provide and improve our registry services; process payments; display your kennel profile and dog listings publicly (as you choose); send account-related communications; and maintain the integrity of the registry database. We do not sell your personal information to third parties. Ever."],
          ["4. Information Shared Publicly",
           "Breeder profiles, kennel names, dog pedigrees, and health records you register are displayed publicly on UPR as part of the registry service. You control what information you enter. Dog listings marked as 'hidden' are not visible to the public."],
          ["5. Payment Processing",
           "All payments are processed by Stripe, Inc. UPR does not store credit card numbers or full payment details. Stripe's privacy policy applies to payment data. We receive confirmation of successful payments and your email address to activate your membership."],
          ["6. Data Storage & Security",
           "Your data is stored securely using Supabase (PostgreSQL database with row-level security) hosted on secure cloud infrastructure. We implement industry-standard security measures including encryption, access controls, and secure HTTPS connections. While we take security seriously, no system is 100% impenetrable."],
          ["7. Cookies",
           "UPR uses essential cookies for authentication and session management. We do not use advertising cookies or tracking pixels. We do not run ads on UPR products."],
          ["8. Your Rights",
           "You have the right to: access your personal data; correct inaccurate data; request deletion of your account and associated data; export your dog records. To exercise these rights, contact us at unitedpedigreeregistry.com. We will respond within 30 days."],
          ["9. Children's Privacy",
           "UPR is not intended for use by children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us personal information, please contact us immediately."],
          ["10. International Users",
           "UPR is operated from the United States. If you access UPR from outside the US, your information may be transferred to and processed in the United States. By using UPR, you consent to this transfer."],
          ["11. Third-Party Services",
           "UPR uses the following third-party services: Stripe (payments), Supabase (database), Vercel (hosting). Each has their own privacy policies. We are not responsible for third-party privacy practices."],
          ["12. Changes to This Policy",
           "We may update this Privacy Policy periodically. We will notify registered users of significant changes via email. Continued use of UPR after changes constitutes acceptance."],
          ["13. Contact Us",
           "Privacy questions or requests: United Pedigree Registry / 3R Ministries · TAGA Ranch · Coon Rapids, Iowa · unitedpedigreeregistry.com"],
        ].map(([title, body])=>(
          <div key={title} style={{marginBottom:24}}>
            <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,color:C.goldL,marginBottom:6}}>{title}</div>
            <div style={{fontSize:14,color:C.creamD,lineHeight:1.8}}>{body}</div>
          </div>
        ))}

        <div style={{marginTop:32,padding:"20px",background:C.bg3,borderRadius:8,textAlign:"center",border:`1px solid ${C.gold}33`}}>
          <div style={{fontSize:12,color:C.gold,letterSpacing:1}}>© 2026 United Pedigree Registry · All Rights Reserved</div>
          <div style={{fontSize:11,color:C.slate,marginTop:4}}>We will never sell your data · No ads · Ever</div>
        </div>
      </div>
    </div>
  );
}

// ── COPYRIGHT NOTICE ──────────────────────────────────────────────────────────
function CopyrightNotice({onBack}){
  return(
    <div style={{maxWidth:860,margin:"0 auto"}}>
      <button className="bgs" onClick={onBack} style={{marginBottom:24}}>← Back</button>
      <div style={{background:C.card,border:`1px solid ${C.gold}44`,borderRadius:12,padding:"40px 48px"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:28,marginBottom:8}}>⚖️</div>
          <div style={{fontFamily:"serif",fontSize:28,fontWeight:900,color:C.goldXL}}>Copyright & IP Notice</div>
          <div style={{fontSize:12,color:C.slate,marginTop:4}}>United Pedigree Registry · All Rights Reserved</div>
          <div style={{width:80,height:1,background:C.gold,margin:"16px auto 0"}}/>
        </div>

        <div style={{background:C.bg3,border:`2px solid ${C.gold}`,borderRadius:8,padding:24,marginBottom:32,textAlign:"center"}}>
          <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.goldXL,marginBottom:8}}>
            © 2026 United Pedigree Registry
          </div>
          <div style={{fontSize:13,color:C.creamD,lineHeight:1.8}}>
            All content, design, software, database structure, pedigree certificate design,<br/>
            logo, and brand identity are the exclusive intellectual property of<br/>
            <strong style={{color:C.goldL}}>United Pedigree Registry / 3R Ministries / TAGA Ranch</strong><br/>
            Coon Rapids, Iowa, United States<br/>
            All Rights Reserved Worldwide.
          </div>
        </div>

        {[
          ["What Is Protected",
           "The following are protected by U.S. and international copyright, trademark, and intellectual property law: the United Pedigree Registry name and brand; the UPR pedigree certificate design and layout; the UPR logo and paw print mark; all website content, text, and graphics; the database architecture and software code; the TAGA Method name and associated educational content; and all original creative works produced by UPR and 3R Ministries."],
          ["What You May Not Do",
           "Without express written permission from UPR, you may not: reproduce, copy, or redistribute any UPR content; create derivative works based on UPR designs or software; scrape or bulk download registry data; use the UPR name, logo, or branding in any way; claim ownership of pedigrees or certificates generated by UPR; or use UPR content for commercial purposes."],
          ["What Breeders Retain",
           "Breeders and members retain ownership of their own dog photos, kennel descriptions, and personal information they submit. By submitting content to UPR, you grant UPR a non-exclusive license to display that content as part of the registry service. You represent that you own or have rights to all content you submit."],
          ["Pedigree Certificates",
           "UPR-issued pedigree certificates are official documents of United Pedigree Registry. The certificate design, layout, and UPR branding are protected intellectual property. Certificates may be shared by the dog's owner for legitimate breeding and ownership purposes but may not be altered, forged, or misrepresented."],
          ["DMCA & Infringement",
           "If you believe your intellectual property has been infringed on UPR, please contact us immediately at unitedpedigreeregistry.com with details of the alleged infringement. We take IP violations seriously and will respond promptly."],
          ["Faith-Based Foundation",
           "United Pedigree Registry is built on the foundation of 3R Ministries, ordained through SMOCH and the Church of Hope. Our work is guided by principles of integrity, stewardship, and excellence. We believe responsible breeding honors the Creator's design in every breed and every line."],
        ].map(([title, body])=>(
          <div key={title} style={{marginBottom:24}}>
            <div style={{fontFamily:"serif",fontSize:16,fontWeight:700,color:C.goldL,marginBottom:6}}>{title}</div>
            <div style={{fontSize:14,color:C.creamD,lineHeight:1.8}}>{body}</div>
          </div>
        ))}

        <div style={{marginTop:32,padding:"20px",background:C.bg3,borderRadius:8,textAlign:"center",border:`1px solid ${C.gold}33`}}>
          <div style={{fontSize:13,color:C.goldXL,fontFamily:"serif",fontStyle:"italic",marginBottom:8}}>
            "Preserving Every Breed's Heritage and Promoting Responsible Breeding Worldwide"
          </div>
          <div style={{fontSize:12,color:C.gold,letterSpacing:1}}>© 2026 United Pedigree Registry · 3R Ministries · TAGA Ranch</div>
          <div style={{fontSize:11,color:C.slate,marginTop:4}}>Coon Rapids, Iowa · All Rights Reserved Worldwide</div>
        </div>
      </div>
    </div>
  );
}
// ── PAYMENT MODAL ─────────────────────────────────────────────────────────────
function PaymentModal({item, price, stripeUrl, onClose}){
  return(
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.card,border:`1px solid ${C.gold}66`,borderRadius:12,padding:32,maxWidth:480,width:"100%"}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:32,marginBottom:8}}>🙏</div>
          <div style={{fontFamily:"serif",fontSize:20,fontWeight:700,color:C.goldXL,marginBottom:4}}>{item}</div>
          <div style={{fontSize:24,fontWeight:700,color:C.goldL,marginBottom:16}}>{price}</div>
        </div>
        <div style={{background:C.bg3,border:`1px solid ${C.gold}33`,borderRadius:8,padding:16,marginBottom:20,fontSize:13,color:C.creamD,lineHeight:1.8,textAlign:"center"}}>
          This is a ministry-supported service. Your payment is processed as a donation that supports our work while granting access to this offering. A small processing fee is added for secure payment handling. Donations are non-refundable.
        </div>
        <div style={{display:"flex",gap:10}}>
          <button className="bgs" style={{flex:1}} onClick={onClose}>Cancel</button>
          <a href={stripeUrl} target="_blank" rel="noreferrer" style={{flex:2,display:"block",textAlign:"center",background:`linear-gradient(135deg,${C.gold},${C.goldL})`,color:C.bg,fontWeight:700,fontSize:14,textDecoration:"none",padding:"12px 0",borderRadius:6}} onClick={onClose}>
            Continue to Payment →
          </a>
        </div>
        <div style={{textAlign:"center",marginTop:12,fontSize:11,color:C.slate}}>Secured by Stripe · unitedpedigreeregistry.com · 3R Ministries</div>
      </div>
    </div>
  );
}
// ── SMALL COMPONENTS ──────────────────────────────────────────────────────────
const MetaRow=({l,v,vc})=><div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:10,color:C.slate}}>{l}</span><span style={{fontSize:10,color:vc||C.creamD,fontWeight:500}}>{v}</span></div>;
const MetaCol=({l,v,vc})=><div><div style={{fontSize:9,color:C.slate,textTransform:"uppercase",letterSpacing:0.5}}>{l}</div><div style={{fontSize:13,color:vc||C.cream,fontWeight:500}}>{v}</div></div>;

